import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { ANONYMOUS_CHAT_NOTICE } from '../config/loginConsent';
import { PARTS_FLOW_ENABLED } from '../config/features';
import { renderWithPolicyLinks } from './PhoneAuth';
import { relabelReport } from '../utils/displayText';
import { useSearchParams, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
    getApiBaseUrl,
    getAuthenticatedChatSessionId,
    getOrCreateAnonymousId,
    getOrCreateAnonymousSessionId,
    refreshAuthenticatedSessionActivity,
    refreshAnonymousSessionActivity,
    getTesterModeHeaders,
    getUserDetailsBaseUrl,
    eraseAllSessionData,
    FRESH_CHAT_RESET_EVENT,
    FRESH_CHAT_RESET_STORAGE_KEY,
    ADD_VEHICLE_TAB_ID,
    CLOSE_ADD_VEHICLE_TAB_EVENT,
    SESSION_EXPIRED_MESSAGE,
} from '../utils/chatIdentity';
import {
    buildPartsCards as buildPartsCardsFrom,
    mergePartsData,
    stripRetailerLinkProse,
} from '../utils/partsSources';
// #711: infographic intro disabled — new visitors land directly in chat.
// import OnboardingBanner from './OnboardingPage';
// import { isOnboardingSeen, setOnboardingSeen } from '../utils/chatIdentity';
import { looksLikePromoCode, promoReplyText, setPendingPromoCode } from '../utils/promo';
import { markPwaEngagement } from '../utils/pwaInstall';
import {
    conversationLooksNonDiagnostic,
    createDiagnosticReport,
    createMaintenanceReport,
    extractMaintenanceTitleFromMessages,
    getDiagnosticStartIndex,
    getPreReportDiagnosticContent,
    isDiagnosticFinalizedMessage,
    isDiagnosticReportMessage,
    isReportCommand,
    isReportReadyPromptMessage,
    isRepairActionSuggestion,
    isRepairGuidanceContent,
    isReportChoiceText,
    loadDiagnosticReport,
    normalizeDiagnosticReportForUi,
    removeReportChoicePrompt,
    saveDiagnosticReport,
} from '../utils/diagnosticReport';
import HandsFreeVoiceButton from './HandsFreeVoiceButton';
import { useHandsFreeVoice } from '../hooks/useHandsFreeVoice';
import useAutoScroll from '../hooks/useAutoScroll';
import usePartnerRequestUpdates from '../hooks/usePartnerRequestUpdates';
import DashAvatar from './chat/DashAvatar';
import AudioMessage from './chat/AudioMessage';
import ScrollToBottomButton from './chat/ScrollToBottomButton';
import AppointmentSlotPicker from './AppointmentSlotPicker';
import {
    ConfirmedCauseCard as CompactConfirmedCauseCard,
    EvidenceCaptureCard as CompactEvidenceCaptureCard,
    GuidedCardSkeleton,
    GuidedCheckResultCard as CompactGuidedCheckResultCard,
    GuidedCheckSavedResultCard,
    GuidedCheckStepCard as CompactGuidedCheckStepCard,
    GuidedStepSelectionCard,
    NextLikelyCauseCard as CompactNextLikelyCauseCard,
    RepairSafetyChecklistCard as CompactRepairSafetyChecklistCard,
    RepairGuideStepperCard as CompactRepairGuideStepperCard,
    RepairOutcomeCard as CompactRepairOutcomeCard,
    RepairLogChoiceCard as CompactRepairLogChoiceCard,
    RepairLogFormCard as CompactRepairLogFormCard,
    RepairLoggedCard as CompactRepairLoggedCard,
} from './chat/GuidedDiagnosticCards';
import {
    InlineDiagnosticReportCard,
} from './chat/DiagnosticReportCards';
import ShopReadyReportCard from './chat/ShopReadyReportCard';
import {
    cleanInternalTags,
    extractServiceCard,
    extractFollowupCard,
    extractSuggestionsAndContent,
    getMessageContent,
    getMessageSuggestions,
    normalizeStatusMessage,
} from '../utils/chatMessageParsing';
import { tryEnableWebPushQuietly } from '../utils/webPush';
import { announceTicketTitle } from '../utils/ticketTitleEvents';
import { FUEL_TYPE_OPTIONS, VIN_LOCKED_NOTE, applyVinSpecs, getVinError, lookupVin, replacedNote } from '../utils/vehicleSpecs';
import {
    GARAGE_RESPONSE_SUGGESTIONS,
    isGarageVehicleListResponse,
} from '../utils/chatSuggestions';
import {
    isAddVehicleFormRequest,
    parseVehicleFromAddRequest,
    extractDecodedVinPrefill,
    isSaveDecodedVehicleReply,
    makeForModel,
    VIN_TOKEN_PATTERN,
} from '../utils/addVehicleIntent';
import {
    buildPartnerGoogleCalendarUrl,
    createPartnerRepairRequest,
    createMaintenanceEstimateTicket,
    formatPartnerMoney,
    formatPartnerScheduledAppointment,
    getPartnerPreferredAppointment,
    getPartnerStatusLabel,
    listPartnerShops,
    listPartnerRepairRequests,
    schedulePartnerRepairRequest,
    sendPartnerRepairRequestMessage,
    updatePartnerEstimateLineAuthorizations,
    updatePartnerRepairRequestStatus,
} from '../utils/partnerShopApi';
import {
    buildPartnerRequestConversationMessages,
} from '../utils/partnerRequestDisplay';
import { resolveMaintenanceServiceKey } from '../utils/maintenanceSchedule';
import { formatMileage, toMiles, unitLabel as distanceUnitLabel, MAX_ODOMETER_MILES, ODOMETER_TOO_HIGH_MESSAGE } from '../utils/distance';

// const WHATSAPP_URL = 'https://wa.me/+19086566404?text=Hi%20Dash%2C%20I%20need%20help%20with%20my%20car';

// ── Internal tag cleanup helper ───────────────────────────────────────────────
// Strips all backend-internal action tags and markers that should never be
// shown to the user as raw text. Applied in fetchHistory, done event, and
// error handler.
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE_URL = getApiBaseUrl();
const CHAT_API_BASE_URL = `${API_BASE_URL}/api/chat`;
const PENDING_CHAT_INTENT_STORAGE_KEY = 'dash_pending_chat_intent';
const CHAT_REPORT_STORAGE_PREFIX = 'dash_chat_diagnostic_reports:';
const GUIDED_CHECK_STORAGE_PREFIX = 'dash_guided_check_session:';
const GUIDED_CHECK_LAST_STORAGE_KEY = `${GUIDED_CHECK_STORAGE_PREFIX}last`;
const VISIBLE_CHAT_SNAPSHOT_PREFIX = 'dash_visible_chat_snapshot:';
const CHAT_SCROLL_STATE_PREFIX = 'dash_chat_scroll_state:';

// ONE-TIME CACHE PURGE. Earlier chat-bleed bugs wrote cross-thread guided sessions, report cards,
// and visible-chat snapshots into localStorage. Those persist across code fixes and refreshes and
// keep re-injecting a PREVIOUS chat's DIY steps into the current chat. Wipe those cache families
// once per client (bump the version to force a fresh purge). Server chat history is untouched, so
// existing conversations reload their real messages; only the leaked frontend caches are cleared.
const CHAT_CACHE_PURGE_KEY = 'dash_chat_cache_purge_version';
const CHAT_CACHE_PURGE_VERSION = '2026-08-19-guided-bleed';
(function purgeContaminatedChatCachesOnce() {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        if (localStorage.getItem(CHAT_CACHE_PURGE_KEY) === CHAT_CACHE_PURGE_VERSION) return;
        const prefixes = [CHAT_REPORT_STORAGE_PREFIX, GUIDED_CHECK_STORAGE_PREFIX, VISIBLE_CHAT_SNAPSHOT_PREFIX];
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key && prefixes.some((p) => key.startsWith(p))) toRemove.push(key);
        }
        toRemove.forEach((k) => localStorage.removeItem(k));
        localStorage.setItem(CHAT_CACHE_PURGE_KEY, CHAT_CACHE_PURGE_VERSION);
    } catch (_) { /* ignore storage errors */ }
})();

function getReportDiagnosticTicketId(report = {}) {
    return (
        report.diagnosticTicketId ||
        report.diagnostic_ticket_id ||
        report.ticketId ||
        report.ticket_id ||
        report.diagnostic_ticket?.diagnostic_ticket_id ||
        report.diagnosticTicket?.diagnostic_ticket_id ||
        report.diagnosticTicket?.diagnosticTicketId ||
        report.ticket?.diagnostic_ticket_id ||
        report.ticket?.diagnosticTicketId ||
        null
    );
}
const VISIBLE_CHAT_SNAPSHOT_LIMIT = 140;
const CHAT_HISTORY_PAGE_SIZE = 20;
const INITIAL_CHAT_HISTORY_LIMIT = 500;
const ATTACHMENT_MARKER_PREFIX = '__ATTACHMENTS__:';
const STORED_SHOPS_PER_RESULT_LIMIT = 20;
// Report generation is CONFIDENCE-DRIVEN — keep in sync with the backend diagnostic flow
// (supervisor_agent.py / diagnostic_agent.py): the backend declares the report "ready" ONLY
// when its confidence in the single most likely cause is >= 90% (accuracy first), with an
// evidence floor of 3 answers and a hard cap of 7 turns. The frontend must FOLLOW that ready
// signal — it must NOT force a report on a fixed turn count, or it would finalize a
// low-confidence diagnosis. So: generate when (backend says ready AND >= floor answers), or
// at the hard cap; otherwise keep asking.
const REPORT_MIN_FLOOR_TURNS = 3;  // evidence floor — never generate before this many REAL answers
const MAX_DIAGNOSTIC_USER_TURNS_BEFORE_FORCED_REPORT = 7;  // hard cap — backend declares ready here regardless
const STREAM_IDLE_TIMEOUT_MS = 45000;
// Cap how long we wait for the backend to build the report before falling back to a
// client-built one, so the skeleton never spins forever.
const REPORT_FETCH_TIMEOUT_MS = 30000;

let streamIdleTimer = null;

const safeText = (value = '') => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
        return (
            value.title ||
            value.name ||
            value.label ||
            value.instruction ||
            value.description ||
            value.text ||
            ''
        );
    }
    return String(value);
};

const cleanDisplayText = (value = '') => safeText(value).replace(/\s+/g, ' ').trim();
const truncateForStorage = (value = '', max = 240) => cleanDisplayText(value).slice(0, max);

const PARTS_CONTEXT_PATTERN = /\b(find|buy|search|look(?:ing)? for|need|get|order|source|price|replace)\b.{0,60}\b(part|parts|tool|tools|oem|aftermarket|replacement)\b|\b(part|parts|tools?)\b.{0,60}\b(for|fit|fits|buy|price|link|links)\b|\b(best|recommend(?:ed|ation)?|which|what|good|top|suggest)\b.{0,40}\b(kit|product|brand|polish|cleaner|sealant|coating|compound|lubricant|additive|wax)\b|\b(restoration|repair|touch[- ]?up|cleaning|polish(?:ing)?|detailing)\s+kit\b/i;
const SHOP_CONTEXT_PATTERN = /\b(find|search|look(?:ing)? for|need|recommend|book|call)\b.{0,60}\b(shop|shops|mechanic|garage|dealer|dealership|service center|repair center)\b|\b(shop|shops|mechanic|garage|dealer|dealership|service center|repair center)\b.{0,40}\b(near me|nearby|local|around|location|locations)\b/i;
const DIAGNOSTIC_CONTEXT_PATTERN = /\b(diagnos|problem|issue|warning light|check engine|obd|dtc|fault code|won'?t|will not|can'?t|cannot|doesn'?t|not working|not start|no start|overheat|misfire|noise|leak|stuck|smell|vibration|shaking|squeal|grind)\b/i;
const EXPLICIT_DIAGNOSTIC_SWITCH_PATTERN = /\b(diagnos|problem|issue|warning light|check engine|obd|dtc|fault code|won'?t|will not|can'?t|cannot|doesn'?t|not working|not start|no start|overheat|misfire|noise|leak|stuck|smell|vibration|shaking|squeal|grind)\b/i;
const BARE_URL_PATTERN = /\bhttps?:\/\/[^\s<>()]+/g;
const GOOGLE_MAPS_URL_PATTERN = /\bhttps?:\/\/(?:www\.)?(?:google\.com\/maps|maps\.google\.com)\/[^\s<>()\]]+/gi;
const TRAILING_URL_PUNCTUATION_PATTERN = /[.,;:!?]+$/;

const linkifyBareUrls = (value = '') => {
    const text = safeText(value);
    if (!text) return '';

    return text.replace(BARE_URL_PATTERN, (match, offset, source) => {
        // Leave existing Markdown links alone: [label](https://example.com)
        if (source[offset - 1] === '(' && source[offset - 2] === ']') return match;

        const trailingPunctuation = match.match(TRAILING_URL_PUNCTUATION_PATTERN)?.[0] || '';
        const url = trailingPunctuation ? match.slice(0, -trailingPunctuation.length) : match;
        return `[${url}](${url})${trailingPunctuation}`;
    });
};

const markdownLinkComponents = {
    a: ({ href, children, ...props }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
};

const renderMarkdownWithLinks = (content = '') => (
    <ReactMarkdown components={markdownLinkComponents}>
        {linkifyBareUrls(content)}
    </ReactMarkdown>
);

const LINK_LIST_HEADING_PATTERN = /^\s*(?:#{1,6}\s*)?(?:\*\*)?(?:here (?:are|is)\s+)?(?:(?:buy|order(?:ing)?|shop(?:ping)?|source|sourcing)\s+links?(?:\s+for\s+[^:]+)?|parts?\s+sources?|sources?|available from these sources|repair shops?(?: near you)?|nearby repair shops?|nearby shops?|shops?|mechanics?|garages?)\s*:?(?:\*\*)?\s*$/i;
const RETAILER_LINK_LINE_PATTERN = /^\s*(?:[-*•]\s*)?(?:\*\*)?([^:\n]{2,60}?)(?:\*\*)?\s*:\s*(https?:\/\/[^\s|]+)/i;
const ANY_URL_PATTERN = /(https?:\/\/\S+)/i;
// searchParams.get() is case-SENSITIVE, so the retailer's exact spelling has to be listed:
// AutoZone uses searchText and NAPA uses text. Without them the card falls back to the URL
// path and shows "Searchresult" / "En Search" instead of what is being searched for.
const PART_QUERY_KEYS = [
    'searchText', 'searchtext', 'text',
    'query', 'q', 'k', 'keyword', 'keywords', 'search',
];

const cleanRetailerUrl = (value = '') => value
    .trim()
    .replace(/[),.;!?]+$/g, '');

const titleCaseWords = (value = '') => value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getHostFromUrl = (url = '') => {
    try {
        return new URL(url).hostname.replace(/^www\./i, '');
    } catch (_) {
        return '';
    }
};

const getPartQueryFromUrl = (url = '') => {
    try {
        const parsed = new URL(url);
        for (const key of PART_QUERY_KEYS) {
            const value = parsed.searchParams.get(key);
            if (value) return titleCaseWords(value.replace(/\+/g, ' '));
        }
        const pathText = decodeURIComponent(parsed.pathname || '')
            .replace(/\.(html?|php|aspx?)$/i, '')
            .split('/')
            .filter(Boolean)
            .slice(-3)
            .join(' ');
        return titleCaseWords(pathText);
    } catch (_) {
        return '';
    }
};

const getRetailerAccentClasses = (retailer = '') => {
    const normalized = retailer.toLowerCase();
    if (normalized.includes('autozone')) return 'bg-[#fff3e8] text-[#b64b00] border-[#ffd9b8]';
    if (normalized.includes('rockauto')) return 'bg-[#fff0f0] text-[#9e1f1f] border-[#ffd0d0]';
    if (normalized.includes('napa')) return 'bg-[#F9F9F9] text-[#414141] border-[#d9d9d9]';
    if (normalized.includes('amazon') || normalized.includes('ebay')) return 'bg-[#fff7e6] text-[#9a5a00] border-[#ffe4a6]';
    if (normalized.includes('nhtsa') || normalized.includes('recall')) return 'bg-[#F9F9F9] text-[#414141] border-[#d9d9d9]';
    if (normalized.includes('oem') || normalized.includes('dealer')) return 'bg-[#ecfff3] text-[#087a43] border-[#bdebcf]';
    return 'bg-[#F9F9F9] text-[#414141] border-[#d9d9d9]';
};

const getRetailerSourceLabel = (card = {}, type = 'parts') => {
    const label = `${card.retailer || ''} ${card.host || ''}`.toLowerCase();
    if (type === 'shops') return 'Map';
    if (label.includes('nhtsa') || label.includes('recall')) return 'Recall';
    if (label.includes('ebay') || label.includes('amazon')) return 'Marketplace';
    if (label.includes('oem') || label.includes('dealer')) return 'OEM';
    if (label.includes('rockauto')) return 'Catalog';
    return 'Retailer';
};

const LINK_LIST_VISIBLE_COUNT = 3;

const getLinkListCopy = (type = 'parts') => (
    type === 'shops'
        ? { eyebrow: 'Shop Links', fallback: 'Repair shop location' }
        : { eyebrow: 'Parts Sources', fallback: 'Matching replacement part' }
);

const formatShopConversationTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

const ShopConversationStatusNotice = ({ message }) => (
    <div className="flex w-full justify-center">
        <div className="flex flex-col items-center gap-0.5">
            <span className="rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-1.5 text-[12px] font-semibold text-[#414141]">
                {message.message_text}
            </span>
            {message.created_at && (
                <span className="text-[10px] font-bold text-[#9d9d9d]">
                    {formatShopConversationTime(message.created_at)}
                </span>
            )}
        </div>
    </div>
);

const getShopDeclineReason = (request = {}) => {
    const notes = String(request?.notes || '').trim();
    if (!notes || /^Customer requested an estimate/i.test(notes)) return '';
    return notes.replace(/^Decline reason:\s*/i, '').trim();
};

export const ShopConversationDeclineCard = ({ request = {}, message = {} }) => {
    const shopName = request?.partner_shop?.name || 'The shop';
    const reason = getShopDeclineReason(request);
    const [reasonLabel, ...detailParts] = reason.split(/\s+-\s+/);
    const detail = detailParts.join(' - ').trim();

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff1f1] text-[#b42318]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#b42318]">{shopName}</p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-[#ffd2d2] border-l-2 border-l-[#b42318] bg-[#fff8f8] px-4 py-3 text-[#414141] shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#b42318]">Lead declined</p>
                            <p className="mt-1 text-base font-black text-[#414141]">{shopName} declined this job.</p>
                        </div>
                        <span className="rounded-full border border-[#ffd2d2] bg-white px-2.5 py-1 text-[10px] font-black text-[#b42318]">
                            Declined
                        </span>
                    </div>
                    <div className="mt-3 rounded-xl border border-[#ffd2d2] bg-white px-3 py-2">
                        <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#7f1d1d]">Reason</p>
                        <p className="mt-1 text-sm font-black text-[#b42318]">{reasonLabel || 'No reason provided.'}</p>
                        {detail && (
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-[#414141]">{detail}</p>
                        )}
                    </div>
                    <p className="mt-3 text-sm font-semibold leading-relaxed text-[#414141]">
                        You can message the shop here if you need clarification, or choose another repair option.
                    </p>
                    {message.created_at && (
                        <p className="mt-2 text-[10px] font-bold text-[#9e9e9e]">
                            {formatShopConversationTime(message.created_at)}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

const shouldClampShopConversationMessage = (text = '') => {
    const value = String(text || '');
    const lineCount = value.split(/\r?\n/).length;
    return lineCount > 8 || value.length > 520;
};

const ShopConversationMessageText = ({ text = '', mine = false }) => {
    const [expanded, setExpanded] = useState(false);
    const displayText = String(text || '').replace(/\n{2,}/g, '\n').trim();
    const compactMarkdownComponents = useMemo(() => ({
        ...markdownLinkComponents,
        p: ({ children }) => <p className="m-0">{children}</p>,
        ul: ({ children }) => <ul className="my-1 list-disc space-y-0.5 pl-5">{children}</ul>,
        ol: ({ children }) => <ol className="my-1 list-decimal space-y-0.5 pl-5">{children}</ol>,
        li: ({ children }) => <li className="m-0">{children}</li>,
    }), []);
    const shouldClamp = shouldClampShopConversationMessage(displayText);

    return (
        <>
            <div className={`whitespace-pre-wrap break-words ${shouldClamp && !expanded ? 'shop-chat-message-clamp' : ''}`}>
                <ReactMarkdown components={compactMarkdownComponents}>
                    {linkifyBareUrls(displayText)}
                </ReactMarkdown>
            </div>
            {shouldClamp && (
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                    className={`mt-2 text-xs font-black ${mine ? 'text-white/80 hover:text-white' : 'text-[#D81F26] hover:text-[#B8181E]'}`}
                >
                    {expanded ? 'Show less' : 'Read more'}
                </button>
            )}
        </>
    );
};

// Full date + time for the "Revisiting an older thread..." greeting — unlike the
// time-only formatters above, a stale thread can be from a previous day (or year),
// so the date needs to be shown too or the label stays just as confusing.
const formatThreadStartedAt = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

// "Xm/h/d ago" relative label for an individual chat message, same style used in the
// internal ops dashboard's thread-detail view.
const formatMessageAgo = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    const secs = Math.floor((Date.now() - date.getTime()) / 1000);
    if (secs < 60) return 'just now';
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
};

const formatMessageTimestamp = (value) => {
    const ago = formatMessageAgo(value);
    if (!ago) return '';
    const date = new Date(value);
    const clock = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(date);
    return `${ago} · ${clock}`;
};

const formatDayDividerLabel = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
};

const isSameLocalDay = (a, b) => {
    if (!a || !b) return false;
    const da = new Date(a);
    const db = new Date(b);
    if (!Number.isFinite(da.getTime()) || !Number.isFinite(db.getTime())) return false;
    return da.getFullYear() === db.getFullYear()
        && da.getMonth() === db.getMonth()
        && da.getDate() === db.getDate();
};

const getGarageShopTabLabel = (request = {}) => {
    const name = cleanDisplayText(request?.partner_shop?.name || 'Shop');
    return name.split(/\s+/)[0] || 'Shop';
};

const getGarageShopContextLabel = (request = {}) => {
    const vehicle = request?.vehicle || {};
    const vehicleLabel = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
    return (
        request?.services?.[0]?.title ||
        request?.shop_ready_summary?.customerComplaint ||
        vehicleLabel ||
        'Garage request'
    );
};

const ShopModeEmptyState = ({ request }) => (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-teal-200 bg-teal-50">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-600">
                <path d="M3 9 5 3h14l2 6" />
                <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                <path d="M9 22V12h6v10" />
            </svg>
        </div>
        <p className="mb-2 text-sm font-semibold text-[#414141]">{request?.partner_shop?.name || 'Partner shop'}</p>
        <p className="max-w-[240px] text-xs font-semibold text-[#414141]">
            Your direct messages with the shop will appear here. Type below to send them a message.
        </p>
    </div>
);

const SHOP_ESTIMATE_ACCEPTED_STATUSES = new Set([
    'estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed',
]);
const SHOP_ESTIMATE_DECLINED_STATUSES = new Set(['declined', 'cancelled', 'failed']);
const SHOP_APPOINTMENT_FINAL_STATUSES = new Set(['in_service', 'ready_for_pickup', 'picked_up', 'completed', 'declined', 'cancelled', 'failed']);
const DEFAULT_PARTNER_SHOP_TIMEZONE = 'America/Denver';

const getPartnerShopTimezone = (request = {}) => (
    request?.partner_shop?.timezone || request?.scheduled_timezone || DEFAULT_PARTNER_SHOP_TIMEZONE
);

const formatPartnerTimezoneLabel = (timezone) => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone || DEFAULT_PARTNER_SHOP_TIMEZONE,
            timeZoneName: 'short',
        }).formatToParts(new Date());
        return parts.find((part) => part.type === 'timeZoneName')?.value || timezone || 'local time';
    } catch (_) {
        return timezone || 'local time';
    }
};

const getShopDatePartsForTimezone = (timezone) => {
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone || DEFAULT_PARTNER_SHOP_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());
    } catch (_) {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: DEFAULT_PARTNER_SHOP_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());
    }
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
    };
};

// Today's date (YYYY-MM-DD) in the shop's timezone. Used as the earliest bookable day so a
// driver can still grab a same-day slot — the backend already drops any time that's already
// passed (plus the shop's notice window), so today only ever offers genuinely future slots.
const getShopTodayISO = (timezone = DEFAULT_PARTNER_SHOP_TIMEZONE) => {
    const t = getShopDatePartsForTimezone(timezone);
    return `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
};

const getDateISOForTimezone = (value, timezone = DEFAULT_PARTNER_SHOP_TIMEZONE) => {
    if (!value) return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone || DEFAULT_PARTNER_SHOP_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date(value));
        const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
        return `${values.year}-${values.month}-${values.day}`;
    } catch (_) {
        return String(value).slice(0, 10);
    }
};

const getAppointmentCalendarStatus = (calendar) => {
    const status = String(calendar?.sync_status || '').toLowerCase();
    if (status === 'synced') return { label: 'Google Calendar synced', tone: 'success' };
    if (status === 'failed') return { label: 'Google Calendar sync failed', tone: 'danger' };
    return { label: '', tone: 'muted' };
};

const canSchedulePartnerAppointment = (request = {}) => {
    const status = String(request?.request_status || '').toLowerCase();
    const estimateStatus = String(request?.latest_estimate?.estimate_status || '').toLowerCase();
    const estimateAccepted = estimateStatus === 'accepted' || SHOP_ESTIMATE_ACCEPTED_STATUSES.has(status);
    return Boolean(request?.request_id) &&
        estimateAccepted &&
        !request?.scheduled_start_at &&
        !SHOP_APPOINTMENT_FINAL_STATUSES.has(status);
};

const canReschedulePartnerAppointment = (request = {}) => {
    const status = String(request?.request_status || '').toLowerCase();
    return Boolean(request?.request_id) &&
        Boolean(request?.scheduled_start_at) &&
        !SHOP_APPOINTMENT_FINAL_STATUSES.has(status);
};

const getPendingRescheduleRequest = (request = {}) => {
    const explicitRequest = (request?.reschedule_requests || [])
        .filter((item) => String(item?.status || '').toLowerCase() === 'pending')
        .slice(-1)[0];
    if (explicitRequest) return explicitRequest;
    return (request?.timeline || [])
        .filter((item) => (
            item?.event === 'reschedule_requested' &&
            String(item?.status || 'pending').toLowerCase() === 'pending'
        ))
        .map((item) => ({
            reschedule_request_id: item.reschedule_request_id || '',
            request_id: request?.request_id || request?.id || '',
            reason: item.reason || '',
            status: item.status || 'pending',
            previous_scheduled_start_at: item.previous_scheduled_start_at || '',
            previous_scheduled_end_at: item.previous_scheduled_end_at || '',
            previous_scheduled_timezone: item.previous_scheduled_timezone || '',
            created_at: item.created_at || '',
        }))
        .slice(-1)[0] || null;
};

const buildAppointmentFromRequest = (request = {}) => ({
    request_id: request?.request_id || '',
    ticket_number: request?.ticket_number || '',
    scheduled_start_at: request?.scheduled_start_at || '',
    scheduled_end_at: request?.scheduled_end_at || '',
    scheduled_timezone: request?.scheduled_timezone || getPartnerShopTimezone(request),
    schedule_notes: request?.schedule_notes || '',
    partner_shop: request?.partner_shop || {},
    vehicle: request?.vehicle || {},
    shop_ready_summary: request?.shop_ready_summary || {},
    services: request?.services || [],
    latest_estimate: request?.latest_estimate || null,
    calendar: request?.calendar || {},
});

const getRequestTimelineEventCreatedAt = (request = {}, eventName = '') => {
    const event = Array.isArray(request?.timeline)
        ? request.timeline.find((item) => item?.event === eventName && item?.created_at)
        : null;
    return event?.created_at || '';
};

const ShopConversationAppointmentCard = ({ appointment, createdAt }) => {
    const calendar = appointment?.calendar || {};
    const statusBadge = getAppointmentCalendarStatus(calendar);
    const appointmentLabel = formatPartnerScheduledAppointment(appointment, appointment?.scheduled_timezone || DEFAULT_PARTNER_SHOP_TIMEZONE);
    const addToCalendarUrl = buildPartnerGoogleCalendarUrl(appointment);

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecfff5] text-[#087a43]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M16 3v4M8 3v4M3 10h18M8 15l2 2 5-5" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#087a43]">
                    {appointment?.partner_shop?.name || 'Partner shop'}
                </p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-[#bde7d5] border-l-2 border-l-[#0a8f53] bg-[#ecfff5] px-4 py-3 text-[#414141] shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#087a43]">Appointment confirmed</p>
                            <p className="mt-1 text-base font-black text-[#414141]">
                                {appointmentLabel || 'Drop-off time scheduled'}
                            </p>
                        </div>
                        {statusBadge.label && (
                            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${
                                statusBadge.tone === 'success'
                                    ? 'border-[#bde7d5] bg-white text-[#087a43]'
                                    : statusBadge.tone === 'danger'
                                    ? 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                                    : 'border-[#d9d9d9] bg-white text-[#414141]'
                            }`}>
                                {statusBadge.label}
                            </span>
                        )}
                    </div>
                    {appointment?.partner_shop?.address && (
                        <p className="mt-2 text-sm font-semibold leading-relaxed text-[#414141]">
                            {appointment.partner_shop.address}
                        </p>
                    )}
                    {appointment?.schedule_notes && (
                        <p className="mt-2 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-[#414141]">
                            {appointment.schedule_notes}
                        </p>
                    )}
                    {(addToCalendarUrl || calendar.event_link) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {addToCalendarUrl && (
                                <a
                                    href={addToCalendarUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex min-h-[32px] items-center rounded-full border border-[#D9D9D9] bg-white px-3 text-xs font-black text-[#414141] no-underline hover:border-[#D81F26] hover:text-[#D81F26]"
                                >
                                    Add to my Google Calendar
                                </a>
                            )}
                            {calendar.event_link && (
                                <a
                                    href={calendar.event_link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex min-h-[32px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-xs font-black text-[#087a43] no-underline"
                                >
                                    Open synced event
                                </a>
                            )}
                        </div>
                    )}
                </div>
                {createdAt && (
                    <p className="mt-1 text-[10px] font-bold text-[#087a43]">
                        {formatShopConversationTime(createdAt)}
                    </p>
                )}
            </div>
        </div>
    );
};

const ShopConversationRescheduleNoticeCard = ({ request, rescheduleRequest, createdAt }) => {
    const reason = String(rescheduleRequest?.reason || '').trim();
    const appointment = {
        ...request,
        scheduled_start_at: rescheduleRequest?.previous_scheduled_start_at || request?.scheduled_start_at || '',
        scheduled_end_at: rescheduleRequest?.previous_scheduled_end_at || request?.scheduled_end_at || '',
        scheduled_timezone: rescheduleRequest?.previous_scheduled_timezone || request?.scheduled_timezone || '',
    };
    const appointmentLabel = formatPartnerScheduledAppointment(appointment, appointment?.scheduled_timezone || DEFAULT_PARTNER_SHOP_TIMEZONE);
    const shopName = request?.partner_shop?.name || 'The shop';

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff8e8] text-[#936000]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M3 21v-5h5" />
                    <path d="M3 12a9 9 0 0 1 15.74-5.74L21 8" />
                    <path d="M16 8h5V3" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#936000]">{shopName}</p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-[#f7d58f] border-l-2 border-l-[#d49300] bg-[#fff8e8] px-4 py-3 text-[#414141] shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#936000]">Reschedule requested</p>
                    <p className="mt-1 text-base font-black leading-snug text-[#414141]">{shopName} is asking for a new appointment time</p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        Current appointment{appointmentLabel ? `: ${appointmentLabel}` : ' is scheduled'}.
                    </p>
                    {reason && (
                        <p className="mt-2 whitespace-pre-wrap break-words rounded-xl border border-[#f7d58f] bg-white px-3 py-2 text-sm font-semibold text-[#414141]">
                            {reason}
                        </p>
                    )}
                </div>
                {createdAt && (
                    <p className="mt-1 text-[10px] font-bold text-[#936000]">
                        {formatShopConversationTime(createdAt)}
                    </p>
                )}
            </div>
        </div>
    );
};

const shouldShowScheduledVisitReminder = (request = {}) => (
    Boolean(request?.scheduled_start_at) && String(request?.request_status || '').toLowerCase() === 'scheduled'
);

const getDriverVisitUpdateMessage = (request = {}) => {
    const shopName = request?.partner_shop?.name || 'the shop';
    return `I visited ${shopName} for my scheduled appointment.`;
};

const hasDriverVisitUpdateMessage = (messages = [], request = {}) => {
    const expected = getDriverVisitUpdateMessage(request).toLowerCase();
    return messages.some((message) => (
        message?.sender_role === 'driver' &&
        String(message?.message_text || '').trim().toLowerCase() === expected
    ));
};

const ShopConversationVisitReminderCard = ({
    request,
    onConfirmVisit,
    isSubmitting = false,
    visitUpdateSent = false,
    actionsEnabled = true,
}) => {
    if (!request?.scheduled_start_at) return null;
    const shopName = request?.partner_shop?.name || 'the shop';
    const appointmentLabel = formatPartnerScheduledAppointment(request, request?.scheduled_timezone || DEFAULT_PARTNER_SHOP_TIMEZONE);
    const addToCalendarUrl = buildPartnerGoogleCalendarUrl(request);
    const address = request?.partner_shop?.address || '';
    const directionsUrl = address ? `https://maps.google.com/?q=${encodeURIComponent(address)}` : '';

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff8e8] text-[#936000]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2v4M16 2v4M3 10h18" />
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M9 16l2 2 4-5" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#936000]">CarFido reminder</p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-[#f7d58f] border-l-2 border-l-[#d49300] bg-[#fff8e8] px-4 py-3 text-[#414141] shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#936000]">Scheduled service</p>
                    <p className="mt-1 text-base font-black leading-snug text-[#414141]">Did you visit {shopName}?</p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        You have service scheduled{appointmentLabel ? ` for ${appointmentLabel}` : ''}.
                        {address ? ` ${address}` : ''}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {visitUpdateSent || !actionsEnabled ? (
                            <span className="inline-flex min-h-[34px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-xs font-black text-[#087a43]">
                                {visitUpdateSent ? 'Visit update sent' : 'Scheduled'}
                            </span>
                        ) : onConfirmVisit ? (
                            <button
                                type="button"
                                onClick={onConfirmVisit}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#D81F26] px-4 text-xs font-black text-white transition hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isSubmitting ? 'Sending...' : 'Yes, tell shop'}
                            </button>
                        ) : null}
                        {directionsUrl && (
                            <a
                                href={directionsUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex min-h-[34px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] no-underline hover:border-[#D81F26] hover:text-[#D81F26]"
                            >
                                Directions
                            </a>
                        )}
                        {addToCalendarUrl && (
                            <a
                                href={addToCalendarUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex min-h-[34px] items-center rounded-full border border-[#D9D9D9] bg-white px-3 text-xs font-black text-[#414141] no-underline hover:border-[#D81F26] hover:text-[#D81F26]"
                            >
                                Add to Google Calendar
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SHOP_CONVERSATION_STATUS_ORDER = [
    'submitted',
    'viewed',
    'accepted',
    'estimate_sent',
    'estimate_accepted',
    'scheduled',
    'in_service',
    'ready_for_pickup',
    'picked_up',
    'completed',
];

const SHOP_SERVICE_CARD_STATUSES = ['in_service', 'ready_for_pickup'];
const SHOP_SERVICE_STATUS_SORT_RANK = {
    scheduled: 10,
    in_service: 20,
    ready_for_pickup: 30,
    picked_up: 40,
    completed: 50,
};

const hasReachedShopConversationStatus = (currentStatus, targetStatus) => {
    const currentIndex = SHOP_CONVERSATION_STATUS_ORDER.indexOf(String(currentStatus || '').toLowerCase());
    const targetIndex = SHOP_CONVERSATION_STATUS_ORDER.indexOf(String(targetStatus || '').toLowerCase());
    return currentIndex >= 0 && targetIndex >= 0 && currentIndex >= targetIndex;
};

const getShopConversationEventCreatedAt = (messages = [], eventName = '') => {
    const event = Array.isArray(messages)
        ? messages.find((message) => message?.is_status_update && message?.event === eventName && message?.created_at)
        : null;
    return event?.created_at || '';
};

const getShopServicePromptSortTime = (request = {}, messages = [], eventName = '') => {
    if (eventName === 'in_service') {
        return getRequestTimelineEventCreatedAt(request, 'in_service') ||
            getShopConversationEventCreatedAt(messages, 'ready_for_pickup') ||
            getRequestTimelineEventCreatedAt(request, 'ready_for_pickup') ||
            getShopConversationEventCreatedAt(messages, 'picked_up') ||
            getRequestTimelineEventCreatedAt(request, 'picked_up') ||
            request.updated_at ||
            request.created_at ||
            request.scheduled_start_at;
    }
    if (eventName === 'ready_for_pickup') {
        return getRequestTimelineEventCreatedAt(request, 'ready_for_pickup') ||
            getShopConversationEventCreatedAt(messages, 'picked_up') ||
            getRequestTimelineEventCreatedAt(request, 'picked_up') ||
            request.updated_at ||
            request.created_at ||
            request.scheduled_start_at;
    }
    return request.updated_at || request.created_at || request.scheduled_start_at;
};

const getShopConversationSortRank = (message = {}) => {
    if (!message?.is_status_update) return 0;
    return SHOP_SERVICE_STATUS_SORT_RANK[String(message?.event || '').toLowerCase()] || 15;
};

const ShopConversationServicePickupCard = ({
    request,
    onConfirmPickup,
    isSubmitting = false,
    statusOverride = '',
    actionsEnabled = true,
}) => {
    const status = String(statusOverride || request?.request_status || '').toLowerCase();
    if (!['in_service', 'ready_for_pickup'].includes(status)) return null;
    const readyForPickup = status === 'ready_for_pickup';
    const currentStatus = String(request?.request_status || '').toLowerCase();
    const shopName = request?.partner_shop?.name || 'the shop';
    const canConfirmPickup = actionsEnabled && readyForPickup && currentStatus === 'ready_for_pickup' && onConfirmPickup;
    const statusLabel = readyForPickup && ['picked_up', 'completed'].includes(currentStatus) ? 'Car taken' : 'Waiting for shop';

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${readyForPickup ? 'bg-[#ecfff5] text-[#087a43]' : 'bg-[#f7f2ff] text-[#6f3cc3]'}`}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className={`mb-1 ml-0.5 text-[10px] font-semibold ${readyForPickup ? 'text-[#087a43]' : 'text-[#6f3cc3]'}`}>
                    CarFido reminder
                </p>
                <div className={`w-full rounded-2xl rounded-tl-sm border border-l-2 px-4 py-3 text-[#414141] shadow-sm ${
                    readyForPickup
                        ? 'border-[#bde7d5] border-l-[#0a8f53] bg-[#ecfff5]'
                        : 'border-[#dfd0ff] border-l-[#6f3cc3] bg-[#f7f2ff]'
                }`}>
                    <p className={`text-[10px] font-black uppercase tracking-[0.08em] ${readyForPickup ? 'text-[#087a43]' : 'text-[#6f3cc3]'}`}>
                        {readyForPickup ? 'Ready for pickup' : 'Vehicle in service'}
                    </p>
                    <p className="mt-1 text-base font-black leading-snug text-[#414141]">
                        {readyForPickup ? 'Is service complete and car taken?' : 'Service is in progress'}
                    </p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        {readyForPickup
                            ? `The shop marked service complete. Confirm after you have taken your car from ${shopName}.`
                            : `Your vehicle is in service at ${shopName}. Once the shop marks it ready, confirm here after you take the car.`
                        }
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {canConfirmPickup ? (
                            <button
                                type="button"
                                onClick={onConfirmPickup}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isSubmitting ? 'Confirming...' : 'Yes, car taken'}
                            </button>
                        ) : (
                            <span className={`inline-flex min-h-[34px] items-center rounded-full border bg-white px-3 text-xs font-black ${
                                readyForPickup
                                    ? 'border-[#bde7d5] text-[#087a43]'
                                    : 'border-[#dfd0ff] text-[#6f3cc3]'
                            }`}>
                                {statusLabel}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Shown after the driver confirms pickup: asks whether the issue is resolved so the ticket
// can be CLOSED, or kept under monitoring when the driver isn't sure yet.
const ShopConversationPickupOutcomeCard = ({ outcome = '', isSubmitting = false, status = '', onResolved, onNotSure }) => (
    <div className="flex w-full justify-start gap-4 md:gap-6">
        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecfff5] text-[#087a43]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
            </svg>
        </div>
        <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
            <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#087a43]">CarFido reminder</p>
            <div className="w-full rounded-2xl rounded-tl-sm border border-l-2 border-[#bde7d5] border-l-[#0a8f53] bg-[#ecfff5] px-4 py-3 text-[#414141] shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#087a43]">{status === 'completed' ? 'Service completed' : 'Vehicle picked up'}</p>
                {outcome === 'resolved' ? (
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">Your issue is marked resolved — this case is now closed.</p>
                ) : outcome === 'monitoring' ? (
                    <>
                        <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">We're keeping this issue under monitoring. Once you're confident it's fixed, mark it resolved anytime.</p>
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={onResolved}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isSubmitting ? 'Saving...' : "It's fixed — mark resolved"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="mt-1 text-base font-black leading-snug text-[#414141]">Is your issue resolved?</p>
                        <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">Confirm so we can close this case, or keep it under monitoring if you're not sure yet.</p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={onResolved}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isSubmitting ? 'Saving...' : "Yes, it's fixed"}
                            </button>
                            <button
                                type="button"
                                onClick={onNotSure}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[#bde7d5] bg-white px-4 text-xs font-black text-[#087a43] transition hover:bg-[#f0fff7] disabled:cursor-wait disabled:opacity-60"
                            >
                                Not sure — keep monitoring
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    </div>
);

// The 3-day monitoring follow-up card CarFido posts when a repair's outcome was left unsure.
// Routes exactly like the original confirmation: resolved → close, still happening → resume
// diagnostics, not sure → keep monitoring (and re-arm the follow-up).
const MonitoringFollowupCard = ({ card, outcome = '', isSubmitting = false, onResolved, onStillHappening, onNotSure }) => {
    const issue = card?.issue || 'your issue';
    return (
        <div className="mt-3 w-full max-w-[460px] overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-none">
            <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] px-4 py-2.5">
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#414141]">Repair follow-up</p>
                <p className="mt-0.5 text-[13px] font-bold text-[#414141]">{issue}</p>
            </div>
            <div className="px-4 py-3">
                {outcome === 'resolved' ? (
                    <p className="text-sm font-semibold leading-relaxed text-[#414141]">Great — marked resolved and this case is now closed.</p>
                ) : outcome === 'still' ? (
                    <p className="text-sm font-semibold leading-relaxed text-[#414141]">Thanks — let's keep diagnosing. I've picked it back up above with your earlier details.</p>
                ) : outcome === 'not_sure' ? (
                    <p className="text-sm font-semibold leading-relaxed text-[#414141]">No problem — I'll check back again in a few days.</p>
                ) : (
                    <>
                        <p className="text-[15px] font-black leading-snug text-black">Is the problem resolved now, or still happening?</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={onResolved}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isSubmitting ? 'Saving...' : "It's resolved"}
                            </button>
                            <button
                                type="button"
                                onClick={onStillHappening}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[#f4c76a] bg-[#fff8e8] px-4 text-xs font-black text-[#a04a00] transition hover:bg-[#fff1cf] disabled:cursor-wait disabled:opacity-60"
                            >
                                Still happening
                            </button>
                            <button
                                type="button"
                                onClick={onNotSure}
                                disabled={isSubmitting}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#515151] transition hover:bg-[#F9F9F9] disabled:cursor-wait disabled:opacity-60"
                            >
                                Still not sure
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const ShopConversationScheduleCard = ({ request, onScheduled, mode = 'schedule', rescheduleRequest = null }) => {
    const timezone = getPartnerShopTimezone(request);
    const todayISO = useMemo(() => getShopTodayISO(timezone), [timezone]);
    const preferredAppointment = useMemo(() => getPartnerPreferredAppointment(request), [request]);
    const currentAppointmentDate = getDateISOForTimezone(request?.scheduled_start_at, timezone);
    const shopId = request?.partner_shop_id || request?.partner_shop?.id || null;
    const isRescheduleMode = mode === 'reschedule';
    const initialDate = isRescheduleMode && currentAppointmentDate && currentAppointmentDate >= todayISO
        ? currentAppointmentDate
        : todayISO;
    const [selectedDate, setSelectedDate] = useState(initialDate);
    const [selectedTime, setSelectedTime] = useState('');
    const [notes, setNotes] = useState('');
    const [isScheduling, setIsScheduling] = useState(false);
    const [error, setError] = useState('');
    // Bumped to force the slot grid to refetch — after a lost booking race, or when the
    // driver should see a freshly-updated set of open times.
    const [reloadToken, setReloadToken] = useState(0);
    const calendarConnected = Boolean(request?.partner_shop?.google_calendar_connected || request?.partner_shop?.google_calendar_id);
    const appointmentLabel = formatPartnerScheduledAppointment(request, request?.scheduled_timezone || DEFAULT_PARTNER_SHOP_TIMEZONE);
    const rescheduleReason = String(rescheduleRequest?.reason || '').trim();

    useEffect(() => {
        if (isRescheduleMode) {
            const nextCurrentDate = getDateISOForTimezone(request?.scheduled_start_at, timezone);
            setSelectedDate(nextCurrentDate && nextCurrentDate >= todayISO ? nextCurrentDate : todayISO);
            setSelectedTime('');
            setNotes('');
            setError('');
            return;
        }
        const usePreferred = preferredAppointment?.date && preferredAppointment.date >= todayISO;
        setSelectedDate(usePreferred ? preferredAppointment.date : todayISO);
        setSelectedTime(usePreferred ? preferredAppointment.time : '');
        setNotes('');
        setError('');
    }, [isRescheduleMode, request?.request_id, request?.scheduled_start_at, timezone, todayISO, preferredAppointment?.date, preferredAppointment?.time]);

    const handleSchedule = async () => {
        if (!request?.request_id || !selectedDate || !selectedTime || isScheduling) return;
        setIsScheduling(true);
        setError('');
        try {
            const updated = await schedulePartnerRepairRequest(request.request_id, {
                scheduled_date: selectedDate,
                scheduled_time: selectedTime,
                timezone,
                duration_minutes: 15,
                notes,
            });
            if (updated) onScheduled?.(updated);
        } catch (scheduleError) {
            // A 409 means the slot filled between fetch and confirm — clear the choice,
            // refresh the grid, and ask the driver to pick another (per the spec).
            if (Number(scheduleError?.status) === 409) {
                setSelectedTime('');
                setReloadToken((t) => t + 1);
                setError('That time was just booked. Please pick another slot.');
            } else {
                setError(scheduleError?.message || 'Could not schedule this appointment.');
            }
        } finally {
            setIsScheduling(false);
        }
    };

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] text-[#414141]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M16 3v4M8 3v4M3 10h18" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-teal-700">
                    {request?.partner_shop?.name || 'Partner shop'}
                </p>
                <div className={`w-full rounded-2xl rounded-tl-sm border border-l-2 px-4 py-3 text-[#414141] shadow-sm ${
                    isRescheduleMode
                        ? 'border-[#f7d58f] border-l-[#d49300] bg-[#fff8e8]'
                        : 'border-[#d9d9d9] border-l-[#414141] bg-[#fafafa]'
                }`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className={`text-[10px] font-black uppercase tracking-[0.08em] ${isRescheduleMode ? 'text-[#936000]' : 'text-[#D81F26]'}`}>
                                {isRescheduleMode ? 'Reschedule requested' : 'Next step'}
                            </p>
                            <p className="mt-1 text-base font-black text-[#414141]">
                                {isRescheduleMode ? 'Pick a new appointment time' : 'Schedule a shop visit'}
                            </p>
                            <p className="mt-1 text-sm font-semibold text-[#414141]">
                                {isRescheduleMode
                                    ? `${request?.partner_shop?.name || 'The shop'} asked you to choose a new drop-off time.`
                                    : `Pick a drop-off time at ${request?.partner_shop?.name || 'the shop'}.`}
                            </p>
                            {isRescheduleMode && appointmentLabel && (
                                <p className="mt-1 text-xs font-bold text-[#414141]">
                                    Current appointment: {appointmentLabel}
                                </p>
                            )}
                        </div>
                        {calendarConnected && (
                            <span className="rounded-full border border-[#bde7d5] bg-white px-2.5 py-1 text-[10px] font-black text-[#087a43]">
                                Shop calendar sync enabled
                            </span>
                        )}
                    </div>
                    {isRescheduleMode && rescheduleReason && (
                        <p className="mt-3 whitespace-pre-wrap break-words rounded-xl border border-[#f7d58f] bg-white px-3 py-2 text-sm font-semibold text-[#414141]">
                            {rescheduleReason}
                        </p>
                    )}

                    <div className="mt-4">
                        <AppointmentSlotPicker
                            shopId={shopId}
                            minDate={todayISO}
                            selectedDate={selectedDate}
                            onSelectDate={(d) => { setSelectedDate(d); setSelectedTime(''); setError(''); }}
                            selectedTime={selectedTime}
                            onSelectSlot={(d, t) => { setSelectedDate(d); setSelectedTime(t); setError(''); }}
                            reloadToken={reloadToken}
                            disabled={isScheduling}
                        />
                    </div>
                    {preferredAppointment?.label && (
                        <p className="mt-2 rounded-xl border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-bold text-[#414141]">
                            Pre-filled from shop intake: {preferredAppointment.label}. Choose another open time if needed.
                        </p>
                    )}

                    <textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value.slice(0, 600))}
                        rows={3}
                        placeholder="Anything the shop should know before drop-off?"
                        className="mt-3 w-full resize-none rounded-xl border border-[#d9d9d9] bg-white px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26]"
                    />

                    <div className="mt-4 flex flex-col gap-3 border-t border-[#eaeaea] pt-4 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs font-semibold text-[#414141]">
                            Times shown in {formatPartnerTimezoneLabel(timezone)}
                        </p>
                        <button
                            type="button"
                            onClick={handleSchedule}
                            disabled={isScheduling || !selectedDate || !selectedTime}
                            className="inline-flex min-h-[40px] items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isScheduling
                                ? (isRescheduleMode ? 'Rescheduling...' : 'Scheduling...')
                                : (isRescheduleMode ? 'Confirm new time' : 'Confirm appointment')}
                        </button>
                    </div>
                    {error && (
                        <p className="mt-3 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">
                            {error}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

const getEstimateAuthorizationUnits = (lines = []) => {
    const units = [];
    const grouped = new Map();
    lines.forEach((line, index) => {
        const groupKey = String(line?.approval_group_key || '').trim();
        if (!groupKey) {
            units.push({ key: line?.estimate_line_id || `line-${index}`, isGroup: false, label: '', lines: [line], firstIndex: index });
            return;
        }
        if (!grouped.has(groupKey)) {
            const unit = {
                key: `group-${groupKey}`,
                isGroup: true,
                label: line?.approval_group_label || 'Required together',
                lines: [],
                firstIndex: index,
            };
            grouped.set(groupKey, unit);
            units.push(unit);
        }
        grouped.get(groupKey).lines.push(line);
    });
    return units.sort((a, b) => a.firstIndex - b.firstIndex);
};

export const ShopConversationEstimateCard = ({
    message,
    request,
    onUpdateLineAuthorization,
    onSendEstimateMessage,
    isMessageBusy = false,
}) => {
    const estimate = message?.estimate || request?.latest_estimate;
    const [questionDraft, setQuestionDraft] = useState('');
    const [updatingLineId, setUpdatingLineId] = useState('');
    const [isSendingQuestion, setIsSendingQuestion] = useState(false);
    const [actionNotice, setActionNotice] = useState('');
    // #575: '' | 'accepted' | 'declined' while the Approve all / Decline all confirmation is open.
    const [bulkConfirmDecision, setBulkConfirmDecision] = useState('');
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    // State updates are async, so a fast double click can slip past isUpdatingLine; the ref blocks it.
    const decisionInFlightRef = useRef(false);

    useEffect(() => {
        setQuestionDraft('');
        setActionNotice('');
        setBulkConfirmDecision('');
    }, [request?.request_id, estimate?.estimate_id, request?.request_status]);

    if (!estimate) return null;

    const shopName = request?.partner_shop?.name || 'Shop';
    const lines = Array.isArray(estimate.lines) ? estimate.lines : [];
    const authorizationUnits = getEstimateAuthorizationUnits(lines);
    const requestStatus = String(request?.request_status || '');
    const estimateStatus = String(estimate?.estimate_status || '');
    const estimateAccepted = estimateStatus === 'accepted' || SHOP_ESTIMATE_ACCEPTED_STATUSES.has(requestStatus);
    const estimateDeclined = estimateStatus === 'declined' || SHOP_ESTIMATE_DECLINED_STATUSES.has(requestStatus);
    // Only the newest estimate is actionable. Older versions stay in the chat as read-only history
    // (is_latest === false); default to true for legacy payloads without the flag.
    const isLatestEstimate = estimate.is_latest !== false;
    const canRespond = Boolean(request?.request_id) && requestStatus === 'estimate_sent' && !estimateAccepted && !estimateDeclined && isLatestEstimate;
    const isUpdatingLine = Boolean(updatingLineId);
    const questionText = questionDraft.trim();
    const noticeIsError = /could not|failed|already|not|unavailable/i.test(actionNotice);

    const getLineStatus = (line) => {
        const status = String(line?.authorization_status || 'pending').toLowerCase();
        return ['accepted', 'declined', 'pending'].includes(status) ? status : 'pending';
    };

    const getLineStatusLabel = (status) => {
        if (status === 'accepted') return 'Approved';
        if (status === 'declined') return 'Declined';
        return '';
    };

    const getLineStatusClass = (status) => {
        if (status === 'accepted') return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
        if (status === 'declined') return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
        return 'border-amber-200 bg-amber-50 text-amber-800';
    };

    // #575: bulk actions only ever touch undecided lines, so earlier approvals/declines (including
    // approvals carried forward onto a revised estimate) are never changed.
    const bulkEligibleLines = canRespond
        ? lines.filter((line) => line?.estimate_line_id && getLineStatus(line) === 'pending')
        : [];
    const bulkEligibleTotal = bulkEligibleLines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const bulkHasPriorDecisions = lines.some((line) => getLineStatus(line) !== 'pending');

    const handleLineDecision = async (linesToUpdate, decision, { bulk = false } = {}) => {
        const validLines = (Array.isArray(linesToUpdate) ? linesToUpdate : [linesToUpdate])
            .filter((line) => line?.estimate_line_id && getLineStatus(line) === 'pending');
        if (!canRespond || isUpdatingLine || decisionInFlightRef.current || !onUpdateLineAuthorization || !validLines.length) return;
        decisionInFlightRef.current = true;
        setIsBulkUpdating(bulk);
        setUpdatingLineId(validLines.map((line) => line.estimate_line_id).join('|'));
        setActionNotice('');
        try {
            await onUpdateLineAuthorization(request, validLines, decision);
            setBulkConfirmDecision('');
            if (bulk) {
                const itemLabel = `${validLines.length} item${validLines.length === 1 ? '' : 's'}`;
                setActionNotice(decision === 'accepted' ? `Approved ${itemLabel}.` : `Declined ${itemLabel}.`);
            } else {
                setActionNotice(decision === 'accepted' ? 'Estimate item approved.' : 'Estimate item declined.');
            }
        } catch (error) {
            setActionNotice(error?.message || (bulk ? 'Could not update this estimate.' : 'Could not update this estimate line.'));
        } finally {
            decisionInFlightRef.current = false;
            setIsBulkUpdating(false);
            setUpdatingLineId('');
        }
    };

    const handleQuestionSubmit = async (event) => {
        event.preventDefault();
        if (!questionText || isSendingQuestion || isMessageBusy || !onSendEstimateMessage) return;
        setIsSendingQuestion(true);
        setActionNotice('');
        try {
            await onSendEstimateMessage(questionText);
            setQuestionDraft('');
            setActionNotice('Sent to the shop.');
        } catch (error) {
            setActionNotice(error?.message || 'Could not send this message.');
        } finally {
            setIsSendingQuestion(false);
        }
    };

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-600/12 text-teal-700">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9 5 3h14l2 6" />
                    <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                    <path d="M9 22V12h6v10" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-teal-700">
                    {shopName}
                </p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-teal-200/80 border-l-2 border-l-teal-500 bg-teal-50/70 px-4 py-3 text-[#414141] shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-teal-700">
                                Itemized estimate
                            </p>
                            <p className="mt-1 text-xl font-black text-[#414141]">
                                {formatPartnerMoney(estimate.total_amount, estimate.currency)}
                            </p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${
                            !isLatestEstimate
                                ? 'border-[#e6e6e6] bg-[#f4f4f4] text-[#414141]'
                                : estimateAccepted
                                ? 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'
                                : estimateDeclined
                                ? 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                                : estimate.is_update && canRespond
                                ? 'border-amber-200 bg-amber-50 text-amber-800'
                                : 'border-teal-200 bg-white/80 text-teal-700'
                        }`}>
                            {!isLatestEstimate
                                ? `Previous estimate${estimate.revision ? ` (v${estimate.revision})` : ''}`
                                : estimateAccepted
                                ? 'Estimate accepted'
                                : estimateDeclined
                                ? 'Estimate declined'
                                : estimate.is_update && canRespond
                                ? 'Estimate updated'
                                : 'Estimate ready'}
                        </span>
                    </div>
                    {!isLatestEstimate && (
                        <p className="mt-3 rounded-lg border border-[#e7e7e7] bg-[#fafafa] px-3 py-2 text-xs font-bold text-[#414141]">
                            This estimate was replaced by a newer version below.
                        </p>
                    )}
                    {isLatestEstimate && estimate.is_update && canRespond && (
                        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
                            The shop updated this estimate. Previously approved work stays authorized. Please review the new or undecided items below.
                        </p>
                    )}
                    {estimate.message && (
                        <p className="mt-3 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-[#414141]">
                            {estimate.message}
                        </p>
                    )}
                    {lines.length > 0 && (
                        <div className="mt-3 divide-y divide-teal-100">
                            {authorizationUnits.map((unit) => {
                                const pendingUnitLines = unit.lines.filter((line) => getLineStatus(line) === 'pending' && line?.estimate_line_id);
                                const unitBusy = pendingUnitLines.some((line) => updatingLineId.split('|').includes(line.estimate_line_id));
                                if (unit.isGroup) {
                                    const groupTotal = unit.lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
                                    return (
                                        <div key={unit.key} className="py-2">
                                            <div className="rounded-xl border border-teal-200 bg-white/70 p-3">
                                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] font-black uppercase tracking-[0.08em] text-teal-700">Grouped approval</p>
                                                        <p className="mt-0.5 text-sm font-black text-[#414141]">{unit.label}</p>
                                                    </div>
                                                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                                        <span className="text-sm font-black text-[#414141]">{formatPartnerMoney(groupTotal, estimate.currency)}</span>
                                                        {canRespond && pendingUnitLines.length > 0 && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    disabled={isUpdatingLine || isSendingQuestion}
                                                                    onClick={() => handleLineDecision(pendingUnitLines, 'accepted')}
                                                                    className="inline-flex h-8 items-center rounded-lg bg-[#087a43] px-3 text-[11px] font-black text-white transition hover:bg-[#066738] disabled:cursor-wait disabled:opacity-60"
                                                                >
                                                                    {unitBusy ? 'Saving...' : 'Approve group'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    disabled={isUpdatingLine || isSendingQuestion}
                                                                    onClick={() => handleLineDecision(pendingUnitLines, 'declined')}
                                                                    className="inline-flex h-8 items-center rounded-lg border border-[#ffd2d2] bg-white px-3 text-[11px] font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-wait disabled:opacity-60"
                                                                >
                                                                    Decline group
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="mt-2 divide-y divide-teal-100">
                                                    {unit.lines.map((line) => {
                                                        const lineStatus = getLineStatus(line);
                                                        return (
                                                            <div key={line.estimate_line_id || line.description} className="flex items-center justify-between gap-3 py-1.5">
                                                                <div className="min-w-0">
                                                                    <span className="text-sm font-bold text-[#414141]">{line.description || 'Estimate line'}</span>
                                                                    {lineStatus !== 'pending' && (
                                                                        <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-black ${getLineStatusClass(lineStatus)}`}>
                                                                            {getLineStatusLabel(lineStatus)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="shrink-0 text-sm font-black text-[#414141]">{formatPartnerMoney(line.amount, estimate.currency)}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                const line = unit.lines[0];
                                const lineStatus = getLineStatus(line);
                                const lineCanRespond = canRespond && lineStatus === 'pending' && line.estimate_line_id;
                                const lineBusy = updatingLineId === line.estimate_line_id;
                                return (
                                    <div key={line.estimate_line_id || unit.key} className="flex flex-col gap-2 py-2 text-sm font-bold sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0">
                                            <span className="text-[#414141]">
                                                {line.description || 'Estimate line'}
                                            </span>
                                            {lineStatus !== 'pending' && (
                                                <div className="mt-1">
                                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${getLineStatusClass(lineStatus)}`}>
                                                        {getLineStatusLabel(lineStatus)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
                                            <span className="text-[#414141]">
                                                {formatPartnerMoney(line.amount, estimate.currency)}
                                            </span>
                                            {lineCanRespond && (
                                                <div className="flex shrink-0 items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        disabled={isUpdatingLine || isSendingQuestion}
                                                        onClick={() => handleLineDecision(line, 'accepted')}
                                                        className="inline-flex h-8 items-center rounded-lg bg-[#087a43] px-3 text-[11px] font-black text-white transition hover:bg-[#066738] disabled:cursor-wait disabled:opacity-60"
                                                    >
                                                        {lineBusy ? 'Saving...' : 'Approve'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={isUpdatingLine || isSendingQuestion}
                                                        onClick={() => handleLineDecision(line, 'declined')}
                                                        className="inline-flex h-8 items-center rounded-lg border border-[#ffd2d2] bg-white px-3 text-[11px] font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-wait disabled:opacity-60"
                                                    >
                                                        Decline
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {/* #575: full-estimate actions. Hidden when nothing is left to decide. */}
                    {bulkEligibleLines.length > 0 && (
                        <div className="mt-3 border-t border-teal-100 pt-3">
                            {bulkConfirmDecision ? (
                                <div
                                    role="alertdialog"
                                    aria-label={bulkConfirmDecision === 'accepted' ? 'Confirm approve all' : 'Confirm decline all'}
                                    className={`rounded-xl border bg-white p-3 ${bulkConfirmDecision === 'accepted' ? 'border-[#bde7d5]' : 'border-[#ffd2d2]'}`}
                                >
                                    <p className="text-sm font-black text-[#182233]">
                                        {bulkConfirmDecision === 'accepted' ? 'Approve' : 'Decline'} {bulkEligibleLines.length} remaining item{bulkEligibleLines.length === 1 ? '' : 's'} totaling {formatPartnerMoney(bulkEligibleTotal, estimate.currency)}?
                                    </p>
                                    {bulkHasPriorDecisions && (
                                        <p className="mt-1 text-xs font-semibold text-[#5f728a]">
                                            Items you already approved or declined will not change.
                                        </p>
                                    )}
                                    {bulkConfirmDecision === 'declined' && !lines.some((line) => getLineStatus(line) === 'accepted') && (
                                        <p className="mt-1 text-xs font-semibold text-[#5f728a]">This will close the estimate.</p>
                                    )}
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            disabled={isUpdatingLine || isSendingQuestion}
                                            onClick={() => handleLineDecision(bulkEligibleLines, bulkConfirmDecision, { bulk: true })}
                                            className={bulkConfirmDecision === 'accepted'
                                                ? 'inline-flex h-8 items-center rounded-lg bg-[#087a43] px-3 text-[11px] font-black text-white transition hover:bg-[#066738] disabled:cursor-wait disabled:opacity-60'
                                                : 'inline-flex h-8 items-center rounded-lg bg-[#b42318] px-3 text-[11px] font-black text-white transition hover:bg-[#931c13] disabled:cursor-wait disabled:opacity-60'}
                                        >
                                            {isBulkUpdating ? 'Saving...' : bulkConfirmDecision === 'accepted' ? 'Yes, approve all' : 'Yes, decline all'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isUpdatingLine}
                                            onClick={() => setBulkConfirmDecision('')}
                                            className="inline-flex h-8 items-center rounded-lg border border-[#dfe7ef] bg-white px-3 text-[11px] font-black text-[#42566c] transition hover:bg-[#f8fafc] disabled:cursor-wait disabled:opacity-60"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        disabled={isUpdatingLine || isSendingQuestion}
                                        onClick={() => { setActionNotice(''); setBulkConfirmDecision('accepted'); }}
                                        className="inline-flex h-8 items-center rounded-lg bg-[#087a43] px-3 text-[11px] font-black text-white transition hover:bg-[#066738] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        Approve all
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isUpdatingLine || isSendingQuestion}
                                        onClick={() => { setActionNotice(''); setBulkConfirmDecision('declined'); }}
                                        className="inline-flex h-8 items-center rounded-lg border border-[#ffd2d2] bg-white px-3 text-[11px] font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        Decline all
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {canRespond && (
                        <div className="mt-4 border-t border-teal-100 pt-3">
                            <div className="flex flex-col gap-2">
                                <form onSubmit={handleQuestionSubmit} className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-teal-200 bg-white px-3 py-2">
                                    <input
                                        type="text"
                                        value={questionDraft}
                                        onChange={(event) => setQuestionDraft(event.target.value.slice(0, 600))}
                                        placeholder='Ask the shop, e.g. "Can this be reduced?"'
                                        aria-label="Message the shop about this estimate"
                                        disabled={isUpdatingLine || isSendingQuestion || isMessageBusy}
                                        className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#414141] outline-none placeholder:text-[#9d9d9d] disabled:cursor-wait"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!questionText || isUpdatingLine || isSendingQuestion || isMessageBusy}
                                        className="inline-flex h-8 shrink-0 items-center justify-center rounded-lg bg-[#D81F26] px-3 text-xs font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-45"
                                    >
                                        {isSendingQuestion ? 'Sending...' : 'Send'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}
                    {actionNotice && (
                        <p className={`mt-3 rounded-xl border px-3 py-2 text-xs font-bold ${
                            noticeIsError
                                ? 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                                : 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'
                        }`}>
                            {actionNotice}
                        </p>
                    )}
                </div>
                {message.created_at && (
                    <p className="mt-1 text-[10px] font-bold text-teal-600">
                        {formatShopConversationTime(message.created_at)}
                    </p>
                )}
            </div>
        </div>
    );
};

const LinkedGarageShopBanner = ({
    requests = [],
    chatMode = 'dash',
    selectedRequestId = '',
    onSelectDash,
    onSelectRequest,
}) => {
    if (!requests.length) return null;
    const selectedRequest = requests.find((request) => String(request?.request_id || '') === String(selectedRequestId || '')) || requests[0];

    return (
        <>
            <div className="border-b border-teal-100 bg-teal-50/70 px-3 py-2 md:px-6">
                <div className="mx-auto flex max-w-5xl items-center gap-2 overflow-x-auto">
                    <div className="flex shrink-0 items-center overflow-hidden rounded-full border border-teal-200 bg-white shadow-sm">
                        <button
                            type="button"
                            onClick={onSelectDash}
                            className={`whitespace-nowrap px-2.5 py-1 text-[10px] font-medium transition-colors ${
                                chatMode === 'dash'
                                    ? 'bg-teal-600 text-white'
                                    : 'text-teal-700 hover:bg-teal-50'
                            }`}
                        >
                            Ask CarFido
                        </button>
                        {requests.map((request) => (
                            <button
                                key={request.request_id}
                                type="button"
                                onClick={() => onSelectRequest(request)}
                                className={`whitespace-nowrap border-l border-teal-200 px-2.5 py-1 text-[10px] font-medium transition-colors ${
                                    chatMode === 'shop' && String(selectedRequest?.request_id || '') === String(request.request_id)
                                        ? 'bg-teal-600 text-white'
                                        : 'text-teal-700 hover:bg-teal-50'
                                }`}
                            >
                                {getGarageShopTabLabel(request)}
                            </button>
                        ))}
                    </div>
                    {chatMode === 'shop' && selectedRequest && (
                        <span className="shrink-0 rounded-full bg-teal-100/80 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">
                            {getPartnerStatusLabel(selectedRequest.request_status)}
                        </span>
                    )}
                </div>
            </div>
            {chatMode === 'shop' && selectedRequest && (
                <div className="border-b border-teal-100/60 bg-teal-600/5 px-4 py-2 md:px-6">
                    <div className="mx-auto flex max-w-5xl items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-teal-600">
                            <path d="M3 9 5 3h14l2 6" />
                            <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                            <path d="M9 22V12h6v10" />
                        </svg>
                        <span className="min-w-0 shrink-0 truncate text-[11px] font-semibold text-teal-800">
                            {selectedRequest.partner_shop?.name || 'Partner shop'}
                        </span>
                        <span className="min-w-0 truncate text-[10px] font-semibold text-teal-600">
                            · {getGarageShopContextLabel(selectedRequest)}
                        </span>
                    </div>
                </div>
            )}
        </>
    );
};

const getRetailerLabelFromLinkLine = (line = '', urlIndex = -1) => {
    if (urlIndex < 0) return '';
    const beforeUrl = line.slice(0, urlIndex);
    const baseLabelSource = beforeUrl
        .replace(/\[[^\]]+\]\($/, '')
        .replace(/\b(view|open|directions?|map)\s*\(?$/i, '')
        .trim();
    const labelSource = baseLabelSource.includes(':')
        ? baseLabelSource.slice(0, baseLabelSource.indexOf(':'))
        : baseLabelSource.includes(' - ')
        ? baseLabelSource.slice(0, baseLabelSource.indexOf(' - '))
        : baseLabelSource.includes(' – ')
        ? baseLabelSource.slice(0, baseLabelSource.indexOf(' – '))
        : baseLabelSource.includes(' — ')
        ? baseLabelSource.slice(0, baseLabelSource.indexOf(' — '))
        : baseLabelSource;

    return cleanDisplayText(labelSource)
        .replace(/^[-*•\s]+/, '')
        .replace(/^\d+[.)]\s*/, '')
        .replace(/[:*\[\]()]+$/g, '')
        .replace(/^\*+|\*+$/g, '')
        .trim();
};

const getLinkCardDetail = (url = '', type = 'parts') => {
    const detail = getPartQueryFromUrl(url);
    if (detail) return detail;

    return type === 'shops' ? 'View location and directions' : '';
};

const parseLinkListBlock = (content = '') => {
    const lines = safeText(content).split(/\r?\n/);
    let headingIndex = lines.findIndex((line) => (
        LINK_LIST_HEADING_PATTERN.test(line) ||
        /\bavailable from these sources\s*:?\s*$/i.test(line)
    ));
    if (headingIndex === -1) {
        const firstRetailerLinkIndex = lines.findIndex((line) => (
            RETAILER_LINK_LINE_PATTERN.test(line) ||
            /\b(?:autozone|rockauto|napa|amazon|ebay|oem dealer|dealer search)\b\s*:/i.test(line) && ANY_URL_PATTERN.test(line)
        ));
        if (firstRetailerLinkIndex > 0) {
            const previousLine = lines[firstRetailerLinkIndex - 1].trim();
            headingIndex = /:\s*$/.test(previousLine) ? firstRetailerLinkIndex - 1 : firstRetailerLinkIndex;
        }
    }
    if (headingIndex === -1) return null;

    const headingText = lines[headingIndex].toLowerCase();
    const type = /\b(shop|mechanic|garage)\b/.test(headingText) ? 'shops' : 'parts';
    const copy = getLinkListCopy(type);
    const cards = [];
    let endIndex = headingIndex + 1;
    let hasStartedLinks = false;

    for (let i = headingIndex + 1; i < lines.length; i += 1) {
        const rawLine = lines[i];
        const line = rawLine.trim();

        if (!line) {
            endIndex = i + 1;
            continue;
        }

        const labeledMatch = rawLine.match(RETAILER_LINK_LINE_PATTERN);
        const urlMatch = rawLine.match(ANY_URL_PATTERN);

        if (urlMatch) {
            const url = cleanRetailerUrl(labeledMatch?.[2] || urlMatch[1]);
            const host = getHostFromUrl(url);
            const labelFromLine = getRetailerLabelFromLinkLine(rawLine, urlMatch.index);
            const retailer = cleanDisplayText(labeledMatch?.[1] || labelFromLine || titleCaseWords(host.split('.')[0] || copy.fallback))
                .replace(/\*+/g, '');

            // Parse pipe-separated rich metadata: | Price: $X | Image: URL | Title: text
            let cardPrice = '';
            let cardImage = '';
            let cardTitle = '';
            let cardSeller = '';
            let cardFeedback = '';
            let cardBrand = '';
            let cardAvailability = '';
            let cardCondition = '';
            let cardShipping = '';
            let cardLocation = '';
            const pipeIndex = rawLine.indexOf('|');
            if (pipeIndex !== -1) {
                const metaPart = rawLine.slice(pipeIndex);
                metaPart.split('|').forEach((seg) => {
                    const s = seg.trim();
                    if (/^price:/i.test(s)) cardPrice = s.replace(/^price:/i, '').trim();
                    else if (/^image:/i.test(s)) cardImage = s.replace(/^image:/i, '').trim();
                    else if (/^title:/i.test(s)) cardTitle = s.replace(/^title:/i, '').trim();
                    else if (/^(seller|shop):/i.test(s)) cardSeller = s.replace(/^(seller|shop):/i, '').trim();
                    else if (/^feedback:/i.test(s)) cardFeedback = s.replace(/^feedback:/i, '').trim();
                    else if (/^brand:/i.test(s)) cardBrand = s.replace(/^brand:/i, '').trim();
                    else if (/^availability:/i.test(s)) cardAvailability = s.replace(/^availability:/i, '').trim();
                    else if (/^condition:/i.test(s)) cardCondition = s.replace(/^condition:/i, '').trim();
                    else if (/^shipping:/i.test(s)) cardShipping = s.replace(/^shipping:/i, '').trim();
                    else if (/^location:/i.test(s)) cardLocation = s.replace(/^location:/i, '').trim();
                });
            }

            cards.push({
                retailer,
                url,
                host,
                query: cardTitle || getLinkCardDetail(url, type),
                price: cardPrice,
                image: cardImage,
                seller: cardSeller,
                feedback: cardFeedback,
                brand: cardBrand,
                availability: cardAvailability,
                condition: cardCondition,
                shipping: cardShipping,
                location: cardLocation,
            });
            hasStartedLinks = true;
            endIndex = i + 1;
            continue;
        }

        if (hasStartedLinks) {
            const nextLinkIndex = lines.slice(i + 1).findIndex((nextLine) => {
                const trimmed = nextLine.trim();
                return trimmed && ANY_URL_PATTERN.test(trimmed);
            });
            if (/:\s*$/.test(line) && nextLinkIndex >= 0) {
                endIndex = i + 1;
                continue;
            }
            break;
        }
        endIndex = i + 1;
    }

    if (cards.length < 2) return null;

    const inlineHeadingPrefix = lines[headingIndex]
        .replace(/\bavailable from these sources\s*:?\s*$/i, '')
        .trim();
    const before = [
        lines.slice(0, headingIndex).join('\n').trim(),
        LINK_LIST_HEADING_PATTERN.test(lines[headingIndex]) ? '' : inlineHeadingPrefix,
    ].filter(Boolean).join('\n').trim();
    const after = lines.slice(endIndex).join('\n').trim();
    const fallbackQuery = cards.find((card) => card.query)?.query || copy.fallback;

    return {
        before,
        after,
        type,
        cards: cards.map((card) => ({
            ...card,
            query: card.query || fallbackQuery,
        })),
    };
};

const hasUnparsedLinkListLikeContent = (content = '') => {
    const text = safeText(content);
    if (!ANY_URL_PATTERN.test(text)) return false;
    return /(?:buy links?|shop links?|shopping links?|sourcing links?|available from these sources|nearby repair shops?|google maps|maps\.google|autozone|rockauto|napa|amazon|oem dealer)/i.test(text);
};

const cleanShopText = (value = '') => cleanDisplayText(value)
    .replace(/^\d+[.)]\s*/, '')
    .replace(/\*\*/g, '')
    .trim();

const extractGoogleMapsUrl = (value = '') => {
    const markdownMatch = safeText(value).match(/\]\((https?:\/\/(?:www\.)?(?:google\.com\/maps|maps\.google\.com)\/[^)\s]+)\)/i);
    if (markdownMatch) return cleanRetailerUrl(markdownMatch[1]);
    const urlMatch = safeText(value).match(GOOGLE_MAPS_URL_PATTERN);
    return urlMatch ? cleanRetailerUrl(urlMatch[0]) : '';
};

const parseShopResultBlock = (content = '') => {
    const text = safeText(content);
    GOOGLE_MAPS_URL_PATTERN.lastIndex = 0;
    if (!/\b(?:repair shops?|mechanics?|service centres?|service centers?)\b/i.test(text) || !GOOGLE_MAPS_URL_PATTERN.test(text)) return null;
    GOOGLE_MAPS_URL_PATTERN.lastIndex = 0;

    const mechanicIndex = text.search(/\bWhat to tell the mechanic\b/i);
    const searchArea = mechanicIndex >= 0 ? text.slice(0, mechanicIndex) : text;
    const after = mechanicIndex >= 0 ? text.slice(mechanicIndex).trim() : '';
    const paragraphs = searchArea.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
    const firstShopIndex = paragraphs.findIndex((part) => extractGoogleMapsUrl(part));
    if (firstShopIndex < 0) return null;

    const before = paragraphs.slice(0, firstShopIndex).join('\n\n').trim();
    const shopBlocks = paragraphs.slice(firstShopIndex).flatMap((part) => {
        const urlCount = (part.match(GOOGLE_MAPS_URL_PATTERN) || []).length;
        if (urlCount <= 1) return [part];
        return part.split(/\n(?=\s*\d+[.)]\s*)/).map((block) => block.trim()).filter(Boolean);
    });

    const shops = shopBlocks
        .map((part) => {
            const mapsUrl = extractGoogleMapsUrl(part);
            if (!mapsUrl) return null;
            const withoutUrl = part
                .replace(/\[https?:\/\/[^\]]+\]\(https?:\/\/[^)]+\)/gi, '')
                .replace(GOOGLE_MAPS_URL_PATTERN, '')
                .replace(/\bView:\s*/gi, '')
                .replace(/\s+/g, ' ')
                .trim();
            const ratingMatch = withoutUrl.match(/\bRating:\s*([\d.]+)(?:\s*⭐)?(?:\s*\(([\d,]+)\s+reviews?\))?/i);
            const detailText = ratingMatch ? withoutUrl.slice(0, ratingMatch.index).trim() : withoutUrl;
            const commaIndex = detailText.indexOf(',');
            const nameSource = commaIndex > 0 ? detailText.slice(0, commaIndex) : detailText;
            const addressSource = commaIndex > 0 ? detailText.slice(commaIndex + 1) : '';
            return {
                name: cleanShopText(nameSource) || 'Repair shop',
                address: cleanShopText(addressSource),
                rating: ratingMatch?.[1] || '',
                reviewCount: ratingMatch?.[2] || '',
                mapsUrl,
            };
        })
        .filter(Boolean);

    if (shops.length < 2) return null;
    const locationMatch = before.match(/\bin\s+([^:\n]+?)(?:\s+for\b|:|\.)/i);
    const vehicleMatch = before.match(/\bfor\s+(?:your\s+)?([^:\n.]+?)(?:\s+in\b|:|\.)/i);
    const key = shops.map((shop) => shop.mapsUrl).join('|');

    return {
        key,
        before,
        after,
        location: cleanDisplayText(locationMatch?.[1] || ''),
        vehicle: cleanDisplayText(vehicleMatch?.[1] || ''),
        issueSummary: 'general car service',
        shops,
    };
};

const contentWithoutShopResultBlock = (content = '', shopResult = null) => {
    if (!shopResult) return content;
    return [shopResult.before, shopResult.after].filter(Boolean).join('\n\n').trim();
};

const LinkListStreamingPlaceholder = ({ type = 'parts' }) => {
    const copy = getLinkListCopy(type);
    return (
        <div className="not-prose my-5 max-w-3xl rounded-3xl border border-neutral-200 bg-white/70 p-4 shadow-sm">
            <p className="m-0 text-[11px] font-black uppercase tracking-[0.16em] text-neutral-500">{copy.eyebrow}</p>
            <div className="mt-3 space-y-2">
                {[0, 1, 2].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4">
                        <div className="h-12 w-12 animate-pulse rounded-xl bg-neutral-100" />
                        <div className="min-w-0 flex-1 space-y-2">
                            <div className="h-3 w-28 animate-pulse rounded-full bg-neutral-200" />
                            <div className="h-3 w-44 animate-pulse rounded-full bg-neutral-100" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LinkListRow = ({ card, type = 'parts' }) => {
    const accentClasses = getRetailerAccentClasses(card.retailer);
    const faviconUrl = card.host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(card.host)}&sz=64` : '';
    const actionLabel = type === 'shops' ? `Open ${card.retailer} location` : `Open ${card.retailer} link`;
    const sourceLabel = getRetailerSourceLabel(card, type);
    const hasRichImage = Boolean(card.image);
    const hasPrice = Boolean(card.price);
    const sellerLabel = [card.seller && `Seller: ${card.seller}`, card.feedback && `${card.feedback}% positive`].filter(Boolean).join(' / ');
    const detailBadges = type === 'parts'
        ? [
            card.brand && `Brand: ${card.brand}`,
            card.availability,
            card.condition,
            card.shipping,
            card.location && `Ships from ${card.location}`,
        ].filter(Boolean)
        : [];

    return (
        <a
            href={card.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group/link-row grid w-full grid-cols-[64px_minmax(0,1fr)_36px] items-center gap-3 rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-3 text-left no-underline shadow-none transition-colors duration-150 hover:border-[#D81F26] hover:bg-[#F9F9F9]"
        >
            <div className={`flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border ${hasRichImage ? 'border-[#d9d9d9] bg-[#F9F9F9]' : accentClasses}`}>
                {hasRichImage ? (
                    <img
                        src={card.image}
                        alt={card.query || card.retailer}
                        className="h-16 w-16 rounded-xl object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                ) : faviconUrl ? (
                    <img
                        src={faviconUrl}
                        alt=""
                        className="h-6 w-6 rounded-md"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <span className="text-base font-black">{card.retailer.slice(0, 1).toUpperCase()}</span>
                )}
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <h5 className="m-0 truncate text-sm font-black text-[#414141]">{card.retailer}</h5>
                    <span className="rounded-full bg-[#F9F9F9] px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-[#414141]">
                        {sourceLabel}
                    </span>
                    {hasPrice && (
                        <span className="rounded-full bg-[#e6f9ee] px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-[#0a7a3e]">
                            {card.price}
                        </span>
                    )}
                    {!hasRichImage && card.host && (
                        <span className="truncate text-[10px] font-extrabold uppercase tracking-wide text-[#989898]">
                            {card.host}
                        </span>
                    )}
                </div>
                <p className="m-0 mt-1 line-clamp-2 text-sm font-semibold leading-5 text-[#5e5e5e]">
                    {card.query}
                </p>
                {sellerLabel && (
                    <p className="m-0 mt-1 truncate text-xs font-bold text-[#414141]">{sellerLabel}</p>
                )}
                {detailBadges.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {detailBadges.slice(0, 4).map((detail) => (
                            <span key={detail} className="rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-2 py-0.5 text-[10px] font-extrabold text-[#414141]">
                                {detail}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <span
                className="ml-auto inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#414141] text-white transition-colors duration-150 group-hover/link-row:bg-[#414141]"
                aria-label={actionLabel}
                title={actionLabel}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 17 17 7" />
                    <path d="M7 7h10v10" />
                </svg>
            </span>
        </a>
    );
};

const LinkListCards = ({ cards = [], type = 'parts' }) => {
    const copy = getLinkListCopy(type);
    // Always show all cards in a scrollable container so eBay listings are never hidden
    const isScrollable = cards.length > 3;

    return (
        <div className="not-prose my-5 max-w-2xl">
            <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                    <p className="m-0 text-[11px] font-black uppercase tracking-[0.16em] text-[#414141]">{copy.eyebrow}</p>
                </div>
                {cards.length > 1 && (
                    <span className="text-[10px] font-bold text-[#989898]">{cards.length} results</span>
                )}
            </div>

            <div className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-2 shadow-none">
                <div className={`${isScrollable ? 'max-h-[420px] overflow-y-auto pr-1 custom-scrollbar' : ''} space-y-2`}>
                    {cards.map((card, index) => (
                        <LinkListRow
                            key={`${card.retailer}-${card.url}-${index}`}
                            card={card}
                            type={type}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const ShopResultsButton = ({ result, onOpen }) => {
    if (!result?.shops?.length) return null;
    const isReferralLocked = Boolean(result?.referralLock);
    return (
        <button
            type="button"
            onClick={() => onOpen?.(result)}
            className="not-prose my-4 inline-flex w-full max-w-sm items-center justify-between gap-3 rounded-2xl border border-[#D9D9D9] bg-[#F9F9F9] px-4 py-3 text-left text-[#414141] shadow-sm transition hover:border-[#414141] hover:bg-[#F2F2F2] sm:w-auto sm:min-w-[280px]"
        >
            <span className="min-w-0">
                <span className="block text-sm font-black">
                    {isReferralLocked ? `Continue with ${result.referralLock?.shop?.name || 'referring shop'}` : 'View repair shops'}
                </span>
                <span className="block truncate text-xs font-bold text-[#6B6B6B]">
                    {isReferralLocked ? 'Referral shop selected for this job' : `${result.shops.length} options${result.location ? ` near ${result.location}` : ''}`}
                </span>
            </span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#D81F26] text-white">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 17 17 7" />
                    <path d="M7 7h10v10" />
                </svg>
            </span>
        </button>
    );
};

const getReferralShopActionLabel = (referralLock, fallback = 'Find a Shop') => {
    if (!referralLock) return fallback;
    return `Continue with ${referralLock.shop?.name || 'your shop'}`;
};

const getZipFromShopResult = (result = null) => {
    const match = cleanDisplayText(result?.location || '').match(/\b\d{5}\b/);
    return match ? match[0] : '';
};

const normalizeShopResultForStorage = (result = null) => {
    if (!result || typeof result !== 'object') return null;
    const shops = Array.isArray(result.shops) ? result.shops.slice(0, STORED_SHOPS_PER_RESULT_LIMIT).map((shop) => ({
        name: truncateForStorage(cleanShopText(shop.name || '') || 'Repair shop', 160),
        address: truncateForStorage(cleanShopText(shop.address || ''), 260),
        rating: shop.rating != null ? String(shop.rating).slice(0, 16) : '',
        reviewCount: shop.reviewCount != null ? String(shop.reviewCount).slice(0, 24) : '',
        mapsUrl: String(shop.mapsUrl || '').slice(0, 1024),
        phone: String(shop.phone || '').slice(0, 40),
        openNow: shop.openNow,
        photoUrl: String(shop.photoUrl || '').slice(0, 1024),
    })).filter((shop) => shop.name || shop.mapsUrl) : [];
    const key = String(result.key || shops.map((shop) => shop.mapsUrl || shop.name).join('|')).slice(0, 12000);
    if (!key || shops.length === 0) return null;
    return {
        key,
        before: truncateForStorage(result.before || '', 800),
        after: truncateForStorage(result.after || '', 1200),
        location: truncateForStorage(result.location || '', 120),
        vehicle: truncateForStorage(result.vehicle || '', 120),
        issueSummary: truncateForStorage(result.issueSummary || result.issue_summary || 'general car service', 160),
        shops,
    };
};

const normalizeStoredShopResults = (stored) => {
    const source = stored?.results || stored;
    const rawResults = Array.isArray(source)
        ? source
        : (source && typeof source === 'object' ? Object.values(source) : []);
    return rawResults.reduce((cache, result) => {
        const normalized = normalizeShopResultForStorage(result);
        if (normalized?.key) cache[normalized.key] = normalized;
        return cache;
    }, {});
};

// Card rows from the `parts_data` stream event. The host/label helpers live in this file, so
// they are handed to the (separately tested) builder in src/utils/partsSources.js.
const buildPartsCards = (partsData) => buildPartsCardsFrom(partsData, {
    getHostFromUrl,
    cleanDisplayText,
    titleCaseWords,
    getLinkCardDetail,
});

const renderMessageWithLinkCards = (content = '', options = {}) => {
    // Driver-facing wording: show "Symptom Report" wherever the text says "Diagnostic Report".
    content = relabelReport(content);
    // Structured parts data wins over anything parsed out of the text: it is the API's own
    // payload rather than the model's transcription of it.
    const partsCards = options.partsCards || [];
    if (partsCards.length > 0) {
        const parsedForProse = parseLinkListBlock(content);
        const prose = stripRetailerLinkProse(
            parsedForProse
                ? [parsedForProse.before, parsedForProse.after].filter(Boolean).join('\n\n')
                : content,
        );
        return (
            <>
                {prose && renderMarkdownWithLinks(prose)}
                <LinkListCards cards={partsCards} type="parts" />
            </>
        );
    }

    const parsed = parseLinkListBlock(content);
    if (!parsed) {
        if (options.suppressRawLinkLists && hasUnparsedLinkListLikeContent(content)) {
            return <LinkListStreamingPlaceholder type={/shop|mechanic|garage/i.test(content) ? 'shops' : 'parts'} />;
        }
        return renderMarkdownWithLinks(content);
    }

    return (
        <>
            {parsed.before && renderMarkdownWithLinks(parsed.before)}
            <LinkListCards cards={parsed.cards} type={parsed.type} />
            {parsed.after && renderMarkdownWithLinks(parsed.after)}
        </>
    );
};

const isPartsFlowSuggestion = (value = '') => (
    /\b(parts?|part number|oem|aftermarket|fitment|compatibility|compare brands?)\b/i.test(String(value || '')) ||
    /\bfind\b.{0,30}\btools?\b/i.test(String(value || ''))
);

const withoutPartsFlowSuggestions = (suggestions = []) => (
    PARTS_FLOW_ENABLED
        ? suggestions
        : suggestions.filter((suggestion) => !isPartsFlowSuggestion(suggestion))
);

const getFallbackSuggestionsForAssistantMessage = (message = {}, context = null) => {
    if (message?.role !== 'assistant') return [];
    const content = extractSuggestionsAndContent(message.content || '').content;
    if (!cleanDisplayText(content)) return [];
    if (isGarageVehicleListResponse(content)) {
        return GARAGE_RESPONSE_SUGGESTIONS;
    }
    const parsedLinks = parseLinkListBlock(content);
    const hasShopResults = Boolean(message.shopResult || parseShopResultBlock(content));
    const isShopContext = hasShopResults || parsedLinks?.type === 'shops' || context?.type === 'shop';
    const isPartsContext = parsedLinks?.type === 'parts' || context?.type === 'parts' || /\b(part|parts|oem|aftermarket|fitment|compatibility|nhtsa|recall)\b/i.test(content);

    if (isShopContext) {
        return withoutPartsFlowSuggestions([
            'What should I tell the shop?',
            'Find parts for this repair',
            'Need a diagnostic report',
        ]);
    }
    if (isPartsContext) {
        return withoutPartsFlowSuggestions([
            'Get part number',
            'Compare brands',
            'Find a repair shop',
        ]);
    }
    if (/\b(diagnostic|diagnosis|symptom|issue|question|report|repair)\b/i.test(content) || context?.type === 'diagnostic') {
        return withoutPartsFlowSuggestions([
            'Continue diagnosis',
            'Find parts',
            'Find a repair shop',
        ]);
    }
    return [];
};

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Did the previous assistant turn ask the user to pick / confirm a vehicle to REMOVE?
// e.g. "You have two Hyundai Vernas. Which one do you want to remove?" or
// "You are about to permanently remove your 2020 Verna... reply 'yes' to remove".
// If so, the user's reply (often just "2020 Verna" — a year + known model) is a delete
// answer, NOT an add request, so we must suppress the Add Vehicle form for that turn.
const looksLikePendingDeletePrompt = (text = '') => {
    const t = String(text).toLowerCase();
    if (!/\b(remove|delete|deletion|removing|deleting|get\s+rid)\b/.test(t)) return false;
    return t.includes('?') || /\b(confirm|which|reply|yes|no|cancel|permanently)\b/.test(t);
};

// Did the previous assistant turn ask whether to add/save a vehicle to Garage?
// If so, confirmations like "yes" / "add it" must open the guided form instead
// of going back to the agent, where add_vehicle can save directly.
const looksLikePendingAddVehiclePrompt = (text = '') => {
    const t = String(text).toLowerCase();
    if (!/\b(add|save|register)\b/.test(t)) return false;
    if (!/\b(vehicle|car|truck|suv|vin|garage)\b/.test(t)) return false;
    return t.includes('?') || /\bshould i|would you like|do you want|confirm|reply|yes|no\b/.test(t);
};

const extractRecentVinPrefillFromMessages = (messageList = []) => {
    for (let i = messageList.length - 1; i >= 0; i--) {
        const msg = messageList[i];
        if (msg?.role !== 'user') continue;
        const vin = (String(msg.content || '').match(VIN_TOKEN_PATTERN) || [])[0];
        if (vin) return { vin: vin.toUpperCase() };
    }
    return null;
};

const CHAT_CONTEXT_INTENT_TYPOS = {
    fnd: 'find',
    fidn: 'find',
    fin: 'find',
    serch: 'search',
    seach: 'search',
    searh: 'search',
    lok: 'look',
    loking: 'looking',
    lookng: 'looking',
    ned: 'need',
    neeed: 'need',
    recomend: 'recommend',
    reccomend: 'recommend',
    ssop: 'shop',
    ssops: 'shops',
    sop: 'shop',
    sops: 'shops',
    shp: 'shop',
    shps: 'shops',
    shpo: 'shop',
    shpos: 'shops',
    sshop: 'shop',
    sshops: 'shops',
    ship: 'shop',
    ships: 'shops',
    mecanic: 'mechanic',
    mecanics: 'mechanics',
    mechnic: 'mechanic',
    mechnics: 'mechanics',
    mechaninc: 'mechanic',
    mechanincs: 'mechanics',
    garag: 'garage',
    garags: 'garages',
    garaje: 'garage',
    garajes: 'garages',
    delaer: 'dealer',
    delaers: 'dealers',
    deelar: 'dealer',
    deelars: 'dealers',
    aprt: 'part',
    aprts: 'parts',
    prt: 'part',
    prts: 'parts',
    prat: 'part',
    prats: 'parts',
    patrs: 'parts',
    parst: 'parts',
    parsts: 'parts',
    parte: 'part',
    partes: 'parts',
    replacment: 'replacement',
    replacments: 'replacements',
};

const normalizeChatContextIntentText = (value = '') => (
    cleanDisplayText(value).replace(/\b[a-z][a-z0-9']*\b/ig, (word) => {
        const normalized = CHAT_CONTEXT_INTENT_TYPOS[word.toLowerCase()];
        return normalized || word;
    })
);

const removeVehicleWordsFromContextTitle = (title = '', vehicleLabel = '') => {
    const vehicle = cleanDisplayText(vehicleLabel);
    if (!vehicle) return title;

    const vehicleWords = vehicle
        .split(/\s+/)
        .map((word) => word.trim())
        .filter((word) => word.length >= 2);

    return vehicleWords.reduce((nextTitle, word) => (
        nextTitle.replace(new RegExp(`\\b${escapeRegExp(word)}\\b`, 'ig'), ' ')
    ), title);
};

// Connector/filler tokens and pronouns that carry no job subject on their own.
// Used both to scrub extracted titles and to detect a title that is nothing but
// filler (e.g. "it" from "find a shop for it"), so the banner can fall back to a
// sensible default instead of showing the bare pronoun.
const CHAT_CONTEXT_STOP_TOKENS = new Set([
    'for', 'a', 'an', 'the', 'of', 'to', 'and', 'or', 'my', 'me',
    'near', 'with', 'on', 'in', 'at', 'by', 'your', 'this', 'that',
    // Pronouns / fillers that reference an earlier subject we can't resolve.
    // Without these, "find a shop for it" collapses to the literal title "it"
    // (2 chars, so it survives the length filter). Drop them so the title
    // falls back to the vehicle/label default instead.
    'it', 'its', 'one', 'ones', 'them', 'they', 'these', 'those',
    'some', 'any', 'here', 'there', 'us', 'we', 'you', 'i',
]);

// True when a title is empty or made up entirely of filler/pronoun/single-char
// tokens — i.e. it names no real subject and should be replaced by a fallback.
const isPlaceholderContextTitle = (title = '') => {
    const tokens = cleanDisplayText(title).toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;
    return tokens.every((word) => word.length <= 1 || CHAT_CONTEXT_STOP_TOKENS.has(word));
};

const cleanupChatContextTitle = (content = '', type = 'parts', vehicleLabel = '') => {
    const label = type === 'shop' ? 'repair shops' : 'parts';
    let title = normalizeChatContextIntentText(content)
        .replace(/\bhttps?:\/\/\S+/gi, ' ')
        .replace(/\b(find|buy|search|look(?:ing)? for|need|get|order|source|price|recommend|book|call|show|give me|help me find)\b/ig, ' ')
        .replace(/\b(part|parts|tool|tools|oem|aftermarket|replacement|buy links?|links?)\b/ig, ' ')
        .replace(/\b(shop|shops|mechanic|mechanics|garage|garages|dealer|dealership|service center|repair center|repair shop|repair shops)\b/ig, ' ')
        .replace(/\b(near me|nearby|local|around me|around|locations?|places?|for my|for the|for a|for an|my car|my vehicle|vehicle|car)\b/ig, ' ');

    title = removeVehicleWordsFromContextTitle(title, vehicleLabel)
        .replace(/\b(19|20)\d{2}\b/g, ' ')
        .replace(/[^a-z0-9\s/-]/ig, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    // Drop leftover connector/filler tokens and stray single letters — e.g. the "Y"
    // from "Model Y", or the "a"/"for" left by "a repair shop for …". Without this the
    // title degrades into meaningless fragments like "for Y" / "a for Y" that then
    // compound into "a for Y a for Y a for Y". Pronouns like "it" are in the set too,
    // so "find a shop for it" falls through to the vehicle/label default below.
    title = title
        .split(/\s+/)
        .filter((word) => word.length > 1 && !CHAT_CONTEXT_STOP_TOKENS.has(word.toLowerCase()))
        .join(' ')
        .trim();

    return title || (vehicleLabel ? `${vehicleLabel} ${label}` : label);
};

const inferLightweightChatContextFromMessages = (messages = [], vehicleLabel = '') => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message?.role !== 'user') continue;
        const content = cleanDisplayText(getMessageContent(message) || message.content || '');
        if (!content || isReportCommand(content) || isReportChoiceText(content)) continue;
        const normalizedContent = normalizeChatContextIntentText(content);

        if (SHOP_CONTEXT_PATTERN.test(normalizedContent)) {
            return {
                type: 'shop',
                title: cleanupChatContextTitle(normalizedContent, 'shop', vehicleLabel),
                vehicleLabel,
            };
        }

        if (PARTS_CONTEXT_PATTERN.test(normalizedContent)) {
            return {
                type: 'parts',
                title: cleanupChatContextTitle(normalizedContent, 'parts', vehicleLabel),
                vehicleLabel,
            };
        }

        if (DIAGNOSTIC_CONTEXT_PATTERN.test(normalizedContent)) return null;
    }

    return null;
};


const normalizeClientIntentType = (value = '') => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'parts' || normalized === 'part') return 'parts';
    if (normalized === 'shop' || normalized === 'shops') return 'shop';
    if (normalized === 'service' || normalized === 'service_log') return 'service';
    if (normalized === 'maintenance') return 'maintenance';
    return '';
};

const shouldSwitchOutOfPartsOrShopIntent = (text = '') => {
    const normalized = normalizeChatContextIntentText(text);
    if (!normalized) return false;
    if (PARTS_CONTEXT_PATTERN.test(normalized) || SHOP_CONTEXT_PATTERN.test(normalized)) return false;
    return EXPLICIT_DIAGNOSTIC_SWITCH_PATTERN.test(normalized);
};

const getIntentTitleFromMessage = (text = '', intentType = 'parts', vehicleLabel = '') => (
    cleanupChatContextTitle(
        normalizeChatContextIntentText(text),
        intentType === 'shop' ? 'shop' : 'parts',
        vehicleLabel
    )
);

const NEED_MORE_DIAGNOSTICS_PATTERN = /\b(?:narrow down the cause before generating the report|more diagnostic answers? before generating the diagnostic report)\b/i;

const isNeedMoreDiagnosticsMessage = (message) => (
    message?.role === 'assistant' &&
    NEED_MORE_DIAGNOSTICS_PATTERN.test(cleanDisplayText(getMessageContent(message) || message.content || ''))
);

const parseStoredJson = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const safeJsonParse = (raw, fallback = null) => {
    const parsed = parseStoredJson(raw);
    return parsed == null ? fallback : parsed;
};

const getStorageAreas = () => {
    if (typeof window === 'undefined') return [];
    return [window.sessionStorage, window.localStorage].filter(Boolean);
};

const normalizeStorageKeyList = (keys) => (
    Array.isArray(keys) ? keys : [keys]
).filter(Boolean);

const getReportCardKey = (key) => `${CHAT_REPORT_STORAGE_PREFIX}${key}`;

const getStoredReportCards = (keys) => {
    const cards = [];
    const seen = new Set();
    normalizeStorageKeyList(keys).forEach((key) => {
        getStorageAreas().forEach((storage) => {
            const stored = parseStoredJson(storage.getItem(getReportCardKey(key)));
            const list = Array.isArray(stored) ? stored : stored ? [stored] : [];
            list.forEach((card) => {
                const id = card?.diagnosticReport?.id || card?.id;
                const uniqueKey = id || JSON.stringify(card);
                if (!uniqueKey || seen.has(uniqueKey)) return;
                seen.add(uniqueKey);
                cards.push(card);
            });
        });
    });
    return cards;
};

const saveStoredReportCard = (keys, reportCard) => {
    if (!reportCard) return;
    normalizeStorageKeyList(keys).forEach((key) => {
        getStorageAreas().forEach((storage) => {
            const storageKey = getReportCardKey(key);
            const existing = parseStoredJson(storage.getItem(storageKey));
            const list = Array.isArray(existing) ? existing : existing ? [existing] : [];
            const reportId = reportCard?.diagnosticReport?.id || reportCard?.id;
            const nextList = [
                ...list.filter((card) => (card?.diagnosticReport?.id || card?.id) !== reportId),
                reportCard,
            ].slice(-8);
            try {
                storage.setItem(storageKey, JSON.stringify(nextList));
            } catch {}
        });
    });
};

const getMessageChronologyMs = (message = {}) => {
    const timestamp = (
        message?.timestamp ||
        message?.createdAt ||
        message?.created_at ||
        message?.diagnosticReport?.timestamp ||
        message?.diagnosticReport?.generatedAt ||
        message?.diagnosticReport?.generated_at ||
        message?.diagnosticReport?.createdAt ||
        message?.diagnosticReport?.created_at ||
        ''
    );
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? null : parsed;
};

const sortMessagesChronologically = (messages = []) => (
    [...messages].sort((a, b) => {
        const aTime = getMessageChronologyMs(a);
        const bTime = getMessageChronologyMs(b);
        if (aTime != null && bTime != null && aTime !== bTime) return aTime - bTime;
        const aServerId = a?.serverId;
        const bServerId = b?.serverId;
        if (aServerId != null && bServerId != null && aServerId !== bServerId) return aServerId - bServerId;
        return 0;
    })
);

const normalizeReportCardForHistory = (card = {}) => {
    if (!card?.diagnosticReport) return card;
    const timestamp = (
        card.timestamp ||
        card.createdAt ||
        card.created_at ||
        card.diagnosticReport.timestamp ||
        card.diagnosticReport.generatedAt ||
        card.diagnosticReport.generated_at ||
        card.diagnosticReport.createdAt ||
        card.diagnosticReport.created_at ||
        ''
    );
    return timestamp ? { ...card, timestamp } : card;
};

const mergeStoredReportCards = (messages = [], keys = [], options = {}) => {
    const { appendUndated = messages.length === 0 } = options;
    const merged = [...messages];
    const existingIds = new Set(messages.map((message) => message?.diagnosticReport?.id).filter(Boolean));
    getStoredReportCards(keys).forEach((card) => {
        const normalizedCard = normalizeReportCardForHistory(card);
        const reportId = normalizedCard?.diagnosticReport?.id;
        if (reportId && existingIds.has(reportId)) return;
        if (reportId) existingIds.add(reportId);
        if (getMessageChronologyMs(normalizedCard) == null && !appendUndated) return;
        merged.push(normalizedCard);
    });
    return sortMessagesChronologically(merged);
};

const loadPendingChatIntent = () => {
    for (const storage of getStorageAreas()) {
        const parsed = parseStoredJson(storage.getItem(PENDING_CHAT_INTENT_STORAGE_KEY));
        if (parsed) return parsed;
    }
    return null;
};

const clearPendingChatIntent = () => {
    getStorageAreas().forEach((storage) => {
        try {
            storage.removeItem(PENDING_CHAT_INTENT_STORAGE_KEY);
        } catch {}
    });
};

const normalizeGuidedCauseIndex = (report, intent = {}) => {
    if (Number.isFinite(Number(intent.causeIndex))) {
        return Math.max(0, Number(intent.causeIndex));
    }
    if (intent.causeId && Array.isArray(report?.likelyCauses)) {
        const index = report.likelyCauses.findIndex((cause) => String(cause?.id) === String(intent.causeId));
        if (index >= 0) return index;
    }
    return 0;
};

const resolveGuidedCheckIntent = (rawIntent) => {
    const intent = rawIntent?.guidedCheck ? rawIntent.guidedCheck : rawIntent;
    if (!intent) return null;
    const reportId = intent.reportId || intent.report_id || intent.id || intent.report?.id;
    const report = normalizeDiagnosticReportForUi(intent.report || loadDiagnosticReport(reportId));
    if (!report) return null;
    return {
        ...intent,
        report,
        reportId: report.id || reportId,
        causeIndex: normalizeGuidedCauseIndex(report, intent),
    };
};

const buildVehicleAddedMessages = (vehicleAddedIntent = {}) => {
    const vehicleLabel = cleanDisplayText(
        vehicleAddedIntent.vehicleLabel ||
        vehicleAddedIntent.vehicle ||
        [vehicleAddedIntent.year, vehicleAddedIntent.make, vehicleAddedIntent.model].filter(Boolean).join(' ')
    );
    const mileage = cleanDisplayText(vehicleAddedIntent.mileage || vehicleAddedIntent.odometer || '');
    const unit = vehicleAddedIntent.unit === 'km' ? 'km' : 'miles';
    const labelWithMileage = mileage ? `${vehicleLabel} (${mileage} ${unit})` : vehicleLabel;
    return [
        {
            id: `vehicle-added-${Date.now()}`,
            role: 'assistant',
            content: `${labelWithMileage || 'Your vehicle'} is now in your Garage. What would you like to do - any symptoms, warning lights, upcoming maintenance, or questions?`,
            suggestions: withoutPartsFlowSuggestions(['Diagnose an Issue', 'Find Parts or Tools', 'Log a Service', 'Maintenance Due?', 'Plan a Modification']),
            type: 'vehicle_added',
            suppressDiagnosticTicketCard: true,
        },
    ];
};

// On a fresh account, when the user describes a problem but has no vehicle, CarFido asks
// them to add a vehicle first ("Before I continue, what's your vehicle Year and Model?").
// After the vehicle is added we want CarFido to RESUME and answer that original issue.
// This finds that original issue message: the user's message right before CarFido's
// vehicle-info prompt. Returns '' when there's no such pending issue (e.g. the user just
// added a vehicle without describing a problem), so the normal confirmation is shown.
const VEHICLE_INFO_PROMPT_MARKERS = [
    "before i continue, what's your vehicle",
    'before i continue, what’s your vehicle',
    'what year is your car',
    "what's your car's model",
    'what’s your car’s model',
];
const extractPendingIssueForResume = (msgs) => {
    if (!Array.isArray(msgs) || msgs.length === 0) return '';
    let promptIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        if ((m?.role || '') !== 'assistant') continue;
        const text = String(m?.content || '').toLowerCase();
        if (VEHICLE_INFO_PROMPT_MARKERS.some((mk) => text.includes(mk))) { promptIdx = i; break; }
    }
    if (promptIdx <= 0) return '';
    for (let i = promptIdx - 1; i >= 0; i--) {
        const m = msgs[i];
        if ((m?.role || '') !== 'user') continue;
        const text = String(m?.content || '').trim();
        if (!text) return '';
        // The issue must be a real problem/question — not an "add a vehicle" request or a
        // bare vehicle spec the user typed to answer the prompt.
        if (isAddVehicleFormRequest(text)) return '';
        return text;
    }
    return '';
};

const suppressesInlineDiagnosticTicket = (message) => (
    message?.suppressDiagnosticTicketCard ||
    message?.type === 'vehicle_added' ||
    message?.type === 'vehicle_added_card' ||
    message?.type === 'vehicle_form'
);

const hasInlineChoiceButtons = (message) => {
    const parsedSuggestions = getMessageSuggestions(message);
    if (Array.isArray(parsedSuggestions) && parsedSuggestions.length > 0) return true;
    return [
        message?.suggestions,
        message?.buttons,
        message?.quickReplies,
        message?.quick_replies,
        message?.options,
        message?.choices,
    ].some((value) => Array.isArray(value) && value.length > 0);
};

const isDiagnosticTicketAnchorMessage = (message) => {
    if (!message || message.role !== 'assistant') return false;
    if (isDiagnosticReportMessage(message) || suppressesInlineDiagnosticTicket(message)) return false;
    if (message?.diagnosticReport || message?.diagnosticTicket || message?.ticket) return true;

    const content = cleanDisplayText(getMessageContent(message)).toLowerCase();
    if (!content) return false;
    const hasChoices = hasInlineChoiceButtons(message);
    const asksDiagnosticQuestion = (
        content.includes('previous issue about') ||
        content.includes('same problem') ||
        content.includes('something new') ||
        content.includes('question:') ||
        /\b(is|are|do|does|did|when|which|what|where)\b[\s\S]{0,180}\?/.test(content)
    );
    const mentionsVehicleSymptom = /\b(engine|crank|click|dashboard|battery|starter|door|lock|latch|window|wheel|tyre|tire|brake|rotat|moving|move|warning|light|sound|smell|ac|cool|heat|transmission|fluid|gear|shift|pedal|noise|leak|overheat|smoke)\b/.test(content);
    if (hasChoices && asksDiagnosticQuestion && (mentionsVehicleSymptom || content.includes('previous issue about'))) {
        return true;
    }
    return (
        content.includes('started a diagnostic') ||
        content.includes('diagnostic on this') ||
        content.includes('narrowing this down') ||
        content.includes('generate the diagnostic report') ||
        content.includes('ready to generate your diagnostic report') ||
        content.includes('diagnostic report generated')
    );
};

const isDiagnosticTicketCardAnchorMessage = (message) => {
    if (!isDiagnosticTicketAnchorMessage(message)) return false;
    const content = cleanDisplayText(getMessageContent(message)).toLowerCase();
    return !(
        content.includes('previous issue about') ||
        content.includes('same problem') ||
        content.includes('something new')
    );
};

const normalizeIssueReplyText = (value) => (
    cleanDisplayText(value)
        .toLowerCase()
        .replace(/[^a-z0-9\s']/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
);

const isNewIssueClarificationPrompt = (message) => {
    if (!message || message.role !== 'assistant') return false;
    const content = normalizeIssueReplyText(getMessageContent(message));
    if (!content) return false;
    const asksAboutNewIssue = (
        content.includes('something new') ||
        content.includes('new issue') ||
        content.includes('new problem') ||
        content.includes('different issue') ||
        content.includes('different problem')
    );
    if (!asksAboutNewIssue) return false;
    return (
        content.includes('previous issue') ||
        content.includes('same problem') ||
        content.includes('same issue') ||
        content.includes('still dealing') ||
        content.includes('do you mean') ||
        content.includes('still')
    );
};

const isNewIssueConfirmationText = (value) => {
    const text = normalizeIssueReplyText(value);
    if (!text) return false;
    if (/^(new|new one|new issue|new problem|different issue|different problem|separate issue|separate problem|another issue|another problem|it is a new issue|it's a new issue|its a new issue|this is a new issue|it is a different issue|it's a different issue|this is different|it is different)$/.test(text)) {
        return true;
    }
    if (/^(no|nope|nah|not same|not the same|not same issue|not the same issue|not same problem|not the same problem|no it is new|no this is new|not that one|not this one)$/.test(text)) {
        return true;
    }
    return (
        /\b(new|different|separate|another)\s+(issue|problem|one|ticket|diagnostic)\b/.test(text) ||
        /\b(?:it|this)\s+is\s+(?:a\s+)?(?:new|different)\b/.test(text) ||
        /\bnot\s+(?:the\s+)?same(?:\s+(issue|problem|one))?\b/.test(text)
    );
};

const isShortIssueClarificationReply = (value) => {
    const text = normalizeIssueReplyText(value);
    if (!text) return true;
    return /^(same|same issue|same problem|yes|yeah|yep|yup|no|nope|nah|new|new one|new issue|new problem|different issue|different problem|separate issue|separate problem|another issue|another problem|it is a new issue|it's a new issue|its a new issue|this is a new issue)$/.test(text);
};

const isSameIssueConfirmationText = (value) => {
    const text = normalizeIssueReplyText(value);
    if (!text) return false;
    // Negations mean a NEW issue ("not same", "no", "new/different one") — never "same".
    if (/\b(no|not|new|different|separate|another)\b/.test(text)) return false;
    // SAME/continue synonyms — includes "old issue", "existing", "previous", etc.
    if (/^(same|same issue|same problem|same one|same thing|the same|that same|same as before|same ticket|yes|yeah|yep|yup|still|still dealing|still that|continue|continue existing|continue existing ticket|old|old issue|old problem|old one|the old one|existing|existing issue|existing problem|existing ticket|previous|previous issue|prior|prior issue|resume|carry on|keep going|that one)$/.test(text)) {
        return true;
    }
    return (
        /\b(same|old|existing|previous|prior)\s+(issue|problem|one|ticket)\b/.test(text) ||
        /\bcontinue\b.*\bticket\b/.test(text) ||
        /\b(resume|carry on|keep going)\b/.test(text) ||
        /\bstill\s+(dealing|the same|that)\b/.test(text)
    );
};

// Pull the matched issue's title out of the "same or new?" prompt, e.g.
// 'I found a previous issue about "Will Not Start Diagnosis".' → 'Will Not Start Diagnosis'.
const extractMatchedIssueTitleFromPrompt = (message) => {
    const content = getMessageContent(message) || '';
    const match = content.match(/previous issue about\s+"([^"]+)"/i)
        || content.match(/open diagnostic ticket for\s+"([^"]+)"/i);
    return match && match[1] ? match[1].trim() : '';
};

const findLastNewIssueClarificationPromptIndex = (messageList = []) => {
    for (let index = messageList.length - 1; index >= 0; index -= 1) {
        if (isNewIssueClarificationPrompt(messageList[index])) return index;
    }
    return -1;
};

const findUserSymptomBeforeNewIssuePrompt = (messageList = []) => {
    const promptIndex = findLastNewIssueClarificationPromptIndex(messageList);
    if (promptIndex < 0) return '';
    for (let index = promptIndex - 1; index >= 0; index -= 1) {
        const message = messageList[index];
        if (message?.role !== 'user') continue;
        const content = cleanDisplayText(getMessageContent(message));
        if (!content || isShortIssueClarificationReply(content)) continue;
        return content;
    }
    return '';
};

const isVehicleManagementChatMessage = (message) => (
    message?.type === 'vehicle_added' ||
    message?.type === 'vehicle_added_user' ||
    message?.type === 'vehicle_form'
);

const createGuidedTextEntry = (role, content) => ({
    id: `guided-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content: cleanDisplayText(content),
});

const createGuidedCardEntry = (session, extra = {}) => {
    if (!session?.activeCard) return null;
    return {
        id: `guided-card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'assistant',
        type: 'guided_card_snapshot',
        content: '',
        card: {
            ...session.activeCard,
            selectedOutcome: session.selectedOutcome || '',
            notes: session.notes || '',
            repairMileage: session.repairMileage || '',
            repairNote: session.repairNote || '',
            ...extra,
        },
    };
};

const appendGuidedTimelineEntries = (timeline = [], ...entries) => (
    [
        ...(Array.isArray(timeline) ? timeline : []),
        ...entries.filter(Boolean),
    ]
);

const normalizeToolOrPart = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return { name: item };
    return {
        ...item,
        name: cleanDisplayText(item.name || item.title || item.label || item.text),
    };
};

const normalizeStepList = (steps, fallbackToolLabel = '') => {
    if (!Array.isArray(steps)) return [];
    return steps
        .map((step) => {
            if (!step) return null;
            if (typeof step === 'string') {
                return {
                    instruction: cleanDisplayText(step),
                    detail: '',
                    toolLabel: fallbackToolLabel,
                };
            }
            return {
                ...step,
                instruction: cleanDisplayText(step.instruction || step.title || step.text || step.description),
                detail: cleanDisplayText(step.detail || step.description || ''),
                toolLabel: cleanDisplayText(step.toolLabel || step.toolsLabel || step.tools || fallbackToolLabel),
                duration: cleanDisplayText(step.duration || step.timeLabel || ''),
                expectedResult: cleanDisplayText(step.expectedResult || ''),
            };
        })
        .filter((step) => step?.instruction);
};

const getGuidedStepsFromCause = (cause = {}) => {
    const toolLabel = cleanDisplayText(cause.toolSummary || cause.recommendedCheckDetail?.toolsLabel || '');
    const explicitSteps = normalizeStepList(
        cause.guidedSteps ||
        cause.checkSteps ||
        cause.diagnosticSteps ||
        cause.recommendedCheckDetail?.steps ||
        [],
        toolLabel
    );
    if (explicitSteps.length) return explicitSteps;

    const checkTitle = cleanDisplayText(cause.recommendedCheck || cause.title || 'Diagnostic Check');
    return [
        {
            instruction: `Reproduce the symptom under safe conditions so you can confirm exactly what the vehicle is doing.`,
            detail: cleanDisplayText(cause.checkDescription || ''),
            toolLabel,
        },
        {
            instruction: `Inspect the area related to ${checkTitle.toLowerCase()} for obvious damage, looseness, corrosion, leaks, or binding.`,
            detail: '',
            toolLabel,
        },
        {
            instruction: `Run the recommended check and compare what you see with the expected result.`,
            detail: cleanDisplayText(cause.expectedResult || ''),
            toolLabel,
        },
        {
            instruction: `Record whether the result supports this likely cause or rules it out.`,
            detail: '',
            toolLabel,
        },
    ];
};

// The diagnosis-specific safety precautions for the pre-repair checklist. Reads whatever the
// report attached to this cause (the backend/report builder sets `safetyPrecautions`); returns
// [] when none, so the card falls back to its generic set.
const getRepairSafetyItems = (cause = {}) => {
    const raw = cause?.safetyPrecautions || cause?.safety_precautions || cause?.safetyNotes || cause?.precautions || [];
    return (Array.isArray(raw) ? raw : [raw])
        .map((item) => (typeof item === 'string' ? item : (item?.text || item?.instruction || item?.note || '')))
        .map((s) => String(s).trim())
        .filter(Boolean);
};

const getRepairGuideStepsFromCause = (cause = {}) => {
    const explicitSteps = normalizeStepList(cause.repairSteps || cause.diySteps || cause.diyRepairSteps || cause.steps || []);
    if (explicitSteps.length) return explicitSteps;
    const repair = cleanDisplayText(cause.likelyRepair || cause.title || 'the affected component');
    const lowerRepair = repair.toLowerCase();
    return [
        {
            instruction: 'Park safely, switch the ignition off, and disconnect power if the repair involves electrical parts.',
            detail: 'Use jack stands before working near raised wheels or under the vehicle.',
        },
        {
            instruction: `Access ${lowerRepair} and inspect the surrounding wiring, connectors, fasteners, and mounting points.`,
            detail: '',
        },
        {
            instruction: `Repair or replace ${lowerRepair} using the listed tools and parts.`,
            detail: 'Do not force stuck parts; stop and ask CarFido if something does not move normally.',
        },
        {
            instruction: 'Reassemble everything, clear tools from the work area, and reconnect anything you disconnected.',
            detail: '',
        },
        {
            instruction: 'Test the vehicle and confirm the original symptom is gone before closing the ticket.',
            detail: '',
        },
    ];
};

const createGuidedCheckSession = ({ report, causeIndex = 0 } = {}) => {
    const normalizedReport = normalizeDiagnosticReportForUi(report);
    if (!normalizedReport) return null;
    const activeCauseIndex = Math.min(
        Math.max(0, Number(causeIndex) || 0),
        Math.max((normalizedReport.likelyCauses || []).length - 1, 0)
    );

    // Scheduled maintenance has no symptom to reproduce, so skip the "diagnostic check"
    // (Reproduce the symptom / What do you observe?) phase entirely and open the DIY
    // repair-guide steps directly.
    const isMaintenance = normalizedReport.diagnosisStage === 'Maintenance';
    const activeCause = (normalizedReport.likelyCauses || [])[activeCauseIndex];
    const startRepairGuide = isMaintenance && getRepairGuideStepsFromCause(activeCause || {}).length > 0;

    return {
        id: `guided-check-${normalizedReport.id || Date.now()}`,
        reportId: normalizedReport.id,
        report: normalizedReport,
        activeCauseIndex,
        currentStepIndex: 0,
        phase: startRepairGuide ? 'repair_step' : 'step',
        selectedOutcome: '',
        notes: '',
        repairMileage: '',
        repairNote: '',
        isClosingTicket: false,
        closeError: '',
        ruledOutCauseIds: [],
        activeCard: startRepairGuide
            ? { type: 'repair_step', causeIndex: activeCauseIndex, stepIndex: 0 }
            : { type: 'step', causeIndex: activeCauseIndex, stepIndex: 0 },
        timeline: [
            createGuidedTextEntry(
                'assistant',
                startRepairGuide
                    ? "This is scheduled maintenance, so let's go straight to the DIY steps. Use the arrows to move between steps, then mark it complete on the last one."
                    : "Let's work through diagnostic checks step by step. I'll guide you through each one - just tap your response and we'll move forward together."
            ),
        ],
    };
};

const buildGuidedCheckSessionFromIntent = (rawIntent) => {
    const guidedCheckIntent = resolveGuidedCheckIntent(rawIntent);
    if (!guidedCheckIntent?.report) return null;
    return {
        guidedCheckIntent,
        session: createGuidedCheckSession({
            report: guidedCheckIntent.report,
            causeIndex: guidedCheckIntent.causeIndex,
        }),
    };
};

const getInitialGuidedCheckSession = () => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search || '');
    if (!params.has('guidedCheck')) return null;
    return buildGuidedCheckSessionFromIntent(loadPendingChatIntent()?.guidedCheck)?.session || null;
};

const getGuidedCheckStorageKey = (key) => key ? `${GUIDED_CHECK_STORAGE_PREFIX}${key}` : null;

const normalizeGuidedCheckSession = (session) => {
    if (!session?.report) return null;
    return {
        ...session,
        report: normalizeDiagnosticReportForUi(session.report),
        ruledOutCauseIds: Array.isArray(session.ruledOutCauseIds) ? session.ruledOutCauseIds : [],
        timeline: Array.isArray(session.timeline) ? session.timeline : [],
        storageKeys: Array.isArray(session.storageKeys) ? session.storageKeys.filter(Boolean) : [],
    };
};

const getGuidedCheckAliasKeys = (session = null) => {
    const report = session?.report || null;
    const keys = [
        session?.reportId ? `report:${session.reportId}` : null,
        report?.id ? `report:${report.id}` : null,
        report?.diagnosticTicketId ? `ticket:${report.diagnosticTicketId}` : null,
        report?.diagnostic_ticket_id ? `ticket:${report.diagnostic_ticket_id}` : null,
        report?.ticketId ? `ticket:${report.ticketId}` : null,
        report?.ticket_id ? `ticket:${report.ticket_id}` : null,
        report?.threadId ? `thread:${report.threadId}` : null,
        report?.thread_id ? `thread:${report.thread_id}` : null,
    ];
    return [...new Set(keys.filter(Boolean).map(String))];
};

// Stable storage keys for a diagnostic report card, mirroring the guided-session
// alias keys. Persisting/restoring report cards under report/ticket/thread ids
// (not just the active thread key) means reopening a chat restores the saved
// report instead of regenerating it from scratch.
const getReportCardAliasKeys = (reportOrCard = null) => {
    const report = reportOrCard?.diagnosticReport || reportOrCard || null;
    const keys = [
        report?.id ? `report:${report.id}` : null,
        report?.diagnosticTicketId ? `ticket:${report.diagnosticTicketId}` : null,
        report?.diagnostic_ticket_id ? `ticket:${report.diagnostic_ticket_id}` : null,
        report?.ticketId ? `ticket:${report.ticketId}` : null,
        report?.ticket_id ? `ticket:${report.ticket_id}` : null,
        report?.threadId ? `thread:${report.threadId}` : null,
        report?.thread_id ? `thread:${report.thread_id}` : null,
    ];
    return [...new Set(keys.filter(Boolean).map(String))];
};

const sessionMatchesGuidedKeys = (session, keys) => {
    const candidateKeys = new Set(normalizeStorageKeyList(keys).map(String));
    if (!candidateKeys.size) return false;
    const sessionKeys = new Set([
        ...(Array.isArray(session?.storageKeys) ? session.storageKeys : []),
        ...getGuidedCheckAliasKeys(session),
    ].filter(Boolean).map(String));
    return [...candidateKeys].some((key) => sessionKeys.has(key));
};

const readGuidedCheckSession = (storageKey) => {
    for (const storage of getStorageAreas()) {
        const session = normalizeGuidedCheckSession(parseStoredJson(storage.getItem(storageKey)));
        if (session) return session;
    }
    return null;
};

const loadGuidedCheckSession = (keys) => {
    const candidateKeys = normalizeStorageKeyList(keys).map(String);
    const storageKeys = [...new Set(candidateKeys.map(getGuidedCheckStorageKey).filter(Boolean))];
    if (storageKeys.length) storageKeys.push(GUIDED_CHECK_LAST_STORAGE_KEY);

    for (const storageKey of storageKeys) {
        const session = readGuidedCheckSession(storageKey);
        if (!session) continue;
        if (storageKey === GUIDED_CHECK_LAST_STORAGE_KEY && !sessionMatchesGuidedKeys(session, candidateKeys)) continue;
        return session;
    }
    return null;
};

const saveGuidedCheckSession = (key, session) => {
    const keys = normalizeStorageKeyList([
        key,
        ...getGuidedCheckAliasKeys(session),
    ]).map(String);
    const storageKeys = [...new Set(keys.map(getGuidedCheckStorageKey).filter(Boolean))];
    if (!storageKeys.length) return;

    getStorageAreas().forEach((storage) => {
        try {
            if (session) {
                const payload = JSON.stringify({
                    ...session,
                    storageKeys: [...new Set([
                        ...(Array.isArray(session.storageKeys) ? session.storageKeys : []),
                        ...keys,
                    ].filter(Boolean).map(String))],
                    updatedAt: Date.now(),
                });
                storageKeys.forEach((storageKey) => storage.setItem(storageKey, payload));
                storage.setItem(GUIDED_CHECK_LAST_STORAGE_KEY, payload);
            } else {
                storageKeys.forEach((storageKey) => storage.removeItem(storageKey));
            }
        } catch {}
    });
};

const clearGuidedCheckSession = (keys = [], session = null) => {
    const candidateKeys = normalizeStorageKeyList([
        ...normalizeStorageKeyList(keys),
        ...getGuidedCheckAliasKeys(session),
        ...(Array.isArray(session?.storageKeys) ? session.storageKeys : []),
    ]).map(String);
    const storageKeys = [...new Set(candidateKeys.map(getGuidedCheckStorageKey).filter(Boolean))];

    getStorageAreas().forEach((storage) => {
        try {
            storageKeys.forEach((storageKey) => storage.removeItem(storageKey));
            const lastSession = normalizeGuidedCheckSession(parseStoredJson(storage.getItem(GUIDED_CHECK_LAST_STORAGE_KEY)));
            if (!lastSession || !candidateKeys.length || sessionMatchesGuidedKeys(lastSession, candidateKeys)) {
                storage.removeItem(GUIDED_CHECK_LAST_STORAGE_KEY);
            }
        } catch {}
    });
};

const consumeFreshChatReset = () => {
    try {
        const value = sessionStorage.getItem(FRESH_CHAT_RESET_STORAGE_KEY);
        if (!value) return false;
        sessionStorage.removeItem(FRESH_CHAT_RESET_STORAGE_KEY);
        return true;
    } catch {
        return false;
    }
};

const hasPersistedDiagnosticReport = (messages = []) => (
    messages.some((message) => message?.diagnosticReport || isDiagnosticReportMessage(message))
);

// Latest generated report object in the thread (for the header "View Report" button).
const getLatestPersistedDiagnosticReport = (messages = []) => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (messages[i]?.diagnosticReport) return messages[i].diagnosticReport;
    }
    return null;
};

const hasGeneratedDiagnosticReportForMessages = hasPersistedDiagnosticReport;

// Parts / shop lookup commands are NOT diagnostic Q&A — they must not count toward the
// diagnostic turn total (otherwise clicking Find Parts / Find a Shop / naming a part
// inflates the count and can unlock a report without any real diagnosis).
const PARTS_SHOP_COMMAND_PATTERN = /\b(find\s+(a\s+)?(parts?|shops?|repair\s+shops?|mechanics?)|parts?\s+(for|lookup|search)|look\s*up\s+parts?|repair\s+shops?|shops?\s+near|nearby\s+(shops?|repair|mechanics?))\b/i;
const isPartsOrShopCommand = (text = '') => PARTS_SHOP_COMMAND_PATTERN.test(String(text || ''));

// During an ACTIVE diagnosis (before the report exists) the only chips we want are the
// diagnostic ANSWER suggestions — never shop / parts / work-list / report-generation
// actions ("Find a repair shop", "Book tyre inspection", "Add brake repair to list",
// "Generate diagnostic report"). Priority is a good likely cause + report, so these are
// suppressed until the report exists.
// A chip that STARTS with "Add" during a diagnosis is a work-list action ("Add brake repair",
// "Add repair to list") — never a diagnostic answer (those are "Yes", "No sound", …).
const isAddToListSuggestion = (text = '') => (
    /^\s*add\b/i.test(String(text || '')) ||
    /\badd\b[^]*\b(list|work\s*plan|plan)\b/i.test(String(text || ''))
);
const isShopActionSuggestion = (text = '') => (
    // A chip that STARTS with a booking / scheduling / find verb is a call-to-action that
    // pulls the user OUT of the diagnosis — never a diagnostic answer ("Yes", "No sound", …).
    /^\s*(book|schedule|reserve|order|find|locate|contact|call|visit|get)\b/i.test(String(text || '')) ||
    /\b(repair shop|tyre shop|tire shop|mechanic|appointment|inspection|alignment check|wheel[- ]?alignment)\b/i.test(String(text || ''))
);
const isNonDiagnosticActionSuggestion = (text = '') => (
    isReportChoiceText(text) ||
    isReportCommand(text) ||
    isPartsOrShopCommand(text) ||
    isRepairActionSuggestion(text) ||
    isAddToListSuggestion(text) ||
    isShopActionSuggestion(text)
);

// "Continue diagnosis" (and similar) is a control command asking CarFido to keep asking
// questions — NOT a diagnostic answer. It must not count toward the Q&A turns nor let a
// report generate on that turn; otherwise clicking it satisfies the minimum and the
// report is built without CarFido ever asking the promised next question.
const CONTINUE_DIAGNOSIS_PATTERN = /^\s*(continue\s+diagnos(is|ing|e)?|keep\s+(going|diagnos(ing|e))|ask\s+(me\s+)?(more|another|the\s+next|one\s+more)|next\s+question)\b/i;
const isContinueDiagnosisCommand = (text = '') => CONTINUE_DIAGNOSIS_PATTERN.test(String(text || '').trim());

const lastUserTurnIsContinueDiagnosis = (messages = []) => {
    const lastUser = [...messages].reverse().find(
        (m) => m?.role === 'user' && String(m?.content || '').trim()
    );
    return Boolean(lastUser && isContinueDiagnosisCommand(lastUser.content));
};

// True when a report request ALSO describes a symptom — e.g. "My car pulls right. Give me a
// diagnostic report." Such a message must START a real diagnosis (reach the backend, create a
// ticket, be persisted), NOT get intercepted as a bare report request and stalled on the canned
// "need more info" note (which drops the symptom and loses the message on reload). Detected by
// finding a sentence/clause that is not itself a report command and carries ≥3 words of content.
const reportRequestCarriesSymptom = (text = '') => String(text)
    .split(/[.!?\n]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .some((part) => (
        !isReportCommand(part) &&
        !isReportChoiceText(part) &&
        part.split(/\s+/).filter(Boolean).length >= 3
    ));

const getDiagnosticUserTurnCountForMessages = (messages = []) => {
    const startIndex = Math.max(getDiagnosticStartIndex(messages), 0);
    return messages
        .slice(startIndex)
        .filter((message) => (
            message?.role === 'user'
            && !isReportCommand(message.content)
            && !isReportChoiceText(message.content)
            && !isPartsOrShopCommand(message.content)
            && !isContinueDiagnosisCommand(message.content)
        ))
        .length;
};

// Diagnostic progress toward the symptom report, for the chat header.
// Progress is MONOTONIC on purpose: it's derived from the number of answered diagnostic
// turns (which only increases) and the backend's "report ready" signal — never from a raw
// per-turn confidence that could visibly drop after an answer. It reaches 100% exactly when
// the report is ready / auto-generating (the same signal the auto-generate effect keys off),
// and only applies to a diagnosis (callers must not render it for parts/shop chats).
const getDiagnosticProgress = (messages = [], { report = null, generating = false, resolved = false } = {}) => {
    if (resolved) return { percent: 100, stage: 'Resolved', nudge: '' };
    // The same readiness signal the auto-generate effect uses, so progress completes in lockstep.
    let signalled = false;
    const lastAssistant = [...messages].reverse().find(
        (m) => m?.role === 'assistant' && !m.isLoadingReport && !isNeedMoreDiagnosticsMessage(m)
    );
    if (lastAssistant) {
        const lastContent = extractSuggestionsAndContent(lastAssistant.content || '').content;
        signalled = isReportReadyPromptMessage(lastContent)
            || isDiagnosticFinalizedMessage({ ...lastAssistant, content: lastContent });
    }
    const reportReady = Boolean(report) || generating || signalled || hasGeneratedDiagnosticReportForMessages(messages);
    if (reportReady) return { percent: 100, stage: 'Report ready', nudge: '' };

    const turns = getDiagnosticUserTurnCountForMessages(messages);
    const cap = MAX_DIAGNOSTIC_USER_TURNS_BEFORE_FORCED_REPORT; // backend forces ready by here
    // 15% on open, rising with each answered question, approaching ~92% before the report lands.
    const percent = Math.max(12, Math.min(92, Math.round(15 + (turns / cap) * 77)));
    let stage = 'Gathering details';
    let nudge = '';
    if (percent >= 70) { stage = 'Almost there'; nudge = '1-2 more questions'; }
    else if (percent >= 40) { stage = 'Narrowing it down'; }
    return { percent, stage, nudge };
};

// How many diagnostic QUESTIONS CarFido has asked in this diagnosis. Used as an alternate evidence
// signal for an explicit "generate report" request: a driver often answers AND asks for the report
// in one breath ("Yes, easily. Give me report"), which the user-turn counter above excludes as a
// report command — so several real exchanges can still read as 0 turns. Counting CarFido's questions
// reflects that the diagnosis genuinely progressed, so an explicit report request can be honored.
const getDiagnosticAssistantQuestionCount = (messages = []) => {
    const startIndex = Math.max(getDiagnosticStartIndex(messages), 0);
    return messages
        .slice(startIndex)
        .filter((message) => (
            message?.role === 'assistant'
            && !message.isLoadingReport
            && !isDiagnosticReportMessage(message)
            && typeof message.content === 'string'
            && message.content.includes('?')
        ))
        .length;
};

const isBackendReadyDiagnosticMessage = (message) => {
    if (!message || message.role !== 'assistant' || message.isLoadingReport || isDiagnosticReportMessage(message)) {
        return false;
    }
    const content = extractSuggestionsAndContent(message.content).content;
    // An explicit "ready to generate the report" prompt always counts (even though it ends
    // with a '?').
    if (isReportReadyPromptMessage(content)) return true;
    // A CONCLUSION signal only counts when CarFido is NOT still asking a diagnostic question —
    // i.e. it has a "Question:" line or ends with a '?'. This lets a broad finalized-pattern
    // catch real conclusions ("worn to metal … needs replacement") without mistaking a
    // mid-diagnosis "…more likely. Do you feel shaking?" turn for a finished diagnosis.
    const stillAsking = (
        /(^|\n)\s*question\s*:/i.test(content || '') ||
        /\?\s*$/.test((content || '').trim())
    );
    if (stillAsking) return false;
    return isDiagnosticFinalizedMessage({ ...message, content });
};

const hasBackendReadyDiagnosticSignal = (messages = []) => {
    // Check the last few assistant turns, not just the immediate one. Once CarFido has
    // CONCLUDED (a ready/finalized message), a follow-up clarification or a "report / shops /
    // add-to-list" choice prompt must NOT erase that "ready" state — otherwise asking for the
    // report after the conclusion loops (CarFido re-offers the choice instead of generating).
    // Note: the auto-generate effect separately requires the LAST message to be eligible, so
    // this stickiness only unlocks the report when the USER explicitly asks — it never
    // auto-fires mid follow-up question.
    const recentAssistants = [...messages]
        .reverse()
        .filter((message) => (
            message?.role === 'assistant' &&
            !message.isLoadingReport &&
            !isNeedMoreDiagnosticsMessage(message)
        ))
        .slice(0, 3);
    return recentAssistants.some(isBackendReadyDiagnosticMessage);
};

const canGenerateDiagnosticReportForMessages = (messages = []) => {
    // CONFIDENCE-DRIVEN gate (accuracy first). A report must follow a REAL diagnostic
    // back-and-forth, so a hard floor of genuine Q&A turns always applies (this also stops
    // a bare "I want a new battery" + Find Parts / Find a Shop from producing a full report
    // with no diagnosis). Above that floor we FOLLOW the backend's "ready" signal, which it
    // only emits once its confidence in the top cause is >= 90%. The 7-turn hard cap is the
    // safety net where the backend declares ready regardless of confidence.
    const turns = getDiagnosticUserTurnCountForMessages(messages);
    if (turns < REPORT_MIN_FLOOR_TURNS) return false;          // not enough evidence yet
    if (turns >= MAX_DIAGNOSTIC_USER_TURNS_BEFORE_FORCED_REPORT) return true;  // hard cap
    // The user explicitly asked to CONTINUE the diagnosis — don't finalize this turn; CarFido
    // must ask (and the user answer) another real question first.
    if (lastUserTurnIsContinueDiagnosis(messages)) return false;
    return hasBackendReadyDiagnosticSignal(messages);          // ready == confidence >= 90%
};

const isDiagnosticReportAnchorCandidate = (message) => {
    if (!message || message.role !== 'assistant' || isDiagnosticReportMessage(message)) return false;
    const visibleContent = extractSuggestionsAndContent(message.content).content;
    return (
        isDiagnosticTicketAnchorMessage(message) ||
        isReportReadyPromptMessage(visibleContent) ||
        isDiagnosticFinalizedMessage({ ...message, content: visibleContent })
    );
};

const getLatestDiagnosticReportAnchorMessage = (messages = []) => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (isDiagnosticReportAnchorCandidate(message)) return message;
    }
    return null;
};

const createReportCardMessage = (report) => {
    const normalizedReport = normalizeDiagnosticReportForUi(report);
    return {
        id: `diagnostic-report-card-${normalizedReport?.id || Date.now()}`,
        role: 'assistant',
        content: "Diagnostic report generated. Here's what I found based on the symptoms and details you provided.",
        diagnosticReport: normalizedReport,
        suggestions: [],
        type: 'diagnostic_report',
        timestamp: new Date().toISOString(),
    };
};

const createReportLoadingMessage = (status = 'Generating diagnostic report...') => ({
    id: `diagnostic-report-loading-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role: 'assistant',
    content: '',
    isLoadingReport: true,
    status,
    suggestions: [],
    type: 'diagnostic_report_loading',
});

const isLegacyDiagnosticPdfMessage = (message = {}) => {
    if (!message?.pdfUrl || message?.diagnosticReport) return false;
    const triggerText = cleanDisplayText(message.replyToUserContent || '');
    const contentText = cleanDisplayText(message.content || '').toLowerCase();

    return (
        isReportCommand(triggerText) ||
        /\bgenerate\b.*\bdiagnostic report\b/i.test(triggerText) ||
        /\bdiagnostic report\b/i.test(triggerText) ||
        /\bdiagnostic report generated\b/.test(contentText)
    );
};

const hasDisplayReadyDiagnosticState = (messages = []) => (
    hasPersistedDiagnosticReport(messages) ||
    messages.some((message) => isDiagnosticReportAnchorCandidate(message))
);

// A bare "diagnose" entry prompt (the "Diagnose an Issue" suggestion) carries no real
// symptom, so it must NOT become the chat's sidebar title. Once the user describes the
// actual issue, the backend re-titles the thread (e.g. "Warning Light Diagnosis").
const isDiagnoseEntryPrompt = (text = '') => (
    /^(diagnose( (an issue|a problem|my car|my vehicle|the car|issue))?|start (a )?diagnosis|run a diagnosis|help me diagnose( (an issue|a problem))?|i need help diagnosing (an issue|a problem))$/i
        .test(String(text || '').trim().replace(/[.!?]+$/, ''))
);

// Display-only: strip the internal "Question:" label so diagnostic questions read
// naturally (e.g. "Any check-engine light on?"). The label is kept in the stored
// message content because backend round-counting and the frontend "still asking"
// detection rely on it — this only cleans what the user sees.
const stripQuestionLabel = (text = '') => (
    String(text || '')
        // Remove the "Question:" / "**Question:**" label at a line start or right after a
        // sentence end (mid-line), keeping the separator and the actual question text.
        .replace(/(^|\n|[.!?]["')\]]?\s+)[*_>\s-]*question[s]?\s*:\s*[*_]*\s*/gi, '$1')
        .replace(/[ \t]{2,}/g, ' ')
);

const stripReportGenerationTransitionText = (text = '') => (
    // Keep the "I have all the details I need. Ready to generate your diagnostic report?"
    // confirmation visible — only strip the redundant "I'm generating the report…" boilerplate
    // that duplicates the loading skeleton.
    String(text || '')
        .replace(/\b(?:I(?:'|’)m|I am|CarFido is)\s+(?:now\s+)?(?:generating|building|creating|preparing|compiling)\s+(?:the|your)?\s*(?:diagnostic\s+)?report[\s\S]*$/i, '')
        .replace(/\b(?:generating|building|creating|preparing|compiling)\s+(?:the|your)?\s*(?:diagnostic\s+)?report[\s\S]*$/i, '')
        .trim()
);

const scheduleStreamIdleTimer = () => {
    clearStreamIdleTimer();
    streamIdleTimer = window.setTimeout(() => {
        streamIdleTimer = null;
    }, STREAM_IDLE_TIMEOUT_MS);
};

const clearStreamIdleTimer = () => {
    if (streamIdleTimer) {
        window.clearTimeout(streamIdleTimer);
        streamIdleTimer = null;
    }
};

const normalizeIssueKey = (value = '') => cleanDisplayText(value)
    .toLowerCase()
    .replace(/\bisn['’]?t\b/g, 'is not')
    .replace(/\bnot starting\b/g, 'no start')
    .replace(/\bwon'?t start\b/g, 'no start')
    .replace(/\bwill not start\b/g, 'no start')
    .replace(/\bopening\b/g, 'open')
    .replace(/\brotating\b/g, 'rotate')
    .replace(/\bcar\b|\bvehicle\b|\bmy\b|\bthe\b/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getDiagnosticTicketVehicleLabel = (ticket) => (
    ticket?.vehicle_label ||
    ticket?.vehicleLabel ||
    [ticket?.vehicle?.year, ticket?.vehicle?.make, ticket?.vehicle?.model].filter(Boolean).join(' ').trim()
);

const resolveReportDiagnosticTicketId = (report) => (
    report?.diagnosticTicketId ||
    report?.diagnostic_ticket_id ||
    report?.ticketId ||
    report?.ticket_id ||
    ''
);

const resolveRealDiagnosticTicketId = (ticket) => (
    ticket?.diagnostic_ticket_id ||
    ticket?.diagnosticTicketId ||
    ticket?.ticket_id ||
    ticket?.ticketId ||
    ticket?.id ||
    ''
);

const resolveDiagnosticTicketNumber = (ticketOrReport) => (
    ticketOrReport?.display_ticket_number ||
    ticketOrReport?.displayTicketNumber ||
    ticketOrReport?.ticket_number ||
    ticketOrReport?.ticketNumber ||
    ''
);

const resolveTicketThreadId = (ticketOrReport) => (
    ticketOrReport?.thread_id ||
    ticketOrReport?.threadId ||
    ticketOrReport?.source_repair_log_id ||
    ticketOrReport?.sourceRepairLogId ||
    ticketOrReport?.repair_log_id ||
    ticketOrReport?.repairLogId ||
    ''
);

const resolveTicketTitle = (ticket) => (
    String(
        ticket?.title ||
        ticket?.symptom_summary ||
        ticket?.symptomSummary ||
        ticket?.issue ||
        ticket?.issueTitle ||
        ''
    )
        .replace(/\s+diagnos(is|tic)\b\.?\s*$/i, '')
        // Drop a dangling connector left by truncation ("…Rear When" → "…Rear").
        .replace(/(?:\s+(?:when|and|or|but|the|a|an|of|for|to|with|on|at|in|is|are|was|were|my|your|that|this|from|into|as|if|so|than|then|while|because|it|its|also|just|still|after|before|during|over|under))+\s*$/i, '')
        .trim()
);

const getThreadDiagnosticTicket = (thread) => {
    if (!thread) return null;
    const ticket = thread.diagnostic_ticket || thread.diagnosticTicket || null;
    const ticketId = thread.diagnostic_ticket_id || thread.diagnosticTicketId || resolveRealDiagnosticTicketId(ticket);
    const explicitType = thread.conversation_type || thread.conversationType || '';
    const isDiagnosis = explicitType ? explicitType === 'diagnosis' : Boolean(ticketId);
    if (!isDiagnosis) return null;

    return {
        ...(ticket || {}),
        diagnostic_ticket_id: ticketId,
        ticket_number: thread.diagnostic_ticket_number || thread.diagnosticTicketNumber || ticket?.ticket_number || ticket?.ticketNumber,
        title: thread.diagnostic_ticket_title || thread.diagnosticTicketTitle || ticket?.title,
        symptom_summary: thread.diagnostic_ticket_symptom_summary || thread.diagnosticTicketSymptomSummary || ticket?.symptom_summary,
        status: thread.diagnostic_ticket_status || thread.diagnosticTicketStatus || ticket?.status,
        stage: thread.diagnostic_ticket_stage || thread.diagnosticTicketStage || ticket?.stage,
        source_repair_log_id: thread.diagnostic_ticket_source_repair_log_id || thread.diagnosticTicketSourceRepairLogId || ticket?.source_repair_log_id || ticket?.sourceRepairLogId || thread.thread_id,
        thread_id: ticket?.thread_id || ticket?.threadId || thread.thread_id,
        vehicle_id: thread.vehicle_id || ticket?.vehicle_id,
        vehicle_label: thread.vehicle_label || thread.vehicleLabel || ticket?.vehicle_label || ticket?.vehicleLabel,
        last_activity_at: ticket?.last_activity_at || ticket?.lastActivityAt || thread.last_activity,
    };
};

const resolveTicketPhone = (ticket) => (
    ticket?.phone_number ||
    ticket?.phoneNumber ||
    ticket?.customer_phone ||
    ticket?.customerPhone ||
    ''
);

const resolveReportPhone = (report) => (
    report?.customerPhone ||
    report?.customer_phone ||
    report?.phone_number ||
    report?.phoneNumber ||
    ''
);

const isClosedDiagnosticReport = (report) => {
    const status = cleanDisplayText(report?.ticketStatus || report?.ticket_status || report?.status).toLowerCase();
    return status === 'closed' || status === 'abandoned' || status === 'resolved';
};

const isClosedDiagnosticTicket = (ticket) => {
    const status = cleanDisplayText(ticket?.status || ticket?.ticketStatus).toLowerCase();
    return status === 'closed' || status === 'abandoned' || status === 'resolved';
};

const resolveSameIssueRedirectTarget = ({
    replyText,
    messageList = [],
    recentDiagnostics = [],
    currentThreadId = null,
}) => {
    const sameIssuePromptIdx = findLastNewIssueClarificationPromptIndex(messageList);
    if (sameIssuePromptIdx < 0 || !isSameIssueConfirmationText(replyText)) return null;

    const matchedTitle = extractMatchedIssueTitleFromPrompt(messageList[sameIssuePromptIdx]);
    if (!matchedTitle) return null;

    const normalizedTitle = normalizeIssueReplyText(matchedTitle);
    const diagnostics = recentDiagnostics || [];
    const matchedTicket = diagnostics.find((ticket) => (
        normalizeIssueReplyText(resolveTicketTitle(ticket)) === normalizedTitle
    )) || diagnostics.find((ticket) => {
        const ticketTitle = normalizeIssueReplyText(resolveTicketTitle(ticket));
        return ticketTitle && normalizedTitle && (
            ticketTitle.includes(normalizedTitle) || normalizedTitle.includes(ticketTitle)
        );
    });
    const matchedThreadId = matchedTicket ? resolveTicketThreadId(matchedTicket) : null;
    const matchedIsOpen = matchedTicket && !isClosedDiagnosticTicket(matchedTicket);

    if (
        matchedTicket &&
        matchedIsOpen &&
        matchedThreadId &&
        String(matchedThreadId) !== String(currentThreadId)
    ) {
        return {
            matchedTicket,
            matchedTitle,
            matchedThreadId,
            redirectTitle: resolveTicketTitle(matchedTicket) || matchedTitle,
        };
    }

    return null;
};

const isNonDiagnosticTicketTitle = (title = '') => {
    const normalized = cleanDisplayText(title)
        .toLowerCase()
        .replace(/[^\w\s']/g, '')
        .replace(/\s+/g, ' ');
    return [
        'new chat',
        'hi',
        'hii',
        'hiii',
        'hello',
        'hey',
        'hiya',
        'hlo',
        'yo',
        'sup',
        'good morning',
        'good afternoon',
        'good evening',
        'whats up',
        "what's up",
        'how are you',
    ].includes(normalized);
};

const isDisplayableDiagnosticTicket = (ticket) => (
    Boolean(ticket) && !isNonDiagnosticTicketTitle(resolveTicketTitle(ticket))
);

const issueKeysMatch = (left = '', right = '') => {
    if (!left || !right) return false;
    if (left === right) return true;
    if (left.includes(right) || right.includes(left)) return true;
    const leftTokens = left.split(' ').filter(Boolean);
    const rightTokens = new Set(right.split(' ').filter(Boolean));
    return leftTokens.filter((token) => rightTokens.has(token)).length >= Math.min(2, leftTokens.length);
};

const resolveDiagnosticTicketForReportFromList = (report, tickets = [], currentThreadId = '') => {
    if (!report || !Array.isArray(tickets)) return null;
    const reportTicketId = resolveReportDiagnosticTicketId(report);
    if (reportTicketId) {
        const idMatch = tickets.find((ticket) => String(resolveRealDiagnosticTicketId(ticket)) === String(reportTicketId));
        if (idMatch) return idMatch;
    }

    const reportThreadId = report.threadId || report.thread_id || currentThreadId || '';
    if (reportThreadId) {
        const threadMatch = tickets.find((ticket) => String(resolveTicketThreadId(ticket)) === String(reportThreadId));
        if (threadMatch) return threadMatch;
    }

    const reportIssueKey = normalizeIssueKey(report.issue || report.title || report.symptom_summary || '');
    const reportVehicleKey = cleanDisplayText(report.vehicle || report.vehicleLabel || report.vehicle_label).toLowerCase();
    const matches = tickets.filter((ticket) => {
        const ticketIssueKey = normalizeIssueKey(resolveTicketTitle(ticket));
        const ticketVehicleKey = cleanDisplayText(getDiagnosticTicketVehicleLabel(ticket)).toLowerCase();
        return (
            (!reportVehicleKey || !ticketVehicleKey || reportVehicleKey === ticketVehicleKey) &&
            issueKeysMatch(ticketIssueKey, reportIssueKey)
        );
    });
    if (matches.length) return matches.find((ticket) => !isClosedDiagnosticTicket(ticket)) || matches[0];
    const openTickets = tickets.filter((ticket) => !isClosedDiagnosticTicket(ticket));
    return openTickets.length === 1 ? openTickets[0] : null;
};

const resolveStrictDiagnosticTicketForReport = (report, tickets = [], currentThreadId = '') => {
    if (!report || !Array.isArray(tickets)) return null;
    const validTickets = tickets.filter(Boolean);
    const reportTicketId = resolveReportDiagnosticTicketId(report);
    if (reportTicketId) {
        const idMatch = validTickets.find((ticket) => String(resolveRealDiagnosticTicketId(ticket)) === String(reportTicketId));
        if (idMatch) return idMatch;
    }

    const reportThreadId = report.threadId || report.thread_id || currentThreadId || '';
    if (reportThreadId) {
        const threadMatch = validTickets.find((ticket) => String(resolveTicketThreadId(ticket)) === String(reportThreadId));
        if (threadMatch) return threadMatch;
    }

    const reportIssueKey = normalizeIssueKey(report.issue || report.title || report.symptom_summary || '');
    const reportVehicleKey = cleanDisplayText(report.vehicle || report.vehicleLabel || report.vehicle_label).toLowerCase();
    if (!reportIssueKey && !reportVehicleKey) return null;

    const matches = validTickets.filter((ticket) => {
        const ticketIssueKey = normalizeIssueKey(resolveTicketTitle(ticket));
        const ticketVehicleKey = cleanDisplayText(getDiagnosticTicketVehicleLabel(ticket)).toLowerCase();
        return (
            (!reportIssueKey || issueKeysMatch(ticketIssueKey, reportIssueKey)) &&
            (!reportVehicleKey || !ticketVehicleKey || reportVehicleKey === ticketVehicleKey)
        );
    });

    return matches.find((ticket) => !isClosedDiagnosticTicket(ticket)) || matches[0] || null;
};

const addUniqueTicketCandidate = (candidates, ticket) => {
    if (!ticket) return;
    const key = [
        resolveRealDiagnosticTicketId(ticket),
        resolveTicketThreadId(ticket),
        resolveDiagnosticTicketNumber(ticket),
        resolveTicketTitle(ticket),
        getDiagnosticTicketVehicleLabel(ticket),
    ].filter(Boolean).join('|');
    if (!key) return;
    if (!candidates.some((candidate) => [
        resolveRealDiagnosticTicketId(candidate),
        resolveTicketThreadId(candidate),
        resolveDiagnosticTicketNumber(candidate),
        resolveTicketTitle(candidate),
        getDiagnosticTicketVehicleLabel(candidate),
    ].filter(Boolean).join('|') === key)) {
        candidates.push(ticket);
    }
};

const fetchActiveDiagnosticTicketForClose = async (phone) => {
    if (!phone) return null;
    try {
        const response = await fetch(`${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/active`);
        if (!response.ok) return null;
        const data = await response.json();
        return data?.ticket || null;
    } catch {
        return null;
    }
};

const resolveCloseableDiagnosticTicket = async ({
    report,
    currentThreadId,
    linkedTicket,
    activeDiagnosticTicket,
    currentDiagnosticTicket,
    inlineDiagnosticTicket,
    recentDiagnostics,
    refreshDiagnosticTickets,
    phone,
}) => {
    const candidates = [];
    [
        linkedTicket,
        activeDiagnosticTicket,
        currentDiagnosticTicket,
        inlineDiagnosticTicket,
        ...(recentDiagnostics || []),
    ].forEach((ticket) => addUniqueTicketCandidate(candidates, ticket));

    let resolved = resolveDiagnosticTicketForReportFromList(report, candidates, currentThreadId);
    if (resolved && resolveRealDiagnosticTicketId(resolved)) return resolved;

    if (refreshDiagnosticTickets) {
        const refreshed = await refreshDiagnosticTickets();
        (refreshed || []).forEach((ticket) => addUniqueTicketCandidate(candidates, ticket));
        resolved = resolveDiagnosticTicketForReportFromList(report, candidates, currentThreadId);
        if (resolved && resolveRealDiagnosticTicketId(resolved)) return resolved;
    }

    addUniqueTicketCandidate(candidates, await fetchActiveDiagnosticTicketForClose(phone));
    resolved = resolveDiagnosticTicketForReportFromList(report, candidates, currentThreadId);
    if (resolved) return resolved;

    const openTickets = candidates.filter((ticket) => resolveRealDiagnosticTicketId(ticket) && !isClosedDiagnosticTicket(ticket));
    return openTickets.length === 1 ? openTickets[0] : null;
};

const closeMatchingDiagnosticTicket = async ({ phone, report, currentThreadId, linkedTicket, summary, maintenanceTitle = '', maintenanceOdometer = null }) => {
    if (!phone) throw new Error('Ticket details are missing for this chat.');
    const response = await fetch(
        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/close-matching`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                diagnostic_ticket_id: Number(resolveRealDiagnosticTicketId(linkedTicket) || resolveReportDiagnosticTicketId(report)) || null,
                ticket_number: String(resolveDiagnosticTicketNumber(linkedTicket) || resolveDiagnosticTicketNumber(report) || ''),
                thread_id: resolveTicketThreadId(linkedTicket) || report?.threadId || report?.thread_id || currentThreadId || '',
                vehicle_label: getDiagnosticTicketVehicleLabel(linkedTicket) || report?.vehicle || report?.vehicleLabel || report?.vehicle_label || '',
                issue: report?.issue || report?.title || resolveTicketTitle(linkedTicket) || '',
                title: resolveTicketTitle(linkedTicket) || report?.issue || report?.title || '',
                symptom_summary: linkedTicket?.symptom_summary || report?.issue || report?.title || '',
                outcome: 'successful',
                summary,
                diy_checks: Array.isArray(report?.completedGuidedChecks) ? report.completedGuidedChecks : [],
                // Set only for scheduled-maintenance reports → backend marks that maintenance
                // service done in the Garage schedule so its reminder clears.
                maintenance_title: maintenanceTitle || '',
                maintenance_odometer: maintenanceOdometer,
            }),
        }
    );
    if (!response.ok) {
        // A 404 here means the server has no matching diagnostic ticket to close
        // (e.g. a website-only guided report whose ticket id is local/stale, or a
        // ticket that was already removed). There is nothing to close server-side,
        // so let the caller finish the local close instead of dead-ending the user.
        if (response.status === 404) {
            return null;
        }
        let errorMessage = 'Unable to close ticket';
        try {
            const errorData = await response.json();
            errorMessage = errorData?.detail || errorData?.error || errorMessage;
        } catch {}
        throw new Error(errorMessage);
    }
    return response.json();
};

const mergeReportWithPersistedClosedState = (report) => {
    const normalized = normalizeDiagnosticReportForUi(report);
    if (!normalized) return normalized;
    const stored = loadDiagnosticReport(normalized.id);
    if (!stored) return normalized;
    const storedClosed = isClosedDiagnosticReport(stored);
    return normalizeDiagnosticReportForUi({
        ...normalized,
        ...stored,
        ticketStatus: storedClosed ? 'closed' : (stored.ticketStatus || normalized.ticketStatus),
        ticketStage: storedClosed ? 'closed' : (stored.ticketStage || normalized.ticketStage),
        status: storedClosed ? 'Closed' : (stored.status || normalized.status),
    });
};

const getDiagnosticReportPath = (report) => {
    const reportId = report?.id || report?.reportId;
    return reportId ? `/diagnostic-report/${encodeURIComponent(reportId)}` : '/diagnostic-report';
};

const GUIDED_CHECK_OUTCOME_LABELS = {
    normal: 'Result was normal / as expected',
    issue: 'Found an issue / result was abnormal',
    not_sure: "Not sure / couldn't complete",
};

const buildCompletedGuidedCheck = ({ cause, outcome, notes, existingEntry }) => {
    const completedAt = new Date().toISOString();
    return {
        ...(existingEntry || {}),
        causeId: cause?.id || '',
        causeTitle: cause?.title || '',
        checkTitle: cause?.recommendedCheck || '',
        // Fields consumed by the Diagnostic Report page (DiagnosticReportPage.jsx).
        recommendedCheck: cause?.recommendedCheck || cause?.title || 'Diagnostic check',
        outcome,
        outcomeLabel: GUIDED_CHECK_OUTCOME_LABELS[outcome] || '',
        summary: cause?.title || '',
        notes: cleanDisplayText(notes),
        completedAt,
        updatedAt: completedAt,
    };
};

const buildCompletedRepairGuide = ({ cause, mileage, note, closedTicket }) => ({
    causeId: cause?.id || '',
    causeTitle: cause?.title || '',
    repairTitle: cause?.likelyRepair || cause?.title || 'Repair',
    mileage: cleanDisplayText(mileage),
    note: cleanDisplayText(note),
    completedAt: new Date().toISOString(),
    closedTicket,
});

const StatusPill = ({ children, tone = 'red' }) => {
    const toneClasses = {
        red: 'bg-[#FDEDEE] text-[#D81F26]',
        green: 'bg-[#dcfce7] text-[#008a36]',
        amber: 'bg-[#fff4dc] text-[#a76500]',
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-black md:px-3 md:text-xs ${toneClasses[tone] || toneClasses.red}`}>
            {children}
        </span>
    );
};

const DiagnosticReportCard = ({ report, onOpen }) => (
    <div className="mt-4 max-w-md rounded-3xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
        <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F2F2F2] text-[#D81F26]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5"/><path d="M9 13h6"/><path d="M9 17h4"/></svg>
            </div>
            <div className="min-w-0">
                <div className="truncate text-xl font-black text-[#414141]">{report?.issue || report?.title || 'Symptom Report'}</div>
                <div className="mt-1 text-sm font-semibold text-[#414141]">{report?.vehicle || report?.vehicleLabel || 'Vehicle'}</div>
            </div>
        </div>
        <button
            type="button"
            onClick={onOpen}
            className="mt-5 flex w-full items-center justify-center gap-3 rounded-2xl bg-[#414141] px-5 py-3 text-base font-black text-white shadow-sm hover:bg-[#414141]"
        >
            View Report
            <span aria-hidden="true">›</span>
        </button>
    </div>
);

const GenerateDiagnosticReportButton = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="mt-4 rounded-full border border-[#d9d9d9] bg-white px-5 py-2 text-sm font-black text-[#414141] shadow-sm hover:bg-[#F9F9F9]"
    >
        Generate symptom report
    </button>
);

const DiagnosticTicketCard = ({ ticket }) => (
    <div className="mt-4 max-w-lg rounded-[28px] border border-[#d9d9d9] bg-white p-5 shadow-[0_4px_18px_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
        <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FDEDEE] text-[#D81F26]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3h7v7"/><path d="M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/></svg>
                </div>
                <div>
                    <div className="text-base font-black text-[#414141]">{resolveTicketTitle(ticket) || 'Diagnostic ticket'}</div>
                    {resolveDiagnosticTicketNumber(ticket) && (
                        <div className="mt-2 text-xs font-black uppercase tracking-widest text-[#6B6B6B]">
                            Ticket #{resolveDiagnosticTicketNumber(ticket)}
                        </div>
                    )}
                </div>
            </div>
            <StatusPill tone={isClosedDiagnosticTicket(ticket) ? 'green' : 'red'}>
                {isClosedDiagnosticTicket(ticket) ? 'Resolved' : 'Ticket Open'}
            </StatusPill>
        </div>
    </div>
);

const ChatContextBanner = ({
    context,
    report = null,
    progress = null,
    onViewReport,
    onFindParts,
    onFindShop,
    findShopLabel = 'Shops',
}) => {
    const type = context?.type || 'diagnosis';
    const ticket = context?.ticket || null;
    const rawTitle = cleanDisplayText(context?.title || resolveTicketTitle(ticket));
    // Guard against a title that is empty or nothing but a pronoun/filler (e.g. "it"
    // from "find a shop for it"). This backstops any code path that sets context.title
    // without running it through cleanupChatContextTitle.
    const title = (rawTitle && !isPlaceholderContextTitle(rawTitle)) ? rawTitle : (
        type === 'shop' ? 'repair shops' : type === 'parts' ? 'parts' : 'Open issue'
    );
    const isDiagnosis = type === 'diagnosis';
    const heading = isDiagnosis
        ? `Diagnosing: ${title}`
        : type === 'shop'
        ? `Finding shops: ${title}`
        : `Finding parts: ${title}`;
    const subtitle = isDiagnosis
        ? report
            ? 'Your symptom report is ready - open it any time.'
            : "Answer CarFido's questions so we can generate a symptom report."
        : type === 'shop'
        ? (PARTS_FLOW_ENABLED
            ? 'Shop search is active. You can also look up parts for this job.'
            : 'Shop search is active for this job.')
        : 'Parts search is active. You can also find a shop for this job.';
    const statusTone = isDiagnosis
        ? (isClosedDiagnosticTicket(ticket) || report ? 'green' : 'red')
        : 'amber';
    const statusLabel = isDiagnosis
        ? isClosedDiagnosticTicket(ticket)
            ? 'Resolved'
            : (report ? 'Report Ready' : 'In Progress')
        : type === 'shop'
        ? 'Shop Search'
        : 'Parts Search';

    return (
        <div className="border-b border-[#E6E6E6] bg-white px-3 py-2.5 shadow-[0_1px_0_rgba(0,0,0,0.02)] md:px-6 md:py-4">
            <div className="mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between md:gap-3">
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2 text-sm font-black text-[#1F1F1F] md:text-base">
                        <span className="h-4 w-1 shrink-0 rounded-full bg-[#D81F26]" aria-hidden="true" />
                        <span className="truncate">{heading}</span>
                    </div>
                    <div className="line-clamp-2 text-xs font-semibold leading-snug text-[#6B6B6B] md:text-sm">
                        {subtitle}
                    </div>
                    {isDiagnosis && progress && (
                        <div className="mt-2 flex items-center gap-2">
                            <div
                                className="h-1.5 w-full max-w-[200px] overflow-hidden rounded-full bg-[#cfe3f5]"
                                role="progressbar"
                                aria-valuenow={progress.percent}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label="Diagnostic progress toward the symptom report"
                            >
                                <div
                                    className={`h-full rounded-full transition-[width] duration-700 ease-out ${progress.percent >= 100 ? 'bg-[#16a34a]' : 'bg-[#0b74b5]'}`}
                                    style={{ width: `${progress.percent}%` }}
                                />
                            </div>
                            <span className={`whitespace-nowrap text-[11px] font-black ${progress.percent >= 100 ? 'text-[#16a34a]' : 'text-[#075c92]'}`}>
                                {progress.stage}
                            </span>
                            {progress.nudge && (
                                <span className="hidden whitespace-nowrap text-[11px] font-semibold text-[#5f728a] sm:inline">· {progress.nudge}</span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-1.5 md:gap-2">
                    {PARTS_FLOW_ENABLED && onFindParts && (
                        <button
                            type="button"
                            onClick={onFindParts}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#d6d6d6] bg-white px-3 py-1.5 text-xs font-black text-[#414141] shadow-sm transition hover:bg-[#fafafa] active:scale-95 md:px-3.5 md:text-[13px]"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                <path d="m3.3 7 8.7 5 8.7-5" />
                                <path d="M12 22V12" />
                            </svg>
                            Parts
                        </button>
                    )}
                    {onFindShop && (
                        <button
                            type="button"
                            onClick={onFindShop}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#d6d6d6] bg-white px-3 py-1.5 text-xs font-black text-[#414141] shadow-sm transition hover:bg-[#fafafa] active:scale-95 md:px-3.5 md:text-[13px]"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9 5 3h14l2 6" />
                                <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                                <path d="M9 22V12h6v10" />
                            </svg>
                            {findShopLabel}
                        </button>
                    )}
                    {isDiagnosis && report && onViewReport && (
                        <button
                            type="button"
                            onClick={(event) => onViewReport(event)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#D81F26] px-3 py-1.5 text-xs font-black text-white shadow-sm transition hover:bg-[#B8181E] active:scale-95 md:px-4 md:text-[13px]"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
                                <path d="M14 2v5h5" />
                                <path d="M8.5 13h7" />
                                <path d="M8.5 17h5" />
                            </svg>
                            View Report
                        </button>
                    )}
                    {statusLabel !== 'Report Ready' && (
                        <StatusPill tone={statusTone}>
                            {statusLabel}
                        </StatusPill>
                    )}
                </div>
            </div>
        </div>
    );
};

const GuidedCheckBanner = ({ session, onViewReport }) => {
    const cause = session?.report?.likelyCauses?.[session.activeCauseIndex] || {};
    const totalSteps = session?.activeCard?.type === 'repair_step'
        ? getRepairGuideStepsFromCause(cause).length
        : getGuidedStepsFromCause(cause).length;
    // Cards reached AFTER the user finishes stepping through the check/repair —
    // they carry no stepIndex, so the bar should read as complete (100%) rather
    // than resetting to "Step 1".
    const COMPLETED_CARD_TYPES = ['result', 'confirmed', 'repair_done_choice', 'repair_outcome_check', 'repair_logged'];
    const cardType = session?.activeCard?.type;
    const rawStepIndex = typeof session?.activeCard?.stepIndex === 'number'
        ? session.activeCard.stepIndex
        : (COMPLETED_CARD_TYPES.includes(cardType) || session?.phase === 'done')
            ? Math.max(totalSteps - 1, 0)
            : 0;
    const currentStep = Math.min(rawStepIndex + 1, Math.max(totalSteps, 1));
    const progressPercent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);
    const isRepair = session?.activeCard?.type === 'repair_step';
    return (
        <div className="border-b border-[#d9d9d9] bg-[#F9F9F9]">
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-3 py-2.5 md:gap-4 md:px-6 md:py-4">
                <div className="min-w-0">
                    <div className="text-xs font-black text-[#414141] md:text-sm">
                        {isRepair ? 'Repair Guide In Progress' : 'Diagnostic Check In Progress'}
                    </div>
                    <div className="truncate text-xs font-semibold text-[#414141] md:text-sm">
                        Continue your guided check or ask CarFido for help.
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5 md:gap-3">
                    <button
                        type="button"
                        onClick={onViewReport}
                        className="rounded-2xl bg-white px-3 py-1.5 text-xs font-black text-[#414141] shadow-sm transition hover:bg-[#F9F9F9] md:px-5 md:py-2 md:text-sm"
                    >
                        View Report
                    </button>
                    <StatusPill>Check In Progress</StatusPill>
                </div>
            </div>
            {session?.activeCard && (
                <div className="border-t border-[#d9d9d9] px-3 py-2 md:px-6 md:py-3">
                    <div className="mx-auto max-w-5xl">
                        <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#D81F26] md:gap-3 md:text-sm">
                            {isRepair ? 'Repair Guide' : 'Diagnostic Check'}
                            <span className="font-semibold normal-case tracking-normal text-[#414141]">
                                {isRepair
                                    ? `${Math.max(totalSteps, 1)} step${Math.max(totalSteps, 1) === 1 ? '' : 's'} · follow in order`
                                    : `Step ${currentStep} of ${Math.max(totalSteps, 1)} · ${progressPercent}%`}
                            </span>
                        </div>
                    </div>
                    <div className="mx-auto h-1 max-w-5xl rounded-full bg-white">
                        <div
                            className="h-1 rounded-full bg-[#414141] transition-all duration-300"
                            style={{ width: `${isRepair ? 100 : Math.min(100, progressPercent)}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const GuidedCheckStepCard = ({ cause, step, stepIndex, totalSteps, onAction, onAskHelp }) => (
    <div className="max-w-lg overflow-hidden rounded-3xl border border-[#d9d9d9] bg-white shadow-sm">
        <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] p-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-black uppercase tracking-widest text-[#414141]">Diagnostic Check</div>
                    <div className="mt-2 text-sm font-black text-[#414141]">{cause?.recommendedCheck || cause?.title || 'Guided check'}</div>
                </div>
                <StatusPill>Step {stepIndex + 1} of {Math.max(totalSteps, 1)}</StatusPill>
            </div>
        </div>
        <div className="p-5">
            <div className="text-lg font-black leading-snug text-[#414141]">{step?.instruction || 'Continue the check.'}</div>
            {step?.detail && <p className="mt-3 text-sm font-semibold leading-relaxed text-[#414141]">{step.detail}</p>}
            {step?.toolLabel && <div className="mt-4 text-sm font-semibold text-[#414141]">Tool: {step.toolLabel}</div>}
        </div>
        <div className="border-t border-[#d9d9d9] p-5">
            <div className="mb-3 text-xs font-black uppercase tracking-widest text-[#414141]">What do you observe?</div>
            <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => onAction('normal')} className="rounded-2xl border border-[#9ee8bd] bg-[#e9fff1] px-4 py-3 text-sm font-black text-[#008a36]">Looks Normal</button>
                <button type="button" onClick={() => onAction('issue')} className="rounded-2xl border border-[#ffd36b] bg-[#fff8e7] px-4 py-3 text-sm font-black text-[#a24e00]">Found an Issue</button>
                <button type="button" onClick={() => onAction('not_sure')} className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141]">Not Sure</button>
                <button type="button" onClick={() => onAction('skip')} className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141]">Skip</button>
            </div>
            <button type="button" onClick={onAskHelp} className="mt-3 w-full rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141]">Ask CarFido for Help</button>
        </div>
    </div>
);

const GuidedCheckResultCard = ({ cause, selectedOutcome, notes, onSelectOutcome, onNotesChange, onSave }) => {
    const options = [
        ['normal', 'Result was normal / as expected', 'This cause looks less likely based on the result.'],
        ['issue', 'Found an issue / result was abnormal', 'This cause may be confirmed based on the result.'],
        ['not_sure', "Not sure / couldn't complete", "You couldn't get a clear result or couldn't finish the check."],
    ];
    return (
        <div className="max-w-lg rounded-3xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
            <div className="text-xs font-black uppercase tracking-widest text-[#414141]">Check Complete</div>
            <div className="mt-2 text-lg font-black text-[#414141]">{cause?.recommendedCheck || cause?.title}</div>
            {cause?.expectedResult && (
                <div className="mt-4 rounded-2xl bg-[#F9F9F9] p-4 text-sm font-semibold text-[#414141]">
                    <span className="font-black uppercase tracking-widest">Expected result: </span>{cause.expectedResult}
                </div>
            )}
            <div className="mt-5 space-y-3">
                {options.map(([value, title, description]) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => onSelectOutcome(value)}
                        className={`w-full rounded-2xl border p-4 text-left ${selectedOutcome === value ? 'border-[#09c45b] bg-[#ecfff3]' : 'border-[#d9d9d9] bg-white'}`}
                    >
                        <div className="text-base font-black text-[#414141]">{title}</div>
                        <div className="mt-1 text-sm font-semibold text-[#414141]">{description}</div>
                    </button>
                ))}
            </div>
            <label className="mt-5 block text-xs font-black uppercase tracking-widest text-[#414141]">Notes & readings (optional)</label>
            <textarea
                value={notes || ''}
                onChange={(event) => onNotesChange(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-3 text-sm outline-none"
                rows={3}
                placeholder="e.g. 12.1V at rest, terminals looked clean..."
            />
            <button
                type="button"
                disabled={!selectedOutcome}
                onClick={onSave}
                className="mt-4 w-full rounded-2xl bg-[#414141] px-5 py-3 text-base font-black text-white disabled:opacity-45"
            >
                Save Result
            </button>
        </div>
    );
};

const NextLikelyCauseCard = ({ cause, ruledOutTitle, onStart }) => (
    <div className="max-w-lg overflow-hidden rounded-3xl border border-[#d9d9d9] bg-white shadow-sm">
        <div className="bg-[#F9F9F9] p-5">
            <div className="text-xs font-black uppercase tracking-widest text-[#D81F26]">Next Likely Cause</div>
            <div className="mt-2 text-lg font-black text-[#414141]">{cause?.title || 'Next likely cause'}</div>
        </div>
        <div className="p-5">
            {ruledOutTitle && <div className="mb-4 text-sm font-semibold text-[#414141]">Ruled out: <span className="rounded-full bg-[#dcfce7] px-2 py-1 font-black text-[#008a36]">{ruledOutTitle}</span></div>}
            {cause?.whyItFits && <p className="text-sm font-semibold leading-relaxed text-[#414141]">{cause.whyItFits}</p>}
            <button type="button" onClick={onStart} className="mt-5 w-full rounded-2xl bg-[#414141] px-5 py-3 text-base font-black text-white">
                Start {cause?.recommendedCheck || 'Next Check'}
            </button>
        </div>
    </div>
);

const ConfirmedCauseCard = ({ cause, completedCheck, onStartRepairGuide, onViewReport }) => (
    <div className="max-w-lg overflow-hidden rounded-3xl border border-[#bdebcf] bg-white shadow-sm">
        <div className="bg-[#ecfff3] p-5">
            <div className="text-xs font-black uppercase tracking-widest text-[#008a36]">Likely Cause Confirmed</div>
            <div className="mt-2 text-lg font-black text-[#414141]">{cause?.title || 'Confirmed cause'}</div>
        </div>
        <div className="p-5">
            <div className="rounded-2xl border border-[#bdebcf] bg-[#f2fff7] p-4">
                <div className="font-black text-[#008a36]">Issue found</div>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-[#414141]">
                    User completed "{completedCheck?.checkTitle || cause?.recommendedCheck || 'the guided check'}" and found a result that supports {cause?.title || 'this likely cause'}.
                </p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                <div><div className="font-black uppercase tracking-widest text-[#414141]">Likely Repair</div><div className="mt-1 font-black text-[#414141]">{cause?.likelyRepair || cause?.title}</div></div>
                <div><div className="font-black uppercase tracking-widest text-[#414141]">DIY Cost</div><div className="mt-1 font-black text-[#414141]">{cause?.diyRepairCost || 'Varies'}</div></div>
            </div>
            <button type="button" onClick={onStartRepairGuide} className="mt-5 w-full rounded-2xl bg-[#414141] px-5 py-3 text-base font-black text-white">Start DIY Repair Guide</button>
            <button type="button" onClick={onViewReport} className="mt-3 w-full rounded-2xl border border-[#d9d9d9] bg-white px-5 py-3 text-sm font-black text-[#414141]">View Report</button>
        </div>
    </div>
);

const RepairGuideStepCard = ({ cause, step, stepIndex, totalSteps, onComplete, onSkip, onAskDash }) => {
    const isLast = stepIndex >= totalSteps - 1;
    return (
        <div className="max-w-lg overflow-hidden rounded-3xl border border-[#d9d9d9] bg-white shadow-sm">
            <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] p-5">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-xs font-black uppercase tracking-widest text-[#414141]">Repair Guide</div>
                        <div className="mt-2 text-sm font-black text-[#414141]">{cause?.likelyRepair || cause?.title || 'Repair'}</div>
                    </div>
                    <StatusPill>Step {stepIndex + 1} of {Math.max(totalSteps, 1)}</StatusPill>
                </div>
            </div>
            <div className="p-5">
                <div className="text-lg font-black leading-snug text-[#414141]">{step?.instruction || 'Continue the repair.'}</div>
                {step?.detail && <p className="mt-3 text-sm font-semibold leading-relaxed text-[#414141]">{step.detail}</p>}
            </div>
            <div className="border-t border-[#d9d9d9] p-5">
                <button type="button" onClick={onComplete} className="w-full rounded-2xl bg-[#414141] px-5 py-3 text-base font-black text-white">
                    {isLast ? 'Mark Repair Complete' : 'Mark Step Complete'}
                </button>
                <div className="mt-3 grid grid-cols-2 gap-3">
                    <button type="button" onClick={onSkip} className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141]">Skip Step</button>
                    <button type="button" onClick={onAskDash} className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141]">Ask CarFido</button>
                </div>
            </div>
        </div>
    );
};

const RepairLogChoiceCard = ({ cause, onLogNow, onLater }) => (
    <div className="max-w-md rounded-3xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
        <div className="text-lg font-black text-[#414141]">{cause?.likelyRepair || cause?.title || 'Repair'} complete?</div>
        <div className="mt-1 text-sm font-semibold text-[#414141]">Log this to service history and close the ticket.</div>
        <div className="mt-5 grid grid-cols-2 gap-3">
            <button type="button" onClick={onLogNow} className="rounded-2xl bg-[#414141] px-4 py-3 text-sm font-black text-white">Yes, log it</button>
            <button type="button" onClick={onLater} className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141]">Maybe later</button>
        </div>
    </div>
);

const RepairLogFormCard = ({ cause, mileage, note, onMileageChange, onNoteChange, onBack, onSubmit, isSubmitting, error, distanceUnit = 'mi' }) => (
    <div className="max-w-md rounded-3xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
        <div className="text-lg font-black text-[#414141]">{cause?.likelyRepair || cause?.title || 'Repair'}</div>
        <div className="mt-1 text-sm font-semibold text-[#414141]">Confirm details before logging and closing the ticket.</div>
        <label className="mt-5 block text-sm font-black text-[#414141]">Odometer reading ({distanceUnitLabel(distanceUnit)})</label>
        <input value={mileage || ''} onChange={(event) => onMileageChange(event.target.value)} inputMode="numeric" className="mt-2 w-full rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-3 outline-none" placeholder={distanceUnit === 'km' ? 'e.g. 140,000' : 'e.g. 87,500'} />
        <label className="mt-4 block text-sm font-black text-[#414141]">Note (optional)</label>
        <input value={note || ''} onChange={(event) => onNoteChange(event.target.value)} className="mt-2 w-full rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-3 outline-none" placeholder="e.g. repair completed successfully" />
        {error && <div className="mt-4 rounded-2xl bg-[#fff4dc] px-4 py-3 text-sm font-black text-[#a76500]">{error}</div>}
        <div className="mt-5 grid grid-cols-[1fr_auto] gap-3">
            <button type="button" disabled={isSubmitting} onClick={onSubmit} className="rounded-2xl bg-[#414141] px-5 py-3 text-base font-black text-white disabled:opacity-50">
                {isSubmitting ? 'Closing...' : 'Log it'}
            </button>
            <button type="button" onClick={onBack} className="rounded-2xl border border-[#d9d9d9] bg-white px-5 py-3 text-base font-black text-[#414141]">Back</button>
        </div>
    </div>
);

const RepairLoggedCard = ({ cause }) => (
    <div className="max-w-md rounded-3xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
        <div className="text-lg font-black text-[#414141]">{cause?.likelyRepair || cause?.title || 'Repair'}</div>
        <div className="mt-2 text-sm font-semibold text-[#414141]">Logged to this ticket record and the ticket is now closed.</div>
    </div>
);

const GuidedCardSnapshot = ({ session, entry }) => {
    const card = entry?.card || {};
    const cause = session?.report?.likelyCauses?.[card.causeIndex] || {};
    const guidedSteps = getGuidedStepsFromCause(cause);
    const repairSteps = getRepairGuideStepsFromCause(cause);
    const guidedStep = guidedSteps[card.stepIndex] || guidedSteps[0] || {};
    const noop = () => {};

    if (card.type === 'step') {
        if (card.action) {
            return (
                <GuidedStepSelectionCard
                    cause={cause}
                    step={guidedStep}
                    action={card.action}
                    label={`Step ${(card.stepIndex || 0) + 1}: ${card.actionLabel || 'Updated'}`}
                />
            );
        }
        return (
            <CompactGuidedCheckStepCard
                cause={cause}
                step={guidedStep}
                stepIndex={card.stepIndex || 0}
                totalSteps={guidedSteps.length}
                onAction={noop}
                onAskHelp={noop}
            />
        );
    }

    if (card.type === 'result') {
        return (
            <GuidedCheckSavedResultCard
                cause={cause}
                step={guidedStep}
                outcome={card.selectedOutcome}
                notes={card.notes}
            />
        );
    }

    if (card.type === 'next_cause') {
        return (
            <CompactNextLikelyCauseCard
                cause={cause}
                ruledOutTitle={card.ruledOutTitle}
                onStart={noop}
            />
        );
    }

    if (card.type === 'confirmed') {
        const completedCheck = (session?.report?.completedGuidedChecks || []).find((item) => item.causeId === cause?.id);
        return (
            <CompactConfirmedCauseCard
                cause={cause}
                completedCheck={completedCheck}
                onStartRepairGuide={noop}
                onViewReport={noop}
            />
        );
    }

    if (card.type === 'repair_step') {
        return (
            <CompactRepairGuideStepperCard
                cause={cause}
                steps={repairSteps}
                stepIndex={card.stepIndex || 0}
                onComplete={noop}
                onAskDash={noop}
                onPrev={noop}
                onNext={noop}
            />
        );
    }

    if (card.type === 'repair_done_choice') {
        return (
            <CompactRepairLogChoiceCard
                cause={cause}
                onLogNow={noop}
                onLater={noop}
            />
        );
    }

    if (card.type === 'repair_log_form') {
        return (
            <CompactRepairLogFormCard
                cause={cause}
                mileage={card.repairMileage}
                note={card.repairNote}
                onMileageChange={noop}
                onNoteChange={noop}
                onBack={noop}
                onSubmit={noop}
                isSubmitting={false}
                error=""
                locked
            />
        );
    }

    if (card.type === 'repair_logged') {
        return <CompactRepairLoggedCard cause={cause} />;
    }

    return null;
};

const FreshChatThinking = () => (
    <div className="flex w-full gap-4 md:gap-6 animate-in fade-in duration-300">
        <DashAvatar thinking />
        <div className="flex-1 pt-2 text-[10px] font-black uppercase tracking-[0.18em] text-black/35">
            Starting chat
        </div>
    </div>
);

const inferAttachmentKind = (file = {}) => {
    const filename = String(file.filename || file.name || '').toLowerCase();
    const mimeType = String(file.mime_type || file.type || '').toLowerCase();
    const rawKind = String(file.kind || file.media_kind || '').toLowerCase();
    if (mimeType === 'application/pdf' || filename.endsWith('.pdf') || rawKind === 'pdf') return 'pdf';
    if (mimeType.startsWith('image/') || rawKind === 'photo' || rawKind === 'image' || /\.(avif|bmp|gif|hei[cf]|jpe?g|jfif|png|tiff?|webp)$/i.test(filename)) return 'image';
    if (mimeType.startsWith('video/') || rawKind === 'video' || /\.(mov|mp4|webm)$/i.test(filename)) return 'video';
    if (mimeType.startsWith('audio/') || rawKind === 'audio') return 'audio';
    if (rawKind === 'document') return 'document';
    return 'file';
};
const getAttachmentMimeType = (file = {}) => {
    if (file.mime_type || file.type) return file.mime_type || file.type;
    const kind = inferAttachmentKind(file);
    if (kind === 'pdf') return 'application/pdf';
    if (kind === 'image') return 'image/*';
    if (kind === 'audio') return 'audio/*';
    if (kind === 'video') return 'video/*';
    return 'application/octet-stream';
};
const BROWSER_PREVIEW_IMAGE_MIME_TYPES = new Set([
    'image/avif',
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/jpg',
    'image/jfif',
    'image/png',
    'image/webp',
]);
const BROWSER_PREVIEW_IMAGE_EXTENSIONS = new Set([
    'avif',
    'bmp',
    'gif',
    'jpeg',
    'jfif',
    'jpg',
    'png',
    'webp',
]);
const isBrowserPreviewableImageAttachment = (file = {}) => {
    if (inferAttachmentKind(file) !== 'image') return false;
    const mimeType = String(file.mime_type || file.type || '').toLowerCase();
    const extension = getFileExtension(file.filename || file.name || '');
    if (mimeType && mimeType !== 'image/*') return BROWSER_PREVIEW_IMAGE_MIME_TYPES.has(mimeType);
    return BROWSER_PREVIEW_IMAGE_EXTENSIONS.has(extension);
};
const getAttachmentFileUrl = (file = {}) => {
    const directUrl = file.file_url || file.fileUrl || file.preview_url || file.previewUrl || file.local_url || file.localUrl || file.object_url || file.objectUrl || file.url || '';
    if (directUrl) return directUrl;
    if (file.doc_id || file.docId) {
        return `${getUserDetailsBaseUrl()}/api/documents/me/${encodeURIComponent(file.doc_id || file.docId)}/file`;
    }
    return '';
};
const getAttachmentImageSrc = (file = {}) => (
    file.imageSrc
    || (isBrowserPreviewableImageAttachment(file) && file.base64 ? `data:${getAttachmentMimeType(file)};base64,${file.base64}` : '')
    || (isBrowserPreviewableImageAttachment(file) ? getAttachmentFileUrl(file) : '')
);
const getAttachmentLabel = (file = {}) => file.filename || file.name || 'Attachment';
const getAttachmentKindLabel = (file = {}) => {
    const kind = inferAttachmentKind(file);
    if (kind === 'pdf') return 'PDF';
    if (kind === 'image') {
        const extension = getFileExtension(getAttachmentLabel(file));
        return extension ? extension.toUpperCase() : 'Image';
    }
    if (kind === 'audio') return 'Audio';
    if (kind === 'video') return 'Video';
    if (kind === 'document') {
        const extension = getFileExtension(getAttachmentLabel(file));
        return extension ? extension.toUpperCase() : 'Document';
    }
    const mimeType = getAttachmentMimeType(file);
    return (mimeType.split('/')[1] || 'File').toUpperCase();
};
const MAX_CHAT_ATTACHMENT_FILES = 10;
const MAX_CHAT_ATTACHMENT_BYTES = 50 * 1024 * 1024;
const ALLOWED_CHAT_ATTACHMENT_EXTENSIONS = new Set([
    'avif', 'bmp', 'csv', 'doc', 'docx', 'gif', 'heic', 'heif',
    'jpeg', 'jfif', 'jpg', 'pdf', 'png', 'tif', 'tiff', 'txt',
    'webp', 'xls', 'xlsx', 'mp4', 'mov', 'webm',
]);
const CHAT_VIDEO_ATTACHMENT_EXTENSIONS = new Set(['mov', 'mp4', 'webm']);
const CHAT_SUPPORTED_FILE_TYPES_MESSAGE = 'Please upload one of these file types: .avif, .bmp, .csv, .doc, .docx, .gif, .heic, .heif, .jpeg, .jfif, .jpg, .mov, .mp4, .pdf, .png, .tif, .tiff, .txt, .webm, .webp, .xls, or .xlsx.';
const CHAT_FILE_INPUT_ACCEPT = [
    'image/*',
    'video/*',
    '.avif',
    '.bmp',
    '.csv',
    '.doc',
    '.docx',
    '.heic',
    '.heif',
    '.jfif',
    '.pdf',
    '.tif',
    '.tiff',
    '.txt',
    '.xls',
    '.xlsx',
].join(',');
const DANGEROUS_CHAT_ATTACHMENT_EXTENSIONS = new Set([
    'app', 'bat', 'bin', 'cmd', 'com', 'cpl', 'dll', 'dmg', 'exe',
    'gadget', 'hta', 'jar', 'js', 'jse', 'msi', 'msp', 'pif', 'ps1',
    'scr', 'sh', 'vb', 'vbe', 'vbs', 'wsf',
]);
const getFileExtension = (name = '') => {
    const match = String(name || '').toLowerCase().match(/\.([^.]+)$/);
    return match ? match[1] : '';
};
const getChatAttachmentValidationError = (file = {}) => {
    const filename = file.name || file.filename || 'This file';
    const extension = getFileExtension(filename);
    const mimeType = String(file.type || file.mime_type || '').toLowerCase();

    if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
        return `${filename} is larger than 50 MB.`;
    }
    if (DANGEROUS_CHAT_ATTACHMENT_EXTENSIONS.has(extension)) {
        return `${filename} file type is not supported. ${CHAT_SUPPORTED_FILE_TYPES_MESSAGE}`;
    }
    if (
        mimeType.startsWith('image/')
        || mimeType.startsWith('video/')
        || mimeType === 'application/pdf'
        || mimeType === 'text/plain'
        || mimeType === 'text/csv'
        || ALLOWED_CHAT_ATTACHMENT_EXTENSIONS.has(extension)
    ) {
        return '';
    }
    if (mimeType.startsWith('audio/') && !CHAT_VIDEO_ATTACHMENT_EXTENSIONS.has(extension)) {
        return `${filename} is an audio file. Use the microphone button for voice notes.`;
    }
    return `${filename} file type is not supported. ${CHAT_SUPPORTED_FILE_TYPES_MESSAGE}`;
};
const fetchAttachmentImagePreview = async (attachment = {}) => {
    if (inferAttachmentKind(attachment) !== 'image' || isBrowserPreviewableImageAttachment(attachment)) {
        return null;
    }
    try {
        const response = await fetch('/api/chat/attachment-preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                filename: attachment.filename,
                mime_type: attachment.mime_type,
                base64: attachment.base64,
            }),
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (!data?.base64 || !data?.mime_type) return null;
        return `data:${data.mime_type};base64,${data.base64}`;
    } catch (error) {
        console.warn('Attachment preview conversion failed:', error);
        return null;
    }
};
const formatAttachmentSize = (size) => {
    const bytes = Number(size);
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const buildAttachmentPreview = (file = {}) => {
    const mimeType = getAttachmentMimeType(file);
    const imageSrc = getAttachmentImageSrc(file);
    const fileUrl = getAttachmentFileUrl(file);
    return {
        id: file.id || `${getAttachmentLabel(file)}-${Math.random().toString(36).slice(2, 8)}`,
        filename: getAttachmentLabel(file),
        mime_type: mimeType,
        kind: inferAttachmentKind(file),
        media_kind: file.media_kind || file.kind || null,
        doc_id: file.doc_id || file.docId || null,
        upload_id: file.upload_id || file.uploadId || null,
        size: file.size || null,
        ...(imageSrc ? { imageSrc } : {}),
        ...(fileUrl ? { url: fileUrl } : {}),
        ...(file.local_url || file.localUrl ? { local_url: file.local_url || file.localUrl } : {}),
    };
};
const sanitizeAttachmentPreview = (file = {}, index = 0) => {
    const filename = getAttachmentLabel(file);
    return {
        id: file.id || `${filename}-${index}`,
        filename,
        mime_type: getAttachmentMimeType(file),
        kind: inferAttachmentKind(file),
        media_kind: file.media_kind || file.kind || null,
        doc_id: file.doc_id || file.docId || null,
        upload_id: file.upload_id || file.uploadId || null,
        url: getAttachmentFileUrl(file),
        size: file.size || null,
    };
};
const parseAttachmentMarker = (value = '') => {
    const raw = safeText(value);
    const markerIndex = raw.indexOf(ATTACHMENT_MARKER_PREFIX);
    if (markerIndex < 0) return { content: raw, attachments: [] };

    const jsonStart = markerIndex + ATTACHMENT_MARKER_PREFIX.length;
    const lineEnd = raw.indexOf('\n', jsonStart);
    const jsonText = raw.slice(jsonStart, lineEnd >= 0 ? lineEnd : undefined).trim();
    let attachments = [];
    try {
        const parsed = JSON.parse(jsonText);
        if (Array.isArray(parsed)) {
            attachments = parsed.map(sanitizeAttachmentPreview).filter(Boolean);
        }
    } catch {}

    const before = raw.slice(0, markerIndex).trim();
    const after = lineEnd >= 0 ? raw.slice(lineEnd + 1).trim() : '';
    return {
        content: [before, after].filter(Boolean).join('\n\n'),
        attachments,
    };
};
const inferLegacyUploadAttachment = (filename = '', descriptor = '', index = 0) => {
    const normalizedDescriptor = String(descriptor || '').toLowerCase();
    const normalizedName = String(filename || '').toLowerCase();
    let kind = 'file';
    if (normalizedDescriptor.includes('photo') || normalizedDescriptor.includes('image')) kind = 'image';
    else if (normalizedDescriptor.includes('audio')) kind = 'audio';
    else if (normalizedDescriptor.includes('video')) kind = 'video';
    else if (normalizedDescriptor.includes('document')) kind = normalizedName.endsWith('.pdf') ? 'pdf' : 'document';
    else if (normalizedName.endsWith('.pdf')) kind = 'pdf';

    return sanitizeAttachmentPreview({
        id: `legacy-upload-${index}-${filename || 'attachment'}`,
        filename: filename || `Attachment ${index + 1}`,
        kind,
    }, index);
};
const parseLegacyUploadPrompt = (value = '') => {
    const raw = safeText(value);
    const hasLeakedUploadContext = raw.includes('[System: The user just uploaded') || raw.includes("I've uploaded the following file(s).");
    if (!hasLeakedUploadContext) return { content: raw, attachments: [] };

    const attachments = [];
    const uploadMatches = [...raw.matchAll(/--- UPLOAD:\s*(.+?)\s*\(([^)]*)\)\s*---/g)];
    uploadMatches.forEach((match, index) => {
        attachments.push(inferLegacyUploadAttachment(match[1].trim(), match[2], index));
    });

    if (attachments.length === 0) {
        const systemBlock = raw.match(/\[System:[\s\S]*?\]/)?.[0] || '';
        const quotedNames = [...systemBlock.matchAll(/'([^']+)'/g)].map(match => match[1]);
        quotedNames.forEach((filename, index) => {
            attachments.push(inferLegacyUploadAttachment(filename, '', index));
        });
    }

    const questionMatch = raw.match(/\bMy question:\s*([\s\S]*)$/i);
    const visibleContent = questionMatch ? questionMatch[1].trim() : '';
    return { content: visibleContent, attachments };
};
const parseUserVisibleContent = (value = '') => {
    const markerParsed = parseAttachmentMarker(value);
    const legacyParsed = parseLegacyUploadPrompt(markerParsed.content);
    const seen = new Set();
    const attachments = [...markerParsed.attachments, ...legacyParsed.attachments].filter((attachment) => {
        const key = `${attachment.filename || ''}|${attachment.kind || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
    return {
        content: legacyParsed.content,
        attachments,
    };
};

const AttachmentPreviewList = ({ files = [], onRemove, variant = 'message', onOpenImage }) => {
    const items = (Array.isArray(files) ? files : []).filter(Boolean);
    if (items.length === 0) return null;

    const isComposer = variant === 'composer';
    const openAttachment = (file) => {
        const imageSrc = getAttachmentImageSrc(file);
        const fileUrl = getAttachmentFileUrl(file);
        if (imageSrc && onOpenImage) {
            onOpenImage({ ...file, imageSrc, url: fileUrl || imageSrc });
            return;
        }
        if (fileUrl) {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
        }
    };
    return (
        <div className={isComposer ? 'flex max-w-full gap-2 overflow-x-auto px-1 pb-1' : 'grid gap-2'}>
            {items.map((file, index) => {
                const key = file.id || `${getAttachmentLabel(file)}-${index}`;
                const imageSrc = getAttachmentImageSrc(file);
                const fileUrl = getAttachmentFileUrl(file);
                const sizeLabel = formatAttachmentSize(file.size);
                const typeLabel = getAttachmentKindLabel(file);
                const isClickable = Boolean(imageSrc || fileUrl);

                return (
                    <div
                        key={key}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        onClick={isClickable ? () => openAttachment(file) : undefined}
                        onKeyDown={isClickable ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openAttachment(file);
                            }
                        } : undefined}
                        className={isComposer
                            ? `group relative min-w-[180px] max-w-[220px] overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm ${isClickable ? 'cursor-pointer' : ''}`
                            : `overflow-hidden rounded-2xl border border-black/10 bg-white/75 ${isClickable ? 'cursor-pointer transition hover:border-[#D81F26]' : ''}`
                        }
                    >
                        {imageSrc ? (
                            <div className="relative">
                                <img
                                    src={imageSrc}
                                    alt={getAttachmentLabel(file)}
                                    className={isComposer ? 'h-24 w-full object-cover' : 'max-h-64 w-full object-cover'}
                                />
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-3 pb-2 pt-8">
                                    <p className="truncate text-xs font-black text-white">{getAttachmentLabel(file)}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 px-3 py-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F9F9F9] text-[#414141]">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <path d="M14 2v6h6" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-black text-black">{getAttachmentLabel(file)}</p>
                                    <p className="text-xs font-bold text-black/45">{[typeLabel, sizeLabel].filter(Boolean).join(' - ')}</p>
                                </div>
                            </div>
                        )}
                        {onRemove && (
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onRemove(file.id);
                                }}
                                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white opacity-90 transition hover:bg-black"
                                aria-label={`Remove ${getAttachmentLabel(file)}`}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6 6 18" />
                                    <path d="m6 6 12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const AttachmentImageModal = ({ file, onClose }) => {
    if (!file) return null;
    const imageSrc = getAttachmentImageSrc(file) || file.imageSrc || file.url;
    const fileUrl = getAttachmentFileUrl(file) || file.url || imageSrc;
    if (!imageSrc) return null;
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 px-4 py-6" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="relative max-h-full w-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
                    aria-label="Close preview"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>
                <img src={imageSrc} alt={getAttachmentLabel(file)} className="mx-auto max-h-[82vh] max-w-full rounded-2xl bg-white object-contain shadow-2xl" />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
                    <p className="min-w-0 truncate text-sm font-black text-black">{getAttachmentLabel(file)}</p>
                    {fileUrl && (
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-full bg-[#D81F26] px-4 py-2 text-xs font-black text-white">
                            Open in new tab
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

const FRESH_CHAT_ACTIONS = [
    { id: 'add-vehicle', label: 'Add my vehicle' },
    { id: 'diagnose-problem', label: 'Diagnose a problem' },
    ...(PARTS_FLOW_ENABLED ? [{ id: 'find-part-tool', label: 'Find part/tool' }] : []),
    { id: 'log-service', label: 'Log service' },
    { id: 'maintenance-due', label: 'Maintenance due?' },
    { id: 'plan-modification', label: 'Plan a modification' },
];

// New-chat greeting: words rise in one after another. The total stagger is capped so long
// greetings never hold the user up; already-open threads render the text plainly.
const INTRO_WORD_START_MS = 120;
const INTRO_WORD_STEP_MS = 16;
const INTRO_WORD_MAX_DELAY_MS = 760;

const introWordDelay = (index) => Math.min(INTRO_WORD_START_MS + index * INTRO_WORD_STEP_MS, INTRO_WORD_MAX_DELAY_MS);

const IntroWords = ({ text, animate }) => {
    if (!animate) return text;
    return text.split(' ').map((word, index) => (
        <React.Fragment key={index}>
            <span className="inline-block animate-dash-intro-rise" style={{ animationDelay: `${introWordDelay(index)}ms` }}>{word}</span>{' '}
        </React.Fragment>
    ));
};

// Timestamp and chips start once the greeting has mostly landed.
const introAfterTextDelay = (text) => introWordDelay(text.split(' ').length) + 80;

const FRESH_CHAT_GREETING = "Hey, I'm CarFido — your AI car assistant. Add your vehicle to get started, then describe any symptoms, warning lights, or questions and I'll diagnose, create reports, and build repair plans for you.";

const FreshChatIntro = ({ timestamp, onQuickAction, showSuggestions = true }) => {
    const afterText = introAfterTextDelay(FRESH_CHAT_GREETING);
    return (
    <div className="flex gap-4 md:gap-6 w-full animate-in fade-in duration-500">
        <DashAvatar intro={showSuggestions} />
        <div className="flex-1 text-black overflow-hidden pt-1">
            <p className="m-0 max-w-2xl text-[15px] md:text-base leading-relaxed font-medium text-black">
                <IntroWords text={FRESH_CHAT_GREETING} animate={showSuggestions} />
            </p>
            <div className={`mt-2 ml-1 text-[13px] font-semibold text-[#414141] ${showSuggestions ? 'animate-dash-intro-rise' : ''}`} style={showSuggestions ? { animationDelay: `${afterText}ms` } : undefined}>
                {timestamp}
            </div>
            {showSuggestions && (
                <div className="mt-3 flex max-w-2xl flex-wrap gap-2">
                    {FRESH_CHAT_ACTIONS.map((action, index) => (
                        <button
                            key={action.id}
                            type="button"
                            onClick={() => onQuickAction(action.id)}
                            style={{ animationDelay: `${afterText + 60 + index * 45}ms` }}
                            className="animate-dash-intro-rise px-4 py-2.5 rounded-full text-[13px] font-bold text-[#414141] bg-[#F9F9F9] hover:bg-[#F2F2F2] hover:border-[#414141] border border-[#d9d9d9] shadow-sm transition-all duration-200 active:scale-95 whitespace-normal break-words text-center leading-snug flex max-w-full items-center justify-center min-h-[40px]"
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    </div>
    );
};

// Returning user (has a vehicle) gets a personalised greeting referencing their car
// and any open issue. New users (no vehicle) get FreshChatIntro instead.
const ReturningUserIntro = ({ timestamp, vehicleLabel, vehicleDetail, openIssue, onSuggestion, showSuggestions = true, revisitedStartedAt }) => {
    const suggestions = ['Diagnose an Issue', 'Find Parts', 'Log a Service', 'Plan a Modification'];
    const greeting = `Hey! I'm CarFido, your AI car assistant. I'm loaded up on your ${vehicleLabel}${vehicleDetail ? ` (${vehicleDetail})` : ''}.${openIssue ? ` I'm aware of an open issue — ${openIssue}.` : ''} What's going on today?`;
    const afterText = introAfterTextDelay(greeting);
    return (
        <div className="flex gap-4 md:gap-6 w-full animate-in fade-in duration-500">
            <DashAvatar intro={showSuggestions} />
            <div className="flex-1 text-black overflow-hidden pt-1">
                <p className="m-0 max-w-2xl text-[15px] md:text-base leading-relaxed font-medium text-black">
                    <IntroWords text={greeting} animate={showSuggestions} />
                </p>
                <div className={`mt-2 ml-1 text-[13px] font-semibold text-[#414141] ${showSuggestions ? 'animate-dash-intro-rise' : ''}`} style={showSuggestions ? { animationDelay: `${afterText}ms` } : undefined}>
                    {revisitedStartedAt || timestamp}
                </div>
                {showSuggestions && (
                    <div className="mt-3 flex max-w-xl flex-wrap gap-2">
                        {suggestions.map((s, index) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => onSuggestion(s)}
                                style={{ animationDelay: `${afterText + 60 + index * 45}ms` }}
                                className="animate-dash-intro-rise px-4 py-2.5 rounded-full text-[13px] font-bold text-[#414141] bg-[#F9F9F9] hover:bg-[#F2F2F2] hover:border-[#414141] border border-[#d9d9d9] shadow-sm transition-all duration-200 active:scale-95 whitespace-normal break-words text-center leading-snug flex max-w-full items-center justify-center min-h-[40px]"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const getReportGenerationTitle = (value = '') => {
    const text = cleanDisplayText(value).toLowerCase();
    if (text.includes('vehicle history')) return 'Compiling Vehicle History';
    if (text.includes('repair guide') || text.includes('diy')) return 'Building Repair Guide';
    if (text.includes('work plan') || text.includes('plan')) return 'Preparing Work Plan';
    if (text.includes('summary')) return 'Preparing Summary';
    return 'Generating Symptom Report';
};

const ReportGenerationSkeleton = ({ status }) => {
    const title = getReportGenerationTitle(status);
    const rawDetail = relabelReport(cleanDisplayText(status));
    // Avoid printing the same label twice: the bold header already shows e.g.
    // "Generating Symptom Report", so when the status line is just that same phrase,
    // use a generic progress sub-line instead of repeating it.
    const normalizeLabel = (value) => String(value || '')
        .toLowerCase()
        .replace(/[.…]+$/, '')
        .replace(/\s+/g, ' ')
        .trim();
    const detail = (!rawDetail || normalizeLabel(rawDetail) === normalizeLabel(title))
        ? 'CarFido is organizing the details for this report.'
        : rawDetail;

    return (
        <div className="w-full max-w-[460px] overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-none">
            <div className="flex items-center justify-between gap-3 border-b border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#414141]">
                        {title}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-[#414141]">
                        {detail}
                    </div>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full border border-[#d9d9d9] bg-[#F9F9F9]">
                    <div className="h-3 w-3 animate-pulse rounded-full bg-[#D81F26]" />
                </div>
            </div>
            <div className="px-4 py-3">
                <div className="mb-3 grid grid-cols-3 gap-2">
                    {[0, 1, 2].map((item) => (
                        <div key={item} className="h-14 animate-pulse rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] p-2">
                            <div className="h-2 w-8 rounded-full bg-[#d9d9d9]" />
                            <div className="mt-4 h-2 w-12 rounded-full bg-[#F9F9F9]" />
                        </div>
                    ))}
                </div>
                <div className="space-y-2 animate-pulse">
                    <div className="h-2.5 w-11/12 rounded-full bg-[#d9d9d9]" />
                    <div className="h-2.5 w-3/4 rounded-full bg-[#d9d9d9]" />
                    <div className="h-2.5 w-1/2 rounded-full bg-[#d9d9d9]" />
                </div>
            </div>
        </div>
    );
};

// past-maintenance UI (temporarily hidden — see the commented block in
// ChatVehicleFormCard). The high-value services we ask about at onboarding so next-due
// maintenance can be worked out from the correct starting point (keys match the backend
// schedule). Commented out with the UI so it does not sit here unused.
// const KEY_MAINTENANCE_SERVICES = [
//     { key: 'oil_change',        title: 'Oil Change' },
//     { key: 'tire_rotation',     title: 'Tire Rotation' },
//     { key: 'brake_fluid',       title: 'Brakes / Brake Fluid' },
//     { key: 'engine_air_filter', title: 'Air Filter' },
//     { key: 'transmission',      title: 'Transmission Fluid' },
// ];

const createEmptyChatVehicleForm = () => ({
    vin: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    odometerReading: '',
    odometerUnit: '',   // '' → falls back to the user's preferred unit in the UI
    engine: '',
    transmission: '',
    drivetrain: '',
    fuel_type: '',
    plate: '',
    // Onboarding "past maintenance" — per key-service: { enabled, odometer, odometerBased }
    maintenanceHistory: {},
});

const getCurrentChatTime = () => (
    new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
);

// Common vehicle makes (lowercased) used to split a "Did you mean" suggestion string
// into make + model. Merged with makes discovered from the validation metadata.
const COMMON_VEHICLE_MAKES = [
    'toyota', 'honda', 'ford', 'chevrolet', 'chevy', 'nissan', 'hyundai', 'kia', 'mazda',
    'subaru', 'volkswagen', 'audi', 'bmw', 'mercedes-benz', 'mercedes', 'lexus', 'acura',
    'infiniti', 'jeep', 'ram', 'dodge', 'chrysler', 'gmc', 'buick', 'cadillac', 'tesla',
    'volvo', 'porsche', 'jaguar', 'land rover', 'mini', 'mitsubishi', 'suzuki',
    'maruti suzuki', 'maruti', 'tata', 'mahindra', 'mg', 'renault', 'skoda', 'fiat',
    'peugeot', 'citroen', 'rolls-royce', 'bentley', 'ferrari', 'lamborghini', 'genesis',
];

// Parse a "Did you mean" suggestion string (e.g. "Hyundai Verna", "2020 Toyota Camry")
// into { year, make, model, label }. `knownMakes` is a Set of lowercased make names used
// to correctly split multi-word makes ("Maruti Suzuki Baleno" → make "Maruti Suzuki").
const parseVehicleSuggestion = (raw, knownMakes, fallbackYear = '') => {
    let text = String(raw || '').replace(/["'?.]/g, '').trim();
    if (!text) return null;
    let year = fallbackYear ? String(fallbackYear) : '';
    const yearMatch = text.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
        year = yearMatch[0];
        text = text.replace(yearMatch[0], '').trim();
    }
    const tokens = text.split(/\s+/).filter(Boolean);
    if (!tokens.length) return null;
    let make = '';
    let model = '';
    for (let take = Math.min(3, tokens.length); take >= 1; take -= 1) {
        const candidate = tokens.slice(0, take).join(' ');
        if (knownMakes && knownMakes.has(candidate.toLowerCase())) {
            make = candidate;
            model = tokens.slice(take).join(' ');
            break;
        }
    }
    if (!make) {
        make = tokens[0];
        model = tokens.slice(1).join(' ');
    }
    if (!make || !model) return null;
    return { year, make, model, label: [year, make, model].filter(Boolean).join(' ') };
};

// "WAGON R" → "Wagon R", "GRAND I10" → "Grand I10" (keep tokens with digits uppercase).
const titleCaseModel = (value = '') => (
    String(value).trim().split(/\s+/).filter(Boolean).map((word) => (
        /\d/.test(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )).join(' ')
);

const normalizeOdometerInput = (value = '') => {
    const digits = String(value).replace(/[^\d]/g, '');
    return digits ? Number(digits) : null;
};

// Strict whole-number odometer parser for the Add Vehicle form (ADDVEH-ODO-004).
// Unlike normalizeOdometerInput, which strips stray characters and would silently
// coerce "45000abc" → 45000 or "45.5" → 455, this REJECTS anything that isn't a
// clean whole number: text, decimals, and mixed alphanumeric all return null so the
// vehicle can't be saved with an invalid mileage. Thousands separators (commas and
// spaces, e.g. "45,000") are allowed; "0" is a valid reading.
const parseWholeOdometer = (value) => {
    if (value === null || value === undefined) return null;
    const cleaned = String(value).trim().replace(/[,\s]/g, '');
    // Allow an optional leading sign so a negative whole number parses to a negative
    // Number (rejected separately with a specific message, ADDVEH-ODO-005) rather than
    // being lumped in with non-numeric input.
    if (!/^-?\d+$/.test(cleaned)) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
};

const VehicleFormField = ({ label, value, onChange, required = false, placeholder = '', className = '', children, inputMode, disabled = false }) => (
    <label className={`block ${className}`}>
        <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">
            {label}{required ? ' *' : ''}
        </span>
        {children || (
            <input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                inputMode={inputMode}
                disabled={disabled}
                className={`mt-1.5 w-full rounded-xl border border-[#d9d9d9] px-3.5 py-2.5 text-sm font-semibold text-[#414141] shadow-none outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15 ${disabled ? 'cursor-not-allowed bg-[#f4f4f4] text-[#414141]' : 'bg-white'}`}
            />
        )}
    </label>
);

// A read-only snapshot card shown in the chat AFTER a vehicle is added via the form,
// so the "what I added" card stays in the conversation instead of vanishing.
const VehicleAddedCard = ({ vehicle = {} }) => {
    const label = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Your vehicle';
    const odo = (vehicle.odometer != null && vehicle.odometer !== '')
        ? `${Number(vehicle.odometer).toLocaleString('en-US')} ${vehicle.unit === 'km' ? 'km' : 'mi'}`
        : '';
    const rows = [
        ['Odometer', odo],
        ['Engine', cleanDisplayText(vehicle.engine)],
        ['Transmission', cleanDisplayText(vehicle.transmission)],
        ['Drivetrain', cleanDisplayText(vehicle.drivetrain)],
        ['Plate', cleanDisplayText(vehicle.plate)],
        ['VIN', cleanDisplayText(vehicle.vin)],
    ].filter(([, v]) => v);
    return (
        <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-none">
            <div className="flex items-center gap-3 border-b border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#414141] text-white">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" />
                    </svg>
                </span>
                <div className="min-w-0">
                    <p className="m-0 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Added to your garage</p>
                    <h3 className="m-0 truncate text-base font-black text-[#414141]">{label}</h3>
                </div>
                <span className="ml-auto shrink-0 rounded-full bg-[#e6f7ee] px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-[#0a8f53]">Added</span>
            </div>
            {rows.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 px-4 py-3.5">
                    {rows.map(([k, v]) => (
                        <div key={k} className="min-w-0">
                            <p className="m-0 text-[10px] font-black uppercase tracking-[0.1em] text-[#414141]">{k}</p>
                            <p className="m-0 truncate text-sm font-semibold text-[#414141]">{v}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// A card shown in chat when a service record is created or updated, with all known
// details (type, date, mileage, shop, cost, notes) so the driver sees the saved record.
const formatServiceCardDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
        return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', year: 'numeric' }).format(parsed);
    }
    return String(value);
};

const ServiceCard = ({ service = {}, vehicleLabel = '', onOpen = null, distanceUnit = 'mi' }) => {
    const title = cleanDisplayText(service.serviceType) || 'Service record';
    // Always openable (jumps to the Glove Box service history); when the record id is known
    // the Glove Box also expands/highlights that exact record.
    const canOpen = Boolean(onOpen);
    const handleOpen = () => { if (canOpen) onOpen(service); };
    const isUpdate = service.action === 'updated';
    // service.mileage is stored in MILES; show it in the user's preferred unit.
    const mileage = (service.mileage != null && service.mileage !== '')
        ? formatMileage(service.mileage, distanceUnit)
        : '';
    const cost = (service.cost != null && service.cost !== '')
        ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: Number(service.cost) % 1 === 0 ? 0 : 2 }).format(Number(service.cost))
        : '';
    const shop = cleanDisplayText(service.shop);
    const performedBy = cleanDisplayText(service.performedBy);
    // Don't repeat the shop as "Performed by" when they're the same value.
    const performedByRow = performedBy && performedBy.toLowerCase() !== shop.toLowerCase() ? performedBy : '';
    const rows = [
        ['Date', formatServiceCardDate(service.date)],
        ['Mileage', mileage],
        ['Shop', shop],
        ['Performed by', performedByRow],
        ['Cost', cost],
    ].filter(([, v]) => v);
    const notes = cleanDisplayText(service.notes);
    return (
        <div
            className={`mt-3 w-full max-w-xl overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-none transition ${canOpen ? 'cursor-pointer hover:border-[#D81F26] hover:shadow-sm' : ''}`}
            onClick={canOpen ? handleOpen : undefined}
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
            onKeyDown={canOpen ? ((e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); } }) : undefined}
        >
            <div className="flex items-center gap-3 border-b border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0a8f53] text-white">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.4-.6-.6-2.4z" /></svg>
                </span>
                <div className="min-w-0">
                    <p className="m-0 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">
                        Service {isUpdate ? 'updated' : 'logged'}{vehicleLabel ? ` · ${vehicleLabel}` : ''}
                    </p>
                    <h3 className="m-0 truncate text-base font-black text-[#414141]">{title}</h3>
                </div>
                <span className="ml-auto shrink-0 rounded-full bg-[#e6f7ee] px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-[#0a8f53]">
                    {isUpdate ? 'Updated' : 'Saved'}
                </span>
            </div>
            {rows.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 px-4 py-3.5">
                    {rows.map(([k, v]) => (
                        <div key={k} className="min-w-0">
                            <p className="m-0 text-[10px] font-black uppercase tracking-[0.1em] text-[#414141]">{k}</p>
                            <p className="m-0 truncate text-sm font-semibold text-[#414141]">{v}</p>
                        </div>
                    ))}
                </div>
            )}
            {notes && (
                <div className="border-t border-[#f2f2f2] px-4 py-3">
                    <p className="m-0 text-[10px] font-black uppercase tracking-[0.1em] text-[#414141]">Notes</p>
                    <p className="m-0 mt-1 whitespace-pre-wrap break-words text-sm font-semibold text-[#414141]">{notes}</p>
                </div>
            )}
            {canOpen && (
                <div className="flex items-center justify-end gap-1.5 border-t border-[#f2f2f2] px-4 py-2.5 text-[12px] font-black text-[#D81F26]">
                    View in Glove Box
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </div>
            )}
        </div>
    );
};

export const ChatVehicleFormCard = ({
    form,
    onFieldChange,
    // past-maintenance UI (temporarily hidden): still passed by the parent, just not
    // read here while the block below is commented out.
    // onMaintenanceChange,
    onSubmit,
    onClose,
    isSubmitting,
    error,
    suggestions = [],
    onApplySuggestion,
    distanceUnit = 'mi',
}) => {
    const unitLabel = distanceUnit === 'km' ? 'km' : 'mi';
    // past-maintenance UI (temporarily hidden) — restore these three with the block below.
    // const odoUnit = form.odometerUnit || unitLabel;
    // const [showMaintenance, setShowMaintenance] = useState(false);
    // const maintenanceHistory = form.maintenanceHistory || {};
    // Enable submit when the required fields are non-empty (not on a valid parse) so an
    // invalid odometer still reaches the submit handler, which shows the precise
    // whole-number validation message instead of the button silently staying disabled.
    const isReady = Boolean(
        cleanDisplayText(form.year) &&
        cleanDisplayText(form.model) &&
        String(form.odometerReading ?? '').trim()
    );

    const [isLookingUpVin, setIsLookingUpVin] = useState(false);
    const [vinLookupError, setVinLookupError] = useState('');
    // #380: once a VIN decodes, the specs it provides are filled in and locked (read-only)
    // until the VIN changes. Entered values the VIN replaced are listed, never silent.
    const [vinLockedFields, setVinLockedFields] = useState(() => new Set());
    const [vinReplaced, setVinReplaced] = useState([]);
    const isVinLocked = (key) => vinLockedFields.has(key);
    const lockedSelectClass = (key) => `mt-1.5 w-full rounded-xl border border-[#d9d9d9] px-3.5 py-2.5 text-sm font-semibold text-[#414141] shadow-none outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15 ${isVinLocked(key) ? 'cursor-not-allowed bg-[#f4f4f4] text-[#414141]' : 'bg-white'}`;

    const handleVinLookup = async () => {
        const vin = cleanDisplayText(form.vin).toUpperCase();
        if (!vin) {
            setVinLookupError('Enter a VIN first.');
            return;
        }
        // Validate before calling so a random or incomplete number can't come back with a
        // bogus WMI-derived make (format + check digit, shared with the Garage form).
        const vinError = getVinError(vin);
        if (vinError) {
            setVinLookupError(`${vinError} Or fill in the details manually.`);
            return;
        }
        setVinLookupError('');
        setVinReplaced([]);
        setIsLookingUpVin(true);
        try {
            const result = await lookupVin(vin);
            if (!result.ok) {
                setVinLookupError(result.unavailable
                    ? "VIN lookup isn't available right now. You can still enter the details manually."
                    : `${result.error} You can still enter the details manually.`);
                return;
            }
            const { next, locked, replaced } = applyVinSpecs(form, result.specs);
            locked.forEach((key) => onFieldChange(key, next[key]));
            setVinLockedFields(new Set(locked));
            setVinReplaced(replaced);
        } finally {
            setIsLookingUpVin(false);
        }
    };

    // When the form opens pre-filled with a VIN (e.g. the user pasted a VIN in chat), or a
    // complete VIN is typed, run the lookup automatically once so the details fill in
    // (and lock) without a manual click.
    const autoLookupVinRef = useRef('');
    useEffect(() => {
        const vin = cleanDisplayText(form.vin).toUpperCase();
        // Only auto-run once the VIN is complete and well-formed (17 valid chars) — auto-looking
        // up partial input made NHTSA return a WMI-only make for an incomplete/invalid VIN.
        if (
            /^[A-HJ-NPR-Z0-9]{17}$/.test(vin) &&
            autoLookupVinRef.current !== vin
        ) {
            autoLookupVinRef.current = vin;
            handleVinLookup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.vin]);

    return (
        <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-none">
            <div className="relative border-b border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close add vehicle"
                        title="Close"
                        className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-[#d9d9d9] bg-white text-[#414141] transition hover:bg-[#F9F9F9] hover:text-[#414141]"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                )}
                <p className="m-0 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">
                    Vehicle setup
                </p>
                <h3 className="m-0 mt-1 text-base font-black text-[#414141]">Add your vehicle</h3>
                <p className="m-0 mt-1 text-xs font-semibold text-[#414141] pr-8">
                    Year, make, model, and odometer reading required.
                </p>
            </div>
            <form onSubmit={onSubmit} className="space-y-3.5 px-4 py-4">
                <div>
                    <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">
                        VIN (optional - auto-fills details)
                    </span>
                    <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2">
                        <input
                            value={form.vin}
                            onChange={(event) => {
                                // A VIN is at most 17 chars and never contains I, O, or Q.
                                // Sanitizing here caps both typing and pasting at 17 valid
                                // characters — an 18th (or invalid) character can't appear.
                                // This is INPUT hygiene only; full VIN validity is still checked
                                // on Lookup/Save.
                                const cleaned = String(event.target.value)
                                    .toUpperCase()
                                    .replace(/[^A-HJ-NPR-Z0-9]/g, '')
                                    .slice(0, 17);
                                onFieldChange('vin', cleaned);
                                if (vinLookupError) setVinLookupError('');
                                // Editing the VIN unlocks the decoded fields until the next lookup.
                                if (vinLockedFields.size) setVinLockedFields(new Set());
                                if (vinReplaced.length) setVinReplaced([]);
                            }}
                            placeholder="Enter VIN"
                            maxLength={17}
                            autoCapitalize="characters"
                            className="min-w-0 rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#414141] shadow-none outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                        />
                        <button
                            type="button"
                            onClick={handleVinLookup}
                            disabled={isLookingUpVin || !cleanDisplayText(form.vin)}
                            className="rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-2.5 text-xs font-black text-[#414141] shadow-none transition hover:bg-[#F2F2F2] disabled:cursor-not-allowed disabled:text-[#a6a6a6]"
                            title="Auto-fill vehicle details from the VIN"
                        >
                            {isLookingUpVin ? 'Looking…' : 'Lookup'}
                        </button>
                    </div>
                    {vinLookupError && (
                        <p className="m-0 mt-1.5 text-[11px] font-semibold text-[#c0392b]">{vinLookupError}</p>
                    )}
                    {vinLockedFields.size > 0 && (
                        <p className="m-0 mt-1.5 text-[11px] font-semibold text-[#414141]">
                            {VIN_LOCKED_NOTE}
                        </p>
                    )}
                    {vinReplaced.length > 0 && (
                        <p className="m-0 mt-1 text-[11px] font-semibold text-[#8a5a00]">
                            {replacedNote(vinReplaced)}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <VehicleFormField
                        label="Year"
                        required
                        value={form.year}
                        // #393: Year accepts digits only. Sanitizing here catches BOTH typing and
                        // pasting (onChange fires for both) — any non-numeric character is stripped,
                        // capped at 4 digits, so "abcd"/"202a"/"@2025"/"20 25" can't be entered.
                        onChange={(value) => onFieldChange('year', String(value).replace(/[^\d]/g, '').slice(0, 4))}
                        placeholder="2020"
                        inputMode="numeric"
                        disabled={isVinLocked('year')}
                    />
                    <VehicleFormField
                        label="Make"
                        value={form.make}
                        onChange={(value) => onFieldChange('make', value)}
                        placeholder="Toyota"
                        disabled={isVinLocked('make')}
                    />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <VehicleFormField
                        label="Model"
                        required
                        value={form.model}
                        onChange={(value) => onFieldChange('model', value)}
                        placeholder="Camry"
                        disabled={isVinLocked('model')}
                    />
                    <VehicleFormField
                        label="Trim"
                        value={form.trim}
                        onChange={(value) => onFieldChange('trim', value)}
                        placeholder="SE"
                        disabled={isVinLocked('trim')}
                    />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">
                            Odometer Reading <span className="text-[#c0392b]">*</span>
                        </span>
                        <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2">
                            <input
                                value={form.odometerReading}
                                onChange={(event) => onFieldChange('odometerReading', event.target.value)}
                                placeholder="45,000"
                                inputMode="numeric"
                                className="min-w-0 rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#414141] shadow-none outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                            />
                            {/* #409: imperial only — odometer is always entered in miles. */}
                            <span className="flex items-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-black text-[#414141]">miles</span>
                        </div>
                    </div>
                    <VehicleFormField label="Fuel Type" value={form.fuel_type} onChange={() => {}}>
                        <select
                            value={form.fuel_type}
                            onChange={(event) => onFieldChange('fuel_type', event.target.value)}
                            disabled={isVinLocked('fuel_type')}
                            className={lockedSelectClass('fuel_type')}
                        >
                            <option value="">Select</option>
                            {FUEL_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                    </VehicleFormField>
                </div>

                <VehicleFormField
                    label="Engine"
                    value={form.engine}
                    onChange={(value) => onFieldChange('engine', value)}
                    placeholder="2.5L 4-cylinder"
                    disabled={isVinLocked('engine')}
                />

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <VehicleFormField label="Transmission" value={form.transmission} onChange={() => {}}>
                        <select
                            value={form.transmission}
                            onChange={(event) => onFieldChange('transmission', event.target.value)}
                            disabled={isVinLocked('transmission')}
                            className={lockedSelectClass('transmission')}
                        >
                            <option value="">Select</option>
                            <option value="Automatic">Automatic</option>
                            <option value="Manual">Manual</option>
                            <option value="CVT">CVT</option>
                            <option value="DCT">DCT</option>
                            <option value="Other">Other</option>
                        </select>
                    </VehicleFormField>
                    <VehicleFormField label="Drivetrain" value={form.drivetrain} onChange={() => {}}>
                        <select
                            value={form.drivetrain}
                            onChange={(event) => onFieldChange('drivetrain', event.target.value)}
                            disabled={isVinLocked('drivetrain')}
                            className={lockedSelectClass('drivetrain')}
                        >
                            <option value="">Select</option>
                            <option value="FWD">FWD</option>
                            <option value="RWD">RWD</option>
                            <option value="AWD">AWD</option>
                            <option value="4WD">4WD</option>
                            <option value="Other">Other</option>
                        </select>
                    </VehicleFormField>
                </div>

                <VehicleFormField
                    label="Plate"
                    value={form.plate}
                    onChange={(value) => onFieldChange('plate', value)}
                    placeholder="ABC-1234"
                />

                {/* Past maintenance (optional) — TEMPORARILY HIDDEN, not deleted.
                    Not wanted on the form right now, but it is coming back, so the markup
                    is preserved verbatim below. It captures the correct starting point so
                    upcoming-maintenance reminders are accurate rather than guessed.

                    TO RESTORE: delete this comment's opening and closing markers, then
                    un-comment the four declarations marked "past-maintenance UI" earlier
                    in this file (KEY_MAINTENANCE_SERVICES, the onMaintenanceChange prop,
                    odoUnit, showMaintenance and maintenanceHistory). Nothing else needs
                    touching — form.maintenanceHistory, handleChatVehicleMaintenanceChange
                    and the save step are all left intact and simply go unused meanwhile.

                <div className="rounded-xl border border-[#d9d9d9] bg-[#F9F9F9]">
                    <button
                        type="button"
                        onClick={() => setShowMaintenance((v) => !v)}
                        className="flex w-full items-center justify-between gap-2 px-3.5 py-3 text-left"
                    >
                        <span>
                            <span className="block text-[11px] font-black uppercase tracking-[0.1em] text-[#414141]">
                                Past maintenance (optional)
                            </span>
                            <span className="mt-0.5 block text-[11px] font-semibold text-[#414141]">
                                When were these last done? Helps us schedule reminders accurately.
                            </span>
                        </span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#414141" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-transform ${showMaintenance ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6" /></svg>
                    </button>
                    {showMaintenance && (
                        <div className="space-y-2.5 border-t border-[#d9d9d9] px-3.5 py-3">
                            {KEY_MAINTENANCE_SERVICES.map((svc) => {
                                const entry = maintenanceHistory[svc.key] || {};
                                const enabled = Boolean(entry.enabled);
                                return (
                                    <div key={svc.key} className="rounded-lg border border-[#d9d9d9] bg-white px-3 py-2.5">
                                        <label className="flex cursor-pointer items-center gap-2.5">
                                            <input
                                                type="checkbox"
                                                checked={enabled}
                                                onChange={(e) => onMaintenanceChange(svc.key, { enabled: e.target.checked })}
                                                className="h-4 w-4 shrink-0 accent-[#414141]"
                                            />
                                            <span className="text-sm font-black text-[#414141]">{svc.title}</span>
                                        </label>
                                        {enabled && (
                                            <div className="mt-2.5 space-y-2 pl-6">
                                                <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                                                    <input
                                                        value={entry.odometer || ''}
                                                        onChange={(e) => onMaintenanceChange(svc.key, { odometer: e.target.value })}
                                                        placeholder={`Odometer at last ${svc.title.toLowerCase()}`}
                                                        inputMode="numeric"
                                                        className="min-w-0 rounded-lg border border-[#d9d9d9] bg-white px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                                                    />
                                                    <span className="text-xs font-black text-[#414141]">{odoUnit}</span>
                                                </div>
                                                <label className="flex cursor-pointer items-center gap-2 text-[12px] font-bold text-[#414141]">
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.odometerBased !== false}
                                                        onChange={(e) => onMaintenanceChange(svc.key, { odometerBased: e.target.checked })}
                                                        className="h-3.5 w-3.5 shrink-0 accent-[#414141]"
                                                    />
                                                    This is an actual odometer reading (not an estimate)
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                */}

                {(error || suggestions.length > 0) && (() => {
                    const corrections = suggestions.filter((s) => s.kind !== 'make_model');
                    const makeModels = suggestions.filter((s) => s.kind === 'make_model');
                    const makeName = makeModels[0]?.make || '';
                    const renderChip = (s, index) => (
                        <button
                            key={`${s.label}-${index}`}
                            type="button"
                            onClick={() => onApplySuggestion && onApplySuggestion(s)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#e2b96a] bg-white px-3 py-1.5 text-[13px] font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95"
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                            {s.label}
                        </button>
                    );
                    return (
                        <div className="rounded-xl border border-[#ffd48c] bg-[#fff8e8] px-3.5 py-2.5 text-sm font-black text-[#a76500]">
                            {error}
                            {corrections.length > 0 && (
                                <div className="mt-2">
                                    <p className="m-0 text-[11px] font-black uppercase tracking-[0.08em] text-[#a76500]/80">
                                        Did you mean{corrections.length > 1 ? ' one of these' : ''}?
                                    </p>
                                    <div className="mt-1.5 flex flex-wrap gap-2">
                                        {corrections.map(renderChip)}
                                    </div>
                                </div>
                            )}
                            {makeModels.length > 0 && (
                                <div className="mt-2.5">
                                    <p className="m-0 text-[11px] font-black uppercase tracking-[0.08em] text-[#a76500]/80">
                                        Or a {makeName} model:
                                    </p>
                                    <div className="mt-1.5 flex flex-wrap gap-2">
                                        {makeModels.map(renderChip)}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                <button
                    type="submit"
                    disabled={!isReady || isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#414141] px-4 py-3 text-sm font-black text-white shadow-none transition hover:bg-[#2A2A2A] disabled:cursor-not-allowed disabled:bg-[#b8b8b8]"
                >
                    <span className="text-xl leading-none">+</span>
                    {isSubmitting ? 'Validating & Saving...' : 'Add Vehicle to Garage'}
                </button>
            </form>
        </div>
    );
};

const ChatPage = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        authState,
        refreshAuthState,
        openAuthModal,
        isAuthModalOpen,
        onLogout,
        pendingQuestion,
        clearPendingQuestion,
        // Thread management (authenticated users only)
        threads = [],
        currentThreadId,
        onNewThread,
        onStartNewChat,
        onSelectThread,
        refreshThreadTitles,
        applyThreadTitleLocal,
        recentDiagnostics = [],
        activeDiagnosticTicket,
        refreshDiagnosticTickets,
        refreshDisplayName,
        addVehicleThreadIds,
        localAddVehicleCardId,
        openPromoConversation,
    } = useOutletContext();
    // This chat is an "Add Vehicle" conversation → the card is pinned to the top, with the
    // conversation continuing below it. Each add-vehicle is its own thread, so many can coexist.
    const isAddVehicleThreadCard = Array.isArray(addVehicleThreadIds)
        && currentThreadId != null
        && addVehicleThreadIds.map(String).includes(String(currentThreadId));
    // …or the card is open with no thread behind it: an anonymous visitor (who cannot own a
    // thread at all), or a signed-in one whose thread creation failed. Showing the card is a
    // question about this screen, not about what the user owns on the server, so it must not
    // require an authenticated resource to answer.
    const isAddVehicleCard = isAddVehicleThreadCard
        || (localAddVehicleCardId != null && currentThreadId == null);
    const [messages, setMessages] = useState([]);
    // A scanned promo QR opens /chat?code=… — the code waits in the chat box for the
    // user to send, it is not sent for them.
    const [input, setInput] = useState(() => (searchParams.get('code') || '').trim().slice(0, 64));
    const [isLoading, setIsLoading] = useState(false);
    const isLoadingRef = useRef(false);
    const [status, setStatus] = useState('');
    const [playingAudioId, setPlayingAudioId] = useState(null);
    const [stopAssistantAudioSignal, setStopAssistantAudioSignal] = useState(0);
    const [audioProgress, setAudioProgress] = useState({ current: 0, duration: 0 });
    const audioPlaybackCanceledRef = useRef(false);
    // Timestamp of the last pointer-down on the composer. Used to tell a genuine
    // tap-to-type focus from a window-restore focus (e.g. after a screenshot or tab
    // switch) so the latter does NOT scroll the chat to the bottom.
    const lastComposerPointerDownRef = useRef(0);
    const activeEventSourceRef = useRef(null);
    const activeResponseStopRef = useRef(null);
    const canceledEventSourcesRef = useRef(new Set());
    const activeReportPlaceholderIdRef = useRef(null);
    // Guards the auto-generate effect so it can't re-enter / loop while a report
    // build is already in flight. The skeleton is shown via a state flag (not by
    // injecting a message) so it never mutates the effect's `messages` dependency.
    const autoReportGenInFlightRef = useRef(false);
    // Hard backstop: thread keys we've already kicked off auto-generation for this
    // session, so the effect can NEVER re-request a report in a loop.
    const autoReportAttemptedThreadsRef = useRef(new Set());
    // Per-thread count of FAILED auto-generation attempts, so a transient failure can be
    // retried (instead of leaving the attempt key stuck and the report never completing)
    // while still capping retries so a persistent failure can't loop.
    const autoReportFailCountRef = useRef(new Map());
    // Auto-generation is only valid immediately after a live diagnostic response
    // reaches the "ready for report" state. Restored history may contain the same
    // wording, but it must not move/recreate old report cards at the bottom.
    const liveReportReadyAttemptKeysRef = useRef(new Set());
    const [autoReportGenerating, setAutoReportGenerating] = useState(false);
    const [liveDiagnosticReportCard, setLiveDiagnosticReportCard] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const recordingActionRef = useRef('discard');
    const [isTranscribingVoiceDraft, setIsTranscribingVoiceDraft] = useState(false);
    const [pendingVoiceDraft, setPendingVoiceDraft] = useState(false);
    const [activeMessage, setActiveMessage] = useState(null);
    const [activeClientIntent, setActiveClientIntent] = useState(null);
    const [activeTicketGarageRequests, setActiveTicketGarageRequests] = useState([]);
    const [referralLocksByTicketId, setReferralLocksByTicketId] = useState({});
    const [referralReportSendByTicketId, setReferralReportSendByTicketId] = useState({});
    const [chatMode, setChatMode] = useState('dash');
    const [selectedGarageRequestId, setSelectedGarageRequestId] = useState('');
    // A shop request opened directly from the sidebar (its full object passed via navigation),
    // which may belong to a diagnostic ticket other than the active one — so it can't be found
    // in the ticket-scoped linkedGarageRequests list. Lets the shop pane open for ANY request.
    const [externalShopRequest, setExternalShopRequest] = useState(null);
    const [isSendingShopMessage, setIsSendingShopMessage] = useState(false);
    const [isUpdatingGarageRequestStatus, setIsUpdatingGarageRequestStatus] = useState(false);
    const shopResultsByKeyRef = useRef({});
    const autoOpenedShopResultKeysRef = useRef(new Set());
    const pendingLiveShopResultKeyRef = useRef('');
    const openGarageForShopRef = useRef(null);
    const guidedActiveCardRef = useRef(null);
    const initialGuidedCheckSessionRef = useRef();
    if (initialGuidedCheckSessionRef.current === undefined) {
        initialGuidedCheckSessionRef.current = getInitialGuidedCheckSession();
    }
    const [guidedCheckSession, setGuidedCheckSession] = useState(initialGuidedCheckSessionRef.current);
    // Whether the pre-repair safety checklist has been acknowledged for the CURRENT repair,
    // this app session only. Deliberately NOT persisted with the guided session, so reopening
    // a repair (or deep-linking to a step) re-presents the checklist before the guide resumes.
    const [repairSafetyAcked, setRepairSafetyAcked] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [isInitialLoading, setIsInitialLoading] = useState(() => !initialGuidedCheckSessionRef.current);
    const [anonymousUserId, setAnonymousUserId] = useState(() => getOrCreateAnonymousId());
    const [anonymousSessionId, setAnonymousSessionId] = useState(() => getOrCreateAnonymousSessionId());
    const [openTranscriptId, setOpenTranscriptId] = useState(null);
    const [freshChatIntroTime, setFreshChatIntroTime] = useState(() => (
        new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    ));
    const [isFreshChatIntroThinking, setIsFreshChatIntroThinking] = useState(false);
    // Set once the initial history fetch for currentThreadId confirms this thread already had
    // messages before this page view — i.e. the user switched to a pre-existing conversation,
    // not a brand-new one. Holds the first message's timestamp (when the thread started), or
    // null otherwise.
    const [revisitedThreadStartedAt, setRevisitedThreadStartedAt] = useState(null);
    const [showChatVehicleForm, setShowChatVehicleForm] = useState(false);
    const [chatVehicleFormTime, setChatVehicleFormTime] = useState('');
    const [chatVehicleForm, setChatVehicleForm] = useState(() => createEmptyChatVehicleForm());
    const [chatVehicleFormError, setChatVehicleFormError] = useState('');
    const [chatVehicleFormSuggestions, setChatVehicleFormSuggestions] = useState([]);
    // The exact request the user typed to open this add-vehicle card (e.g. "add my new vehicle
    // Hyundai Verna 2020"), shown as their message bubble above the card instead of a generic label.
    const [chatVehicleUserMessage, setChatVehicleUserMessage] = useState('');
    const addVehicleCardRef = useRef(null);
    // The freshly-generated symptom report card — used to anchor the view at its TOP so the
    // user reads the report from the beginning instead of landing at its bottom.
    const liveReportCardRef = useRef(null);
    const pendingAddVehicleTopScrollRef = useRef(false);
    // Open signature we last scrolled-to-top for, so the card shows its top exactly once per open.
    const lastScrolledAddVehicleKeyRef = useRef('');
    // Bumped on every open so the scroll-to-top effect always re-runs. The form time alone
    // can't drive it: it's a minute-resolution string ("6:57 PM") that is often unchanged.
    const [addVehicleScrollRequest, setAddVehicleScrollRequest] = useState(0);
    // Tracks the model we've already offered a make suggestion for, so a second submit
    // (user chose not to add a make) proceeds instead of blocking again.
    const makeSuggestSeenRef = useRef('');
    const [isSavingChatVehicle, setIsSavingChatVehicle] = useState(false);
    const [validationMetadata, setValidationMetadata] = useState(null);

    // Set of known make names (lowercased) for splitting suggestion strings — the common
    // list plus every make discovered in the validation metadata.
    const knownVehicleMakes = useMemo(() => {
        const makes = new Set(COMMON_VEHICLE_MAKES);
        const meta = validationMetadata || {};
        Object.values(meta.model_belongs_to_make || {}).forEach((m) => m && makes.add(String(m).toLowerCase()));
        Object.values(meta.static_make_aliases || {}).forEach((m) => m && makes.add(String(m).toLowerCase()));
        return makes;
    }, [validationMetadata]);

    useEffect(() => {
        fetch(`${getUserDetailsBaseUrl()}/api/validation_metadata`)
            .then(res => res.json())
            .then(data => setValidationMetadata(data))
            .catch(err => console.warn('Failed to load validation metadata:', err));
    }, []);

    // Clear the "revisiting an existing thread" signal on every thread switch — it's
    // re-derived from the initial history fetch below (fetchHistory's isInitial branch)
    // once that confirms whether the newly-selected thread actually has prior messages.
    useEffect(() => {
        setRevisitedThreadStartedAt(null);
    }, [currentThreadId]);

    // Selecting a conversation or starting a New Chat (currentThreadId changes) must leave the
    // shop conversation and show that thread. Shop mode is otherwise sticky (a sidebar-opened
    // shop chat is kept alive even when the active ticket has no requests), so an explicit exit
    // is needed here. Skipped while a shop-open is in flight (openGarageRequestId still in state),
    // so arriving to open a shop chat doesn't immediately close it.
    const prevThreadIdRef = useRef(currentThreadId);
    useEffect(() => {
        const prev = prevThreadIdRef.current;
        prevThreadIdRef.current = currentThreadId;
        if (String(prev ?? '') === String(currentThreadId ?? '')) return;
        if (location.state?.openGarageRequestId) return;
        setChatMode((mode) => (mode === 'shop' ? 'dash' : mode));
        setSelectedGarageRequestId('');
        setExternalShopRequest(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentThreadId, location.state]);

    const hasProcessedInitialIntent = useRef(false);
    const lastGuidedCheckHandoffRef = useRef('');
    const lastLoadedIdentityRef = useRef('');
    const historyRequestVersionRef = useRef(0);
    const firstSendThreadIdRef = useRef(null);
    const suppressDiagnosticRestoreRef = useRef(false);
    // Tracks which storage key the current `messages` array belongs to.
    // Used by the snapshot-write effect to prevent writing old-thread messages
    // into the new thread's snapshot when the user switches threads.
    const messagesStorageKeyRef = useRef(null);
    // The thread id whose data currently lives in chatVehicleForm. Guards the per-thread persist so
    // that on a chat switch (key changes a render before the form resets) we never write one add-
    // vehicle chat's data under another chat's key (which leaked the input across chats).
    const vehicleFormThreadRef = useRef(null);
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState('');
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const [showGloveboxOverlay, setShowGloveboxOverlay] = useState(false);
    const [overlayDocs, setOverlayDocs] = useState([]);
    const [overlayTitle, setOverlayTitle] = useState('');
    const [mobileKeyboardOffset, setMobileKeyboardOffset] = useState(0);

    useEffect(() => {
        setAttachmentError('');
    }, [currentThreadId]);
    const [isChatInputFocused, setIsChatInputFocused] = useState(false);
    const lastMobileKeyboardOffsetRef = useRef(0);
    const fileInputRef = useRef(null);
    const chatInputRef = useRef(null);
    const responseAnchorRef = useRef(null);
    const selectedGarageRequestIdRef = useRef('');
    // Deep-linking from a freshly submitted estimate should introduce the shop
    // conversation from its first message, not inherit the Dash chat's bottom position.
    // Keep the request id until both the shop request and initial thread history are ready.
    const pendingShopConversationTopRef = useRef('');
    const localAttachmentUrlsRef = useRef(new Set());
    const isAuthenticated = authState?.status === 'authenticated';
    const isMobileViewport = useCallback(() => (
        typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    ), []);

    const revokeLocalAttachmentUrl = useCallback((url) => {
        if (!url || !localAttachmentUrlsRef.current.has(url)) return;
        try {
            URL.revokeObjectURL(url);
        } catch {}
        localAttachmentUrlsRef.current.delete(url);
    }, []);

    useEffect(() => () => {
        localAttachmentUrlsRef.current.forEach((url) => {
            try {
                URL.revokeObjectURL(url);
            } catch {}
        });
        localAttachmentUrlsRef.current.clear();
    }, []);

    // #711: infographic intro disabled — new visitors land directly in chat.
    // // Show onboarding banner only for anonymous first-timers who haven't seen it yet
    // const [showOnboarding, setShowOnboarding] = useState(
    //     () => !isOnboardingSeen()
    // );
    // // Once authenticated, permanently hide onboarding
    // const shouldShowOnboarding = showOnboarding && authState?.status === 'anonymous';
    // const handleDismissOnboarding = () => {
    //     setOnboardingSeen();
    //     setShowOnboarding(false);
    // };

    const focusChatInput = useCallback((options = {}) => {
        const { forceMobile = false } = options;
        if (
            isAuthModalOpen ||
            // shouldShowOnboarding || // #711
            showChatVehicleForm ||
            showGloveboxOverlay ||
            isRecording ||
            isTranscribingVoiceDraft
        ) {
            return;
        }

        if (isMobileViewport() && !forceMobile) return;

        const activeElement = document.activeElement;
        const isTypingElsewhere = activeElement && activeElement !== document.body && activeElement !== chatInputRef.current && (
            ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) ||
            activeElement.isContentEditable
        );
        if (isTypingElsewhere) return;

        window.requestAnimationFrame(() => {
            const inputNode = chatInputRef.current;
            if (!inputNode || inputNode.disabled) return;
            inputNode.focus({ preventScroll: true });
        });
    }, [
        isAuthModalOpen,
        // shouldShowOnboarding, // #711
        showChatVehicleForm,
        showGloveboxOverlay,
        isRecording,
        isTranscribingVoiceDraft,
        isMobileViewport,
    ]);
    
    const activeUserId = isAuthenticated ? authState.identity : anonymousUserId;
    const activeSessionId = isAuthenticated && authState.identity
        ? getAuthenticatedChatSessionId(authState.identity)
        : anonymousSessionId;
    const getActiveChatStorageKey = (threadIdOverride = null) => {
        const threadId = threadIdOverride || currentThreadId;
        if (isAuthenticated) return threadId ? `thread:${threadId}` : null;
        if (activeUserId && activeSessionId) return `anonymous:${activeUserId}:${activeSessionId}`;
        return null;
    };
    const getReportStorageKeys = (threadIdOverride = null) => {
        return [getActiveChatStorageKey(threadIdOverride)].filter(Boolean);
    };
    const activeReportStorageKey = getActiveChatStorageKey();
    const guidedCheckStorageKey = getActiveChatStorageKey();

    // ── Add-Vehicle card form persistence ──────────────────────────────────────
    // Each add-vehicle is its own thread; the card's entered fields are stored PER THREAD so every
    // add-vehicle chat keeps its own data and none of it vanishes when switching between them or
    // reloading. Stored in localStorage keyed by thread id so it persists across new tabs, `npm
    // start`, and browser restarts (sessionStorage is empty in a fresh tab).
    const getVehicleFormStorageKey = (threadIdOverride = null) => {
        const tid = threadIdOverride || currentThreadId;
        return tid ? `dash_add_vehicle_form:${tid}` : null;
    };
    const readVehicleFormState = (threadIdOverride = null) => {
        const key = getVehicleFormStorageKey(threadIdOverride);
        if (!key) return null;
        try { return safeJsonParse(localStorage.getItem(key), null); } catch { return null; }
    };
    const clearVehicleFormStorage = (threadIdOverride = null) => {
        const key = getVehicleFormStorageKey(threadIdOverride);
        if (!key) return;
        try { localStorage.removeItem(key); } catch { /* ignore */ }
    };
    const postGuidedMessageStorageKey = activeReportStorageKey
        ? `dash_post_guided_messages:${activeReportStorageKey}`
        : null;
    const getPendingAssistantStorageKey = (storageKey = activeReportStorageKey) => (
        storageKey ? `dash_pending_assistant_messages:${storageKey}` : null
    );
    const getPendingUserStorageKey = (storageKey = activeReportStorageKey) => (
        storageKey ? `dash_pending_user_messages:${storageKey}` : null
    );

    // ---- Server-side per-thread state persistence (guided session + report card) ----
    // Authoritative for authenticated threads, keyed strictly by thread_id. This
    // replaces the fragile multi-key localStorage scheme as the source of truth so
    // the guided DIY session and the diagnostic report card survive page reloads,
    // "New Chat", and thread/ticket switching without cross-thread leakage.
    // localStorage is retained as a fast cache and the anonymous fallback.
    const fetchThreadStateFromServer = async (threadId) => {
        if (!isAuthenticated || !threadId) return null;
        try {
            const resp = await fetch(`${CHAT_API_BASE_URL}/secure/threads/${threadId}/state`, {
                credentials: 'include',
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            return {
                guidedSession: data?.guided_session || null,
                reportCard: data?.report_card || null,
                shopResults: normalizeStoredShopResults(data?.shop_results || null),
            };
        } catch {
            return null;
        }
    };
    // Fire-and-forget PUT. `patch` may include guided_session and/or report_card;
    // omitted fields are left untouched server-side.
    const saveThreadStateToServer = (threadId, patch) => {
        if (!isAuthenticated || !threadId || !patch) return;
        try {
            fetch(`${CHAT_API_BASE_URL}/secure/threads/${threadId}/state`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(patch),
            }).catch(() => {});
        } catch {}
    };

    const getAssistantMessageSignature = (message = {}) => (
        String(message.content || '').replace(/\s+/g, ' ').trim().slice(0, 1000)
    );
    const getAttachmentSignature = (message = {}) => (
        (Array.isArray(message.attachments) ? message.attachments : [])
            .map(attachment => `${getAttachmentLabel(attachment)}:${inferAttachmentKind(attachment)}`)
            .join('|')
    );
    const getUserMessageSignature = (message = {}) => (
        [
            String(message.content || '').replace(/\s+/g, ' ').trim().slice(0, 1000),
            getAttachmentSignature(message),
        ].filter(Boolean).join('|')
    );
    const sanitizePendingUserMessage = (message = {}) => ({
        id: message.id || `pending-user-${Date.now()}`,
        role: 'user',
        content: String(message.content || '').trim(),
        // Never persist inline base64/blob image data in localStorage — it's huge (blows the
        // ~5MB quota) and useless after a reload. Keep lightweight metadata; the real image
        // loads from the backend once the message is echoed. http(s) URLs are small, so kept.
        attachments: Array.isArray(message.attachments)
            ? message.attachments.map(sanitizeAttachmentPreview).filter(Boolean).map((attachment) => (
                (attachment && typeof attachment.url === 'string' && /^(data:|blob:)/i.test(attachment.url))
                    ? { ...attachment, url: null }
                    : attachment
            ))
            : [],
        isVoice: Boolean(message.isVoice),
        timestamp: message.timestamp || new Date().toISOString(),
        afterGuidedSession: Boolean(message.afterGuidedSession),
        pendingLocalUser: true,
    });
    const readPendingUserMessages = (storageKey = activeReportStorageKey) => {
        const pendingKey = getPendingUserStorageKey(storageKey);
        if (!pendingKey) return [];
        const stored = safeJsonParse(localStorage.getItem(pendingKey), []);
        if (!Array.isArray(stored)) return [];
        return stored
            .map(sanitizePendingUserMessage)
            .filter(message => getUserMessageSignature(message));
    };
    const writePendingUserMessages = (messagesToStore, storageKey = activeReportStorageKey) => {
        const pendingKey = getPendingUserStorageKey(storageKey);
        if (!pendingKey) return;
        const cleanMessages = (Array.isArray(messagesToStore) ? messagesToStore : [])
            .map(sanitizePendingUserMessage)
            .filter(message => getUserMessageSignature(message))
            .slice(-10);

        if (cleanMessages.length === 0) {
            try { localStorage.removeItem(pendingKey); } catch (_) { /* ignore */ }
            return;
        }

        // localStorage is small (~5MB) and shared across threads. If the write exceeds the
        // quota, progressively drop the oldest pending messages and retry; if it still won't
        // fit, clear this key. Never throw — a full cache must not crash the chat (the
        // pending cache is only an optimistic view until the backend echoes the messages).
        let toStore = cleanMessages;
        while (toStore.length > 0) {
            try {
                localStorage.setItem(pendingKey, JSON.stringify(toStore));
                return;
            } catch (err) {
                const isQuota = err && (
                    err.name === 'QuotaExceededError'
                    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
                    || err.code === 22 || err.code === 1014
                );
                if (!isQuota) {
                    try { localStorage.removeItem(pendingKey); } catch (_) { /* ignore */ }
                    return;
                }
                toStore = toStore.slice(1); // drop the oldest and retry with fewer
            }
        }
        try { localStorage.removeItem(pendingKey); } catch (_) { /* ignore */ }
    };
    const cachePendingUserMessage = (message, storageKey = activeReportStorageKey) => {
        const pendingMessage = sanitizePendingUserMessage(message);
        const signature = getUserMessageSignature(pendingMessage);
        if (!signature) return;

        const existingMessages = readPendingUserMessages(storageKey);
        const nextMessages = [
            ...existingMessages.filter(existing => getUserMessageSignature(existing) !== signature),
            pendingMessage,
        ];
        writePendingUserMessages(nextMessages, storageKey);
    };
    const mergePendingUserMessages = (messageList = [], storageKey = activeReportStorageKey) => {
        const pendingMessages = readPendingUserMessages(storageKey);
        if (pendingMessages.length === 0) return messageList;

        const backendUserSignatures = new Set(
            messageList
                .filter(message => message.role === 'user')
                .map(getUserMessageSignature)
                .filter(Boolean)
        );
        const missingPendingMessages = pendingMessages.filter(
            message => !backendUserSignatures.has(getUserMessageSignature(message))
        );

        writePendingUserMessages(missingPendingMessages, storageKey);
        if (missingPendingMessages.length === 0) return messageList;
        return [...messageList, ...missingPendingMessages];
    };
    const getPendingAssistantSignature = (message = {}) => {
        const assistantContent = getAssistantMessageSignature(message);
        const userContent = String(message.replyToUserContent || '').replace(/\s+/g, ' ').trim().slice(0, 500);
        return `${assistantContent}::${userContent}`;
    };
    const sanitizePendingAssistantMessage = (message = {}) => {
        const shopResult = normalizeShopResultForStorage(message.shopResult || null);
        return {
            id: message.id || `pending-assistant-${Date.now()}`,
            role: 'assistant',
            content: String(message.content || '').trim(),
            replyToUserContent: String(message.replyToUserContent || '').trim(),
            pdfUrl: message.pdfUrl || null,
            docUrl: message.docUrl || null,
            mapUrl: message.mapUrl || null,
            gloveboxDocs: message.gloveboxDocs || null,
            // Persist the catalogue so the cards survive a reload — re-parsing the prose
            // would not bring them back, because the prose no longer carries the links.
            partsData: message.partsData || null,
            suggestions: Array.isArray(message.suggestions) ? message.suggestions : [],
            ...(shopResult ? { shopResult } : {}),
            type: message.type || 'text',
            timestamp: message.timestamp || new Date().toISOString(),
            afterGuidedSession: Boolean(message.afterGuidedSession),
            pendingLocalAssistant: true,
        };
    };
    const readPendingAssistantMessages = (storageKey = activeReportStorageKey) => {
        const pendingKey = getPendingAssistantStorageKey(storageKey);
        if (!pendingKey) return [];
        const stored = safeJsonParse(localStorage.getItem(pendingKey), []);
        if (!Array.isArray(stored)) return [];
        return stored
            .map(sanitizePendingAssistantMessage)
            .filter(message => getAssistantMessageSignature(message));
    };
    const writePendingAssistantMessages = (messagesToStore, storageKey = activeReportStorageKey) => {
        const pendingKey = getPendingAssistantStorageKey(storageKey);
        if (!pendingKey) return;
        const cleanMessages = (Array.isArray(messagesToStore) ? messagesToStore : [])
            .map(sanitizePendingAssistantMessage)
            .filter(message => getAssistantMessageSignature(message))
            .slice(-25);

        if (cleanMessages.length === 0) {
            try { localStorage.removeItem(pendingKey); } catch (_) { /* ignore */ }
            return;
        }

        // Crash-proof: on a quota error, drop the oldest and retry, then clear the key.
        // Never throw — a full localStorage must not break the chat.
        let toStore = cleanMessages;
        while (toStore.length > 0) {
            try {
                localStorage.setItem(pendingKey, JSON.stringify(toStore));
                return;
            } catch (err) {
                const isQuota = err && (
                    err.name === 'QuotaExceededError'
                    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
                    || err.code === 22 || err.code === 1014
                );
                if (!isQuota) {
                    try { localStorage.removeItem(pendingKey); } catch (_) { /* ignore */ }
                    return;
                }
                toStore = toStore.slice(1);
            }
        }
        try { localStorage.removeItem(pendingKey); } catch (_) { /* ignore */ }
    };
    const cachePendingAssistantMessage = (message, storageKey = activeReportStorageKey) => {
        const pendingMessage = sanitizePendingAssistantMessage(message);
        const signature = getAssistantMessageSignature(pendingMessage);
        if (!signature) return;

        const existingMessages = readPendingAssistantMessages(storageKey);
        const nextMessages = [
            ...existingMessages.filter(existing => getPendingAssistantSignature(existing) !== getPendingAssistantSignature(pendingMessage)),
            pendingMessage,
        ];
        writePendingAssistantMessages(nextMessages, storageKey);
    };
    const mergePendingAssistantMessages = (messageList = [], storageKey = activeReportStorageKey) => {
        const pendingMessages = readPendingAssistantMessages(storageKey);
        if (pendingMessages.length === 0) return messageList;

        const hasMatchingBackendAssistant = (pendingMessage) => {
            const pendingAssistantSignature = getAssistantMessageSignature(pendingMessage);
            const pendingUserSignature = String(pendingMessage.replyToUserContent || '').replace(/\s+/g, ' ').trim().slice(0, 500);
            if (!pendingAssistantSignature) return true;

            if (pendingUserSignature) {
                let userIndex = -1;
                for (let index = messageList.length - 1; index >= 0; index -= 1) {
                    const message = messageList[index];
                    const userSignature = String(message.content || '').replace(/\s+/g, ' ').trim().slice(0, 500);
                    if (message.role === 'user' && userSignature === pendingUserSignature) {
                        userIndex = index;
                        break;
                    }
                }
                if (userIndex >= 0) {
                    return messageList.slice(userIndex + 1).some(message => (
                        message.role === 'assistant' &&
                        getAssistantMessageSignature(message) === pendingAssistantSignature
                    ));
                }
            }

            return messageList.some(message => (
                message.role === 'assistant' &&
                getAssistantMessageSignature(message) === pendingAssistantSignature
            ));
        };
        const missingPendingMessages = pendingMessages.filter(
            message => !hasMatchingBackendAssistant(message)
        );

        writePendingAssistantMessages(missingPendingMessages, storageKey);
        if (missingPendingMessages.length === 0) return messageList;

        const existingSignatures = new Set(
            messageList
                .filter(message => message.role === 'assistant')
                .map(getAssistantMessageSignature)
                .filter(Boolean)
        );
        const dedupedMissingMessages = missingPendingMessages.filter((message) => {
            const signature = getAssistantMessageSignature(message);
            if (existingSignatures.has(signature)) return false;
            existingSignatures.add(signature);
            return true;
        });

        return [...messageList, ...dedupedMissingMessages];
    };
    const getVisibleChatSnapshotStorageKey = (storageKey = activeReportStorageKey) => (
        storageKey ? `${VISIBLE_CHAT_SNAPSHOT_PREFIX}${storageKey}` : null
    );
    const getChatScrollStateStorageKey = (storageKey = activeReportStorageKey) => (
        storageKey ? `${CHAT_SCROLL_STATE_PREFIX}${storageKey}` : null
    );
    const readChatScrollState = (storageKey = activeReportStorageKey) => {
        const scrollStateKey = getChatScrollStateStorageKey(storageKey);
        if (!scrollStateKey || typeof window === 'undefined') return null;
        const stored = safeJsonParse(window.sessionStorage.getItem(scrollStateKey), null);
        if (!stored || typeof stored !== 'object') return null;
        const scrollTop = Number(stored.scrollTop);
        const scrollHeight = Number(stored.scrollHeight);
        const clientHeight = Number(stored.clientHeight);
        if (!Number.isFinite(scrollTop) || !Number.isFinite(scrollHeight) || !Number.isFinite(clientHeight)) {
            return null;
        }
        return {
            scrollTop: Math.max(0, scrollTop),
            scrollHeight: Math.max(0, scrollHeight),
            clientHeight: Math.max(0, clientHeight),
            distanceFromBottom: Math.max(0, Number(stored.distanceFromBottom) || 0),
            wasAtBottom: Boolean(stored.wasAtBottom),
        };
    };
    const writeChatScrollState = (storageKey = activeReportStorageKey, container = scrollContainerRef.current) => {
        const scrollStateKey = getChatScrollStateStorageKey(storageKey);
        if (!scrollStateKey || typeof window === 'undefined' || !container) return;
        const distanceFromBottom = Math.max(0, container.scrollHeight - container.scrollTop - container.clientHeight);
        try {
            window.sessionStorage.setItem(scrollStateKey, JSON.stringify({
                scrollTop: container.scrollTop,
                scrollHeight: container.scrollHeight,
                clientHeight: container.clientHeight,
                distanceFromBottom,
                wasAtBottom: distanceFromBottom < 100,
            }));
        } catch {}
    };
    const restoreChatScrollState = (scrollState) => {
        if (!scrollState) return;
        window.setTimeout(() => {
            const container = scrollContainerRef.current;
            if (!container) return;
            const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
            container.scrollTop = Math.min(scrollState.scrollTop, maxScrollTop);
        }, 0);
    };
    const sanitizeVisibleSnapshotMessage = (message = {}) => {
        if (!message || !message.role) return null;
        const content = typeof message.content === 'string'
            ? message.content
            : cleanDisplayText(getMessageContent(message));
        const snapshot = {
            id: message.id || `snapshot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            role: message.role,
            content,
            timestamp: message.timestamp || message.createdAt || message.created_at || new Date().toISOString(),
            type: message.type || 'text',
            isVoice: Boolean(message.isVoice),
            attachments: Array.isArray(message.attachments)
                ? message.attachments.map(sanitizeAttachmentPreview).filter(Boolean)
                : [],
            afterGuidedSession: Boolean(message.afterGuidedSession),
            suppressDiagnosticTicketCard: Boolean(message.suppressDiagnosticTicketCard),
        };

        ['pdfUrl', 'docUrl', 'mapUrl'].forEach((key) => {
            if (message[key]) snapshot[key] = message[key];
        });
        if (Array.isArray(message.suggestions)) snapshot.suggestions = message.suggestions;
        if (message.shopResult) snapshot.shopResult = normalizeShopResultForStorage(message.shopResult);
        if (message.diagnosticReport) snapshot.diagnosticReport = message.diagnosticReport;
        if (message.diagnosticTicket) snapshot.diagnosticTicket = message.diagnosticTicket;
        if (message.ticket) snapshot.ticket = message.ticket;
        if (message.card) snapshot.card = message.card;
        if (message.vehicle) snapshot.vehicle = message.vehicle; // "Added to your garage" card

        if (snapshot.role === 'user') {
            const parsedUserMessage = parseUserVisibleContent(snapshot.content);
            snapshot.content = parsedUserMessage.content;
            const snapshotAttachments = [
                ...(Array.isArray(snapshot.attachments) ? snapshot.attachments : []),
                ...parsedUserMessage.attachments,
            ];
            snapshot.attachments = snapshotAttachments.map(sanitizeAttachmentPreview).filter(Boolean);
        }

        const hasVisiblePayload = (
            cleanDisplayText(snapshot.content) ||
            snapshot.diagnosticReport ||
            snapshot.diagnosticTicket ||
            snapshot.ticket ||
            snapshot.card ||
            snapshot.vehicle ||
            snapshot.shopResult ||
            (Array.isArray(snapshot.attachments) && snapshot.attachments.length > 0) ||
            (Array.isArray(snapshot.suggestions) && snapshot.suggestions.length > 0)
        );
        return hasVisiblePayload ? snapshot : null;
    };
    const getVisibleMessageSignature = (message = {}) => {
        const reportId = message?.diagnosticReport?.id || message?.diagnosticReport?.reportId || '';
        const ticketId = resolveRealDiagnosticTicketId(message?.diagnosticTicket || message?.ticket) || '';
        const cardKey = message?.card
            ? [
                message.card.type || '',
                message.card.title || message.card.checkTitle || message.card.repairTitle || '',
                message.card.currentStep || message.card.stepIndex || '',
                message.card.totalSteps || '',
            ].join(':')
            : '';
        const vehicleKey = message?.vehicle
            ? [message.vehicle.year, message.vehicle.make, message.vehicle.model].filter(Boolean).join(' ')
            : '';
        const content = cleanDisplayText(getMessageContent(message) || message.content || '').slice(0, 800);
        const normalizedType = ['vehicle_added', 'vehicle_added_user'].includes(message.type)
            ? 'text'
            : (message.type || 'text');
        return [
            message.role || '',
            normalizedType,
            reportId,
            ticketId,
            cardKey,
            vehicleKey,
            content,
        ].join('|');
    };
    const readVisibleChatSnapshot = (storageKey = activeReportStorageKey) => {
        const snapshotKey = getVisibleChatSnapshotStorageKey(storageKey);
        if (!snapshotKey || typeof window === 'undefined') return [];
        const stored = safeJsonParse(window.localStorage.getItem(snapshotKey), []);
        if (!Array.isArray(stored)) return [];
        return stored
            .map(sanitizeVisibleSnapshotMessage)
            .filter(Boolean);
    };
    const writeVisibleChatSnapshot = (messageList = messages, storageKey = activeReportStorageKey) => {
        const snapshotKey = getVisibleChatSnapshotStorageKey(storageKey);
        if (!snapshotKey || typeof window === 'undefined') return;
        const snapshots = (Array.isArray(messageList) ? messageList : [])
            .filter((message) => !message?.isStreamError) // transient error bubbles never persist
            .map(sanitizeVisibleSnapshotMessage)
            .filter(Boolean)
            .slice(-VISIBLE_CHAT_SNAPSHOT_LIMIT);
        if (snapshots.length === 0) return;
        try {
            window.localStorage.setItem(snapshotKey, JSON.stringify(snapshots));
        } catch (error) {
            console.warn('Unable to persist visible chat snapshot', error);
        }
    };
    // A snapshot entry worth re-merging is a FRONTEND-OWNED card/element (report card, guided
    // card, doc/pdf/map/shop card) or a tagged local-only bubble (non-'text' type) — never plain
    // server-backed conversational text. Server history is authoritative for user/assistant text,
    // so a snapshot (which can carry another thread's text if ever mis-scoped) can NOT re-inject
    // text messages and bleed a previous chat's turns into the current one. Local-only turns that
    // must survive reload (e.g. the maintenance-DIY trigger bubble) carry a distinct `type`.
    const isSnapshotMergeableCard = (message = {}) => {
        if (message?.diagnosticReport || message?.isLoadingReport || message?.card) return true;
        if (message?.pdfUrl || message?.docUrl || message?.mapUrl || message?.shopResult) return true;
        const t = message?.type || '';
        return Boolean(t && t !== 'text');
    };

    const mergeVisibleChatSnapshotMessages = (messageList = [], storageKey = activeReportStorageKey) => {
        const snapshotMessages = readVisibleChatSnapshot(storageKey);
        if (snapshotMessages.length === 0) return messageList;
        const signatures = new Set(
            messageList
                .map(getVisibleMessageSignature)
                .filter(Boolean)
        );
        const missingMessages = snapshotMessages.filter((message) => {
            const signature = getVisibleMessageSignature(message);
            if (!signature || signatures.has(signature)) return false;
            if (!isSnapshotMergeableCard(message)) return false;
            signatures.add(signature);
            return true;
        });
        if (missingMessages.length === 0) return messageList;
        return sortMessagesChronologically([...messageList, ...missingMessages]);
    };
    const getMessageSignature = (message = {}) => {
        const content = String(message.content || '').replace(/\s+/g, ' ').trim().slice(0, 500);
        return `${message.role || ''}:${content}`;
    };
    const readPostGuidedMessageSignatures = () => {
        if (!postGuidedMessageStorageKey) return new Set();
        const stored = safeJsonParse(localStorage.getItem(postGuidedMessageStorageKey), []);
        return new Set(Array.isArray(stored) ? stored : []);
    };
    const writePostGuidedMessageSignatures = (signatures) => {
        if (!postGuidedMessageStorageKey || signatures.size === 0) return;
        localStorage.setItem(postGuidedMessageStorageKey, JSON.stringify([...signatures].slice(-300)));
    };
    const getGuidedOrderingStartTime = () => {
        const restoredSession = guidedCheckSession || loadGuidedCheckSession([
            guidedCheckStorageKey,
            currentThreadId ? `thread:${currentThreadId}` : null,
            currentTicketThreadId ? `thread:${currentTicketThreadId}` : null,
            currentTicketId ? `ticket:${currentTicketId}` : null,
        ]);
        const timestamp = restoredSession?.startedAt || restoredSession?.timeline?.[0]?.createdAt || '';
        const parsed = Date.parse(timestamp);
        return Number.isNaN(parsed) ? null : parsed;
    };
    const applyGuidedOrderingToHistory = (messageList = []) => {
        const startTime = getGuidedOrderingStartTime();
        const signatures = readPostGuidedMessageSignatures();
        if (!startTime && signatures.size === 0) return messageList;

        return messageList.map((message) => {
            const messageTime = Date.parse(message.timestamp || '');
            const afterByTime = startTime && !Number.isNaN(messageTime) && messageTime >= startTime;
            const afterBySignature = signatures.has(getMessageSignature(message));
            return afterByTime || afterBySignature
                ? { ...message, afterGuidedSession: true }
                : message;
        });
    };

    // Used by ticket matching below, so this state must be initialized before
    // currentDiagnosticTicket is derived.
    const [activeVehicleLabel, setActiveVehicleLabel] = useState('');
    // Active vehicle details (mileage, engine) for the returning-user greeting.
    const [activeVehicleInfo, setActiveVehicleInfo] = useState(null);
    // User's preferred distance unit ('mi' | 'km') from their profile — shown on the
    // odometer field so they know which unit to enter.
    const [distanceUnit, setDistanceUnit] = useState('mi');

    useEffect(() => {
        if (!isAuthenticated || !authState?.identity) { setActiveVehicleInfo(null); return; }
        let cancelled = false;
        const load = async () => {
            try {
                const resp = await fetch(`${getUserDetailsBaseUrl()}/api/lookup_user_data?phone_number=${encodeURIComponent(authState.identity)}&query=vehicle_worklist`);
                if (!resp.ok) return;
                const data = await resp.json();
                if (cancelled) return;
                // #409: CarFido communicates only in imperial — always display miles,
                // ignoring any stored metric preference.
                setDistanceUnit('mi');
                const av = data?.result?.active_vehicle;
                if (!av) return;
                const label = [av.year, av.make, av.model].filter(Boolean).join(' ');
                const miles = av.odometer ?? av.total_distance_miles ?? av.mileage;
                const milesNum = (miles !== null && miles !== undefined && miles !== '') ? Number(miles) : null;
                setActiveVehicleInfo({
                    vehicle_id: av.vehicle_id ?? null,
                    label,
                    // `mileage` stays the raw MILES value (used by mileage-based calcs like the
                    // repair decrease-check); `mileageMiles` is the same as a number, and display
                    // code converts it to the user's unit via formatMileage().
                    mileage: milesNum !== null ? milesNum.toLocaleString('en-US') : '',
                    mileageMiles: milesNum,
                    engine: cleanDisplayText(av.engine || ''),
                });
                if (label) setActiveVehicleLabel((prev) => prev || label);
            } catch (_) { /* best effort */ }
        };
        load();
        const on = () => load();
        window.addEventListener('dash_vehicle_switched', on);
        window.addEventListener('dash_vehicle_updated', on);
        return () => {
            cancelled = true;
            window.removeEventListener('dash_vehicle_switched', on);
            window.removeEventListener('dash_vehicle_updated', on);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, authState?.identity]);

    const isFreshThreadlessChat = !currentThreadId && !guidedCheckSession;
    const shouldSuppressInlineDiagnostics = suppressDiagnosticRestoreRef.current && isFreshThreadlessChat;
    const currentThread = useMemo(() => (threads || []).find((thread) => (
        String(thread?.thread_id || '') === String(currentThreadId || '')
    )) || null, [threads, currentThreadId]);
    const currentThreadDiagnosticTicket = useMemo(
        () => getThreadDiagnosticTicket(currentThread),
        [currentThread]
    );

    // One vehicle per chat. When the active vehicle changes to a DIFFERENT vehicle
    // from inside a chat, don't keep this chat on the old car — open the chat that
    // belongs to the new vehicle instead (reuse its most recent chat, or start a
    // fresh one). This covers:
    //   • an explicit switch ("Switch to Wagon R")  → switch_tag / digit_switch
    //   • ADDING a new vehicle, which makes it active → add_vehicle. The whole
    //     conversation is now about the new car, so it belongs in the new car's chat
    //     rather than staying linked to the previously-active vehicle in the sidebar.
    // The backend has already made the target vehicle active, so a new thread binds
    // to it. Inferred/keyword switches and manual vehicle-selector changes are left alone.
    const switchRedirectRef = useRef(null);
    useEffect(() => {
        const handleVehicleSwitch = async (event) => {
            const detail = event?.detail || {};
            // Trigger ONLY on a genuine ACTIVE-vehicle change. Adding a new car, or
            // switching cars, both surface the NEW active_vehicle_id in the event. Plain
            // detail updates (odometer, specs) do NOT set active_vehicle_id, so they never
            // redirect. Keying on active_vehicle_id (rather than a specific source string)
            // makes this robust: whether the car was added via add_vehicle OR update_user_car,
            // the conversation moves to the new car's own chat.
            const targetVehicleId = detail.active_vehicle_id;
            // [vehicle-redirect debug] shows whether the event carried a new active vehicle
            // and how it compares to the current chat's vehicle — helps confirm the chain end-to-end.
            console.debug('[vehicle-redirect]', {
                source: detail.source, active_vehicle_id: detail.active_vehicle_id,
                vehicle_id: detail.vehicle_id, currentThreadId,
                currentChatVehicleId: currentThread?.vehicle_id,
            });
            if (targetVehicleId == null || currentThreadId == null) return;
            const currentVehicleId = currentThread?.vehicle_id;
            // Only redirect when the new active vehicle DIFFERS from the vehicle this chat
            // is bound to (a same-vehicle event is a no-op).
            if (currentVehicleId == null || String(currentVehicleId) === String(targetVehicleId)) return;
            if (switchRedirectRef.current === String(targetVehicleId)) return;
            switchRedirectRef.current = String(targetVehicleId);
            try {
                const existing = (threads || []).find((thread) => (
                    String(thread?.vehicle_id) === String(targetVehicleId)
                ));
                if (existing && onSelectThread) {
                    onSelectThread(existing.thread_id);
                } else if (onNewThread) {
                    const created = await onNewThread();
                    if (created?.thread_id && onSelectThread) onSelectThread(created.thread_id);
                }
                setShouldAutoScroll(true);
            } finally {
                setTimeout(() => { switchRedirectRef.current = null; }, 2000);
            }
        };
        window.addEventListener('dash_vehicle_switched', handleVehicleSwitch);
        return () => window.removeEventListener('dash_vehicle_switched', handleVehicleSwitch);
    }, [currentThread, currentThreadId, threads, onNewThread, onSelectThread]);
    const activeTicketBelongsToCurrentThread = currentThreadId && (
        String(resolveTicketThreadId(activeDiagnosticTicket)) === String(currentThreadId)
    );
    const recentTicketForCurrentThread = currentThreadId
        ? (recentDiagnostics || []).find((ticket) => (
            isDisplayableDiagnosticTicket(ticket) &&
            !isClosedDiagnosticTicket(ticket) &&
            String(resolveTicketThreadId(ticket)) === String(currentThreadId)
        ))
        : null;
    const fallbackRecentTicket = isFreshThreadlessChat
        ? null
        : (recentDiagnostics || []).find((ticket) => isDisplayableDiagnosticTicket(ticket) && !isClosedDiagnosticTicket(ticket));
    const currentThreadIdString = currentThreadId ? String(currentThreadId) : '';
    const hasInlineDiagnosticConversationSignal = (messageList = messages) => (
        (messageList || []).some((message) => (
            isDiagnosticTicketAnchorMessage(message) ||
            resolveRealDiagnosticTicketId(message?.diagnosticTicket || message?.ticket) ||
            resolveReportDiagnosticTicketId(message?.diagnosticReport)
        ))
    );
    const firstDiagnosticUserMessage = (messages || []).find((message) => (
        message?.role === 'user' &&
        cleanDisplayText(getMessageContent(message))
    ));
    const diagnosticConversationIssueKey = normalizeIssueKey(
        getMessageContent(firstDiagnosticUserMessage) || ''
    );
    const messageDiagnosticTicket = (messages || [])
        .map((message) => message?.diagnosticTicket || message?.ticket)
        .find((ticket) => isDisplayableDiagnosticTicket(ticket) && !isClosedDiagnosticTicket(ticket)) || null;
    const closedMessageDiagnosticTicket = (messages || [])
        .map((message) => message?.diagnosticTicket || message?.ticket)
        .find((ticket) => isDisplayableDiagnosticTicket(ticket) && isClosedDiagnosticTicket(ticket)) || null;
    const activeTicketThreadId = resolveTicketThreadId(activeDiagnosticTicket);
    const activeTicketBelongsToThread = Boolean(
        currentThreadIdString &&
        isDisplayableDiagnosticTicket(activeDiagnosticTicket) &&
        String(activeTicketThreadId || '') === currentThreadIdString
    );
    const activeTicketBelongsToDiagnosticConversation = Boolean(
        isDisplayableDiagnosticTicket(activeDiagnosticTicket) &&
        (!currentThreadIdString || activeTicketBelongsToThread) &&
        hasInlineDiagnosticConversationSignal(messages) &&
        (
            !diagnosticConversationIssueKey ||
            issueKeysMatch(
                normalizeIssueKey(resolveTicketTitle(activeDiagnosticTicket)),
                diagnosticConversationIssueKey
            )
        )
    );
    const recentThreadDiagnosticTicket = currentThreadIdString
        ? (recentDiagnostics || []).find((ticket) => (
            isDisplayableDiagnosticTicket(ticket) &&
            !isClosedDiagnosticTicket(ticket) &&
            String(resolveTicketThreadId(ticket) || '') === currentThreadIdString
        ))
        : null;
    const closedRecentThreadDiagnosticTicket = currentThreadIdString
        ? (recentDiagnostics || []).find((ticket) => (
            isDisplayableDiagnosticTicket(ticket) &&
            isClosedDiagnosticTicket(ticket) &&
            String(resolveTicketThreadId(ticket) || '') === currentThreadIdString
        ))
        : null;
    const recentIssueDiagnosticTicket = diagnosticConversationIssueKey
        ? (recentDiagnostics || []).find((ticket) => {
            if (!isDisplayableDiagnosticTicket(ticket) || isClosedDiagnosticTicket(ticket)) return false;
            const ticketThreadId = String(resolveTicketThreadId(ticket) || '');
            if (currentThreadIdString && ticketThreadId !== currentThreadIdString) return false;
            const ticketIssueKey = normalizeIssueKey(resolveTicketTitle(ticket));
            if (!issueKeysMatch(ticketIssueKey, diagnosticConversationIssueKey)) return false;
            const ticketVehicleKey = cleanDisplayText(getDiagnosticTicketVehicleLabel(ticket)).toLowerCase();
            const activeVehicleKey = cleanDisplayText(activeVehicleLabel).toLowerCase();
            return !activeVehicleKey || !ticketVehicleKey || ticketVehicleKey === activeVehicleKey;
        })
        : null;
    const hasExplicitNonDiagnosticThreadType = Boolean(
        (currentThread?.conversation_type || currentThread?.conversationType) &&
        (currentThread?.conversation_type || currentThread?.conversationType) !== 'diagnosis'
    );
    const currentDiagnosticTicket = (shouldSuppressInlineDiagnostics || hasExplicitNonDiagnosticThreadType) ? null : (
        messageDiagnosticTicket ||
        (activeTicketBelongsToThread ? activeDiagnosticTicket : null) ||
        currentThreadDiagnosticTicket ||
        (activeTicketBelongsToDiagnosticConversation ? activeDiagnosticTicket : null) ||
        recentThreadDiagnosticTicket ||
        recentIssueDiagnosticTicket ||
        closedMessageDiagnosticTicket ||
        closedRecentThreadDiagnosticTicket ||
        null
    );
    const currentTicketThreadId = resolveTicketThreadId(currentDiagnosticTicket);
    const currentTicketId = resolveRealDiagnosticTicketId(currentDiagnosticTicket);
    const latestPersistedReportTicketId = getReportDiagnosticTicketId(getLatestPersistedDiagnosticReport(messages) || {});
    const garageProgressTicketId = (() => {
        const candidate = currentTicketId || latestPersistedReportTicketId;
        return /^\d+$/.test(String(candidate || '')) ? String(candidate) : '';
    })();

    // Kept in sync every render (not just in an effect) so the fetch callbacks below always
    // see the latest requested ticket, and reset to null on unmount so a late response never
    // applies after this component is gone.
    const garageProgressTicketIdRef = useRef(garageProgressTicketId);
    garageProgressTicketIdRef.current = garageProgressTicketId;
    useEffect(() => () => {
        garageProgressTicketIdRef.current = null;
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !garageProgressTicketId) {
            setActiveTicketGarageRequests([]);
        }
    }, [isAuthenticated, garageProgressTicketId]);

    useEffect(() => {
        if (!isAuthenticated || !garageProgressTicketId) return undefined;
        if (Object.prototype.hasOwnProperty.call(referralLocksByTicketId, garageProgressTicketId)) return undefined;

        let cancelled = false;
        listPartnerShops({ diagnosticTicketId: garageProgressTicketId })
            .then((shops) => {
                if (cancelled) return;
                setReferralLocksByTicketId((prev) => ({
                    ...prev,
                    [garageProgressTicketId]: shops?.referralLock || null,
                }));
            })
            .catch(() => {
                if (cancelled) return;
                setReferralLocksByTicketId((prev) => ({
                    ...prev,
                    [garageProgressTicketId]: null,
                }));
            });

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, garageProgressTicketId, referralLocksByTicketId]);

    const getReferralLockForReport = useCallback((reportArg = null) => {
        const reportTicketId = getReportDiagnosticTicketId(reportArg || {});
        const ticketId = reportTicketId || garageProgressTicketId;
        if (!ticketId) return null;
        return referralLocksByTicketId[String(ticketId)] || null;
    }, [garageProgressTicketId, referralLocksByTicketId]);

    const getFindShopLabelForReport = useCallback((reportArg = null, fallback = 'Find a Shop') => (
        getReferralShopActionLabel(getReferralLockForReport(reportArg), fallback)
    ), [getReferralLockForReport]);

    const sendReportToReferringShop = useCallback(async (reportArg) => {
        const report = normalizeDiagnosticReportForUi(reportArg);
        const ticketId = getReportDiagnosticTicketId(report || {});
        const lock = ticketId ? referralLocksByTicketId[String(ticketId)] : null;
        const shopId = lock?.partner_shop_id || lock?.shop?.partner_shop_id;
        if (!ticketId || !shopId) return;

        const key = String(ticketId);
        setReferralReportSendByTicketId((prev) => ({ ...prev, [key]: { status: 'sending', error: '' } }));
        try {
            const request = await createPartnerRepairRequest({
                diagnostic_ticket_id: Number(ticketId),
                partner_shop_id: Number(shopId),
                referral_consent_confirmed: true,
                notes: 'Customer sent the completed diagnostic report back to the referring shop.',
                report_snapshot: report,
            });
            setReferralReportSendByTicketId((prev) => ({
                ...prev,
                [key]: { status: 'sent', error: '', request },
            }));
            if (request) {
                setActiveTicketGarageRequests((prev) => (
                    prev.some((item) => String(item?.request_id) === String(request?.request_id))
                        ? prev
                        : [request, ...prev]
                ));
            }
            setReferralLocksByTicketId((prev) => ({
                ...prev,
                [key]: prev[key] ? { ...prev[key], consented_at: prev[key].consented_at || new Date().toISOString() } : prev[key],
            }));
        } catch (error) {
            setReferralReportSendByTicketId((prev) => ({
                ...prev,
                [key]: { status: 'error', error: error?.message || 'Could not send the report. Please try again.' },
            }));
        }
    }, [referralLocksByTicketId]);

    const referralPromptPropsForReport = useCallback((reportArg) => {
        const ticketId = getReportDiagnosticTicketId(reportArg || {});
        const lock = ticketId ? referralLocksByTicketId[String(ticketId)] : null;
        if (!ticketId || !lock) return {};
        const existingRequest = activeTicketGarageRequests.find((request) => (
            String(request?.diagnostic_ticket_id || '') === String(ticketId)
        ));
        const sendState = referralReportSendByTicketId[String(ticketId)] || {};
        return {
            referralShopName: lock.shop?.name || 'your referring shop',
            referralSendStatus: existingRequest ? 'sent' : (sendState.status || 'ready'),
            referralSendError: sendState.error || '',
            onSendToReferralShop: sendReportToReferringShop,
        };
    }, [activeTicketGarageRequests, referralLocksByTicketId, referralReportSendByTicketId, sendReportToReferringShop]);

    const loadGarageRequests = useCallback(() => {
        const requestedTicketId = garageProgressTicketIdRef.current;
        if (!requestedTicketId) return;
        listPartnerRepairRequests({ diagnostic_ticket_id: requestedTicketId })
            .then((requests) => {
                // Discard a stale response if the user switched tickets (or this component
                // unmounted) while the request was in flight.
                if (garageProgressTicketIdRef.current !== requestedTicketId) return;
                setActiveTicketGarageRequests(requests);
            })
            .catch(() => {
                if (garageProgressTicketIdRef.current !== requestedTicketId) return;
                setActiveTicketGarageRequests([]);
            });
    }, []);

    usePartnerRequestUpdates({
        diagnosticTicketId: garageProgressTicketId,
        isAuthenticated,
        onUpdate: loadGarageRequests,
    });

    useEffect(() => {
        if (!garageProgressTicketId || !activeTicketGarageRequests.length) return;
        const released = activeTicketGarageRequests.some((request) => (
            SHOP_ESTIMATE_DECLINED_STATUSES.has(String(request?.request_status || '').toLowerCase())
        ));
        if (!released) return;
        setReferralLocksByTicketId((prev) => {
            if (!prev[String(garageProgressTicketId)]) return prev;
            return {
                ...prev,
                [String(garageProgressTicketId)]: null,
            };
        });
    }, [activeTicketGarageRequests, garageProgressTicketId]);

    const isVehicleManagementChat = showChatVehicleForm || (
        messages.length > 0 &&
        messages.every(isVehicleManagementChatMessage)
    );
    const syncedInlineDiagnosticTicket = isVehicleManagementChat ? null : currentDiagnosticTicket;
    const activeInlineDiagnosticTicket = syncedInlineDiagnosticTicket && !isClosedDiagnosticTicket(syncedInlineDiagnosticTicket)
        ? syncedInlineDiagnosticTicket
        : null;
    const linkedGarageRequests = useMemo(() => (
        [...activeTicketGarageRequests].sort((a, b) => String(b?.updated_at || b?.created_at || '').localeCompare(String(a?.updated_at || a?.created_at || '')))
    ), [activeTicketGarageRequests]);
    const selectedGarageRequest = useMemo(() => {
        const fromList = linkedGarageRequests.find((request) => String(request?.request_id || '') === String(selectedGarageRequestId || ''));
        if (fromList) return fromList;
        // A sidebar-opened request that isn't in the active ticket's list.
        if (externalShopRequest && String(externalShopRequest.request_id || '') === String(selectedGarageRequestId || '')) {
            return externalShopRequest;
        }
        return linkedGarageRequests[0] || externalShopRequest || null;
    }, [linkedGarageRequests, selectedGarageRequestId, externalShopRequest]);
    const shopConversationMessages = useMemo(() => (
        selectedGarageRequest ? buildPartnerRequestConversationMessages(selectedGarageRequest) : []
    ), [selectedGarageRequest]);
    const isShopMode = chatMode === 'shop' && Boolean(selectedGarageRequest);
    selectedGarageRequestIdRef.current = selectedGarageRequest?.request_id || '';

    const applyGarageRequestUpdate = useCallback((updatedRequest) => {
        if (!updatedRequest?.request_id) return null;
        setActiveTicketGarageRequests((prev) => {
            const existing = prev.some((request) => String(request?.request_id || '') === String(updatedRequest.request_id));
            return existing
                ? prev.map((request) => (String(request?.request_id || '') === String(updatedRequest.request_id) ? updatedRequest : request))
                : [updatedRequest, ...prev];
        });
        // Keep a sidebar-opened (external) request in sync too, so the driver's own action
        // (approve/decline/send) reflects immediately and survives an active-ticket refetch.
        setExternalShopRequest((prev) => (
            prev && String(prev.request_id || '') === String(updatedRequest.request_id) ? updatedRequest : prev
        ));
        setSelectedGarageRequestId(updatedRequest.request_id);
        return updatedRequest;
    }, []);

    // A sidebar-opened shop chat may belong to a ticket other than the active thread's, so the
    // primary live-update subscription (keyed to garageProgressTicketId) doesn't cover it.
    // Subscribe to THIS request's own ticket too, and refetch it on every event, so shop
    // messages and status changes (e.g. after the driver approves) appear live on the driver side.
    const shopExternalTicketId = (
        isShopMode && externalShopRequest?.diagnostic_ticket_id
        && String(externalShopRequest.diagnostic_ticket_id) !== String(garageProgressTicketId || '')
    ) ? String(externalShopRequest.diagnostic_ticket_id) : '';
    const shopExternalTicketIdRef = useRef('');
    shopExternalTicketIdRef.current = shopExternalTicketId;

    const loadExternalShopRequest = useCallback(() => {
        const ticketId = shopExternalTicketIdRef.current;
        const reqId = selectedGarageRequestIdRef.current;
        if (!ticketId || !reqId) return;
        listPartnerRepairRequests({ diagnostic_ticket_id: ticketId })
            .then((requests) => {
                if (shopExternalTicketIdRef.current !== ticketId) return;
                const match = (requests || []).find((r) => String(r?.request_id || '') === String(reqId));
                if (match) setExternalShopRequest(match);
            })
            .catch(() => {});
    }, []);

    usePartnerRequestUpdates({
        diagnosticTicketId: shopExternalTicketId,
        isAuthenticated,
        onUpdate: loadExternalShopRequest,
    });

    useEffect(() => {
        // Don't drop out of shop mode when the active-ticket list is empty but a sidebar-opened
        // (external) shop request is in view.
        if (!linkedGarageRequests.length) {
            if (externalShopRequest) return;
            setChatMode('dash');
            setSelectedGarageRequestId('');
            return;
        }
        if (!selectedGarageRequestId) {
            setSelectedGarageRequestId(linkedGarageRequests[0].request_id || '');
        }
    }, [linkedGarageRequests, selectedGarageRequestId, externalShopRequest]);

    useEffect(() => {
        const openGarageRequestId = location.state?.openGarageRequestId;
        if (!openGarageRequestId) return;
        const match = linkedGarageRequests.find((request) => String(request?.request_id || '') === String(openGarageRequestId));
        const passed = location.state?.openGarageRequest;
        // Prefer the live active-ticket copy; otherwise open the request object the sidebar
        // passed through navigation (covers requests outside the active ticket).
        if (!match && !passed) return;
        if (!match && passed) setExternalShopRequest(passed);
        pendingShopConversationTopRef.current = String((match || passed).request_id || openGarageRequestId);
        setChatMode('shop');
        setSelectedGarageRequestId((match || passed).request_id || '');
        navigate('/chat', { replace: true, state: null });
    }, [linkedGarageRequests, location.state, navigate]);

    // Leaving shop mode (opening a normal thread) clears the external request so it doesn't linger.
    // (While in shop mode it stays as a fallback; selectedGarageRequest already prefers the live
    // active-ticket copy whenever that same request is present there.)
    useEffect(() => {
        if (chatMode !== 'shop') setExternalShopRequest(null);
    }, [chatMode]);

    useEffect(() => {
        if (!isShopMode || attachments.length === 0) {
            return;
        }
        setAttachments([]);
    }, [attachments.length, isShopMode]);
    const lightweightChatContext = useMemo(
        () => inferLightweightChatContextFromMessages(messages, activeVehicleLabel),
        [messages, activeVehicleLabel]
    );
    const explicitClientChatContext = activeClientIntent?.type
        ? {
            type: activeClientIntent.type,
            title: activeClientIntent.title || (activeClientIntent.type === 'shop' ? 'repair shops' : 'parts'),
            vehicleLabel: activeClientIntent.vehicleLabel || activeVehicleLabel,
        }
        : null;
    const diagnosticChatContext = activeInlineDiagnosticTicket
        ? {
            type: 'diagnosis',
            title: resolveTicketTitle(activeInlineDiagnosticTicket) || 'Open issue',
            vehicleLabel: getDiagnosticTicketVehicleLabel(activeInlineDiagnosticTicket) || activeVehicleLabel,
            ticket: activeInlineDiagnosticTicket,
        }
        : null;
    const activeChatContext = diagnosticChatContext || explicitClientChatContext || lightweightChatContext;
    const hasDiagnosticTicketContext = (messageList = messages) => {
        const threadId = currentThreadId ? String(currentThreadId) : '';
        const linkedTicket = currentDiagnosticTicket || syncedInlineDiagnosticTicket;
        const linkedTicketId = resolveRealDiagnosticTicketId(linkedTicket);
        const linkedTicketThreadId = resolveTicketThreadId(linkedTicket);
        const hasDiagnosticSignal = hasInlineDiagnosticConversationSignal(messageList);
        if (
            linkedTicketId &&
            (hasDiagnosticSignal || !threadId || String(linkedTicketThreadId || '') === threadId)
        ) {
            return true;
        }
        if (threadId && (recentDiagnostics || []).some((ticket) => (
            resolveRealDiagnosticTicketId(ticket) &&
            String(resolveTicketThreadId(ticket) || '') === threadId
        ))) {
            return true;
        }
        // Anonymous (not-logged-in) users never get a backend diagnostic TICKET — tickets
        // require a DB user — so the ticket-based checks above all fail and the report
        // would never generate. Fall back to the diagnostic CONVERSATION signal so a
        // report still generates without login (it's built client-side by
        // createDiagnosticReport). The min-turns / ready-signal gates still apply, so
        // this only ENABLES a report, it doesn't force one.
        if (!isAuthenticated && hasDiagnosticSignal) {
            return true;
        }
        return (messageList || []).some((message) => (
            resolveRealDiagnosticTicketId(message?.diagnosticTicket || message?.ticket) ||
            resolveReportDiagnosticTicketId(message?.diagnosticReport)
        ));
    };

    const notifyDiagnosticTicketChanged = useCallback((ticket = null) => {
        const nextTicket = ticket || currentDiagnosticTicket || syncedInlineDiagnosticTicket;
        if (nextTicket && typeof window !== 'undefined') {
            markPwaEngagement('diagnostic-started');
            window.dispatchEvent(new CustomEvent('dash_active_diagnostic_ticket', {
                detail: nextTicket,
            }));
        }
        refreshDiagnosticTickets?.();
    }, [currentDiagnosticTicket, syncedInlineDiagnosticTicket, refreshDiagnosticTickets]);

    useEffect(() => {
        // Do not clear a stored guided session during the initial null render.
        // The restore effect runs after mount; deleting here makes DIY cards vanish on refresh.
        if (!guidedCheckSession) return;
        // Never persist a session under a thread key it doesn't belong to. When the
        // active thread changes (e.g. opening a new ticket/chat) before the previous
        // in-memory session is cleared, this effect would otherwise write the old
        // chat's DIY session into the NEW thread's storage — leaking the previous
        // DIY/chat into the new ticket. Only save when the session has no thread yet
        // (a fresh session being associated with this thread) or its thread matches.
        const sessionThreadId = (
            guidedCheckSession?.report?.threadId ||
            guidedCheckSession?.report?.thread_id ||
            guidedCheckSession?.threadId ||
            ''
        );
        if (currentThreadId && sessionThreadId && String(sessionThreadId) !== String(currentThreadId)) return;
        saveGuidedCheckSession(guidedCheckStorageKey, guidedCheckSession);
        // Persist server-side as the source of truth for this thread.
        if (currentThreadId) {
            saveThreadStateToServer(currentThreadId, { guided_session: guidedCheckSession });
        }
    }, [guidedCheckStorageKey, guidedCheckSession, currentThreadId]);

    useEffect(() => {
        const nextSignatures = readPostGuidedMessageSignatures();
        let changed = false;
        messages.forEach((message) => {
            if (!message.afterGuidedSession || !String(message.content || '').trim()) return;
            const signature = getMessageSignature(message);
            if (!nextSignatures.has(signature)) {
                nextSignatures.add(signature);
                changed = true;
            }
        });
        if (changed) writePostGuidedMessageSignatures(nextSignatures);
    }, [messages, postGuidedMessageStorageKey]);

    useEffect(() => {
        if (guidedCheckSession || isInitialLoading) return;
        // Don't resurrect a guided session into a fresh chat. After "New Chat"
        // there is no thread and restore is suppressed — a thread-less fresh chat
        // must stay empty instead of pulling a prior session in via ticket/last keys.
        if (!currentThreadId && suppressDiagnosticRestoreRef.current) return;

        // Restore strictly by the current thread (or the anonymous session key when
        // there is no thread). Ticket-based keys are intentionally excluded here:
        // they can surface another chat's session when ticket resolution is loose.
        const fallbackSession = loadGuidedCheckSession(
            currentThreadId
                ? [guidedCheckStorageKey, `thread:${currentThreadId}`]
                : [guidedCheckStorageKey]
        );
        if (fallbackSession?.timeline?.length) {
            setGuidedCheckSession(fallbackSession);
            return;
        }

        if (messages.length === 0) return;

        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const report = messages[index]?.diagnosticReport;
            if (!report?.id) continue;

            const restoredSession = loadGuidedCheckSession([
                guidedCheckStorageKey,
                currentThreadId ? `thread:${currentThreadId}` : null,
                report.threadId || report.thread_id ? `thread:${report.threadId || report.thread_id}` : null,
                report.id ? `report:${report.id}` : null,
            ]);
            if (restoredSession?.timeline?.length) {
                setGuidedCheckSession(restoredSession);
                break;
            }
        }
    }, [currentThreadId, currentTicketId, currentTicketThreadId, guidedCheckSession, guidedCheckStorageKey, isInitialLoading, messages]);

    // HARD THREAD SCOPE for the guided session: drop an in-memory guided session whose report is
    // stamped with a home threadId that does NOT match the chat now open. Last line of defense
    // against a completed DIY/guided session from a previous chat rendering inside the chat you
    // switched to. Only keys on threadId (not on messages) so it can't oscillate with the restore
    // effect. Legacy sessions without a threadId are handled by clearing site data on reload.
    useEffect(() => {
        if (!guidedCheckSession || !currentThreadId) return;
        const sessionThreadId = (
            guidedCheckSession?.report?.threadId ||
            guidedCheckSession?.report?.thread_id ||
            guidedCheckSession?.threadId ||
            ''
        );
        if (sessionThreadId && String(sessionThreadId) !== String(currentThreadId)) {
            setGuidedCheckSession(null);
        }
    }, [guidedCheckSession, currentThreadId]);

    // Fetch active vehicle label
    useEffect(() => {
        if (authState?.status !== 'authenticated' || !authState.identity) {
            setActiveVehicleLabel('');
            return;
        }
        let isMounted = true;
        const fetchActive = async () => {
            try {
                const base = getUserDetailsBaseUrl();
                const res = await fetch(`${base}/api/lookup_user_data?phone_number=${encodeURIComponent(authState.identity)}&query=vehicle_worklist`);
                if (!res.ok) return;
                const data = await res.json();
                if (!isMounted) return;
                const result = data?.result;
                const av = result?.active_vehicle;
                if (av) {
                    const label = [av.year, av.make, av.model].filter(Boolean).join(' ');
                    setActiveVehicleLabel(label);
                }
            } catch (_) {}
        };
        fetchActive();
        // Re-fetch when a vehicle is switched
        const onSwitch = () => fetchActive();
        window.addEventListener('dash_vehicle_switched', onSwitch);
        window.addEventListener('dash_vehicle_updated', onSwitch);
        return () => {
            isMounted = false;
            window.removeEventListener('dash_vehicle_switched', onSwitch);
            window.removeEventListener('dash_vehicle_updated', onSwitch);
        };
    }, [authState?.status, authState?.identity]);

    // While the DIY repair guide is running, the user works the card (arrows + buttons),
    // so the chat composer is locked — no free typing until the guide is done. The terminal
    // 'repair_logged' state is intentionally excluded so the composer re-enables once done.
    const isRepairGuideActive = [
        'repair_step', 'repair_outcome_check', 'repair_done_choice', 'repair_log_form',
    ].includes(guidedCheckSession?.activeCard?.type);

    const inputPlaceholder = isShopMode
        ? `Message ${selectedGarageRequest?.partner_shop?.name || 'the shop'}…`
        : isRepairGuideActive
        ? 'Follow the repair guide above — use the steps to continue.'
        : isRecording
        ? 'Recording voice...'
        : activeVehicleLabel
        ? `Ask CarFido about your ${activeVehicleLabel}…`
        : 'Ask CarFido about your car…';

    const inputPlaceholderMobile = isShopMode
        ? `Message ${getGarageShopTabLabel(selectedGarageRequest || {})}…`
        : isRepairGuideActive
        ? 'Follow the repair guide above…'
        : isRecording
        ? 'Recording...'
        : activeVehicleLabel
        ? `Ask about ${activeVehicleLabel.split(' ').slice(-2).join(' ')}…`
        : 'Ask CarFido…';

    // Pick shorter placeholder on mobile to avoid overflow
    const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 640);
    useEffect(() => {
        const onResize = () => setIsMobileView(window.innerWidth < 640);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const finalPlaceholder = isMobileView ? inputPlaceholderMobile : inputPlaceholder;

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = null;
        if (!files.length) return;

        const rejected = [];
        const validFiles = [];
        files.forEach((file) => {
            const rejection = getChatAttachmentValidationError(file);
            if (rejection) {
                rejected.push(rejection);
            } else {
                validFiles.push(file);
            }
        });

        const availableSlots = Math.max(0, MAX_CHAT_ATTACHMENT_FILES - attachments.length);
        const filesToRead = validFiles.slice(0, availableSlots);
        if (validFiles.length > availableSlots) {
            rejected.push(`You can attach up to ${MAX_CHAT_ATTACHMENT_FILES} files at once.`);
        }
        setAttachmentError(rejected.slice(0, 3).join(' '));
        if (!filesToRead.length) {
            focusChatInput();
            return;
        }

        filesToRead.forEach(file => {
            const localUrl = URL.createObjectURL(file);
            localAttachmentUrlsRef.current.add(localUrl);
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64 = reader.result.split(',')[1];
                const attachment = {
                    id: Math.random().toString(36).substr(2, 9),
                    filename: file.name,
                    mime_type: file.type || 'application/octet-stream',
                    size: file.size,
                    local_url: localUrl,
                    base64,
                };
                const convertedPreview = await fetchAttachmentImagePreview(attachment);
                setAttachments(prev => [...prev, {
                    ...attachment,
                    ...(convertedPreview ? { imageSrc: convertedPreview } : {}),
                }]);
            };
            reader.readAsDataURL(file);
        });
        focusChatInput();
    };

    const removeAttachment = (id) => {
        setAttachments(prev => {
            const removed = prev.find(a => a.id === id);
            revokeLocalAttachmentUrl(removed?.local_url || removed?.localUrl);
            return prev.filter(a => a.id !== id);
        });
        setAttachmentError('');
        focusChatInput();
    };

    const handleAuthExpired = async () => {
        setStatus(SESSION_EXPIRED_MESSAGE);
        await refreshAuthState();
        openAuthModal();
    };

    const fetchHistory = async (newOffset = 0, isInitial = false, threadIdOverride = undefined) => {
        if (authState?.status === 'loading') return 0;
        // Authenticated users load by thread_id; anonymous users load by session_id
        const fetchThreadId = threadIdOverride !== undefined ? threadIdOverride : currentThreadId;
        if (isAuthenticated && !fetchThreadId) return 0;
        if (!isAuthenticated && (!activeSessionId || !activeUserId)) return 0;

        const requestVersion = historyRequestVersionRef.current;
        const isCurrentHistoryRequest = () => requestVersion === historyRequestVersionRef.current;
        setIsLoading(true);
        if (isInitial) setStatus('Restoring your chat...');
        try {
            const limit = isInitial ? INITIAL_CHAT_HISTORY_LIMIT : CHAT_HISTORY_PAGE_SIZE;
            const historyUrl = isAuthenticated
                ? `${CHAT_API_BASE_URL}/secure/threads/${fetchThreadId}/history?limit=${limit}&offset=${newOffset}`
                : `${CHAT_API_BASE_URL}/history?user_id=${encodeURIComponent(activeUserId)}&session_id=${encodeURIComponent(activeSessionId)}&limit=${limit}&offset=${newOffset}`;
            const resp = await fetch(historyUrl, {
                credentials: isAuthenticated ? 'include' : 'same-origin',
            });

            if (resp.status === 401 && isAuthenticated) {
                await handleAuthExpired();
                return 0;
            }
            
            if (resp.ok) {
                const data = await resp.json();
                if (!isCurrentHistoryRequest()) return 0;
                if (data.status === 'success') {
                    // Internal placeholders saved by the backend — never display these
                    const HIDDEN_PLACEHOLDERS = [
                        'responded with past reports list.',
                        'responded with digital glovebox list.',
                    ];

                    const newMessages = (data.messages || []).map(m => {
                        let content = m.content || '';
                        let pdfUrl = null;
                        let docUrl = null;
                        let mapUrl = null;
                        let isVoice = false;
                        let suggestions = [];
                        let messageAttachments = [];
                        let serviceCard = null;
                        let followupCard = null;

                        // 1. Detect voice user messages (tagged with [VOICE] prefix by backend)
                        if (m.role === 'user' && content.startsWith('[VOICE] ')) {
                            isVoice = true;
                            content = content.slice(8);
                        }

                        if (m.role === 'user') {
                            const parsedUserMessage = parseUserVisibleContent(content);
                            content = parsedUserMessage.content;
                            messageAttachments = parsedUserMessage.attachments;
                        }

                        // 2. Filter out internal backend placeholder assistant messages
                        if (
                            m.role === 'assistant' &&
                            HIDDEN_PLACEHOLDERS.some(p => content.toLowerCase().trim() === p)
                        ) {
                            return null;
                        }

                        if (m.role === 'assistant') {
                            // 3. Extract embedded PDF URL marker → restore PDF card button
                            const pdfMatch = content.match(/__PDF_URL__:(https?:\/\/[^\s]+)/);
                            if (pdfMatch) {
                                pdfUrl = pdfMatch[1];
                                content = content.replace(pdfMatch[0], '').trim();
                            }
                            // Also handle legacy __PDF__: marker
                            if (!pdfUrl && content.includes('__PDF__:')) {
                                const legacyPath = content.split('__PDF__:')[1]?.split('__TEXT__:')[0]?.trim();
                                if (legacyPath?.startsWith('http')) pdfUrl = legacyPath;
                                const parts = content.split('__TEXT__:');
                                content = parts.length > 1 ? parts[1].trim() : content.replace(/__PDF__:[^\s]+/g, '').replace(/__PDF_URL__:[^\s]+/g, '').trim();
                            }
                            // 4. Extract __DOCUMENT__ marker → restore document card
                            const docMatch = content.match(/__DOCUMENT__:(https?:\/\/[^\s]+)/);
                            if (docMatch) {
                                docUrl = docMatch[1];
                                content = content.replace(docMatch[0], '').trim();
                            }
                            // 5. Remove legacy YouTube markers from old saved chat text.
                            const youtubeMatch = content.match(/▶️\s*Video:?\s*(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s]+)/i);
                            if (youtubeMatch) {
                                content = content.replace(youtubeMatch[0], '').trim();
                            }
                            // 6. Extract Google Maps URL
                            const shopResultBeforeMapCleanup = parseShopResultBlock(content);
                            if (!shopResultBeforeMapCleanup) {
                                const mapMatch = content.match(/(https?:\/\/(?:www\.)?google\.com\/maps\/[^\s,"'<>]+)/);
                                if (mapMatch) {
                                    mapUrl = mapMatch[1];
                                    content = content.replace(mapMatch[0], '').trim();
                                }
                            }
                            // 6b. Restore a persisted service-record card from its marker.
                            const serviceExtract = extractServiceCard(content);
                            content = serviceExtract.content;
                            serviceCard = serviceExtract.serviceCard;
                            // 6c. Restore the 3-day monitoring follow-up card from its marker.
                            // Must run before cleanInternalTags (which would otherwise strip the
                            // marker and leave only bare text with no card).
                            const followupExtract = extractFollowupCard(content);
                            content = followupExtract.content;
                            followupCard = followupExtract.followupCard;
                            // 7. Clean any remaining internal tags
                            content = cleanInternalTags(content);
                            const parsed = extractSuggestionsAndContent(content);
                            content = parsed.content;
                            suggestions = parsed.suggestions;
                        }

                        const parsedShopResult = m.role === 'assistant'
                            ? parseShopResultBlock(content)
                            : null;
                        const storedShopResult = parsedShopResult?.key
                            ? shopResultsByKeyRef.current[parsedShopResult.key]
                            : null;

                        return {
                            // Prefer the stable server message id so React keys and
                            // reconciliation survive refetches; fall back to a random id
                            // only for rows the backend hasn't assigned an id to.
                            id: m.id != null ? `srv-${m.id}` : Math.random().toString(36).substr(2, 9),
                            serverId: m.id != null ? m.id : null,
                            role: m.role,
                            // A follow-up renders as a self-contained notification card; drop the
                            // duplicate lead-in paragraph so only the clean card shows.
                            content: followupCard ? '' : content.trim(),
                            timestamp: m.timestamp || m.created_at || null,
                            pdfUrl: pdfUrl || null,
                            docUrl: docUrl || null,
                            mapUrl: mapUrl || null,
                            serviceCard: serviceCard || null,
                            followupCard: followupCard || null,
                            // A follow-up is a self-contained prompt (Resolved / Still / Not sure);
                            // its "…since the repair" wording otherwise trips the generic
                            // Find-parts/Find-a-shop suggestions, so drop them for this message.
                            suggestions: followupCard ? [] : suggestions,
                            shopResult: storedShopResult || parsedShopResult || null,
                            isVoice: isVoice,
                            attachments: messageAttachments,
                            type: 'text'
                        };
                    }).filter(Boolean);
                    const orderedMessages = applyGuidedOrderingToHistory(newMessages);
                    // Compute the storage key for THIS specific thread fetch to avoid
                    // snapshot data from a different thread bleeding into the current view.
                    const fetchStorageKey = isAuthenticated && fetchThreadId
                        ? `thread:${fetchThreadId}`
                        : activeReportStorageKey;
                    const restoredMessages = isInitial
                        ? mergePendingAssistantMessages(mergePendingUserMessages(orderedMessages, fetchStorageKey), fetchStorageKey)
                        : orderedMessages;

                    if (isInitial) {
                        // A non-empty initial fetch means this thread already had messages
                        // before this page view — the user is revisiting an existing
                        // conversation, not starting a fresh one. Record the FIRST message's
                        // timestamp so the greeting can say when the thread started.
                        setRevisitedThreadStartedAt(
                            orderedMessages.length > 0
                                ? (orderedMessages[0]?.timestamp || null)
                                : null
                        );
                        // Restore report cards strictly for THIS thread. Using
                        // ticket-based keys here can pull another chat's report card
                        // in when ticket resolution is ambiguous (cross-thread leak).
                        const restoredWithReportCards = mergeStoredReportCards(restoredMessages, [
                            fetchStorageKey,
                            fetchThreadId ? `thread:${fetchThreadId}` : null,
                        ].filter(Boolean), { appendUndated: orderedMessages.length === 0 });
                        // Server history is the source of truth for backend messages, but
                        // report cards/guided UI cards are frontend-owned state. Merge the
                        // thread-scoped snapshot back by timestamp so those cards keep their
                        // original position instead of being re-added after newer chat.
                        const finalMessages = mergeVisibleChatSnapshotMessages(restoredWithReportCards, fetchStorageKey);
                        if (!isCurrentHistoryRequest()) return 0;
                        const previousScrollState = readChatScrollState(fetchStorageKey);
                        const shouldRestorePreviousScroll = previousScrollState && !previousScrollState.wasAtBottom;
                        setShouldAutoScroll(!shouldRestorePreviousScroll);
                        // Mark the messages as belonging to this thread before setting state
                        // so the snapshot-write effect knows they are current.
                        messagesStorageKeyRef.current = fetchStorageKey;
                        setMessages(finalMessages);
                        if (shouldRestorePreviousScroll) restoreChatScrollState(previousScrollState);
                        // Rewrite a clean snapshot to purge any previously contaminated data.
                        if (fetchStorageKey && finalMessages.length > 0) {
                            writeVisibleChatSnapshot(finalMessages, fetchStorageKey);
                        }
                    } else if (restoredMessages.length > 0) {
                        if (!isCurrentHistoryRequest()) return 0;
                        // Prepend older messages and maintain scroll position
                        const container = scrollContainerRef.current;
                        const oldHeight = container ? container.scrollHeight : 0;
                        setMessages(prev => [...restoredMessages, ...prev]);

                        if (container) {
                            setTimeout(() => {
                                container.scrollTop = container.scrollHeight - oldHeight;
                            }, 0);
                        }
                    }

                    if (!isCurrentHistoryRequest()) return 0;
                    setHasMore(data.has_more);
                    setOffset(newOffset + orderedMessages.length);
                    return orderedMessages.length;
                }
            }
        } catch (err) {
            console.error("Failed to fetch history:", err);
        } finally {
            if (isCurrentHistoryRequest()) {
                if (isInitial) setStatus('');
                setIsLoading(false);
            }
        }
        return 0;
    };

    const {
        messageEndRef,
        scrollContainerRef,
        shouldAutoScroll,
        setShouldAutoScroll,
        handleScroll,
        scrollToBottom,
    } = useAutoScroll({
        messages,
        activeMessage,
        hasMore,
        isLoading,
        loadMore: fetchHistory,
        responseAnchorRef,
        preferResponseStart: isChatInputFocused && isMobileViewport(),
    });

    useEffect(() => {
        const pendingRequestId = pendingShopConversationTopRef.current;
        const activeRequestId = String(selectedGarageRequest?.request_id || '');
        if (!pendingRequestId || !isShopMode || pendingRequestId !== activeRequestId) return;

        // Stop the regular Dash-history loader from snapping this newly opened shop
        // conversation to the end. Once history has settled, position it at the start
        // exactly once; later shop activity is not forced back to the top.
        setShouldAutoScroll(false);
        if (isInitialLoading) return;

        const container = scrollContainerRef.current;
        if (!container) return;
        container.scrollTo({ top: 0, behavior: 'auto' });
        pendingShopConversationTopRef.current = '';
    }, [
        isInitialLoading,
        isShopMode,
        messages.length,
        scrollContainerRef,
        selectedGarageRequest?.request_id,
        setShouldAutoScroll,
        shopConversationMessages.length,
    ]);

    // Bring the Add Vehicle card's TOP into view. The card is taller than the viewport, so
    // scrolling to the session bottom would reveal only its lower fields (Engine/odometer)
    // and hide the "Add your vehicle" header + VIN/Year/Make. Align the card's top edge to
    // the top of the scroll area so the user starts from the top of the form.
    const scrollAddVehicleCardToTop = useCallback(() => {
        const container = scrollContainerRef.current;
        const card = addVehicleCardRef.current;
        if (!card) return;
        if (!container) {
            card.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
        }
        const containerRect = container.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const nextTop = container.scrollTop + cardRect.top - containerRect.top - 8;
        container.scrollTo({ top: Math.max(0, nextTop), behavior: 'auto' });
    }, [scrollContainerRef]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.visualViewport) return undefined;

        let animationFrame = null;
        const commitMobileKeyboardOffset = (nextOffset) => {
            if (Math.abs(lastMobileKeyboardOffsetRef.current - nextOffset) < 3) return;
            lastMobileKeyboardOffsetRef.current = nextOffset;
            setMobileKeyboardOffset(nextOffset);
        };
        const updateMobileKeyboardOffset = () => {
            if (animationFrame) window.cancelAnimationFrame(animationFrame);
            animationFrame = window.requestAnimationFrame(() => {
                if (!isChatInputFocused) {
                    commitMobileKeyboardOffset(0);
                    return;
                }

                const viewport = window.visualViewport;
                const isMobileWidth = window.matchMedia('(max-width: 767px)').matches;
                const keyboardHeight = Math.max(
                    0,
                    window.innerHeight - viewport.height - viewport.offsetTop
                );
                const nextOffset = isMobileWidth && keyboardHeight > 90 ? Math.round(keyboardHeight) : 0;

                commitMobileKeyboardOffset(nextOffset);
            });
        };

        updateMobileKeyboardOffset();
        window.visualViewport.addEventListener('resize', updateMobileKeyboardOffset);
        window.visualViewport.addEventListener('scroll', updateMobileKeyboardOffset);
        window.addEventListener('orientationchange', updateMobileKeyboardOffset);
        window.addEventListener('resize', updateMobileKeyboardOffset);

        return () => {
            if (animationFrame) window.cancelAnimationFrame(animationFrame);
            window.visualViewport.removeEventListener('resize', updateMobileKeyboardOffset);
            window.visualViewport.removeEventListener('scroll', updateMobileKeyboardOffset);
            window.removeEventListener('orientationchange', updateMobileKeyboardOffset);
            window.removeEventListener('resize', updateMobileKeyboardOffset);
        };
    }, [isChatInputFocused]);

    const handleChatScroll = () => {
        handleScroll();
        writeChatScrollState(activeReportStorageKey, scrollContainerRef.current);
    };

    useEffect(() => {
        const storageKey = activeReportStorageKey;
        const container = scrollContainerRef.current;
        return () => {
            const scrollStateKey = storageKey ? `${CHAT_SCROLL_STATE_PREFIX}${storageKey}` : null;
            if (!scrollStateKey || typeof window === 'undefined' || !container) return;
            const distanceFromBottom = Math.max(0, container.scrollHeight - container.scrollTop - container.clientHeight);
            try {
                window.sessionStorage.setItem(scrollStateKey, JSON.stringify({
                    scrollTop: container.scrollTop,
                    scrollHeight: container.scrollHeight,
                    clientHeight: container.clientHeight,
                    distanceFromBottom,
                    wasAtBottom: distanceFromBottom < 100,
                }));
            } catch {}
        };
    }, [activeReportStorageKey, scrollContainerRef]);

    const resetToFreshChat = useCallback(() => {
        historyRequestVersionRef.current += 1;
        suppressDiagnosticRestoreRef.current = true;
        clearPendingChatIntent();
        // New Chat must leave any open shop conversation (covers the case where the thread id
        // was already null, so the currentThreadId-change exit wouldn't fire).
        setChatMode('dash');
        setSelectedGarageRequestId('');
        setExternalShopRequest(null);
        // IMPORTANT: only purge persisted diagnostic/guided data when we are NOT
        // leaving a real saved thread. When currentThreadId is set, "New Chat" is
        // navigating away from an existing thread — wiping its stored guided
        // session / report card / snapshot would make the DIY steps and report
        // vanish (and force a regenerate) when the user returns to that thread.
        // The suppression ref below already prevents the old diagnostic from
        // leaking into the fresh, thread-less chat, so no clearing is needed here.
        if (!currentThreadId) {
            clearGuidedCheckSession([
                guidedCheckStorageKey,
                activeReportStorageKey,
                currentTicketThreadId ? `thread:${currentTicketThreadId}` : null,
                currentTicketId ? `ticket:${currentTicketId}` : null,
            ], guidedCheckSession);
            if (activeReportStorageKey) {
                getStorageAreas().forEach((storage) => {
                    try {
                        storage.removeItem(getReportCardKey(activeReportStorageKey));
                        storage.removeItem(getVisibleChatSnapshotStorageKey(activeReportStorageKey));
                    } catch {}
                });
            }
        }
        try {
            activeResponseStopRef.current?.();
            activeEventSourceRef.current?.close?.();
        } catch {}
        activeResponseStopRef.current = null;
        activeEventSourceRef.current = null;
        messagesStorageKeyRef.current = null; // mark messages as belonging to no thread
        setMessages([]);
        setInput('');
        setActiveMessage(null);
        setActiveClientIntent(null);
        setLiveDiagnosticReportCard(null);
        setGuidedCheckSession(null);
        // The Add-Vehicle card lives in its own sidebar tab now, so a fresh chat does NOT touch it —
        // the tab (and its entered data) persists. It is removed only by its ✕ or a successful Submit.
        setAttachments([]);
        setStatus('');
        setIsLoading(false);
        isLoadingRef.current = false;
        setIsInitialLoading(false);
        setHasMore(false);
        setOffset(0);
        lastLoadedIdentityRef.current = 'fresh-chat';
        hasProcessedInitialIntent.current = false;
        setShouldAutoScroll(true);
    }, [
        activeReportStorageKey,
        currentThreadId,
        currentTicketId,
        currentTicketThreadId,
        guidedCheckSession,
        guidedCheckStorageKey,
        setShouldAutoScroll,
    ]);

    useEffect(() => {
        const handleFreshChatReset = () => resetToFreshChat();
        window.addEventListener(FRESH_CHAT_RESET_EVENT, handleFreshChatReset);
        if (consumeFreshChatReset()) handleFreshChatReset();
        return () => window.removeEventListener(FRESH_CHAT_RESET_EVENT, handleFreshChatReset);
    }, [resetToFreshChat]);

    useEffect(() => {
        if (!activeReportStorageKey || isInitialLoading || messages.length === 0) return;
        // Guard: only write snapshot when the messages actually belong to the current
        // thread. If messagesStorageKeyRef doesn't match activeReportStorageKey it means
        // the thread just changed and React fired this effect before init() had a chance
        // to clear messages — writing here would contaminate the new thread's snapshot.
        if (
            messagesStorageKeyRef.current !== null &&
            messagesStorageKeyRef.current !== activeReportStorageKey
        ) return;
        writeVisibleChatSnapshot(messages, activeReportStorageKey);
    }, [activeReportStorageKey, isInitialLoading, messages]);

    useEffect(() => {
        setLiveDiagnosticReportCard(null);
    }, [activeReportStorageKey]);

    useEffect(() => {
        if (authState?.status === 'loading') return;
        if (location.state?.referralToken) clearPendingChatIntent();
        const pendingIntent = location.state?.referralToken ? null : loadPendingChatIntent();
        const guidedCheckIntent = resolveGuidedCheckIntent(location.state?.guidedCheck || pendingIntent?.guidedCheck);
        const vehicleAddedIntent = location.state?.vehicleAdded || pendingIntent?.vehicleAdded;
        const shouldSuppressRestore = suppressDiagnosticRestoreRef.current && !currentThreadId && !guidedCheckIntent?.report && !vehicleAddedIntent;
        const nextCauseIndexFromIntent = guidedCheckIntent?.report
            ? (
                typeof guidedCheckIntent.causeIndex === 'number'
                    ? guidedCheckIntent.causeIndex
                    : Math.max(
                        0,
                        (guidedCheckIntent.report.likelyCauses || []).findIndex(
                            (cause) => cause.id === guidedCheckIntent.causeId
                        )
                    )
            )
            : null;
        const pendingGuidedSession = guidedCheckIntent?.report
            ? createGuidedCheckSession({
                report: {
                    ...guidedCheckIntent.report,
                    threadId: guidedCheckIntent.report.threadId || guidedCheckIntent.report.thread_id || guidedCheckIntent.threadId || currentThreadId || '',
                },
                causeIndex: nextCauseIndexFromIntent,
            })
            : null;
        const restoredGuidedSession = pendingGuidedSession
            ? null
            : shouldSuppressRestore
                ? null
                // Scope restore strictly to the current thread. Ticket-based keys can
                // surface another chat's guided session when ticket resolution is
                // ambiguous; a session is always saved under its own thread key.
                : loadGuidedCheckSession(
                    currentThreadId
                        ? [getActiveChatStorageKey(), `thread:${currentThreadId}`]
                        : [getActiveChatStorageKey()]
                );
        if (isAuthenticated && !currentThreadId) {
            setActiveClientIntent(null);
            if (guidedCheckIntent?.threadId && onSelectThread) {
                if (!pendingGuidedSession) setMessages([]);
                setHasMore(false);
                setIsInitialLoading(false);
                setGuidedCheckSession(pendingGuidedSession);
                setShouldAutoScroll(true);
                onSelectThread(guidedCheckIntent.threadId);
                return;
            }
            const sessionWithoutThread = pendingGuidedSession || restoredGuidedSession;
            // No thread selected - reset loading state so the empty-state UI can render.
            if (!sessionWithoutThread) setMessages([]);
            setIsInitialLoading(false);
            setHasMore(false);
            lastLoadedIdentityRef.current = null;
            if (sessionWithoutThread) {
                setGuidedCheckSession(sessionWithoutThread);
                setShouldAutoScroll(true);
                if (pendingGuidedSession) {
                    clearPendingChatIntent();
                    window.history.replaceState({}, document.title);
                }
            } else if (vehicleAddedIntent) {
                setMessages(buildVehicleAddedMessages(vehicleAddedIntent));
                setShouldAutoScroll(true);
                clearPendingChatIntent();
                window.history.replaceState({}, document.title);
            } else {
                if (isAuthenticated && location.state?.selectThreadId && String(currentThreadId || '') !== String(location.state.selectThreadId)) {
                    return;
                }
                const query = searchParams.get('q') || searchParams.get('message') || location.state?.q || pendingIntent?.q;
                const initialAttachments = location.state?.attachments;
                const wasVoice = location.state?.wasVoice;
                const redirectedClientIntent = normalizeClientIntentType(location.state?.clientIntent || pendingIntent?.clientIntent || '');
                const redirectedSuppressDiagnosticTicket = Boolean(
                    location.state?.suppressDiagnosticTicket ||
                    pendingIntent?.suppressDiagnosticTicket ||
                    redirectedClientIntent === 'parts' ||
                    redirectedClientIntent === 'shop'
                );

                if ((query || initialAttachments) && !hasProcessedInitialIntent.current) {
                    hasProcessedInitialIntent.current = true;
                    if (initialAttachments && !wasVoice) {
                        setAttachments(initialAttachments);
                    }

                    if (query || (wasVoice && initialAttachments)) {
                        sendMessage(query || '', initialAttachments, wasVoice, {
                            clientIntent: redirectedClientIntent || undefined,
                            suppressDiagnosticTicket: redirectedSuppressDiagnosticTicket,
                            vehicleId: location.state?.odometerVehicleId || pendingIntent?.odometerVehicleId || undefined,
                        });
                        if (pendingIntent?.q || pendingIntent?.clientIntent) clearPendingChatIntent();
                        window.history.replaceState({}, document.title);
                        navigate('/chat', { replace: true, state: null });
                    }
                }
            }
            return;
        }
        if (!isAuthenticated && (!activeUserId || !activeSessionId)) return;

        // For authenticated users, key on thread_id; for anonymous, key on session
        const identityKey = isAuthenticated
            ? `thread:${currentThreadId}`
            : `anon:${activeUserId}:${activeSessionId}`;
        if (lastLoadedIdentityRef.current === identityKey) return;
        lastLoadedIdentityRef.current = identityKey;

        // Capture the thread ID at the time this effect fires so that async operations
        // inside init() always use the correct thread, even if currentThreadId changes
        // between when the effect fires and when the async fetch resolves.
        const capturedThreadId = currentThreadId;
        if (
            isAuthenticated &&
            capturedThreadId &&
            firstSendThreadIdRef.current === String(capturedThreadId) &&
            isLoadingRef.current
        ) {
            setIsInitialLoading(false);
            setHasMore(false);
            return;
        }

        const init = async () => {
            // Invalidate any in-flight history request from a previously selected thread
            // so its late response can't overwrite the thread we're switching to.
            historyRequestVersionRef.current += 1;
            const initRequestVersion = historyRequestVersionRef.current;
            // Always clear messages before loading a new thread to prevent cross-thread leakage.
            // Guided sessions are re-applied after history loads.
            messagesStorageKeyRef.current = null; // mark messages as belonging to no thread
            setMessages([]);
            setActiveMessage(null);
            setActiveClientIntent(null);
            // NOTE: the inline "Add your vehicle" form is NOT touched here. Its open/close +
            // restore is handled by the dedicated per-key effect (keyed on activeReportStorageKey),
            // which restores a saved form for this thread or closes one that belongs to another —
            // so a partly-filled form survives navigation instead of vanishing on thread switch.
            shopResultsByKeyRef.current = {};

            // Pull the authoritative server-side state for this thread before loading
            // history. The report card is seeded into localStorage so the existing
            // mergeStoredReportCards path inside fetchHistory folds it into the
            // rendered messages; the guided session is applied as React state. When
            // the server has state it wins over any (possibly stale or cross-thread)
            // localStorage copy, which is what keeps reloads/thread-switches correct.
            let serverGuidedSession = null;
            if (!pendingGuidedSession && isAuthenticated && capturedThreadId) {
                const serverState = await fetchThreadStateFromServer(capturedThreadId);
                // Ignore a late response if the user switched threads meanwhile.
                if (initRequestVersion === historyRequestVersionRef.current && serverState) {
                    if (serverState.reportCard) {
                        saveStoredReportCard([`thread:${capturedThreadId}`], serverState.reportCard);
                        autoReportAttemptedThreadsRef.current.add(`thread:${capturedThreadId}`);
                        liveReportReadyAttemptKeysRef.current.delete(`thread:${capturedThreadId}`);
                    }
                    if (serverState.guidedSession?.timeline?.length) {
                        serverGuidedSession = serverState.guidedSession;
                        saveGuidedCheckSession(`thread:${capturedThreadId}`, serverGuidedSession);
                    }
                    if (serverState.shopResults && Object.keys(serverState.shopResults).length > 0) {
                        shopResultsByKeyRef.current = serverState.shopResults;
                    }
                }
            }

            setGuidedCheckSession(pendingGuidedSession || serverGuidedSession || restoredGuidedSession || null);
            setHasMore(true);
            setOffset(0);
            setIsInitialLoading(true);
            setShouldAutoScroll(true);

            const loadedCount = await fetchHistory(0, true, capturedThreadId);
            setIsInitialLoading(false);

            if (pendingGuidedSession) {
                setGuidedCheckSession((prev) => prev || pendingGuidedSession);
                setShouldAutoScroll(true);
                clearPendingChatIntent();
            } else if (serverGuidedSession) {
                setGuidedCheckSession(serverGuidedSession);
            } else if (restoredGuidedSession) {
                setGuidedCheckSession(restoredGuidedSession);
            }

            if (guidedCheckIntent?.report) {
                window.history.replaceState({}, document.title);
            }

            if (vehicleAddedIntent && loadedCount === 0) {
                setMessages(buildVehicleAddedMessages(vehicleAddedIntent));
                setShouldAutoScroll(true);
                clearPendingChatIntent();
                window.history.replaceState({}, document.title);
            }

            // A job link restores its saved context; it never submits a new turn.
            if (location.state?.referralToken) {
                hasProcessedInitialIntent.current = true;
                return;
            }
            if (hasProcessedInitialIntent.current) return;

            if (isAuthenticated && location.state?.selectThreadId && String(currentThreadId || '') !== String(location.state.selectThreadId)) {
                return;
            }

            const query = searchParams.get('q') || searchParams.get('message') || location.state?.q || pendingIntent?.q;
            const initialAttachments = location.state?.attachments;
            const wasVoice = location.state?.wasVoice;
            const redirectedClientIntent = normalizeClientIntentType(location.state?.clientIntent || pendingIntent?.clientIntent || '');
            const redirectedSuppressDiagnosticTicket = Boolean(
                location.state?.suppressDiagnosticTicket ||
                pendingIntent?.suppressDiagnosticTicket ||
                redirectedClientIntent === 'parts' ||
                redirectedClientIntent === 'shop'
            );

            if (query || initialAttachments) {
                if (initialAttachments && !wasVoice) {
                    setAttachments(initialAttachments);
                }
                
                if (query || (wasVoice && initialAttachments)) {
                    sendMessage(query || '', initialAttachments, wasVoice, {
                        clientIntent: redirectedClientIntent || undefined,
                        suppressDiagnosticTicket: redirectedSuppressDiagnosticTicket,
                        vehicleId: location.state?.odometerVehicleId || pendingIntent?.odometerVehicleId || undefined,
                    });
                    if (pendingIntent?.q || pendingIntent?.clientIntent) clearPendingChatIntent();
                    window.history.replaceState({}, document.title);
                    navigate('/chat', { replace: true, state: null });
                }
            }

            hasProcessedInitialIntent.current = true;
        };

        init();
    }, [authState?.status, activeUserId, activeSessionId, isAuthenticated, currentThreadId, currentTicketThreadId, currentTicketId, location.state, onSelectThread]); // eslint-disable-line react-hooks/exhaustive-deps

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            audioChunksRef.current = [];
            recordingActionRef.current = 'discard';
            
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const nextAction = recordingActionRef.current;
                recordingActionRef.current = 'discard';
                stream.getTracks().forEach(track => track.stop());

                if (audioBlob.size === 0) {
                    audioChunksRef.current = [];
                    return;
                }

                if (nextAction === 'send') {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const base64 = reader.result.split(',')[1];
                        sendMessage('', [{
                            filename: 'voice_note.webm',
                            mime_type: 'audio/webm',
                            base64: base64
                        }], true);
                        audioChunksRef.current = [];
                    };
                    reader.readAsDataURL(audioBlob);
                    return;
                }

                if (nextAction === 'transcribe') {
                    await transcribeVoiceDraft(audioBlob);
                }

                audioChunksRef.current = [];
            };

            recorder.start();
            mediaRecorderRef.current = recorder;
            setIsRecording(true);
            setRecordingSeconds(0);
            recordingTimerRef.current = setInterval(() => {
                setRecordingSeconds(prev => prev + 1);
            }, 1000);
        } catch (err) {
            console.error("Error accessing microphone:", err);
            alert("Could not access microphone side. Please check permissions.");
        }
    };

    const transcribeVoiceDraft = async (audioBlob) => {
        setIsTranscribingVoiceDraft(true);
        setStatus('Transcribing your voice note...');

        try {
            const audio = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    try {
                        resolve({
                            filename: 'voice_note.webm',
                            mime_type: 'audio/webm',
                            base64: reader.result.split(',')[1]
                        });
                    } catch (error) {
                        reject(error);
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(audioBlob);
            });

            const requestUrl = isAuthenticated
                ? `${CHAT_API_BASE_URL}/secure/voice/transcribe`
                : `${CHAT_API_BASE_URL}/voice/transcribe`;

            const response = await fetch(requestUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getTesterModeHeaders() },
                credentials: isAuthenticated ? 'include' : 'same-origin',
                body: JSON.stringify({ audio }),
            });

            if (!response.ok) {
                throw new Error('Failed to transcribe voice note');
            }

            const data = await response.json();
            const transcript = (data?.transcript || '').trim();

            if (!transcript) {
                throw new Error('Voice transcription returned empty text');
            }

            setInput(transcript);
            setPendingVoiceDraft(true);
            setShouldAutoScroll(true);
            setStatus('');
            focusChatInput();
        } catch (error) {
            console.error('Voice transcription failed:', error);
            setStatus('');
            alert('Could not transcribe your voice note. Please try again.');
        } finally {
            setIsTranscribingVoiceDraft(false);
        }
    };

    const stopRecording = (action = 'discard') => {
        if (mediaRecorderRef.current && isRecording) {
            recordingActionRef.current = action;
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(recordingTimerRef.current);
        }
    };

    const toggleRecording = () => {
        if (isRecording) stopRecording('transcribe');
        else startRecording();
    };

    const sendRecording = () => {
        stopRecording('send');
    };

    const formatTime = (s) => {
        const mins = Math.floor(s / 60);
        const secs = s % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const hasGeneratedDiagnosticReport = (messageList = messages) => (
        hasGeneratedDiagnosticReportForMessages(messageList)
    );

    const getDiagnosticUserTurnCount = (messageList = messages) => (
        getDiagnosticUserTurnCountForMessages(messageList)
    );

    const canGenerateDiagnosticReport = (messageList = messages) => (
        canGenerateDiagnosticReportForMessages(messageList)
    );

    const isReportEligibleDiagnosticMessage = (message, messageList = messages) => {
        if (!message || message.role !== 'assistant') return false;
        if (isDiagnosticReportMessage(message)) return false;
        if (isNeedMoreDiagnosticsMessage(message)) return false;
        if (!canGenerateDiagnosticReport(messageList)) return false;
        if (!hasDiagnosticTicketContext(messageList)) return false;

        const content = extractSuggestionsAndContent(message.content).content;
        if (isReportReadyPromptMessage(content)) return true;
        // Robust conclusion signal (independent of exact wording): after the minimum
        // diagnostic turns, once CarFido STOPS asking a diagnostic "Question:" — i.e. it has
        // named the likely cause / next step instead of gathering more symptoms — the
        // diagnosis has concluded, so the report should generate. This catches phrasings
        // the finalized-pattern misses (e.g. "Most likely fix: …", "The next step is …").
        const stillAskingDiagnosticQuestion = /(^|\n)\s*question\s*:/i.test(content || '');
        if (!stillAskingDiagnosticQuestion) return true;
        return false;
    };

    const getNeedMoreDiagnosticsMessage = () => (
        // Confidence-driven: we don't quote a fixed number of remaining answers — CarFido keeps
        // asking until it's confident in the cause. Wording must stay matchable by
        // NEED_MORE_DIAGNOSTICS_PATTERN.
        "I want to be confident in the diagnosis first — let me ask a couple more questions to narrow down the cause before generating the report."
    );

    const appendNeedMoreDiagnosticsMessage = (placeholderId = null) => {
        setMessages(prev => {
            const nextMessages = placeholderId
                ? prev.filter(message => message.id !== placeholderId)
                : [...prev];
            if (canGenerateDiagnosticReportForMessages(nextMessages)) return nextMessages;

            const content = getNeedMoreDiagnosticsMessage(nextMessages);
            const lastIndex = nextMessages.length - 1;
            const lastMessage = lastIndex >= 0 ? nextMessages[lastIndex] : null;
            if (isNeedMoreDiagnosticsMessage(lastMessage)) {
                return nextMessages.map((message, index) => (
                    index === lastIndex ? { ...message, content } : message
                ));
            }

            return [
                ...nextMessages,
                {
                    id: `diagnostic-needs-more-${Date.now()}`,
                    role: 'assistant',
                    content,
                },
            ];
        });
        if (activeReportPlaceholderIdRef.current === placeholderId) {
            activeReportPlaceholderIdRef.current = null;
        }
        setShouldAutoScroll(true);
    };

    const buildForcedDiagnosticReport = useCallback((messageList = messages) => {
        const anchorMessage = getLatestDiagnosticReportAnchorMessage(messageList);
        if (!anchorMessage) return null;

        return createDiagnosticReport({
            messages: messageList,
            reportMessage: anchorMessage,
            vehicleLabel: activeVehicleLabel,
        });
    }, [messages, activeVehicleLabel]);

    const insertReportLoadingMessage = ({ includeTriggerMessage = false, status: loadingStatus = 'Generating diagnostic report...' } = {}) => {
        const loadingMessage = createReportLoadingMessage(loadingStatus);
        activeReportPlaceholderIdRef.current = loadingMessage.id;
        setMessages(prev => {
            const nextMessages = [...prev];
            if (includeTriggerMessage) {
                nextMessages.push({
                    id: `generate-report-${Date.now()}`,
                    role: 'user',
                    content: 'Generate diagnostic report',
                });
            }
            nextMessages.push(loadingMessage);
            return nextMessages;
        });
        setShouldAutoScroll(true);
        return loadingMessage.id;
    };

    const updateReportLoadingMessage = (placeholderId, loadingStatus) => {
        if (!placeholderId) return;
        setMessages(prev => prev.map(message => (
            message.id === placeholderId && message.isLoadingReport
                ? { ...message, status: loadingStatus || message.status }
                : message
        )));
    };

    const removeReportLoadingMessage = (placeholderId) => {
        if (!placeholderId) return;
        setMessages(prev => prev.filter(message => message.id !== placeholderId));
        if (activeReportPlaceholderIdRef.current === placeholderId) {
            activeReportPlaceholderIdRef.current = null;
        }
    };

    const revealReportCardAfterPaint = useCallback(() => {
        // Anchor the NEW symptom report at its TOP (aligned with the user's last input) so the
        // user reads it from the beginning — the standard chat/document pattern — instead of
        // jumping to the bottom of a long report. Disable auto-scroll-to-bottom so it doesn't
        // fight this alignment.
        setShouldAutoScroll(false);
        if (typeof window === 'undefined') return;
        const alignReportTop = () => {
            const container = scrollContainerRef.current;
            // The generated report usually renders as a normal message (appended to `messages`),
            // NOT the separate live-card block — so the ref alone misses it. Find the report card
            // by its data attribute (last one = the newest), falling back to the live-card ref.
            let card = liveReportCardRef.current;
            if (container) {
                const cards = container.querySelectorAll('[data-diagnostic-report-card]');
                if (cards.length) card = cards[cards.length - 1];
            }
            // Card not painted yet — do nothing now; the nested rAF / timeout retries catch it
            // once it's in the DOM (avoids a brief jump-to-bottom flash).
            if (!card) return;
            if (!container) {
                card.scrollIntoView({ behavior: 'auto', block: 'start' });
                return;
            }
            const containerRect = container.getBoundingClientRect();
            const cardRect = card.getBoundingClientRect();
            // Leave a little room above so the user's last input stays visible just above the report.
            const nextTop = container.scrollTop + cardRect.top - containerRect.top - 16;
            container.scrollTo({ top: Math.max(0, nextTop), behavior: 'auto' });
        };
        window.requestAnimationFrame(() => {
            alignReportTop();
            window.requestAnimationFrame(alignReportTop);
        });
        window.setTimeout(alignReportTop, 120);
    }, [setShouldAutoScroll, scrollContainerRef]);

    useEffect(() => {
        if (liveDiagnosticReportCard?.diagnosticReport) {
            revealReportCardAfterPaint();
        }
    }, [liveDiagnosticReportCard, revealReportCardAfterPaint]);

    const appendDiagnosticReportMessage = (report, options = {}) => {
        const { includeTriggerMessage = false, placeholderId = null, eligibilityMessages = messages } = options;
        const normalizedReport = normalizeDiagnosticReportForUi(report);
        if (!normalizedReport) {
            removeReportLoadingMessage(placeholderId);
            return;
        }
        // Once a report object exists, render it. The readiness gates already run
        // before generation starts; re-checking here can race against stale state and
        // leave the live chat empty until refresh restores the persisted report card.
        if (!normalizedReport.id && !canGenerateDiagnosticReport(eligibilityMessages)) {
            appendNeedMoreDiagnosticsMessage(placeholderId);
            return;
        }

        saveDiagnosticReport(normalizedReport);
        markPwaEngagement('diagnostic-report');
        const reportCard = createReportCardMessage(normalizedReport);
        saveStoredReportCard([...getReportStorageKeys(), ...getReportCardAliasKeys(normalizedReport)], reportCard);
        if (currentThreadId) saveThreadStateToServer(currentThreadId, { report_card: reportCard });
        notifyDiagnosticTicketChanged();
        setLiveDiagnosticReportCard(reportCard);
        setMessages(prev => {
            if (placeholderId && prev.some(message => message.id === placeholderId)) {
                if (activeReportPlaceholderIdRef.current === placeholderId) {
                    activeReportPlaceholderIdRef.current = null;
                }
                return prev.map(message => (
                    message.id === placeholderId ? reportCard : message
                ));
            }
            if (prev.some(message => message.diagnosticReport?.id === normalizedReport.id)) return prev;
            const nextMessages = [...prev];
            if (includeTriggerMessage) {
                nextMessages.push({
                    id: `generate-report-${Date.now()}`,
                    role: 'user',
                    content: 'Generate diagnostic report',
                });
            }
            nextMessages.push(reportCard);
            return nextMessages;
        });
        revealReportCardAfterPaint();
    };

    // True when CarFido's last message was the maintenance next-step offer (do-it-yourself vs
    // check-if-due), so a following "Walk me through DIY" reply builds the maintenance report.
    const lastAssistantOfferedMaintenance = (messageList = messages) => {
        for (let i = messageList.length - 1; i >= 0; i -= 1) {
            const m = messageList[i];
            if (m?.role !== 'assistant') continue;
            const c = (m.content || '').toLowerCase();
            const hits = ['walk you through', 'doing it yourself', 'yourself', 'actually due', 'check whether it', 'help you check']
                .filter((frag) => c.includes(frag)).length;
            return hits >= 2;
        }
        return false;
    };

    // True when the user picked the DO-IT-YOURSELF option after that offer (not "check if due").
    const isMaintenanceDiyChoice = (text, messageList = messages) => {
        if (!lastAssistantOfferedMaintenance(messageList)) return false;
        const t = (text || '').trim().toLowerCase().replace(/[?.!]+$/, '');
        if (!t || t.split(/\s+/).length > 8) return false;
        return /\b(walk me through|show me how|do it myself|myself|diy|step[- ]?by[- ]?step|the steps|how (?:do|to) (?:i )?do)\b/.test(t);
    };

    // Persist client-only chat bubbles (e.g. an explicit "give me diagnostic report"
    // request that we handle locally and never stream to the backend) into the thread's
    // server history, so they survive a reload instead of vanishing. Fire-and-forget:
    // never blocks report generation, and silently no-ops without an authenticated thread.
    const persistThreadMessages = async (threadId, msgs) => {
        if (!isAuthenticated || !threadId || !Array.isArray(msgs) || msgs.length === 0) return;
        try {
            await fetch(
                `${CHAT_API_BASE_URL}/secure/threads/${encodeURIComponent(threadId)}/messages`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getTesterModeHeaders() },
                    credentials: 'include',
                    body: JSON.stringify({ messages: msgs }),
                }
            );
            refreshThreadTitles?.();
        } catch (persistErr) {
            console.warn('Persisting report-request message to thread failed:', persistErr);
        }
    };

    // Returns true when the message was the promo code and has been answered here.
    const handlePromoCodeMessage = async (code, wasVoice) => {
        isLoadingRef.current = true;
        let result = null;
        try {
            const response = await fetch(`${API_BASE_URL}/api/promo/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ code }),
            });
            if (response.ok) result = await response.json();
        } catch (err) {
            console.warn('Promo code check failed:', err);
        } finally {
            isLoadingRef.current = false;
        }
        const reply = promoReplyText(result);
        if (!reply) return false;

        setInput('');
        setMessages(prev => [
            ...prev,
            {
                id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                role: 'user',
                content: code,
                isVoice: wasVoice,
                timestamp: new Date().toISOString(),
            },
            {
                id: `promo-${Date.now()}`,
                role: 'assistant',
                content: reply,
                suppressDiagnosticTicketCard: true,
                timestamp: new Date().toISOString(),
            },
        ]);
        setShouldAutoScroll(true);
        const turns = [
            { role: 'user', content: code, message_type: 'chat' },
            { role: 'assistant', content: reply, message_type: 'chat' },
        ];
        if (result.status === 'login_required') {
            // The reply tells them to log in. ChatLayout redeems the code once they have
            // logged in and completed their profile, and continues this conversation.
            setPendingPromoCode(code);
        } else if (currentThreadId) {
            persistThreadMessages(currentThreadId, turns);
        } else {
            // No chat yet: save the exchange as its own conversation so it stays.
            openPromoConversation?.(turns);
        }
        return true;
    };

    const sendMessage = async (content, manualAttachments = null, wasVoice = false, options = {}) => {
        if (!content.trim() && !manualAttachments && attachments.length === 0 && !wasVoice) return;
        if (authState?.status === 'loading') return;
        if (isLoadingRef.current) return;
        const userMessage = content.trim();

        // Pilot promo code. Only a single code-shaped word is checked, and only the server
        // knows the real code; anything it does not recognise carries on to CarFido as a
        // normal message.
        if (!manualAttachments && attachments.length === 0 && looksLikePromoCode(userMessage)) {
            if (await handlePromoCodeMessage(userMessage, wasVoice)) return;
        }

        // Redirect a plain "add a vehicle" request to the guided Add Vehicle form
        // instead of handling it as a conversation. The form (opened on a fresh chat)
        // captures year/make/model/odometer + optional VIN lookup. Messages that already
        // carry a 17-char VIN keep the fast add path and are NOT redirected.
        // Detect an "add a vehicle" request. We DON'T redirect to a new chat — instead we
        // let the user's exact message get added to THIS chat below, then respond with the
        // guided form inline (so the conversation is preserved and the real input is shown).
        // If CarFido's previous turn asked which vehicle to remove (or to confirm a removal),
        // this reply is a delete answer — never open the Add Vehicle form for it, even when
        // the reply reads like a bare spec ("2020 Verna" from a disambiguation chip).
        let _lastAssistantText = '';
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i]?.role === 'assistant') { _lastAssistantText = String(messages[i].content || ''); break; }
        }
        const _isReplyToDeletePrompt = looksLikePendingDeletePrompt(_lastAssistantText);

        // If CarFido's previous turn offered to add a VIN it decoded from an uploaded
        // document, capture those decoded details so we can pre-fill the form and so
        // replies like "save vehicle to Garage" / "save the decoded VIN to my garage"
        // (not just "add vehicle") open the form.
        const _decodedVinPrefill = _isReplyToDeletePrompt ? null : extractDecodedVinPrefill(_lastAssistantText);
        const _recentVinPrefill = (!_isReplyToDeletePrompt && looksLikePendingAddVehiclePrompt(_lastAssistantText))
            ? extractRecentVinPrefillFromMessages(messages)
            : null;
        const _hasAddVehicleOffer = !_isReplyToDeletePrompt && (
            Boolean(_decodedVinPrefill && _decodedVinPrefill.vin) ||
            Boolean(_recentVinPrefill && _recentVinPrefill.vin) ||
            looksLikePendingAddVehiclePrompt(_lastAssistantText)
        );

        let _vehicleFormPrefill = null;
        let _shouldOpenAddVehicle = false;
        if (userMessage && !_isReplyToDeletePrompt) {
            const _isAddReq = isAddVehicleFormRequest(userMessage);
            const _isSaveDecodedReq = _hasAddVehicleOffer && isSaveDecodedVehicleReply(userMessage);
            if (_isAddReq || _isSaveDecodedReq) {
                _shouldOpenAddVehicle = true;
                // A VIN in the user's own message → pre-fill it (form auto-looks-up).
                const vinMatch = (userMessage.match(VIN_TOKEN_PATTERN) || [])[0];
                if (vinMatch) {
                    _vehicleFormPrefill = { vin: vinMatch.toUpperCase() };
                } else if (_isSaveDecodedReq) {
                    _vehicleFormPrefill = _decodedVinPrefill || _recentVinPrefill;
                } else {
                    _vehicleFormPrefill = parseVehicleFromAddRequest(userMessage);
                }
            }
        }

        if (_shouldOpenAddVehicle) {
            window.dispatchEvent(new CustomEvent('dash_open_add_vehicle', { detail: { prefill: _vehicleFormPrefill, userMessage } }));
            setInput('');
            setAttachments([]);
            setAttachmentError('');
            setStatus('');
            isLoadingRef.current = false;
            setIsLoading(false);
            return;
        }

        const requestedIntent = normalizeClientIntentType(options.clientIntent || options.intent || '');
        const normalizedIntentMessage = normalizeChatContextIntentText(userMessage);
        const inferredIntent = requestedIntent
            || (SHOP_CONTEXT_PATTERN.test(normalizedIntentMessage) ? 'shop' : '')
            || (PARTS_CONTEXT_PATTERN.test(normalizedIntentMessage) ? 'parts' : '');
        const inheritedIntent = normalizeClientIntentType(activeClientIntent?.type || '');
        const effectiveClientIntent = inferredIntent || (
            inheritedIntent && !shouldSwitchOutOfPartsOrShopIntent(userMessage)
                ? inheritedIntent
                : ''
        );
        const shouldSuppressDiagnosticTicketForSend = Boolean(
            options.suppressDiagnosticTicket ||
            effectiveClientIntent === 'parts' ||
            effectiveClientIntent === 'shop' ||
            effectiveClientIntent === 'service' ||
            effectiveClientIntent === 'maintenance'
        );
        if ((inferredIntent || effectiveClientIntent) && !['service', 'maintenance'].includes(effectiveClientIntent)) {
            const nextIntentType = inferredIntent || effectiveClientIntent;
            if (activeInlineDiagnosticTicket) {
                setActiveClientIntent(null);
            } else {
                setActiveClientIntent({
                    type: nextIntentType,
                    title: getIntentTitleFromMessage(userMessage, nextIntentType, activeVehicleLabel),
                    vehicleLabel: activeVehicleLabel,
                });
            }
        } else if (shouldSwitchOutOfPartsOrShopIntent(userMessage)) {
            setActiveClientIntent(null);
        }
        const isGuidedCardInterrupt = Boolean(guidedCheckSession?.activeCard) && Boolean(userMessage);
        const shouldRenderAfterGuidedSession = Boolean(guidedCheckSession) && Boolean(userMessage);

        if (
            (isReportCommand(userMessage) || isReportChoiceText(userMessage)) &&
            !canGenerateDiagnosticReport() &&
            !hasDiagnosticTicketContext(messages) &&
            // A message that ALSO describes a symptom ("My car pulls right. Give me a report")
            // must start a real diagnosis — let it fall through to the backend so a ticket is
            // created, the symptom is diagnosed, and the message is persisted. Only a BARE report
            // request (no symptom) shows the canned "need more info" note here.
            !reportRequestCarriesSymptom(userMessage)
        ) {
            // Bare report request with NO active diagnosis to answer questions in → show the local
            // "need more info" note. (When a diagnosis IS active we instead let the request reach
            // the backend below, so CarFido asks the actual next diagnostic question in THIS turn
            // rather than stalling with a canned "let me ask a couple more questions" and forcing
            // the user to reply 'sure' first.)
            setInput('');
            const bareReportUserMessage = {
                id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                role: 'user',
                content: userMessage,
                isVoice: wasVoice,
                timestamp: new Date().toISOString(),
            };
            setMessages(prev => [...prev, bareReportUserMessage]);
            // Persist the request so the bubble survives a reload (this branch never streams to
            // the backend). No-ops safely when there is no authenticated thread yet.
            persistThreadMessages(currentThreadId, [
                { role: 'user', content: userMessage, message_type: 'chat' },
            ]);
            appendNeedMoreDiagnosticsMessage();
            return;
        }

        if (isReportChoiceText(userMessage) && !hasGeneratedDiagnosticReport()) {
            setInput('');
            // Keep the user's sent message visible alongside CarFido's rejection reply.
            setMessages(prev => [
                ...prev,
                {
                    id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    role: 'user',
                    content: userMessage,
                    isVoice: wasVoice,
                    timestamp: new Date().toISOString(),
                },
                {
                    id: `report-required-${Date.now()}`,
                    role: 'assistant',
                    content: 'Generate the diagnostic report first. After the report is generated, I can create a DIY steps report.',
                },
            ]);
            setShouldAutoScroll(true);
            return;
        }

        // Explicit "generate report" (any phrasing) during a diagnosis → build the DIAGNOSTIC
        // report card. When the DRIVER explicitly asks for it, honor the request and generate
        // from the information available AT THAT TIME — do NOT keep asking more questions. We
        // only require a minimum evidence floor (a few real answers); the 90%-confidence gate is
        // for AUTO-generation and must not block an explicit request. Never send this to the
        // backend chat (which would produce a PDF vehicle report) — we build the report directly.
        if (
            isReportCommand(userMessage) &&
            hasDiagnosticTicketContext(messages) &&
            !hasGeneratedDiagnosticReport() &&
            (
                canGenerateDiagnosticReport() ||
                getDiagnosticUserTurnCountForMessages(messages) >= REPORT_MIN_FLOOR_TURNS ||
                // Driver answered AND asked for the report together (so user-turns undercount) —
                // if the diagnosis has genuinely progressed (CarFido asked ≥2 questions), honor it.
                getDiagnosticAssistantQuestionCount(messages) >= 2
            )
        ) {
            setInput('');
            const reportTriggerMessage = {
                id: Math.random().toString(36).substr(2, 9),
                role: 'user',
                content: userMessage,
                isVoice: wasVoice,
            };
            const nextMessages = [...messages, reportTriggerMessage];
            // Seed from the local transcript when possible; otherwise a minimal seed carrying the
            // thread is enough — resolveReportForOpen then asks the backend to generate the report
            // from the conversation so far (its own auto-generate covers "no report exists yet").
            const seedReport = buildForcedDiagnosticReport(nextMessages) || {
                threadId: currentThreadId || null,
                vehicle: activeVehicleLabel,
            };
            setMessages(prev => [...prev, reportTriggerMessage]);
            // Save the driver's report request into thread history so it stays visible after
            // a reload — this bubble is handled locally and never streamed to the backend.
            persistThreadMessages(currentThreadId, [
                { role: 'user', content: userMessage, message_type: 'chat' },
            ]);
            const placeholderId = insertReportLoadingMessage({ status: 'Generating diagnostic report...' });
            setShouldAutoScroll(true);
            try {
                const resolvedReport = await resolveReportForOpen({
                    ...seedReport,
                    threadId: currentThreadId || seedReport.threadId || null,
                });
                appendDiagnosticReportMessage(resolvedReport, { placeholderId, includeTriggerMessage: false });
            } catch (error) {
                removeReportLoadingMessage(placeholderId);
                if (error?.message !== 'PHONE_AUTH_EXPIRED') {
                    console.error('[Report] manual report generation failed', error);
                }
            }
            setShouldAutoScroll(true);
            return;
        }

        // Maintenance DIY choice → generate a diagnostic-style report for the scheduled service
        // (subject = the maintenance item, single likely-cause = the DIY steps + why) so the user
        // gets the steps PLUS the Find Parts / Find a Shop actions the report card carries. This is
        // routine maintenance, not a fault, so it is built client-side and bypasses the diagnostic
        // turn-count / ticket gates — appendDiagnosticReportMessage renders any report with an id.
        if (isMaintenanceDiyChoice(userMessage, messages)) {
            setInput('');
            const triggerMessage = {
                id: Math.random().toString(36).substr(2, 9),
                role: 'user',
                content: userMessage,
                isVoice: wasVoice,
                // Tagged non-'text' so the cards-only snapshot merge KEEPS this local-only bubble
                // across reload/switch (it is never sent to the server), while server history stays
                // authoritative for all normal text — preventing cross-thread text bleed.
                type: 'maintenance_diy_trigger',
            };
            const nextMessages = [...messages, triggerMessage];
            setMessages(prev => [...prev, triggerMessage]);
            // Ask the LLM for the vehicle-tailored DIY guide (steps/tools/parts/cost) rather than
            // using hardcoded steps. Show the report skeleton while it generates; if the call fails
            // or times out, createMaintenanceReport falls back to its built-in generic steps.
            const placeholderId = insertReportLoadingMessage({ status: 'Preparing your DIY guide...' });
            setShouldAutoScroll(true);
            let maintenanceGuide = null;
            try {
                const maintTitle = extractMaintenanceTitleFromMessages(nextMessages) || 'Scheduled maintenance';
                const resp = await fetch(`${API_BASE_URL}/api/maintenance-diy-guide`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title: maintTitle, vehicle_label: activeVehicleLabel }),
                });
                const data = await resp.json().catch(() => ({}));
                if (data?.status === 'success' && data.guide) maintenanceGuide = data.guide;
            } catch (guideErr) {
                console.warn('Maintenance DIY guide generation failed; using fallback steps:', guideErr);
            }
            const report = createMaintenanceReport({ messages: nextMessages, vehicleLabel: activeVehicleLabel, guide: maintenanceGuide });
            if (report) {
                // Stamp the owning thread so the guided-check session built from this report is
                // tagged with its thread. Without it the guided-session save guard (which compares
                // report.threadId to the current thread) is bypassed, and switching chats saves
                // this maintenance session under the OTHER thread — bleeding it into that chat.
                report.threadId = currentThreadId || report.threadId || null;
                appendDiagnosticReportMessage(report, { placeholderId, includeTriggerMessage: false });
            } else {
                removeReportLoadingMessage(placeholderId);
            }
            setShouldAutoScroll(true);
            return;
        }

        let requestUserMessage = userMessage;
        let displayUserMessage = userMessage;
        let effectiveThreadId = currentThreadId;
        let createdThreadForSend = false;
        let forceNewTicketForSend = false;
        const clearFirstSendThreadGuard = () => {
            if (firstSendThreadIdRef.current === String(effectiveThreadId || '')) {
                firstSendThreadIdRef.current = null;
            }
        };

        const getSameIssueRedirectTarget = (replyText) => {
            if (!isAuthenticated || !onSelectThread) return null;
            return resolveSameIssueRedirectTarget({
                replyText,
                messageList: messages,
                recentDiagnostics,
                currentThreadId,
            });
        };

        const queueSameIssueRedirect = (target, replyText, { includeUserMessage = false } = {}) => {
            if (!target) return false;
            setInput('');
            const timestamp = Date.now();
            setMessages((prev) => {
                const additions = [];
                if (includeUserMessage) {
                    additions.push({
                        id: `same-issue-user-${timestamp}`,
                        role: 'user',
                        content: replyText,
                        isVoice: wasVoice,
                    });
                }
                additions.push({
                    id: `same-issue-redirect-${timestamp}`,
                    role: 'assistant',
                    content: `Got it — this is the same issue. Taking you to your existing "${target.redirectTitle}" conversation so we can pick up where you left off…`,
                });
                return [...prev, ...additions];
            });
            setShouldAutoScroll(true);
            // Keep the notice on screen long enough to read, THEN switch chats.
            setTimeout(() => onSelectThread(target.matchedThreadId), 6000);
            return true;
        };

        // If CarFido asked "same or new?" and the user chose SAME issue:
        //  - ticket is OPEN in another chat → redirect to that issue's original chat.
        //  - ticket is CLOSED (or already this chat) → continue resuming here.
        {
            const sameIssueRedirectTarget = getSameIssueRedirectTarget(userMessage);
            if (queueSameIssueRedirect(sameIssueRedirectTarget, userMessage, { includeUserMessage: true })) {
                return;
            }
            // Closed ticket, or it already lives in this chat → fall through and
            // resume the diagnosis here as before.
        }

        // "New issue" answer to a "same or new?" prompt: send it straight through in the
        // SAME chat and show the user's ACTUAL choice ("New issue") as their bubble — do NOT
        // resend the original symptom text. The backend's confirmation flow opens a fresh
        // ticket seeded with the original symptom and diagnoses it, so display and stored
        // content stay consistent (no duplicated "car wont start" bubble).
        const shouldStartSeparateIssueThread = (
            isAuthenticated &&
            Boolean(effectiveThreadId) &&
            isNewIssueConfirmationText(userMessage) &&
            findLastNewIssueClarificationPromptIndex(messages) >= 0
        );

        if (
            !shouldStartSeparateIssueThread &&
            !isReportCommand(userMessage) &&
            !isReportChoiceText(userMessage) &&
            hasDiagnosticTicketContext(messages) &&
            !hasGeneratedDiagnosticReport() &&
            getDiagnosticUserTurnCount(messages) >= MAX_DIAGNOSTIC_USER_TURNS_BEFORE_FORCED_REPORT
        ) {
            const forcedUserMessage = {
                id: Math.random().toString(36).substr(2, 9),
                role: 'user',
                content: userMessage,
                isVoice: wasVoice,
            };
            const nextMessages = [...messages, forcedUserMessage];
            const forcedReport = buildForcedDiagnosticReport(nextMessages);

            setInput('');
            if (forcedReport) {
                setMessages((prev) => {
                    const finalMessages = [...prev, forcedUserMessage];
                    return finalMessages;
                });
                // Persist this answer (which triggered the auto-report) to thread history so it
                // doesn't vanish on reload — the block returns early and never streams it.
                persistThreadMessages(currentThreadId, [
                    { role: 'user', content: userMessage, message_type: 'chat' },
                ]);
                // Show the loading skeleton during the resolve so the user always sees progress.
                const placeholderId = insertReportLoadingMessage({ status: 'Generating diagnostic report...' });
                setShouldAutoScroll(true);
                try {
                    const resolvedForcedReport = await resolveReportForOpen({
                        ...forcedReport,
                        threadId: currentThreadId || forcedReport.threadId || null,
                    });
                    appendDiagnosticReportMessage(resolvedForcedReport, { placeholderId, includeTriggerMessage: false });
                } catch (error) {
                    removeReportLoadingMessage(placeholderId);
                    if (error?.message !== 'PHONE_AUTH_EXPIRED') {
                        console.error('[Report] forced turn-limit report generation failed', error);
                    }
                }
            } else {
                setMessages((prev) => [
                    ...prev,
                    forcedUserMessage,
                    {
                        id: `forced-report-fallback-${Date.now()}`,
                        role: 'assistant',
                        content: "I have enough information now. Generate the diagnostic report from the current findings.",
                    },
                ]);
            }
            setShouldAutoScroll(true);
            return;
        }

        // For authenticated users, auto-create a thread if none exists then continue sending.
        // Activate it only after this component marks the thread as owned by this
        // first send; otherwise the history-restore effect can load the empty thread
        // and clear the optimistic first user message while the response is streaming.
        if (isAuthenticated && !effectiveThreadId) {
            if (onNewThread) {
                isLoadingRef.current = true;
                setIsLoading(true);
                const optimisticTitle = (!wasVoice && requestUserMessage && !isDiagnoseEntryPrompt(requestUserMessage))
                    ? requestUserMessage.slice(0, 60)
                    : null;
                const newThread = await onNewThread(optimisticTitle, { activate: false });
                if (newThread?.thread_id) {
                    effectiveThreadId = String(newThread.thread_id);
                    createdThreadForSend = true;
                    // The new thread starts with this message. Do not let the
                    // previous selected thread's in-memory chat bleed into it.
                    lastLoadedIdentityRef.current = `thread:${effectiveThreadId}`;
                    // Re-tag which thread the visible messages belong to, so the snapshot-write
                    // effect stores THIS new chat under its own key (and never under the chat we
                    // came from). Without this the snapshot could be skipped or mis-scoped.
                    messagesStorageKeyRef.current = `thread:${effectiveThreadId}`;
                    firstSendThreadIdRef.current = effectiveThreadId;
                    setMessages([]);
                    setActiveMessage(null);
                    setGuidedCheckSession(null);
                    setHasMore(false);
                    setOffset(0);
                    if (onSelectThread) {
                        onSelectThread(effectiveThreadId);
                    }
                } else {
                    isLoadingRef.current = false;
                    setIsLoading(false);
                    return; // thread creation failed
                }
            } else {
                return;
            }
        }
        if (!isAuthenticated && (!activeSessionId || !activeUserId)) return;

        if (!isAuthenticated) {
            refreshAnonymousSessionActivity();
        } else if (authState?.identity) {
            refreshAuthenticatedSessionActivity(authState.identity);
        }

        const messageStorageKey = getActiveChatStorageKey(effectiveThreadId);
        const userMsgId = Math.random().toString(36).substr(2, 9);
        const outgoingAttachments = manualAttachments || (attachments.length > 0 ? attachments : []);
        const outgoingAttachmentPreviews = wasVoice
            ? []
            : outgoingAttachments.map(buildAttachmentPreview).filter(Boolean);
        setInput('');
        if (!manualAttachments) {
            setAttachments([]);
        }
        setAttachmentError('');
        if (isGuidedCardInterrupt && !createdThreadForSend) {
            setGuidedCheckSession((prev) => prev ? {
                ...prev,
                phase: 'paused',
                activeCard: null,
                timeline: [
                    ...prev.timeline,
                    createGuidedTextEntry('assistant', 'Guided check paused. I will answer your message first.'),
                ],
            } : prev);
        }
        const outgoingUserMessage = {
            id: userMsgId,
            role: 'user',
            content: displayUserMessage,
            isVoice: wasVoice,
            attachments: outgoingAttachmentPreviews,
            afterGuidedSession: createdThreadForSend ? false : shouldRenderAfterGuidedSession,
            timestamp: new Date().toISOString(),
        };
        // On an automatic retry (the previous stream produced no output), the user's
        // message is already on screen — don't add a duplicate bubble. Same for an
        // add-vehicle resume: the original issue is already in the thread above, so we
        // re-send it to get a diagnosis WITHOUT echoing a second user bubble.
        if (!options?._autoRetried && !options?.resumeAfterAddVehicle) {
            cachePendingUserMessage(outgoingUserMessage, messageStorageKey);
            setMessages(prev => createdThreadForSend ? [outgoingUserMessage] : [...prev, outgoingUserMessage]);
        }
        setShouldAutoScroll(true);
        setStatus('Reviewing your request...');

        const initController = new AbortController();
        let eventSource = null;
        let streamState = null;
        let assistantMsgId = null;
        let stopHandled = false;
        let clearStreamIdleTimer = () => {};
        let appendFinalAssistantMessage = null;

        function clearActiveResponseStop() {
            if (activeResponseStopRef.current === stopActiveResponse) {
                activeResponseStopRef.current = null;
            }
        }

        function stopActiveResponse() {
            if (stopHandled) return;
            stopHandled = true;
            initController.abort();
            clearStreamIdleTimer();

            if (streamState) {
                streamState.finalized = true;
            }

            if (eventSource) {
                canceledEventSourcesRef.current.add(eventSource);
                eventSource.close();
                canceledEventSourcesRef.current.delete(eventSource);
                if (activeEventSourceRef.current === eventSource) {
                    activeEventSourceRef.current = null;
                }
            }

            const parsedContent = streamState
                ? extractSuggestionsAndContent(cleanInternalTags(streamState.content))
                : { content: '', suggestions: [] };
            const hasPartialAssistantMessage = Boolean(streamState && (
                parsedContent.content ||
                streamState.audioUrl ||
                streamState.audioUrls?.length > 0 ||
                streamState.pdfUrl ||
                streamState.gloveboxDocs ||
                streamState.partsData
            ));

            if (hasPartialAssistantMessage && appendFinalAssistantMessage && assistantMsgId) {
                appendFinalAssistantMessage({
                    id: assistantMsgId,
                    role: 'assistant',
                    content: parsedContent.content,
                    replyToUserContent: displayUserMessage,
                    audioUrl: streamState.audioUrl,
                    audioUrls: streamState.audioUrls,
                    gloveboxDocs: streamState.gloveboxDocs,
                    partsData: streamState.partsData,
                    pdfUrl: streamState.pdfUrl,
                    mapUrl: streamState.mapUrl,
                    suggestions: streamState.suggestions.length > 0 ? streamState.suggestions : parsedContent.suggestions,
                    type: 'text',
                    afterGuidedSession: streamState.afterGuidedSession,
                });
            } else if (streamState?.reportPlaceholderId) {
                removeReportLoadingMessage(streamState.reportPlaceholderId);
            }

            setActiveMessage(null);
            setStopAssistantAudioSignal(value => value + 1);
            setPlayingAudioId(null);
            isLoadingRef.current = false;
            setIsLoading(false);
            setStatus('');
            clearActiveResponseStop();
            clearFirstSendThreadGuard();
            focusChatInput();
        }

        activeResponseStopRef.current = stopActiveResponse;
        isLoadingRef.current = true;
        setIsLoading(true);

        try {
            const requestUrl = isAuthenticated
                ? `${CHAT_API_BASE_URL}/secure/stream/init`
                : `${CHAT_API_BASE_URL}/stream/init`;
            const requestBody = isAuthenticated
                ? {
                    message: requestUserMessage,
                    thread_id: effectiveThreadId || undefined,
                    chat_session_id: effectiveThreadId ? undefined : activeSessionId,
                    // The vehicle this conversation is about — authoritative for the backend so
                    // answers/diagnostics/parts/service stay on the selected car, not a stale global.
                    // options.vehicleId wins when a caller targets a specific car (e.g. Garage
                    // "Add Odometer" for a non-active vehicle) — avoids a race where activeVehicleInfo
                    // hasn't refetched yet and a stale id would update the wrong vehicle.
                    vehicle_id: options?.vehicleId || (currentThread?.vehicle_id ?? activeVehicleInfo?.vehicle_id) || undefined,
                    force_new_ticket: forceNewTicketForSend || undefined,
                    client_intent: effectiveClientIntent || undefined,
                    suppress_diagnostic_ticket: shouldSuppressDiagnosticTicketForSend || undefined,
                    was_voice: wasVoice,
                    resume_after_add_vehicle: options?.resumeAfterAddVehicle || undefined,
                    attachments: outgoingAttachments.length > 0 ? outgoingAttachments : undefined,
                }
                : {
                    user_id: activeUserId,
                    session_id: activeSessionId,
                    message: requestUserMessage,
                    force_new_ticket: forceNewTicketForSend || undefined,
                    client_intent: effectiveClientIntent || undefined,
                    suppress_diagnostic_ticket: shouldSuppressDiagnosticTicketForSend || undefined,
                    was_voice: wasVoice,
                    resume_after_add_vehicle: options?.resumeAfterAddVehicle || undefined,
                    attachments: outgoingAttachments.length > 0 ? outgoingAttachments : undefined,
                };

            const response = await fetch(requestUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getTesterModeHeaders() },
                credentials: isAuthenticated ? 'include' : 'same-origin',
                signal: initController.signal,
                body: JSON.stringify(requestBody),
            });

            if (response.status === 401 && isAuthenticated) {
                throw new Error('PHONE_AUTH_EXPIRED');
            }
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.detail || errorData?.message || errorData?.error || 'Failed to initialize chat');
            }
            const { stream_id, transcript } = await response.json();

            // Update user message with transcript if we got one back
            if (wasVoice && transcript) {
                cachePendingUserMessage({
                    ...outgoingUserMessage,
                    content: transcript,
                    isVoice: true,
                }, messageStorageKey);
                setMessages(prev => prev.map(m => m.id === userMsgId ? { ...m, content: transcript } : m));

                const voiceSameIssueRedirectTarget = getSameIssueRedirectTarget(transcript);
                if (queueSameIssueRedirect(voiceSameIssueRedirectTarget, transcript)) {
                    isLoadingRef.current = false;
                    setIsLoading(false);
                    setStatus('');
                    setActiveMessage(null);
                    clearActiveResponseStop();
                    focusChatInput();
                    return;
                }
            }

            const streamUrl = isAuthenticated
                ? `${CHAT_API_BASE_URL}/secure/stream/${stream_id}`
                : `${CHAT_API_BASE_URL}/stream/${stream_id}`;
            eventSource = new EventSource(streamUrl, isAuthenticated ? { withCredentials: true } : undefined);
            activeEventSourceRef.current = eventSource;
            assistantMsgId = Math.random().toString(36).substr(2, 9);
            const streamStorageKey = messageStorageKey;

            // To hold real-time data avoiding closure traps on 'done'
            streamState = {
                content: '',
                suggestions: [],
                audioUrl: null,
                audioUrls: [],
                gloveboxDocs: null,
                // Parts catalogue pushed by the `parts_data` stream event, straight from the
                // API. Held here so the 'done' handler can attach it to the final message.
                partsData: null,
                pdfUrl: null,
                document: null,
                mapUrl: null,
                reportPlaceholderId: null,
                suppressReportStreaming: false,
                preReportMessage: null,
                preReportMessagePersisted: false,
                afterGuidedSession: shouldRenderAfterGuidedSession,
                doneReceived: false,
                finalized: false,
                idleTimer: null,
                // Independent watchdog for the "SSE connected but produced nothing"
                // failure: server keepalive pings keep the idle timer alive forever, so
                // we track whether any REAL output arrived and time out separately if not.
                gotRealOutput: false,
                firstOutputTimer: null,
            };

            appendFinalAssistantMessage = (message) => {
                const parsedShopResult = parseShopResultBlock(message.content || '');
                const finalMessage = {
                    ...message,
                    role: 'assistant',
                    type: message.type || 'text',
                    timestamp: message.timestamp || new Date().toISOString(),
                    shopResult: message.shopResult || parsedShopResult || null,
                };
                const finalVisibleContent = extractSuggestionsAndContent(finalMessage.content || '').content;
                const finalMessageSignalsReportReady = (
                    isReportReadyPromptMessage(finalVisibleContent) ||
                    isDiagnosticFinalizedMessage({ ...finalMessage, content: finalVisibleContent })
                );
                if (!shouldSuppressDiagnosticTicketForSend && finalMessageSignalsReportReady) {
                    [
                        currentThreadId ? `thread:${currentThreadId}` : null,
                        activeReportStorageKey,
                        streamStorageKey,
                        'default',
                    ].filter(Boolean).forEach((key) => liveReportReadyAttemptKeysRef.current.add(key));
                }
                setMessages(prev => {
                    let messageToStore = finalMessage;
                    if (isLegacyDiagnosticPdfMessage(finalMessage)) {
                        const snapshotMessages = [
                            ...prev.filter(existing => (
                                existing.id !== streamState.reportPlaceholderId &&
                                !existing.isLoadingReport
                            )),
                            finalMessage,
                        ];
                        const snapshotReport = normalizeDiagnosticReportForUi({
                            ...createDiagnosticReport({
                                messages: snapshotMessages,
                                reportMessage: finalMessage,
                                vehicleLabel: activeVehicleLabel,
                            }),
                            pdfUrl: finalMessage.pdfUrl,
                        });
                        messageToStore = createReportCardMessage(snapshotReport);
                        saveDiagnosticReport(snapshotReport);
                        notifyDiagnosticTicketChanged();
                    }

                    cachePendingAssistantMessage(messageToStore, streamStorageKey);
                    if (streamState.reportPlaceholderId && prev.some(existing => existing.id === streamState.reportPlaceholderId)) {
                        if (activeReportPlaceholderIdRef.current === streamState.reportPlaceholderId) {
                            activeReportPlaceholderIdRef.current = null;
                        }
                        return prev.map(existing => (
                            existing.id === streamState.reportPlaceholderId ? messageToStore : existing
                        ));
                    }
                    const alreadyExists = prev.some(existing => existing.id === messageToStore.id);
                    return alreadyExists ? prev : [...prev, messageToStore];
                });
            };

            clearStreamIdleTimer = () => {
                if (streamState.idleTimer) {
                    clearTimeout(streamState.idleTimer);
                    streamState.idleTimer = null;
                }
                if (streamState.firstOutputTimer) {
                    clearTimeout(streamState.firstOutputTimer);
                    streamState.firstOutputTimer = null;
                }
            };

            // Mark that the stream produced REAL output (chunk/status/done/report/etc.),
            // as opposed to bare keepalive pings. Cancels the first-output watchdog.
            const markRealOutput = () => {
                streamState.gotRealOutput = true;
                if (streamState.firstOutputTimer) {
                    clearTimeout(streamState.firstOutputTimer);
                    streamState.firstOutputTimer = null;
                }
            };

            const scheduleStreamIdleTimer = () => {
                clearStreamIdleTimer();
                streamState.idleTimer = setTimeout(() => {
                    if (streamState.doneReceived || streamState.finalized) return;
                    streamState.finalized = true;
                    eventSource.close();
                    if (activeEventSourceRef.current === eventSource) {
                        activeEventSourceRef.current = null;
                    }
                    isLoadingRef.current = false;
                    setActiveMessage(null);
                    setIsLoading(false);
                    clearActiveResponseStop();
                    clearFirstSendThreadGuard();

                    const parsedContent = extractSuggestionsAndContent(cleanInternalTags(streamState.content));
                    if (parsedContent.content || streamState.audioUrl || streamState.pdfUrl || streamState.gloveboxDocs || streamState.partsData) {
                        setStatus('');
                        appendFinalAssistantMessage({
                            id: assistantMsgId,
                            role: 'assistant',
                            content: parsedContent.content,
                            replyToUserContent: displayUserMessage,
                            audioUrl: streamState.audioUrl,
                            audioUrls: streamState.audioUrls,
                            gloveboxDocs: streamState.gloveboxDocs,
                            partsData: streamState.partsData,
                            pdfUrl: streamState.pdfUrl,
                            mapUrl: streamState.mapUrl,
                            suggestions: streamState.suggestions.length > 0 ? streamState.suggestions : parsedContent.suggestions,
                            type: 'text',
                            afterGuidedSession: streamState.afterGuidedSession,
                        });
                    } else {
                        if (streamState.reportPlaceholderId) {
                            removeReportLoadingMessage(streamState.reportPlaceholderId);
                        }
                        // True stall: no output and no heartbeat for the whole idle window.
                        // Restore the message so the user can resend with one tap.
                        setStatus('CarFido is taking longer than usual. Tap send to try again.');
                        if (displayUserMessage) setInput(displayUserMessage);
                    }
                }, 90000);
            };

            setActiveMessage({ 
                id: assistantMsgId, 
                role: 'assistant', 
                content: '', 
                type: 'text',
                audioUrls: [],
                isWaitingForAudio: wasVoice,
                afterGuidedSession: shouldRenderAfterGuidedSession,
            });

            // Heartbeat from the backend during long silent phases (e.g. diagnostic
            // ticket classification). Resets the idle timer so a legitimately long turn
            // isn't mistaken for a dropped connection. No UI change.
            eventSource.addEventListener('ping', () => {
                // Pings keep a legitimately long turn alive — but ONLY once we've seen
                // real output. Before the first real event, pings must NOT reset the
                // idle timer, or a failed SSE handoff (pings but no content) hangs forever.
                if (streamState.gotRealOutput) scheduleStreamIdleTimer();
            });

            // First-output watchdog: if the stream sends no real output within this window
            // (only keepalive pings), the SSE handoff failed — surface a retry instead of
            // hanging. Real turns emit a status pulse well within this window, which cancels it.
            // The very first send on a brand-new thread is where the produce/consume handoff
            // most often races (thread just created, consumer connects a beat too early), so it
            // recovers on a much shorter window — otherwise the driver waits 30s on their first
            // message, assumes it failed, and re-sends by hand (a lost first reply). A real
            // first turn still emits its status pulse within ~1–2s, so this never cuts one short.
            const firstOutputWindowMs = createdThreadForSend ? 12000 : 30000;
            streamState.firstOutputTimer = setTimeout(() => {
                if (streamState.gotRealOutput || streamState.doneReceived || streamState.finalized) return;
                streamState.finalized = true;
                try { eventSource.close(); } catch { /* noop */ }
                if (activeEventSourceRef.current === eventSource) activeEventSourceRef.current = null;
                clearStreamIdleTimer();
                isLoadingRef.current = false;
                setActiveMessage(null);
                setIsLoading(false);
                clearActiveResponseStop();
                clearFirstSendThreadGuard();
                if (streamState.reportPlaceholderId) removeReportLoadingMessage(streamState.reportPlaceholderId);

                // Hands-free recovery: the SSE handoff produced ZERO output (transient race —
                // the consume connection never delivered anything, so the backend almost
                // certainly never processed this turn). Retry the same send ONCE automatically,
                // reusing the existing user bubble (no duplicate). This includes IMAGE/attachment
                // turns — re-send the SAME attachments so the photo isn't dropped on retry. It is
                // safe precisely because nothing came back: there is no partial result to duplicate.
                // (A turn that DID process would have emitted a status pulse, cancelling this
                // watchdog before it fired.) Only fall back to a manual prompt if the retry also
                // produces nothing.
                const canAutoRetry = !options?._autoRetried && (Boolean(content) || outgoingAttachments.length > 0);
                if (canAutoRetry) {
                    console.warn('[SSE] No output within watchdog window — auto-retrying once (attachments:', outgoingAttachments.length, ').');
                    setStatus('Reconnecting…');
                    sendMessage(
                        content,
                        outgoingAttachments.length > 0 ? outgoingAttachments : null,
                        wasVoice,
                        { ...(options || {}), _autoRetried: true },
                    );
                    return;
                }
                console.error('[SSE] No output within watchdog window after auto-retry — surfacing error with Retry.');
                setStatus("CarFido didn't respond. Tap send to try again.");
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `stream-error-${Date.now()}`,
                        role: 'assistant',
                        isStreamError: true,
                        content: "CarFido didn't respond. Please try again.",
                        retryUserMessage: displayUserMessage || content || '',
                        timestamp: new Date().toISOString(),
                    },
                ]);
                setShouldAutoScroll(true);
            }, firstOutputWindowMs);

            eventSource.addEventListener('chunk', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                const chunk = event.data;
                if (streamState.suppressReportStreaming) {
                    setStatus('');
                    return;
                }
                streamState.content += chunk;
                setActiveMessage(prev => prev ? { ...prev, content: prev.content + chunk } : null);
                setStatus('');
            });

            eventSource.addEventListener('status', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                const msg = normalizeStatusMessage(event.data);
                setStatus(msg);
                const lowerStatus = msg.toLowerCase();
                // Service-logging statuses ("Updating service history...", "Saving service
                // record...") must NEVER be treated as report generation — they only write to
                // the glove box. Without this guard "service history" matches the \bhistory\b
                // keyword below and wrongly renders the diagnostic-report skeleton.
                const isServiceLogStatus = /\b(service\s+history|service\s+record|logging\s+service|updating\s+service|saving\s+service|adding\s+service)\b/.test(lowerStatus);
                const isReportStatus = !isServiceLogStatus && (
                    /\b(report|summary|plan|diy|guide|manual|history)\b/.test(lowerStatus) ||
                    /\b(generating|compiling|preparing|building|creating)\b.*\b(report|summary|plan|guide|manual|history)\b/.test(lowerStatus)
                );

                // Trigger skeleton if keywords match
                if (lowerStatus.match(/glovebox|document|service|history|car/)) {
                    setActiveMessage(prev => prev ? { ...prev, isLoadingGlovebox: true } : null);
                }
                if (isReportStatus) {
                    const diagnosticReportStatus = (
                        /\breport\b/.test(lowerStatus) &&
                        hasDiagnosticTicketContext([...messages, outgoingUserMessage]) &&
                        !hasGeneratedDiagnosticReportForMessages([...messages, outgoingUserMessage])
                    );
                    if (diagnosticReportStatus && !streamState.suppressReportStreaming) {
                        const parsedPreReport = extractSuggestionsAndContent(cleanInternalTags(streamState.content));
                        const preReportContent = stripReportGenerationTransitionText(parsedPreReport.content);
                        if (preReportContent && !streamState.preReportMessagePersisted) {
                            streamState.preReportMessage = {
                                id: assistantMsgId,
                                role: 'assistant',
                                content: preReportContent,
                                replyToUserContent: displayUserMessage,
                                suggestions: parsedPreReport.suggestions,
                                type: 'text',
                                afterGuidedSession: streamState.afterGuidedSession,
                            };
                            appendFinalAssistantMessage(streamState.preReportMessage);
                            streamState.preReportMessagePersisted = true;
                        }
                        streamState.content = '';
                        streamState.suggestions = [];
                        streamState.suppressReportStreaming = true;
                    }
                    setActiveMessage(prev => prev ? { ...prev, isReportGenerationAnchored: true } : null);
                    if (!streamState.reportPlaceholderId) {
                        streamState.reportPlaceholderId = insertReportLoadingMessage({ status: msg });
                    } else {
                        updateReportLoadingMessage(streamState.reportPlaceholderId, msg);
                    }
                }
            });

            eventSource.addEventListener('suggestions', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                try {
                    const parsed = JSON.parse(event.data || '[]');
                    const suggestions = withoutPartsFlowSuggestions(Array.isArray(parsed)
                        ? parsed.filter(s => typeof s === 'string' && s.trim())
                        : []);
                    streamState.suggestions = suggestions;
                    setActiveMessage(prev => prev ? { ...prev, suggestions } : null);
                    if (streamState.finalized && assistantMsgId && suggestions.length > 0) {
                        setMessages(prev => {
                            let updatedMessage = null;
                            const next = prev.map((message) => {
                                if (message.id !== assistantMsgId || message.role !== 'assistant') return message;
                                const existingSuggestions = getMessageSuggestions(message);
                                if (
                                    existingSuggestions.length === suggestions.length &&
                                    existingSuggestions.every((reply, index) => reply === suggestions[index])
                                ) {
                                    return message;
                                }
                                updatedMessage = { ...message, suggestions };
                                return updatedMessage;
                            });
                            if (updatedMessage) {
                                cachePendingAssistantMessage(updatedMessage, streamStorageKey);
                                return next;
                            }
                            return prev;
                        });
                    }
                } catch (e) {
                    console.error('[CarFido] Failed to parse suggestions event:', e);
                }
            });

            eventSource.addEventListener('vehicle_updated', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                try {
                    const detail = event.data ? JSON.parse(event.data) : {};
                    window.dispatchEvent(new CustomEvent('dash_vehicle_updated', { detail }));
                    window.dispatchEvent(new CustomEvent('dash_vehicle_switched', { detail }));
                } catch (_) {
                    window.dispatchEvent(new CustomEvent('dash_vehicle_updated'));
                    window.dispatchEvent(new CustomEvent('dash_vehicle_switched'));
                }
            });

            eventSource.addEventListener('account_deleted', async (event) => {
                if (streamState.finalized) return;
                streamState.finalized = true;
                streamState.doneReceived = true;
                historyRequestVersionRef.current += 1;
                clearStreamIdleTimer();

                try {
                    const detail = event.data ? JSON.parse(event.data) : {};
                    window.dispatchEvent(new CustomEvent('dash_account_deleted', { detail }));
                } catch (_) {
                    window.dispatchEvent(new CustomEvent('dash_account_deleted'));
                }

                canceledEventSourcesRef.current.add(eventSource);
                eventSource.close();
                if (activeEventSourceRef.current === eventSource) {
                    activeEventSourceRef.current = null;
                }

                eraseAllSessionData();
                const nextAnonymousUserId = getOrCreateAnonymousId();
                const nextAnonymousSessionId = getOrCreateAnonymousSessionId();
                setAnonymousUserId(nextAnonymousUserId);
                setAnonymousSessionId(nextAnonymousSessionId);
                setMessages([]);
                setInput('');
                setActiveMessage(null);
                setGuidedCheckSession(null);
                setShowChatVehicleForm(false);
                setChatVehicleForm(createEmptyChatVehicleForm());
                setChatVehicleFormError('');
                setAttachments([]);
                setStatus('');
                setIsLoading(false);
                isLoadingRef.current = false;
                clearActiveResponseStop();
                setIsInitialLoading(false);
                setHasMore(false);
                setOffset(0);
                messagesStorageKeyRef.current = null;
                suppressDiagnosticRestoreRef.current = true;
                hasProcessedInitialIntent.current = true;

                try {
                    if (onLogout) await onLogout();
                } finally {
                    lastLoadedIdentityRef.current = `anon:${nextAnonymousUserId}:${nextAnonymousSessionId}`;
                    navigate('/chat', { replace: true });
                }
            });

            eventSource.addEventListener('diagnostic_ticket', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                try {
                    const detail = event.data ? JSON.parse(event.data) : {};
                    if (detail && (detail.diagnostic_ticket_id || detail.ticket_id || detail.thread_id || detail.source_repair_log_id || detail.title || detail.symptom_summary)) {
                        window.dispatchEvent(new CustomEvent('dash_active_diagnostic_ticket', { detail }));
                    }
                    if (isAuthenticated && refreshDiagnosticTickets) {
                        refreshDiagnosticTickets();
                        setTimeout(refreshDiagnosticTickets, 800);
                    }
                } catch (e) {
                    console.error('[CarFido] Failed to parse diagnostic ticket event:', e);
                    if (isAuthenticated && refreshDiagnosticTickets) {
                        refreshDiagnosticTickets();
                    }
                }
            });

            eventSource.addEventListener('service_card', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                try {
                    const serviceCard = JSON.parse(event.data || '{}');
                    if (!serviceCard || typeof serviceCard !== 'object') return;
                    streamState.serviceCard = serviceCard;
                    if (streamState.doneReceived) {
                        setMessages(prev => prev.map(msg => (
                            msg.id === assistantMsgId ? { ...msg, serviceCard } : msg
                        )));
                    } else {
                        setActiveMessage(prev => prev ? { ...prev, serviceCard } : prev);
                    }
                } catch (e) {
                    console.error('[CarFido] Failed to parse service_card event:', e);
                }
            });

            const handleGloveboxData = (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                try {
                    const gloveboxDocs = JSON.parse(event.data);
                    streamState.gloveboxDocs = gloveboxDocs;
                    setActiveMessage(prev => prev ? { ...prev, gloveboxDocs, isLoadingGlovebox: false } : null);
                } catch (e) {
                    console.error("Failed to parse glovebox json", e);
                }
            };

            eventSource.addEventListener('glovebox_data', handleGloveboxData);
            eventSource.addEventListener('glovebox_file_list', handleGloveboxData);

            // Parts catalogue as DATA rather than prose. The model no longer writes these
            // URLs on web, so nothing can corrupt them between the API and the card.
            eventSource.addEventListener('parts_data', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                try {
                    const incoming = JSON.parse(event.data || '{}');
                    if (!incoming || (!incoming.listings?.length && !incoming.retailers?.length)) return;
                    // Merge, never replace: a single question fires several parts tools and they
                    // return different halves of the catalogue.
                    const partsData = mergePartsData(streamState.partsData, incoming);
                    streamState.partsData = partsData;
                    setActiveMessage(prev => (prev ? { ...prev, partsData } : null));
                } catch (e) {
                    // Fall through silently: parseLinkListBlock still renders whatever links
                    // the model wrote, so a bad payload degrades to the old behaviour.
                    console.error('[CarFido] Failed to parse parts_data event:', e);
                }
            });

            eventSource.addEventListener('document', (event) => {
                markRealOutput();
                try {
                    const documentData = JSON.parse(event.data || '{}');
                    if (!documentData?.url) return;
                    streamState.document = documentData;
                    streamState.pdfUrl = documentData.url;
                    setActiveMessage(prev => prev ? {
                        ...prev,
                        pdfUrl: documentData.url,
                        isLoadingReport: false,
                    } : null);
                } catch (e) {
                    console.error('[CarFido] Failed to parse document event:', e);
                }
            });

            eventSource.addEventListener('audio', (event) => {
                markRealOutput();
                scheduleStreamIdleTimer();
                console.log("[SSE] Audio event received");
                if (audioPlaybackCanceledRef.current) return;
                try {
                    const { data_base64, mime_type } = JSON.parse(event.data);
                    const byteCharacters = atob(data_base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: mime_type });
                    const audioUrl = URL.createObjectURL(blob);
                    
                    streamState.audioUrl = audioUrl;
                    if (streamState.doneReceived) {
                        setMessages(prev => prev.map(msg => (
                            msg.id === assistantMsgId
                                ? { ...msg, audioUrl, isWaitingForAudio: false }
                                : msg
                        )));
                    } else {
                        setActiveMessage(prev => prev ? { ...prev, audioUrl, isWaitingForAudio: false } : null);
                    }
                } catch (e) {
                    console.error("Failed to parse audio event", e);
                }
            });

            eventSource.addEventListener('audio_segment', (event) => {
                markRealOutput();
                if (audioPlaybackCanceledRef.current) return;
                try {
                    const { data_base64, mime_type } = JSON.parse(event.data);
                    const byteCharacters = atob(data_base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: mime_type || 'audio/mpeg' });
                    const audioUrl = URL.createObjectURL(blob);

                    streamState.audioUrls = [...streamState.audioUrls, audioUrl];
                    if (streamState.doneReceived) {
                        setMessages(prev => prev.map(msg => (
                            msg.id === assistantMsgId
                                ? { ...msg, audioUrls: streamState.audioUrls, isWaitingForAudio: false }
                                : msg
                        )));
                    } else {
                        setActiveMessage(prev => prev ? {
                            ...prev,
                            audioUrls: streamState.audioUrls,
                            isWaitingForAudio: false,
                        } : null);
                    }
                } catch (e) {
                    console.error("Failed to parse audio segment event", e);
                }
            });

            eventSource.addEventListener('audio_done', () => {
                markRealOutput();
                streamState.audioDone = true;
                setActiveMessage(prev => prev ? { ...prev, isWaitingForAudio: false } : null);
                setMessages(prev => prev.map(msg => (
                    msg.id === assistantMsgId
                        ? { ...msg, isWaitingForAudio: false }
                        : msg
                )));
            });

            eventSource.addEventListener('stream_end', () => {
                eventSource.close();
                canceledEventSourcesRef.current.delete(eventSource);
                if (activeEventSourceRef.current === eventSource) {
                    activeEventSourceRef.current = null;
                }
            });

            // Backend pushes the auto-generated sidebar title as its own event so the
            // conversation list updates instantly, without waiting on a thread-list refetch.
            eventSource.addEventListener('thread_title', (event) => {
                markRealOutput();
                try {
                    const payload = JSON.parse(event.data || '{}');
                    if (payload && payload.thread_id != null && payload.title && applyThreadTitleLocal) {
                        applyThreadTitleLocal(payload.thread_id, payload.title);
                    }
                } catch (_) { /* ignore malformed title events */ }
            });

            eventSource.addEventListener('done', (event) => {
                if (streamState.finalized) return;
                streamState.doneReceived = true;
                streamState.finalized = true;
                clearStreamIdleTimer();
                console.log("[SSE] Done event received");
                let finalContent = streamState.content;
                let finalPdfUrl = streamState.pdfUrl;
                let finalDocUrl = null;
                let finalGlovebox = streamState.gloveboxDocs;
                let finalPartsData = streamState.partsData;
                let finalAudio = streamState.audioUrl;
                let finalId = assistantMsgId;

                if (event.data) {
                    let finalData = event.data;

                    // 1. Extract __PDF_URL__: marker (primary web PDF format)
                    const pdfMatch = finalData.match(/__PDF_URL__:(https?:\/\/[^\s,"'<>]+)/);
                    if (pdfMatch) {
                        if (!finalPdfUrl) finalPdfUrl = pdfMatch[1];
                        finalData = finalData.replace(pdfMatch[0], '').trim();
                    }

                    // 2. Handle legacy __PDF__:path__TEXT__:content format
                    if (finalData.includes('__PDF__:')) {
                        const legacyPath = finalData.split('__PDF__:')[1]?.split('__TEXT__:')[0]?.trim();
                        if (!finalPdfUrl && legacyPath?.startsWith('http')) finalPdfUrl = legacyPath;
                        const parts = finalData.split('__TEXT__:');
                        finalData = parts.length > 1
                            ? parts[1].trim()
                            : finalData.replace(/__PDF__:[^\s]*/, '').trim();
                    }

                    // 3. Extract __DOCUMENT__: marker (re-shared user-uploaded file)
                    const docMatch = finalData.match(/__DOCUMENT__:(https?:\/\/[^\s,"'<>]+)/);
                    if (docMatch) {
                        finalDocUrl = docMatch[1];
                        finalData = finalData.replace(docMatch[0], '').trim();
                    }

                    // 4. Remove legacy YouTube markers; chat no longer renders video cards.
                    const youtubeMatch = finalData.match(/▶️\s*Video:?\s*(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s]+)/i);
                    if (youtubeMatch) {
                        finalData = finalData.replace(youtubeMatch[0], '').trim();
                    }

                    // 5. Extract Google Maps URL
                    const mapMatch = finalData.match(/(https?:\/\/(?:www\.)?google\.com\/maps\/[^\s,"'<>]+)/);
                    if (mapMatch) {
                        streamState.mapUrl = mapMatch[1];
                        finalData = finalData.replace(mapMatch[0], '').trim();
                    }

                    // Pull the persisted service-record card out of the final content (so it
                    // survives reload). Set it here as a backup if the live service_card SSE
                    // event was missed; harmless when already set to the same data.
                    const serviceExtract = extractServiceCard(finalData);
                    if (serviceExtract.serviceCard && !streamState.serviceCard) {
                        streamState.serviceCard = serviceExtract.serviceCard;
                    }
                    finalData = serviceExtract.content;

                    // 5. Strip any remaining internal action tags so they never appear as text
                    finalData = cleanInternalTags(finalData);
                    const parsedFinal = extractSuggestionsAndContent(finalData);
                    finalData = parsedFinal.content;
                    if (parsedFinal.suggestions.length > 0) {
                        streamState.suggestions = parsedFinal.suggestions;
                    }

                    // Source of Truth: done event contains the FINAL hydrated response
                    if (finalData && !streamState.suppressReportStreaming) {
                        finalContent = finalData;
                    }
                }

                if (streamState.suppressReportStreaming) {
                    const reportMessages = [
                        ...messages,
                        outgoingUserMessage,
                        streamState.preReportMessagePersisted ? streamState.preReportMessage : null,
                    ].filter(Boolean);
                    const reportAnchor = (
                        streamState.preReportMessage ||
                        getLatestDiagnosticReportAnchorMessage(reportMessages)
                    );
                    const seedReport = reportAnchor
                        ? createDiagnosticReport({
                            messages: reportMessages,
                            reportMessage: reportAnchor,
                            vehicleLabel: activeVehicleLabel,
                        })
                        : null;

                    if (seedReport) {
                        resolveReportForOpen({
                            ...seedReport,
                            threadId: currentThreadId || seedReport.threadId || null,
                        })
                            .then((resolvedReport) => {
                                appendDiagnosticReportMessage(
                                    normalizeDiagnosticReportForUi(resolvedReport || seedReport),
                                    {
                                        placeholderId: streamState.reportPlaceholderId,
                                        eligibilityMessages: reportMessages,
                                    }
                                );
                            })
                            .catch((error) => {
                                // Auth expired, or the backend refused because this isn't a
                                // diagnostic conversation — in both cases just clear the skeleton
                                // and show NO report card (a non-diagnostic chat gets no report).
                                if (error?.message === 'PHONE_AUTH_EXPIRED' || error?.message === 'NOT_DIAGNOSTIC') {
                                    removeReportLoadingMessage(streamState.reportPlaceholderId);
                                    return;
                                }
                                console.error('[Report] stream report generation failed, using fallback report', error);
                                appendDiagnosticReportMessage(
                                    normalizeDiagnosticReportForUi(seedReport),
                                    {
                                        placeholderId: streamState.reportPlaceholderId,
                                        eligibilityMessages: reportMessages,
                                    }
                                );
                            });
                    } else {
                        removeReportLoadingMessage(streamState.reportPlaceholderId);
                    }

                    setActiveMessage(null);
                    eventSource.close();
                    if (activeEventSourceRef.current === eventSource) {
                        activeEventSourceRef.current = null;
                    }
                    isLoadingRef.current = false;
                    setIsLoading(false);
                    setStatus('');
                    clearActiveResponseStop();
                    clearFirstSendThreadGuard();

                    if (isAuthenticated && refreshThreadTitles) {
                        setTimeout(refreshThreadTitles, 1200);
                    }
                    if (isAuthenticated && refreshDiagnosticTickets) {
                        refreshDiagnosticTickets();
                        setTimeout(refreshDiagnosticTickets, 1200);
                    }
                    if (isAuthenticated && refreshDisplayName) {
                        refreshDisplayName();
                    }
                    return;
                }

                // Final Handover: Move from active to messages list
                appendFinalAssistantMessage({
                    id: finalId,
                    role: 'assistant',
                    content: finalContent,
                    replyToUserContent: displayUserMessage,
                    audioUrl: finalAudio,
                    gloveboxDocs: finalGlovebox,
                    partsData: finalPartsData,
                    pdfUrl: finalPdfUrl,
                    docUrl: finalDocUrl,
                    audioUrl: finalAudio,
                    mapUrl: streamState.mapUrl,
                    serviceCard: streamState.serviceCard || null,
                    suggestions: streamState.suggestions,
                    type: 'text',
                    afterGuidedSession: streamState.afterGuidedSession,
                });

                setActiveMessage(null);
                eventSource.close();
                if (activeEventSourceRef.current === eventSource) {
                    activeEventSourceRef.current = null;
                }
                isLoadingRef.current = false;
                setIsLoading(false);
                setStatus('');
                clearActiveResponseStop();
                clearFirstSendThreadGuard();

                // Refresh thread titles in sidebar — delay slightly so the backend has
                // time to write the auto-generated title before we fetch
                if (isAuthenticated && refreshThreadTitles) {
                    setTimeout(refreshThreadTitles, 1200);
                }
                if (isAuthenticated && refreshDiagnosticTickets) {
                    refreshDiagnosticTickets();
                    setTimeout(refreshDiagnosticTickets, 1200);
                }
                if (isAuthenticated && refreshDisplayName) {
                    refreshDisplayName();
                }
            });

            
            // failed for a known reason, not that the network dropped.
            const finalizeStreamError = (statusMessage, event) => {
                if (streamState.finalized) return;
                if (canceledEventSourcesRef.current.has(eventSource)) {
                    eventSource.close();
                    canceledEventSourcesRef.current.delete(eventSource);
                    if (activeEventSourceRef.current === eventSource) {
                        activeEventSourceRef.current = null;
                    }
                    return;
                }
                if (streamState.doneReceived) {
                    clearStreamIdleTimer();
                    eventSource.close();
                    canceledEventSourcesRef.current.delete(eventSource);
                    if (activeEventSourceRef.current === eventSource) {
                        activeEventSourceRef.current = null;
                    }
                    return;
                }
                console.error('SSE Error:', event);
                // A dropped stream may be a lost connection — ask the shell to re-check
                // reachability now (the ping decides, so a server-side error won't false-flag).
                try { window.dispatchEvent(new CustomEvent('dash_check_connectivity')); } catch { /* noop */ }
                eventSource.close();
                if (activeEventSourceRef.current === eventSource) {
                    activeEventSourceRef.current = null;
                }
                isLoadingRef.current = false;
                setActiveMessage(null);
                setIsLoading(false);
                clearActiveResponseStop();
                clearFirstSendThreadGuard();

                if (streamState.content.length > 0) {
                    const parsedErrorContent = extractSuggestionsAndContent(cleanInternalTags(streamState.content));
                    const cleanedContent = parsedErrorContent.content;
                    streamState.finalized = true;
                    appendFinalAssistantMessage({
                        id: assistantMsgId,
                        role: 'assistant',
                        content: cleanedContent,
                        replyToUserContent: displayUserMessage,
                        audioUrl: streamState.audioUrl,
                        gloveboxDocs: streamState.gloveboxDocs,
                        partsData: streamState.partsData,
                        pdfUrl: null,
                        docUrl: null,
                        suggestions: streamState.suggestions.length > 0 ? streamState.suggestions : parsedErrorContent.suggestions,
                        type: 'text',
                        afterGuidedSession: streamState.afterGuidedSession,
                    });
                } else {
                    if (streamState.reportPlaceholderId) {
                        removeReportLoadingMessage(streamState.reportPlaceholderId);
                    }
                    streamState.finalized = true;
                    // Stream dropped before any content arrived. Show a VISIBLE error bubble with a
                    // Retry button instead of only a faint status line — so the driver never faces
                    // silence. (The failure is already logged above and, server-side, in agent_proxy.)
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `stream-error-${Date.now()}`,
                            role: 'assistant',
                            isStreamError: true,
                            content: statusMessage || 'CarFido hit a problem. Please try again.',
                            retryUserMessage: displayUserMessage || '',
                            timestamp: new Date().toISOString(),
                        },
                    ]);
                    setShouldAutoScroll(true);
                }
                // The failure is already shown as a visible bubble (partial content above, or the
                // red Retry card). Clear the status line so the SAME error text doesn't also echo
                // underneath as a faint "typing" status — the duplicate the driver was seeing.
                setStatus('');
            };

            // On a failure where NOTHING was streamed yet, retry the SAME turn ONCE automatically —
            // transient LLM/API/network hiccups usually succeed on the second attempt, so the driver
            // gets a real answer instead of an error. If the retry also fails (or any content already
            // arrived), fall through to the visible error + Retry bubble.
            const retryOrFinalizeStreamError = (statusMessage, event) => {
                const canAutoRetry = !streamState.finalized
                    && !streamState.doneReceived
                    && streamState.content.length === 0
                    && !options?._autoRetried
                    && (Boolean(content) || outgoingAttachments.length > 0)
                    && !canceledEventSourcesRef.current.has(eventSource);
                if (!canAutoRetry) {
                    finalizeStreamError(statusMessage, event);
                    return;
                }
                console.warn('[SSE] Agent failure with no output — auto-retrying once before surfacing an error.');
                streamState.finalized = true;
                try { eventSource.close(); } catch { /* noop */ }
                if (activeEventSourceRef.current === eventSource) activeEventSourceRef.current = null;
                if (streamState.firstOutputTimer) clearTimeout(streamState.firstOutputTimer);
                clearStreamIdleTimer();
                isLoadingRef.current = false;
                setActiveMessage(null);
                setIsLoading(false);
                clearActiveResponseStop();
                clearFirstSendThreadGuard();
                if (streamState.reportPlaceholderId) removeReportLoadingMessage(streamState.reportPlaceholderId);
                setStatus('Reconnecting…');
                sendMessage(
                    content,
                    outgoingAttachments.length > 0 ? outgoingAttachments : null,
                    wasVoice,
                    { ...(options || {}), _autoRetried: true },
                );
            };

            // Backend explicitly reported a failure (see agent_proxy.py's
            // _iter_chat_stream_events except block) — the connection is fine, the
            // agent hit a problem generating a response.
            eventSource.addEventListener('agent_error', (event) => {
                retryOrFinalizeStreamError(
                    event?.data || 'CarFido hit a problem generating a response. Tap send to try again.',
                    event,
                );
            });

            // Native EventSource 'error' — genuine network/connection failure
            // (server unreachable, connection dropped mid-stream, non-2xx on
            // initial connect, etc).
            eventSource.addEventListener('error', (event) => {
                retryOrFinalizeStreamError('Connection interrupted. Tap send to try again.', event);
            });

        } catch (error) {
            if (stopHandled || error?.name === 'AbortError') {
                clearActiveResponseStop();
                return;
            }
            console.error('Chat Error:', error);
            isLoadingRef.current = false;
            setIsLoading(false);
            clearActiveResponseStop();
            clearFirstSendThreadGuard();
            if (error.message === 'PHONE_AUTH_EXPIRED') {
                await handleAuthExpired();
                return;
            }
            // The request never started a stream — surface a visible error with a Retry so the
            // driver is never left in silence (same as the mid-stream failure path).
            const visibleError = error?.message && error.message !== 'Failed to initialize chat'
                ? error.message
                : 'Couldn’t reach CarFido. Check your connection and tap send to try again.';
            if (displayUserMessage) {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `stream-error-${Date.now()}`,
                        role: 'assistant',
                        isStreamError: true,
                        content: visibleError,
                        retryUserMessage: displayUserMessage,
                        timestamp: new Date().toISOString(),
                    },
                ]);
                setShouldAutoScroll(true);
                // The red Retry card carries the message — don't also echo it as a faint status.
                setStatus('');
            } else {
                // No user message to retry → the status line is the only signal, so keep it.
                setStatus(visibleError);
            }
            // Ask the shell to re-check server reachability now, so the connectivity banner
            // appears immediately if this failure was a lost connection.
            try { window.dispatchEvent(new CustomEvent('dash_check_connectivity')); } catch { /* noop */ }
        }
    };

    const sendMessageRef = useRef(sendMessage);

    useEffect(() => {
        sendMessageRef.current = sendMessage;
    });

    useEffect(() => {
        const liveResult = parseShopResultBlock(activeMessage?.content || '');
        if (liveResult?.key) {
            pendingLiveShopResultKeyRef.current = liveResult.key;
        }
    }, [activeMessage?.content]);

    useEffect(() => {
        const candidates = messages
            .slice(-3)
            .map((message) => message?.role === 'assistant' ? message.content : '')
            .filter(Boolean);
        const result = candidates.map(parseShopResultBlock).find((parsed) => parsed?.shops?.length);
        if (!result?.key || autoOpenedShopResultKeysRef.current.has(result.key)) return;
        if (pendingLiveShopResultKeyRef.current !== result.key) return;
        pendingLiveShopResultKeyRef.current = '';
        autoOpenedShopResultKeysRef.current.add(result.key);
        openGarageForShopRef.current?.(null, result);
    }, [messages]);

    const sendHandsFreeAudio = useCallback(async (audio) => {
        if (!audio || isLoadingRef.current || isRecording || isTranscribingVoiceDraft) return;
        await sendMessageRef.current?.('', [audio], true);
    }, [isRecording, isTranscribingVoiceDraft]);

    const stopAssistantPlaybackForBargeIn = useCallback(() => {
        audioPlaybackCanceledRef.current = true;
        if (activeResponseStopRef.current) {
            activeResponseStopRef.current();
        } else if (activeEventSourceRef.current) {
            canceledEventSourcesRef.current.add(activeEventSourceRef.current);
            activeEventSourceRef.current.close();
            activeEventSourceRef.current = null;
        }
        isLoadingRef.current = false;
        setStopAssistantAudioSignal(value => value + 1);
        setPlayingAudioId(null);
        setIsLoading(false);
        setStatus('');
        setActiveMessage(prev => prev ? {
            ...prev,
            audioUrl: null,
            audioUrls: [],
            isWaitingForAudio: false,
        } : null);
        setMessages(prev => prev.map(msg => (
            msg.role === 'assistant'
                ? { ...msg, audioUrl: null, audioUrls: [], isWaitingForAudio: false }
                : msg
        )));
    }, []);

    const handsFree = useHandsFreeVoice({
        onAudioReady: sendHandsFreeAudio,
        onBargeIn: stopAssistantPlaybackForBargeIn,
        disabled: (isLoading && !playingAudioId) || isRecording || isTranscribingVoiceDraft || authState?.status === 'loading',
        assistantSpeaking: Boolean(playingAudioId),
    });

    useEffect(() => {
        if (pendingQuestion) {
            sendMessage(pendingQuestion);
            clearPendingQuestion();
        }
    }, [pendingQuestion, clearPendingQuestion]);

    const sendShopMessage = useCallback(async (message, options = {}) => {
        const {
            requestId: explicitRequestId = '',
            clearComposer = true,
            focusComposer = true,
        } = options;
        const trimmedMessage = safeText(message).trim();
        const requestId = explicitRequestId || selectedGarageRequestIdRef.current;
        if (!trimmedMessage || !requestId || isSendingShopMessage) return null;
        setIsSendingShopMessage(true);
        try {
            const updatedRequest = await sendPartnerRepairRequestMessage(requestId, trimmedMessage);
            applyGarageRequestUpdate(updatedRequest);
            if (clearComposer) setInput('');
            setShouldAutoScroll(true);
            if (focusComposer) {
                focusChatInput({ forceMobile: isMobileViewport() && document.activeElement === chatInputRef.current });
            }
            return updatedRequest;
        } finally {
            setIsSendingShopMessage(false);
        }
    }, [applyGarageRequestUpdate, focusChatInput, isMobileViewport, isSendingShopMessage, setShouldAutoScroll]);

    const updateGarageEstimateLineAuthorization = useCallback(async (request, lines, decision) => {
        const requestId = request?.request_id || selectedGarageRequestIdRef.current;
        const validLines = (Array.isArray(lines) ? lines : [lines]).filter((line) => line?.estimate_line_id);
        if (!requestId || !validLines.length) {
            throw new Error('Could not find this shop request.');
        }
        const updatedRequest = await updatePartnerEstimateLineAuthorizations(
            requestId,
            validLines.map((line) => ({ estimate_line_id: line.estimate_line_id, decision })),
            ''
        );
        applyGarageRequestUpdate(updatedRequest);
        setShouldAutoScroll(true);
        return updatedRequest;
    }, [applyGarageRequestUpdate, setShouldAutoScroll]);

    const updateGarageRequestStatus = useCallback(async (request, nextStatus, notes = '') => {
        const requestId = request?.request_id || selectedGarageRequestIdRef.current;
        if (!requestId || isUpdatingGarageRequestStatus) return null;
        setIsUpdatingGarageRequestStatus(true);
        try {
            const updatedRequest = await updatePartnerRepairRequestStatus(requestId, nextStatus, notes);
            applyGarageRequestUpdate(updatedRequest);
            setShouldAutoScroll(true);
            return updatedRequest;
        } finally {
            setIsUpdatingGarageRequestStatus(false);
        }
    }, [applyGarageRequestUpdate, isUpdatingGarageRequestStatus, setShouldAutoScroll]);

    const confirmGarageVisit = useCallback(async (request) => {
        if (!request?.request_id || isSendingShopMessage || isUpdatingGarageRequestStatus) return null;
        const requestStatus = String(request?.request_status || '').toLowerCase();
        let updatedRequest = await sendShopMessage(getDriverVisitUpdateMessage(request), {
            requestId: request.request_id,
            clearComposer: false,
            focusComposer: false,
        });
        if (requestStatus === 'scheduled') {
            updatedRequest = await updateGarageRequestStatus(
                updatedRequest || request,
                'in_service',
                'Driver confirmed vehicle drop-off.'
            );
        }
        return updatedRequest;
    }, [isSendingShopMessage, isUpdatingGarageRequestStatus, sendShopMessage, updateGarageRequestStatus]);

    const confirmGaragePickup = useCallback(async (request) => {
        const updated = await updateGarageRequestStatus(request, 'picked_up', 'Vehicle pickup confirmed by driver.');
        // Pickup moves the linked ticket to Monitoring server-side — refresh so the sidebar
        // stops showing "Open".
        refreshDiagnosticTickets?.();
        return updated;
    }, [updateGarageRequestStatus, refreshDiagnosticTickets]);

    // Driver's post-pickup answer to "Is your issue resolved?": 'resolved' closes the linked
    // diagnostic ticket, 'not_sure' leaves it under monitoring. Persisted per request so the
    // prompt doesn't reappear once answered.
    const [garagePickupOutcome, setGaragePickupOutcome] = useState({});
    const [isSavingPickupOutcome, setIsSavingPickupOutcome] = useState(false);

    useEffect(() => {
        const rid = selectedGarageRequest?.request_id;
        if (!rid) return;
        try {
            const saved = window.localStorage.getItem(`carfido_pickup_outcome:${rid}`);
            if (saved) setGaragePickupOutcome((prev) => (prev[rid] ? prev : { ...prev, [rid]: saved }));
        } catch { /* ignore */ }
    }, [selectedGarageRequest?.request_id]);

    const confirmGaragePickupOutcome = useCallback(async (request, outcome) => {
        const rid = request?.request_id;
        if (!rid || isSavingPickupOutcome) return;
        setIsSavingPickupOutcome(true);
        try {
            if (outcome === 'resolved') {
                const ticketId = request?.diagnostic_ticket_id;
                const phone = authState?.identity;
                if (ticketId && phone) {
                    const resp = await fetch(
                        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(ticketId)}`,
                        {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                status: 'closed',
                                stage: 'closed',
                                outcome: 'successful',
                                summary: 'Resolved after shop service and vehicle pickup.',
                            }),
                        },
                    );
                    if (!resp.ok) throw new Error('close failed');
                }
            } else {
                // Not sure → the repair outcome is NOT confirmed, so the ticket must go to
                // Monitoring, never Resolved (Rank 14). Persist the Monitoring state explicitly
                // (so it sticks even if the ticket was prematurely resolved by shop completion),
                // then arm the 3-day follow-up so it doesn't sit unresolved forever.
                tryEnableWebPushQuietly();
                const ticketId = request?.diagnostic_ticket_id;
                const phone = authState?.identity;
                if (ticketId && phone) {
                    try {
                        await fetch(
                            `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(ticketId)}`,
                            {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    stage: 'monitoring',
                                    status: 'awaiting_outcome',
                                    outcome: 'unknown',
                                    summary: "Driver isn't sure the issue is fixed after shop service — keeping it under monitoring.",
                                }),
                            },
                        );
                    } catch (patchErr) {
                        console.warn('[pickup outcome] set monitoring failed', patchErr);
                    }
                    try {
                        await fetch(
                            `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(ticketId)}/schedule-followup`,
                            { method: 'POST' },
                        );
                    } catch (schedErr) {
                        console.warn('[pickup outcome] schedule follow-up failed', schedErr);
                    }
                }
            }
            const val = outcome === 'resolved' ? 'resolved' : 'monitoring';
            setGaragePickupOutcome((prev) => ({ ...prev, [rid]: val }));
            try { window.localStorage.setItem(`carfido_pickup_outcome:${rid}`, val); } catch { /* ignore */ }
            refreshDiagnosticTickets?.();
        } catch (e) {
            console.error('[pickup outcome] failed', e);
        } finally {
            setIsSavingPickupOutcome(false);
        }
    }, [isSavingPickupOutcome, authState?.identity, refreshDiagnosticTickets]);

    // The driver's answer to the 3-day monitoring follow-up card (posted by the backend
    // scheduler). Routes the same way as the original confirmation:
    //   resolved → close the ticket; still → resume diagnostics (backend rebuilds the
    //   prior-context briefing from the failure report); not_sure → re-arm the follow-up.
    const [followupOutcomes, setFollowupOutcomes] = useState({});
    const [followupSubmittingTicketId, setFollowupSubmittingTicketId] = useState(null);

    const handleFollowupOutcome = useCallback(async (card, outcome) => {
        const ticketId = card?.ticket_id;
        if (!ticketId || followupSubmittingTicketId) return;
        const phone = authState?.identity;
        setFollowupSubmittingTicketId(ticketId);
        try {
            if (outcome === 'resolved') {
                if (phone) {
                    await fetch(
                        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(ticketId)}`,
                        {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                status: 'closed',
                                stage: 'closed',
                                outcome: 'successful',
                                summary: 'Resolved at the follow-up check.',
                            }),
                        },
                    );
                }
                setFollowupOutcomes((prev) => ({ ...prev, [ticketId]: 'resolved' }));
                refreshDiagnosticTickets?.();
            } else if (outcome === 'still') {
                setFollowupOutcomes((prev) => ({ ...prev, [ticketId]: 'still' }));
                // Resume diagnostics with full prior context — the backend rebuilds the briefing
                // when a failure report lands on the monitoring ticket.
                sendMessage("The problem is still happening after the repair. Let's keep diagnosing.");
            } else {
                if (phone) {
                    await fetch(
                        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(ticketId)}/schedule-followup`,
                        { method: 'POST' },
                    );
                }
                setFollowupOutcomes((prev) => ({ ...prev, [ticketId]: 'not_sure' }));
                refreshDiagnosticTickets?.();
            }
            // Persist terminal answers so an answered card stays answered on reload. 'not_sure'
            // re-arms the follow-up, so clear any stored value — the next follow-up shows fresh.
            try {
                const key = `carfido_followup_outcome:${ticketId}`;
                if (outcome === 'resolved' || outcome === 'still') window.localStorage.setItem(key, outcome);
                else window.localStorage.removeItem(key);
            } catch { /* ignore */ }
        } catch (e) {
            console.error('[followup outcome] failed', e);
        } finally {
            setFollowupSubmittingTicketId(null);
        }
    }, [followupSubmittingTicketId, authState?.identity, refreshDiagnosticTickets, sendMessage]);

    // Rehydrate answered follow-up cards from localStorage so a resolved/resumed card doesn't
    // re-show its question after a reload.
    useEffect(() => {
        const cards = (messages || []).map((m) => m?.followupCard).filter(Boolean);
        if (cards.length === 0) return;
        setFollowupOutcomes((prev) => {
            let next = prev;
            let changed = false;
            for (const c of cards) {
                const tid = c?.ticket_id;
                if (tid == null || prev[tid] !== undefined) continue;
                try {
                    const saved = window.localStorage.getItem(`carfido_followup_outcome:${tid}`);
                    if (saved) {
                        if (!changed) { next = { ...prev }; changed = true; }
                        next[tid] = saved;
                    }
                } catch { /* ignore */ }
            }
            return next;
        });
    }, [messages]);

    const handleSend = (e) => {
        e.preventDefault();
        if (isLoadingRef.current || isSendingShopMessage) return;
        const shouldKeepMobileKeyboardOpen = isMobileViewport() && document.activeElement === chatInputRef.current;
        if (isShopMode) {
            sendShopMessage(input);
            focusChatInput({ forceMobile: shouldKeepMobileKeyboardOpen });
            return;
        }
        const wasVoiceDraft = pendingVoiceDraft;
        setPendingVoiceDraft(false);
        sendMessage(input, null, wasVoiceDraft);
        focusChatInput({ forceMobile: shouldKeepMobileKeyboardOpen });
    };

    const handleChatVehicleFieldChange = (field, value) => {
        setChatVehicleForm((prev) => {
            const next = { ...prev, [field]: value };
            // Auto-fill the Make from a recognized Model (e.g. "Challenger" -> Dodge)
            // when the user hasn't entered a make yet, so they don't have to type it.
            if (field === 'model' && !cleanDisplayText(prev.make)) {
                const inferred = makeForModel(value);
                if (inferred) next.make = inferred;
            }
            return next;
        });
        setChatVehicleFormError('');
        setChatVehicleFormSuggestions([]);
    };

    const handleChatVehicleMaintenanceChange = (serviceKey, patch) => {
        setChatVehicleForm((prev) => {
            const history = { ...(prev.maintenanceHistory || {}) };
            history[serviceKey] = { odometerBased: true, ...(history[serviceKey] || {}), ...patch };
            return { ...prev, maintenanceHistory: history };
        });
    };

    // Clicking a "Did you mean" suggestion fills the form and clears the error.
    const handleApplyVehicleSuggestion = (suggestion) => {
        if (!suggestion) return;
        setChatVehicleForm((prev) => ({
            ...prev,
            year: suggestion.year ? String(suggestion.year) : prev.year,
            make: suggestion.make || prev.make,
            model: suggestion.model || prev.model,
        }));
        setChatVehicleFormError('');
        setChatVehicleFormSuggestions([]);
    };

    const handleOpenChatVehicleForm = (prefill = null) => {
        const base = createEmptyChatVehicleForm();
        // RESUME an in-progress form: if the user opened Add Vehicle, partly filled it, navigated
        // away, and is re-opening it (no sidebar entry exists for the transient add-vehicle chat,
        // so re-clicking "Add my vehicle" is how they return), restore what they had entered
        // instead of resetting to blank. An explicit prefill (VIN decode / parsed request) wins.
        const saved = prefill ? null : readVehicleFormState();
        if (saved?.form) {
            setChatVehicleForm({ ...base, ...saved.form });
            setChatVehicleFormTime(saved.time || getCurrentChatTime());
            setChatVehicleFormSuggestions(Array.isArray(saved.suggestions) ? saved.suggestions : []);
        } else {
            setChatVehicleForm(prefill ? {
                ...base,
                year: prefill.year || base.year,
                make: prefill.make || base.make,
                model: prefill.model || base.model,
                odometerReading: prefill.odometerReading || base.odometerReading,
                vin: prefill.vin || base.vin,
                engine: prefill.engine || base.engine,
            } : base);
            setChatVehicleFormTime(getCurrentChatTime());
            setChatVehicleFormSuggestions([]);
        }
        setChatVehicleFormError('');
        makeSuggestSeenRef.current = '';
        pendingAddVehicleTopScrollRef.current = true;
        setAddVehicleScrollRequest((n) => n + 1);
        setShowChatVehicleForm(true);
        setShouldAutoScroll(false);
    };

    // The card's ✕ unpins it from THIS add-vehicle chat (the conversation stays as a normal chat).
    const closeChatVehicleForm = () => {
        clearVehicleFormStorage();
        setShowChatVehicleForm(false);
        setChatVehicleForm(createEmptyChatVehicleForm());
        setChatVehicleFormError('');
        setChatVehicleFormSuggestions([]);
        try { window.dispatchEvent(new CustomEvent(CLOSE_ADD_VEHICLE_TAB_EVENT, { detail: { threadId: currentThreadId } })); } catch { /* noop */ }
    };

    // Persist THIS add-vehicle chat's card fields (per thread) on every edit. GUARD: only write when
    // the form data actually belongs to the current thread (vehicleFormThreadRef). On a chat switch
    // the key changes a render before the form resets — without this guard we'd write the previous
    // chat's data under the new chat's key, leaking the input across chats.
    useEffect(() => {
        if (!isAddVehicleCard) return;
        if (String(vehicleFormThreadRef.current) !== String(currentThreadId)) return;
        const key = getVehicleFormStorageKey();
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify({
                form: chatVehicleForm,
                time: chatVehicleFormTime,
                suggestions: chatVehicleFormSuggestions,
                userMessage: chatVehicleUserMessage,
            }));
        } catch { /* quota/serialize errors are non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddVehicleCard, chatVehicleForm, chatVehicleFormTime, chatVehicleFormSuggestions, chatVehicleUserMessage, currentThreadId]);

    // When entering an add-vehicle chat (navigation or reload), reset the card to THIS chat's own
    // data — its saved fields + request bubble, or empty if none — so one add-vehicle chat's input
    // never leaks into another. For a brand-new add-vehicle chat, the openVehicleForm effect runs
    // AFTER this (it's defined below) and fills the prefill + the user's typed request.
    useEffect(() => {
        if (!isAddVehicleCard) return;
        const saved = readVehicleFormState();
        setChatVehicleUserMessage(typeof saved?.userMessage === 'string' ? saved.userMessage : '');
        if (saved?.form) {
            setChatVehicleForm({ ...createEmptyChatVehicleForm(), ...saved.form });
            setChatVehicleFormTime(saved.time || getCurrentChatTime());
            setChatVehicleFormSuggestions(Array.isArray(saved.suggestions) ? saved.suggestions : []);
        } else {
            setChatVehicleForm(createEmptyChatVehicleForm());
            setChatVehicleFormTime(getCurrentChatTime());
            setChatVehicleFormSuggestions([]);
        }
        setChatVehicleFormError('');
        // Mark that chatVehicleForm now holds THIS thread's data (unlocks the persist guard above).
        vehicleFormThreadRef.current = currentThreadId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentThreadId, isAddVehicleCard]);

    // Keep the legacy showChatVehicleForm flag in sync with the actual card visibility. Many render
    // gates (greeting, safety pill, inline diagnostics) key off it; if it went stale-true after
    // leaving the card, a fresh/New Chat rendered BLANK (greeting suppressed). Driving it from
    // isAddVehicleCard keeps every one of those gates correct.
    useEffect(() => {
        setShowChatVehicleForm(isAddVehicleCard);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddVehicleCard]);

    useEffect(() => {
        if (!isAddVehicleCard) {
            lastScrolledAddVehicleKeyRef.current = '';
            return undefined;
        }
        // Scroll the card to the top ONCE per open, keyed by an open signature — regardless of
        // HOW it was opened (sidebar button, "add my corolla" text, reload). The old
        // pendingAddVehicleTopScrollRef gate could miss the text path when `isAddVehicleCard`
        // flipped true on a different tick than the ref was set.
        const openKey = `${currentThreadId || 'local'}:${chatVehicleFormTime || ''}:${addVehicleScrollRequest}`;
        if (lastScrolledAddVehicleKeyRef.current === openKey) return undefined;
        lastScrolledAddVehicleKeyRef.current = openKey;
        pendingAddVehicleTopScrollRef.current = false;

        const runOnNextFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
        const cancelNextFrame = window.cancelAnimationFrame || window.clearTimeout;

        // The Add Vehicle card renders at the BOTTOM of the session (order-[52], after all
        // regular messages), in line with normal conversation flow. But the card is taller than
        // the viewport, so when it appears we align its TOP to the top of the scroll area — the
        // user sees the "Add your vehicle" header and starts from VIN/Year, not the lower fields.
        // Disable auto-scroll-to-bottom so it doesn't fight this card-top alignment.
        setShouldAutoScroll(false);
        const frameId = runOnNextFrame(() => {
            scrollAddVehicleCardToTop();
            runOnNextFrame(scrollAddVehicleCardToTop);
        });
        const timeoutId = window.setTimeout(scrollAddVehicleCardToTop, 120);
        return () => {
            cancelNextFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [isAddVehicleCard, chatVehicleFormTime, currentThreadId, addVehicleScrollRequest, scrollAddVehicleCardToTop, setShouldAutoScroll]);

    // When the Garage "Add Vehicle" button navigates here with openVehicleForm,
    // open the guided "Add your vehicle" form on the fresh chat (the Garage side
    // already started a new chat). Clear the nav state so it doesn't reopen.
    useEffect(() => {
        if (!location.state?.openVehicleForm) return;
        const prefill = location.state.vehicleFormPrefill || null;
        const userMsg = location.state.addVehicleUserMessage || '';
        window.history.replaceState({}, document.title);
        setChatVehicleUserMessage(userMsg);
        handleOpenChatVehicleForm(prefill);
        vehicleFormThreadRef.current = currentThreadId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleSubmitChatVehicleForm = async (event) => {
        event.preventDefault();
        if (isSavingChatVehicle) return;

        let year = Number(String(chatVehicleForm.year).trim());
        // The user enters the odometer in their chosen unit (defaulting to their
        // preference). We keep the entered value for display and convert km → miles for
        // storage, since the backend odometer field is miles-based.
        // Whole-number-only odometer (ADDVEH-ODO-004): reject text/decimals/mixed input
        // instead of silently coercing it, and keep the required-vs-invalid errors distinct.
        const odoRaw = String(chatVehicleForm.odometerReading ?? '').trim();
        const odometerEntered = parseWholeOdometer(chatVehicleForm.odometerReading);
        const odometerUnit = (chatVehicleForm.odometerUnit || distanceUnit) === 'km' ? 'km' : 'mi';
        // Convert the entered value to canonical MILES via the shared helper so every path
        // (Add Vehicle, Garage, chat) stores the same miles value for the same km input.
        const odometerReading = odometerEntered !== null ? toMiles(odometerEntered, odometerUnit) : null;
        let make = cleanDisplayText(chatVehicleForm.make);
        let model = cleanDisplayText(chatVehicleForm.model);
        // identity, not phone: email-only accounts have no phone number, and keying on
        // it here made "Add vehicle" fail with "Unable to identify this chat session".
        const userIdForVehicle = isAuthenticated ? authState?.identity : activeUserId;

        // VIN gate: a VIN is optional, but if one is entered it MUST pass format and
        // check-digit validation, even when Lookup was skipped (shared rules, #380).
        const vinRaw = cleanDisplayText(chatVehicleForm.vin).toUpperCase();
        const vinFormatError = getVinError(vinRaw);
        if (vinFormatError) {
            setChatVehicleFormError(`${vinFormatError} Or clear the VIN field.`);
            return;
        }

        if (!year || !model || !odoRaw) {
            setChatVehicleFormError('Year, model, and odometer reading are required.');
            return;
        }
        if (odometerEntered === null) {
            setChatVehicleFormError('Enter a valid odometer reading using whole numbers only.');
            return;
        }
        if (odometerEntered < 0) {
            setChatVehicleFormError('Odometer reading cannot be negative.');
            return;
        }
        if (odometerReading !== null && odometerReading > MAX_ODOMETER_MILES) {
            setChatVehicleFormError(ODOMETER_TOO_HIGH_MESSAGE);
            return;
        }
        if (year < 1900 || year > 2027) {
            setChatVehicleFormError(`Year must be between 1900 and 2027. You entered ${year}.`);
            return;
        }
        if (!userIdForVehicle) {
            setChatVehicleFormError('Unable to identify this chat session. Refresh and try again.');
            return;
        }

        setIsSavingChatVehicle(true);
        setChatVehicleFormError('');
        setChatVehicleFormSuggestions([]);

        // #380: when a VIN is present it is the source of truth. Decode it: a VIN the decoder
        // rejects blocks the save, and the specs it provides are saved as decoded (they are
        // locked in the form), so make/model validation is skipped. If the decode service is
        // unavailable, the manually entered details are saved.
        let vinAuthoritative = false;
        let vinSpecs = {};
        if (vinRaw) {
            const lookup = await lookupVin(vinRaw);
            if (!lookup.ok && !lookup.unavailable) {
                setIsSavingChatVehicle(false);
                setChatVehicleFormError(`${lookup.error || "This VIN doesn't appear to be valid."} Please check it, or clear the VIN and enter the details manually.`);
                return;
            }
            if (lookup.ok) {
                const { next: vinForm, locked } = applyVinSpecs(chatVehicleForm, lookup.specs);
                vinSpecs = Object.fromEntries(locked.map((key) => [key, vinForm[key]]));
                if (vinSpecs.year) year = Number(vinSpecs.year);
                if (vinSpecs.make) make = vinSpecs.make;
                if (vinSpecs.model) model = vinSpecs.model;
                vinAuthoritative = true;
            }
        }
        const specValue = (key) => vinSpecs[key] || cleanDisplayText(chatVehicleForm[key]);

        try {
            // ── Step 0: Client-side static validation (instant, no network) ─────
            const MODEL_BELONGS_TO = {};
            if (validationMetadata?.model_belongs_to_make) {
                Object.entries(validationMetadata.model_belongs_to_make).forEach(([k, v]) => {
                    MODEL_BELONGS_TO[k.toLowerCase()] = v;
                });
            }
            const MAKE_ALIASES = {};
            if (validationMetadata?.static_make_aliases) {
                Object.entries(validationMetadata.static_make_aliases).forEach(([k, v]) => {
                    MAKE_ALIASES[k.toLowerCase()] = v.toLowerCase();
                });
            }
            const makeNorm = (make || '').toLowerCase().replace(/\s+/g,' ').trim();
            const modelNorm = (model || '').toLowerCase().replace(/\s+/g,' ').trim();
            const correctMake = MODEL_BELONGS_TO[modelNorm];
            const normAlias = (m) => MAKE_ALIASES[m] || m;
            // A model can be shared across brands (e.g. "Aura" is BOTH Saturn and Hyundai).
            // If the model is ALSO a valid model for the make the user entered, accept it —
            // don't flag it as belonging only to the other make.
            const _collapse = (s) => String(s).toLowerCase().replace(/[\s-]/g, '');
            const _staticModels = validationMetadata?.static_valid_models || {};
            const _enteredMakeKey = Object.keys(_staticModels).find(
                (k) => normAlias(k.toLowerCase()) === normAlias(makeNorm)
            );
            const modelValidForEnteredMake = Boolean(make) && Boolean(_enteredMakeKey) &&
                (_staticModels[_enteredMakeKey] || []).some((m) => _collapse(m) === _collapse(modelNorm));
            // Only run the wrong-make check when a make was actually entered (make is optional).
            if (!vinAuthoritative && make && correctMake && normAlias(correctMake.toLowerCase()) !== normAlias(makeNorm) && !modelValidForEnteredMake) {
                setIsSavingChatVehicle(false);
                setChatVehicleFormError(
                    `'${model}' is a ${correctMake} model, not a ${make} model.`
                );
                const _yr = year ? String(year) : '';
                // 1) The correct make for the model the user typed (keep the model, fix make).
                const _corrections = [
                    { kind: 'correction', year: _yr, make: correctMake, model, label: `${correctMake} ${model}` },
                ];
               
                const _makeModels = Object.entries(validationMetadata?.model_belongs_to_make || {})
                    .filter(([, mk]) => normAlias(String(mk).toLowerCase()) === normAlias(makeNorm))
                    .map(([mdl]) => titleCaseModel(mdl))
                    .filter((mdl) => mdl.toLowerCase() !== modelNorm)
                    .slice(0, 6)
                    .map((mdl) => ({ kind: 'make_model', year: _yr, make, model: mdl, label: `${make} ${mdl}` }));
                setChatVehicleFormSuggestions([..._corrections, ..._makeModels]);
                return;
            }

            // Make left blank but the model is a KNOWN model → suggest the make so the user
            // can complete it in one tap (e.g. model "civic" → "Honda Civic"). Only prompt
            // once per model; if they submit again without adding it, proceed (make is optional).
            if (!vinAuthoritative && !make && correctMake && makeSuggestSeenRef.current !== modelNorm) {
                makeSuggestSeenRef.current = modelNorm;
                setIsSavingChatVehicle(false);
                setChatVehicleFormError(`'${titleCaseModel(model)}' is a ${correctMake} model. Add the make?`);
                setChatVehicleFormSuggestions([
                    {
                        kind: 'correction',
                        year: year ? String(year) : '',
                        make: correctMake,
                        model: titleCaseModel(model),
                        label: `${correctMake} ${titleCaseModel(model)}`,
                    },
                ]);
                return;
            }

            // ── Step 1: Server-side validation (NHTSA + LLM) ─────────────────
            // Make is optional — only validate the make/model combination when a make was given.
            let canonicalMake = make;
            let canonicalModel = model;
            let canonicalYear = year;
            try {
                // A VIN-decoded vehicle is already authoritative — skip make/model validation
                // so the NHTSA-decoded values are saved exactly as decoded.
                if (vinAuthoritative) throw new Error('skip-validation-no-make');
                // Validate whenever we have a model — even without a make, so the server can
                // INFER the make from the model (e.g. "330i" → BMW) instead of saving it blank.
                if (!make && !model) throw new Error('skip-validation-no-make');
                const validateResp = await fetch(`${getUserDetailsBaseUrl()}/api/validate_vehicle`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ make, model, year }),
                });
                const vData = await validateResp.json().catch(() => ({}));
                if (vData?.valid === false) {
                    // Invalid vehicle — show reason + clickable "Did you mean" suggestions.
                    const reason = vData.invalid_reason || 'That make/model combination doesn\'t look right.';
                    const parsed = (vData.suggestions || [])
                        .map((s) => parseVehicleSuggestion(s, knownVehicleMakes, year))
                        .filter(Boolean);
                    // Dedupe by label, keep the top 3 closest matches.
                    const seen = new Set();
                    const structured = parsed.filter((s) => {
                        const key = s.label.toLowerCase();
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    }).slice(0, 3);
                    setIsSavingChatVehicle(false);
                    setChatVehicleFormError(
                        structured.length ? reason : `${reason} Please check the make and model and try again.`
                    );
                    setChatVehicleFormSuggestions(structured);
                    return;
                }
                // Use canonical names returned by validation (corrects casing/spelling)
                if (vData?.valid === true) {
                    canonicalMake = vData.canonical_make || make;
                    canonicalModel = vData.canonical_model || model;
                    canonicalYear = vData.canonical_year || year;
                }
                // If valid === null (server error / fallback), proceed with user-entered values
            } catch (validateErr) {
                // Skipped (no make entered) or network error — proceed with user-entered values.
                if (validateErr?.message !== 'skip-validation-no-make') {
                    console.warn('Server vehicle validation failed, proceeding (client check passed):', validateErr);
                }
            }

            // Never save a make-less vehicle. If the make was blank and the server couldn't infer
            // one from the model, ask the driver to enter it instead of storing an unknown make.
            if (!String(canonicalMake || '').trim()) {
                setIsSavingChatVehicle(false);
                setChatVehicleFormError('Please enter the make (brand). We couldn\'t determine it from the model.');
                return;
            }

            // ── Step 2: Save the validated vehicle ───────────────────────────
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone_number: userIdForVehicle,
                    is_new_vehicle: true,
                    vin: vinRaw || null,
                    year: canonicalYear,
                    make: canonicalMake,
                    model: canonicalModel,
                    trim: specValue('trim') || null,
                    engine: specValue('engine') || null,
                    transmission: specValue('transmission') || null,
                    drivetrain: specValue('drivetrain') || null,
                    fuel_type: specValue('fuel_type') || null,
                    plate_number: cleanDisplayText(chatVehicleForm.plate) || null,
                    odometer_reading: odometerReading,
                    mileage: odometerReading,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data?.status === 'error' || data?.error) {
                throw new Error(data?.error || data?.message || 'Unable to save vehicle.');
            }

            // ── Step 3: Save onboarding "past maintenance" baselines (best-effort) ──
            // These give the schedule the correct starting point for accurate reminders.
            try {
                const savedVehicleId = data?.vehicle_id;
                const history = chatVehicleForm.maintenanceHistory || {};
                const baselineItems = Object.entries(history)
                    .filter(([, entry]) => entry && entry.enabled)
                    .map(([serviceKey, entry]) => {
                        const odoRaw = normalizeOdometerInput(entry.odometer);
                        if (odoRaw === null) return null;
                        // Convert to miles for storage, matching the vehicle odometer.
                        const odoMiles = toMiles(odoRaw, odometerUnit);
                        return {
                            service_key: serviceKey,
                            last_done_odometer: odoMiles,
                            odometer_based: entry.odometerBased !== false,
                        };
                    })
                    .filter(Boolean);
                if (savedVehicleId && baselineItems.length > 0) {
                    await fetch(`${getUserDetailsBaseUrl()}/api/maintenance-baselines`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                            phone_number: userIdForVehicle,
                            vehicle_id: savedVehicleId,
                            items: baselineItems,
                        }),
                    });
                }
            } catch (baselineErr) {
                console.warn('Saving maintenance baselines skipped:', baselineErr);
            }

            const vehicleLabel = [canonicalYear, canonicalMake, canonicalModel].filter(Boolean).join(' ');
            setActiveVehicleLabel(vehicleLabel);
            // If the user had described an issue BEFORE being asked to add a vehicle, resume it:
            // skip the generic "what would you like to do" prompt and re-send that original issue
            // so CarFido actually diagnoses it now that a vehicle exists.
            const pendingIssueToResume = extractPendingIssueForResume(messages);
            const confirmationMessages = buildVehicleAddedMessages({
                year: canonicalYear,
                make: canonicalMake,
                model: canonicalModel,
                vehicleLabel,
                mileage: (odometerEntered ?? odometerReading).toLocaleString(),
                unit: odometerUnit,
            });
            const setupUserContent = cleanDisplayText(chatVehicleUserMessage) || 'Add my vehicle';
            // On a resume, don't persist the generic confirmation — the resumed diagnosis
            // (persisted by the backend) is the real next turn.
            const setupAssistantContent = pendingIssueToResume ? '' : (confirmationMessages[0]?.content || '');
            // A persistent read-only card of what was added, so the "vehicle card" stays in
            // the chat after the form is submitted (instead of vanishing).
            const addedVehicleCard = {
                id: `vehicle-added-card-${Date.now()}`,
                role: 'assistant',
                type: 'vehicle_added_card',
                suppressDiagnosticTicketCard: true,
                vehicle: {
                    year: canonicalYear,
                    make: canonicalMake,
                    model: canonicalModel,
                    odometer: odometerEntered ?? odometerReading,
                    unit: odometerUnit,
                    trim: specValue('trim'),
                    engine: specValue('engine'),
                    transmission: specValue('transmission'),
                    drivetrain: specValue('drivetrain'),
                    fuel_type: specValue('fuel_type'),
                    plate: cleanDisplayText(chatVehicleForm.plate),
                    vin: vinRaw || null,
                },
            };

            if (isAuthenticated && currentThreadId && setupAssistantContent) {
                try {
                    const persistResp = await fetch(
                        `${CHAT_API_BASE_URL}/secure/threads/${encodeURIComponent(currentThreadId)}/messages`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...getTesterModeHeaders() },
                            credentials: 'include',
                            body: JSON.stringify({
                                messages: [
                                    { role: 'user', content: setupUserContent, message_type: 'chat' },
                                    { role: 'assistant', content: setupAssistantContent, message_type: 'chat' },
                                ],
                            }),
                        }
                    );
                    if (!persistResp.ok) {
                        throw new Error(`status ${persistResp.status}`);
                    }
                    refreshThreadTitles?.();
                } catch (persistErr) {
                    console.warn('Vehicle setup completion was not persisted to thread history:', persistErr);
                }
            }

            // Re-link this conversation to the newly-added vehicle. The chat was opened
            // under whatever vehicle was active before; once the driver adds a vehicle from
            // inside it, the thread should follow the new vehicle so the sidebar label and
            // header stop showing the previous one.
            if (isAuthenticated && currentThreadId && data?.vehicle_id) {
                try {
                    const relinkResp = await fetch(
                        `${CHAT_API_BASE_URL}/secure/threads/${encodeURIComponent(currentThreadId)}`,
                        {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', ...getTesterModeHeaders() },
                            credentials: 'include',
                            body: JSON.stringify({ vehicle_id: data.vehicle_id }),
                        }
                    );
                    if (!relinkResp.ok) {
                        throw new Error(`status ${relinkResp.status}`);
                    }
                    refreshThreadTitles?.();
                } catch (relinkErr) {
                    console.warn('Re-linking the conversation to the new vehicle was skipped:', relinkErr);
                }
            }

            // Keep the existing conversation (the user's real message is already there) and
            // append the added-vehicle card + confirmation. Only synthesize an "Add my
            // vehicle" bubble when the form was opened on an otherwise-empty chat.
            // On a resume, append only the added-vehicle card (no generic confirmation) — the
            // resumed diagnosis streams in right after as the next turn.
            const trailingMessages = pendingIssueToResume ? [] : confirmationMessages;
            messagesStorageKeyRef.current = getActiveChatStorageKey(currentThreadId);
            setMessages(prev => (prev.length > 0
                ? [...prev, addedVehicleCard, ...trailingMessages]
                : [
                    {
                        id: `add-vehicle-user-${Date.now()}`,
                        role: 'user',
                        content: setupUserContent,
                        type: 'vehicle_added_user',
                    },
                    addedVehicleCard,
                    ...trailingMessages,
                ]
            ));
            clearVehicleFormStorage();
            try { window.dispatchEvent(new CustomEvent(CLOSE_ADD_VEHICLE_TAB_EVENT, { detail: { threadId: currentThreadId } })); } catch { /* noop */ }
            setShowChatVehicleForm(false);
            setChatVehicleForm(createEmptyChatVehicleForm());
            setChatVehicleFormTime('');
            setHasMore(false);
            setOffset(0);
            // NOTE: intentionally do NOT dispatch 'dash_vehicle_updated' here. That event
            // triggers ChatLayout's debounced thread-list refetch, which moves off the
            // current (client-only) add-vehicle chat — wiping the just-added card + summary
            // "after some time". 'dash_vehicle_switched' updates the vehicle selector to the
            // newly-added active vehicle without disturbing this conversation.
            window.dispatchEvent(new CustomEvent('dash_vehicle_switched', {
                detail: {
                    vehicle_id: data?.vehicle_id,
                    vehicleLabel,
                },
            }));
            markPwaEngagement('vehicle-added');
            refreshAuthState?.();
            setShouldAutoScroll(true);

            // Resume the user's ORIGINAL issue now that a vehicle exists. Deferred so the
            // card-append + thread-relink state settles first; the user bubble is suppressed
            // (the issue is already in the thread) and the backend skips re-persisting it.
            if (pendingIssueToResume) {
                window.setTimeout(() => {
                    (sendMessageRef.current || sendMessage)(pendingIssueToResume, null, false, {
                        resumeAfterAddVehicle: true,
                        vehicleId: data?.vehicle_id,
                    });
                }, 0);
            }
        } catch (error) {
            setChatVehicleFormError(error?.message || 'Unable to save vehicle.');
        } finally {
            setIsSavingChatVehicle(false);
        }
    };


    const handleFreshChatQuickAction = (action) => {
        switch (action) {
            case 'add-vehicle':
                // Go through the SAME path as typing "add my vehicle": ChatLayout creates the
                // add-vehicle thread and registers its id, which is what actually renders the
                // card (isAddVehicleCard). Calling handleOpenChatVehicleForm() directly only
                // set the legacy showChatVehicleForm flag, which no longer gates any render.
                window.dispatchEvent(new CustomEvent('dash_open_add_vehicle'));
                return;
            case 'diagnose-problem':
                setActiveClientIntent(null);
                sendMessage('I need help diagnosing a problem.');
                return;
            case 'find-part-tool':
                sendMessage('I need help finding a part or tool for my vehicle.', null, false, {
                    clientIntent: 'parts',
                    suppressDiagnosticTicket: true,
                });
                return;
            case 'log-service':
                sendMessage('I want to log a completed service record for my vehicle.', null, false, {
                    clientIntent: 'service',
                    suppressDiagnosticTicket: true,
                });
                return;
            case 'maintenance-due':
                sendMessage('What maintenance is due for my vehicle?', null, false, {
                    clientIntent: 'maintenance',
                    suppressDiagnosticTicket: true,
                });
                return;
            case 'plan-modification':
                sendMessage('Help me plan a vehicle modification.');
                return;
            default:
                break;
        }
    };

    const fetchBackendDiagnosticReport = useCallback(async (threadId, seedReport = null) => {
        // Authenticated users need a thread; anonymous users have no thread/ticket and
        // instead generate the report from their web-session transcript.
        if (isAuthenticated && !threadId) return null;
        if (!isAuthenticated && !activeUserId) return null;
        // Hard timeout so a slow/hung backend can never leave the skeleton spinning
        // forever — the caller falls back to a client-built report on timeout.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REPORT_FETCH_TIMEOUT_MS);
        let response;
        try {
            response = isAuthenticated
                ? await fetch(
                    `${CHAT_API_BASE_URL}/secure/threads/${encodeURIComponent(threadId)}/diagnostic-report`,
                    {
                        method: 'POST',
                        credentials: 'include',
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json',
                            ...getTesterModeHeaders(),
                        },
                        body: JSON.stringify({
                            vehicle_label: activeVehicleLabel || seedReport?.vehicle || null,
                            report_id: seedReport?.id || `thread-${threadId}-diagnostic-report`,
                        }),
                    }
                )
                : await fetch(
                    // Anonymous variant — same LLM report path, keyed by web session.
                    `${CHAT_API_BASE_URL}/diagnostic-report`,
                    {
                        method: 'POST',
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json',
                            ...getTesterModeHeaders(),
                        },
                        body: JSON.stringify({
                            user_id: activeUserId,
                            session_id: activeSessionId || null,
                            vehicle_label: activeVehicleLabel || seedReport?.vehicle || null,
                            report_id: seedReport?.id || null,
                        }),
                    }
                );
        } catch (err) {
            if (err?.name === 'AbortError') {
                throw new Error('REPORT_TIMEOUT');
            }
            throw err;
        } finally {
            clearTimeout(timeoutId);
        }

        if (response.status === 401) {
            throw new Error('PHONE_AUTH_EXPIRED');
        }

        const data = await response.json().catch(() => ({}));
        // The backend refused because this chat isn't a genuine diagnostic issue (e.g. service
        // logging). Surface a distinct signal so the caller does NOT fall back to a client-built
        // report — a non-diagnostic chat must not produce a symptom report.
        if (response.status === 409 && data?.status === 'not_diagnostic') {
            throw new Error('NOT_DIAGNOSTIC');
        }
        if (!response.ok || !data?.report) {
            throw new Error(data?.message || 'Failed to generate diagnostic report');
        }
        announceTicketTitle(threadId, data);

        return data.report;
    }, [isAuthenticated, activeVehicleLabel, activeUserId, activeSessionId]);

    const resolveReportForOpen = useCallback(async (seedReport = null) => {
        const fallbackReport = seedReport || createDiagnosticReport({
            messages,
            reportMessage: getLatestDiagnosticReportAnchorMessage(messages),
            vehicleLabel: activeVehicleLabel,
        });
        const threadId = seedReport?.threadId || currentThreadId || null;

        try {
            const backendReport = await fetchBackendDiagnosticReport(threadId, fallbackReport);
            return backendReport || normalizeDiagnosticReportForUi(fallbackReport);
        } catch (error) {
            if (error?.message === 'PHONE_AUTH_EXPIRED') {
                await handleAuthExpired();
                throw error;
            }
            // The backend determined this isn't a diagnostic conversation — do NOT fall back
            // to a client-built report. Let the caller cleanly abort showing a report card.
            if (error?.message === 'NOT_DIAGNOSTIC') {
                throw error;
            }
            // The backend was unreachable (timeout/5xx/network) AND this chat is clearly not a
            // fault to diagnose (a how-to, parts-shopping, or service-logging conversation). The
            // client fallback would otherwise fabricate a generic "Primary component fault" report
            // from the first message — exactly what a non-diagnostic chat must NOT get. Surface
            // NOT_DIAGNOSTIC so the caller shows no report card.
            if (conversationLooksNonDiagnostic(messages)) {
                throw new Error('NOT_DIAGNOSTIC');
            }
            // Any other failure (timeout, 5xx, network) on a genuine diagnostic chat must NOT
            // leave the user with no report — fall back to the client-built report so the card
            // always appears.
            console.error('[Report] backend report fetch failed, using fallback report', error);
            return normalizeDiagnosticReportForUi(fallbackReport);
        }
    }, [messages, activeVehicleLabel, currentThreadId, fetchBackendDiagnosticReport, handleAuthExpired, isAuthenticated]);

    useEffect(() => {
        if (!messages.length) return;
        if (isLoading || activeMessage) return;
        if (!hasDiagnosticTicketContext(messages)) return;
        if (hasGeneratedDiagnosticReportForMessages(messages)) return;
        // A manual generation is already showing its own skeleton — don't double up.
        if (messages.some((m) => m.isLoadingReport)) return;
        // Already building (ref survives the re-renders our setState calls trigger).
        if (autoReportGenInFlightRef.current) return;

        // CRITICAL: only auto-generate once the BACKEND has actually signalled it is
        // ready — i.e. the latest assistant message is a "ready to generate" / finalized
        // prompt and is NOT still asking a diagnostic question. Turn-count alone is not
        // enough (that fired mid-questioning and looped). Backend already enforces its
        // own minimum before emitting this signal.
        const lastAssistant = [...messages].reverse().find(
            (m) => m?.role === 'assistant' && !m.isLoadingReport && !isNeedMoreDiagnosticsMessage(m)
        );
        const lastContent = extractSuggestionsAndContent(lastAssistant?.content || '').content;
        // Auto-generate only on the "Ready to generate your symptom report?" PROMPT — not on a
        // "I created your diagnostic report" finalized statement. The prompt means the report is
        // NOT yet made and should be generated now (the loop case). The finalized statement means
        // it was already made, so its card comes from history/storage — synthesizing a fresh one
        // on reopen would duplicate it (and is the backstop of the second effect, below).
        const backendSignalledReady = isReportReadyPromptMessage(lastContent);
        if (!backendSignalledReady) return;

        // Hard backstop against any residual loop: only ever auto-attempt once per
        // thread per session. resolveReportForOpen always returns a report (real or
        // client fallback), so one attempt is guaranteed to produce a card.
        const attemptKey = currentThreadId ? `thread:${currentThreadId}` : (activeReportStorageKey || 'default');
        if (autoReportAttemptedThreadsRef.current.has(attemptKey)) return;
        // Generate as soon as the backend has signalled "ready to generate" (checked above) — we
        // do NOT also require the live-stream ready key. That extra gate meant that whenever the
        // stream didn't register the key (a reload, an intent-suppressed send, or a slightly
        // different message path), the report never generated and CarFido looped on
        // "Ready to generate your symptom report?". The one-shot guard above + the "already
        // generated" / "signalled ready" checks keep this from firing early or repeatedly.

        // Before regenerating, try to restore a previously-generated report for THIS
        // thread from storage. Reopening a chat must not wipe the saved report (and
        // its DIY progress) by silently regenerating a fresh one or moving it to the
        // bottom. Scope strictly to the thread key — ticket-based keys can pull in
        // another chat's report.
        const storedCard = getStoredReportCards([
            activeReportStorageKey,
            currentThreadId ? `thread:${currentThreadId}` : null,
        ]).find((card) => card?.diagnosticReport?.id);
        if (storedCard) {
            autoReportAttemptedThreadsRef.current.add(attemptKey);
            liveReportReadyAttemptKeysRef.current.delete(attemptKey);
            return;
        }

        const generateReportCard = async () => {
            const seedReport = buildForcedDiagnosticReport(messages);
            if (!seedReport) return;

            // Mark in-flight and show the skeleton via a state flag (NOT a message),
            // so we never mutate the `messages` dependency and re-trigger this effect.
            autoReportGenInFlightRef.current = true;
            autoReportAttemptedThreadsRef.current.add(attemptKey);
            setAutoReportGenerating(true);
            setShouldAutoScroll(true);

            let resolvedReport = seedReport;
            try {
                try {
                    const backendReport = await resolveReportForOpen(seedReport);
                    if (backendReport) {
                        resolvedReport = backendReport;
                    }
                } catch (error) {
                    if (error?.message === 'PHONE_AUTH_EXPIRED') {
                        // Can't generate while signed out — release the one-shot so it retries
                        // once the driver re-authenticates.
                        autoReportAttemptedThreadsRef.current.delete(attemptKey);
                        return;
                    }
                    // Backend enrichment failed — fall back to the client-built report so a card
                    // ALWAYS appears. Returning empty here was the stall: the driver stayed stuck
                    // on "Ready to generate your symptom report?" with no report ever produced.
                    console.error('[Report] auto report backend enrichment failed; using client fallback', error);
                    resolvedReport = seedReport;
                }

                const reportCard = createReportCardMessage(resolvedReport);
                saveDiagnosticReport(resolvedReport);
                saveStoredReportCard([
                    ...(activeReportStorageKey ? [activeReportStorageKey] : []),
                    ...getReportCardAliasKeys(resolvedReport),
                ], reportCard);
                if (currentThreadId) saveThreadStateToServer(currentThreadId, { report_card: reportCard });
                notifyDiagnosticTicketChanged();
                liveReportReadyAttemptKeysRef.current.delete(attemptKey);
                autoReportFailCountRef.current.delete(attemptKey);
                setLiveDiagnosticReportCard(reportCard);
                setMessages((prev) => (
                    prev.some((message) => message.diagnosticReport?.id === resolvedReport.id)
                        ? prev
                        : [...prev, reportCard]
                ));
                revealReportCardAfterPaint();
            } finally {
                autoReportGenInFlightRef.current = false;
                setAutoReportGenerating(false);
                setStatus('');
            }
        };

        generateReportCard();
    }, [messages, isLoading, activeMessage, activeReportStorageKey, currentThreadId, currentTicketThreadId, currentTicketId, buildForcedDiagnosticReport, resolveReportForOpen, notifyDiagnosticTicketChanged, revealReportCardAfterPaint]);

    const openDiagnosticReport = async (report, event) => {
        if (!report) return;
        if (event?.preventDefault) event.preventDefault();
        try {
            const normalizedSeedReport = mergeReportWithPersistedClosedState(report);
            if (!normalizedSeedReport) return;
            const linkedTicket = resolveStrictDiagnosticTicketForReport(
                normalizedSeedReport,
                [
                    currentDiagnosticTicket,
                    syncedInlineDiagnosticTicket,
                    activeDiagnosticTicket,
                    ...(recentDiagnostics || []),
                ],
                currentThreadId,
            );
            const hasRealDiagnosticTicketId = Boolean(linkedTicket?.diagnostic_ticket_id);
            const reportShouldStayClosed = isClosedDiagnosticReport(normalizedSeedReport) || isClosedDiagnosticTicket(linkedTicket);
            const enrichedReport = linkedTicket
                ? {
                    ...normalizedSeedReport,
                    diagnosticTicketId: hasRealDiagnosticTicketId ? linkedTicket.diagnostic_ticket_id : (normalizedSeedReport.diagnosticTicketId || null),
                    ticketNumber: linkedTicket.display_ticket_number || linkedTicket.ticket_number || normalizedSeedReport.ticketNumber,
                    ticketStatus: reportShouldStayClosed ? 'closed' : (linkedTicket.status || normalizedSeedReport.ticketStatus),
                    ticketStage: reportShouldStayClosed ? 'closed' : (linkedTicket.stage || normalizedSeedReport.ticketStage),
                    status: reportShouldStayClosed ? 'Closed' : normalizedSeedReport.status,
                    threadId: normalizedSeedReport.threadId || normalizedSeedReport.thread_id || linkedTicket.thread_id || currentThreadId,
                    customerPhone: authState?.phone || normalizedSeedReport.customerPhone,
                }
                : {
                    ...normalizedSeedReport,
                    diagnosticTicketId: normalizedSeedReport.diagnosticTicketId || null,
                    ticketStatus: reportShouldStayClosed ? 'closed' : normalizedSeedReport.ticketStatus,
                    ticketStage: reportShouldStayClosed ? 'closed' : normalizedSeedReport.ticketStage,
                    status: reportShouldStayClosed ? 'Closed' : normalizedSeedReport.status,
                    threadId: normalizedSeedReport.threadId || normalizedSeedReport.thread_id || currentThreadId,
                    customerPhone: authState?.phone || normalizedSeedReport.customerPhone,
            };

            saveDiagnosticReport(enrichedReport);
            navigate(getDiagnosticReportPath(enrichedReport), {
                state: {
                    report: enrichedReport,
                    fromThreadId: currentThreadId,
                },
            });

            resolveReportForOpen(enrichedReport)
                .then((resolvedReport) => {
                    if (!resolvedReport) return;
                    const normalizedResolvedReport = normalizeDiagnosticReportForUi({
                        ...resolvedReport,
                        diagnosticTicketId: enrichedReport.diagnosticTicketId || resolvedReport.diagnosticTicketId || null,
                        ticketNumber: enrichedReport.ticketNumber || resolvedReport.ticketNumber,
                        ticketStatus: reportShouldStayClosed ? 'closed' : (enrichedReport.ticketStatus || resolvedReport.ticketStatus),
                        ticketStage: reportShouldStayClosed ? 'closed' : (enrichedReport.ticketStage || resolvedReport.ticketStage),
                        status: reportShouldStayClosed ? 'Closed' : (enrichedReport.status || resolvedReport.status),
                        threadId: enrichedReport.threadId || resolvedReport.threadId,
                        customerPhone: enrichedReport.customerPhone || resolvedReport.customerPhone,
                    });
                    saveDiagnosticReport(normalizedResolvedReport);
                })
                .catch((error) => {
                    if (error?.message !== 'PHONE_AUTH_EXPIRED') {
                        console.error('[Report] background enrichment failed after navigation', error);
                    }
                });
        } catch (error) {
            if (error?.message === 'PHONE_AUTH_EXPIRED') return;
            console.error('[Report] open failed, falling back to stored report navigation', error);
            navigate(getDiagnosticReportPath(report), {
                state: {
                    report,
                    fromThreadId: currentThreadId,
                },
            });
        }
    };

    const generateDiagnosticReport = async (report) => {
        const placeholderId = insertReportLoadingMessage({
            includeTriggerMessage: true,
            status: 'Generating diagnostic report...',
        });
        try {
            const resolvedReport = normalizeDiagnosticReportForUi(await resolveReportForOpen(report));
            appendDiagnosticReportMessage(resolvedReport, { placeholderId });
        } catch (error) {
            if (error?.message === 'PHONE_AUTH_EXPIRED') {
                removeReportLoadingMessage(placeholderId);
                return;
            }
            console.error('[Report] generate failed, falling back to local report', error);
            appendDiagnosticReportMessage(normalizeDiagnosticReportForUi(report), { placeholderId });
        }
    };

    // Keep a stable ref to the latest generator so the safety-net effect below can
    // call it without re-running (and resetting its timer) on every render.
    const generateDiagnosticReportRef = useRef(generateDiagnosticReport);
    generateDiagnosticReportRef.current = generateDiagnosticReport;

    // ── Safety net ────────────────────────────────────────────────────────────
    // If the "Generate report" / READY TO BUILD card is sitting idle — the primary
    // auto-generate effect didn't fire for some reason and the user hasn't tapped
    // the button — auto-trigger generation after a short delay so nobody gets stuck
    // staring at a manual button. resolveReportForOpen always resolves (real report
    // or client fallback within its timeout), so this guarantees a card appears.
    useEffect(() => {
        if (isLoading || activeMessage) return;
        if (autoReportGenerating) return;
        if (autoReportGenInFlightRef.current) return;
        if (messages.some((m) => m.isLoadingReport)) return;
        if (hasGeneratedDiagnosticReportForMessages(messages)) return;
        if (!hasDiagnosticTicketContext(messages)) return;

        // The last message must be an eligible "ready for report" assistant message
        // (same gate the manual GENERATE REPORT button uses).
        const lastMsg = [...messages].reverse().find(
            (m) => m?.role === 'assistant' && !m.isLoadingReport && !isNeedMoreDiagnosticsMessage(m)
        );
        if (!lastMsg || lastMsg.role !== 'assistant' || isDiagnosticReportMessage(lastMsg)) return;
        if (!canGenerateDiagnosticReportForMessages(messages)) return;
        // At the 7-turn hard cap the report must generate regardless of whether the backend emitted
        // an explicit "ready" signal — that's the whole point of the cap. Below the cap we still
        // require the backend's ready signal (liveReportReadyAttemptKeysRef) as before.
        const _lastVisible = extractSuggestionsAndContent(lastMsg.content || '').content || '';
        const _lastIsQuestion = /(^|\n)\s*question\s*:/i.test(_lastVisible) || /\?\s*$/.test(_lastVisible.trim());
        const hitHardCap = getDiagnosticUserTurnCountForMessages(messages) >= MAX_DIAGNOSTIC_USER_TURNS_BEFORE_FORCED_REPORT;
        const eligible = (
            (hitHardCap && !_lastIsQuestion) ||
            isDiagnosticFinalizedMessage(lastMsg) ||
            isReportEligibleDiagnosticMessage(lastMsg, messages)
        );
        if (!eligible) return;

        const attemptKey = currentThreadId ? `thread:${currentThreadId}` : (activeReportStorageKey || 'default');
        if (autoReportAttemptedThreadsRef.current.has(attemptKey)) return;
        if (!hitHardCap && !liveReportReadyAttemptKeysRef.current.has(attemptKey)) return;

        const timer = setTimeout(() => {
            // Re-check guards at fire time (state may have moved on during the delay).
            if (autoReportGenInFlightRef.current) return;
            if (autoReportAttemptedThreadsRef.current.has(attemptKey)) return;
            if (!hitHardCap && !liveReportReadyAttemptKeysRef.current.has(attemptKey)) return;
            if (hasGeneratedDiagnosticReportForMessages(messages)) return;
            if (messages.some((m) => m.isLoadingReport)) return;
            autoReportAttemptedThreadsRef.current.add(attemptKey);
            liveReportReadyAttemptKeysRef.current.delete(attemptKey);
            const report = createDiagnosticReport({
                messages,
                reportMessage: lastMsg,
                vehicleLabel: activeVehicleLabel,
            });
            if (!report) return;
            generateDiagnosticReportRef.current(report);
        }, 3500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, isLoading, activeMessage, autoReportGenerating, currentThreadId, activeReportStorageKey, activeVehicleLabel]);

    const resolveDiagnosticTicketForReport = useCallback((report) => {
        return resolveDiagnosticTicketForReportFromList(report, recentDiagnostics || [], currentThreadId);
    }, [recentDiagnostics, currentThreadId]);

    const handleStartRepairGuide = useCallback(() => {
        // Starting (or restarting) a repair requires acknowledging the safety checklist first.
        setRepairSafetyAcked(false);
        setGuidedCheckSession((prev) => {
            if (!prev?.report) return prev;
            const cause = prev.report.likelyCauses?.[prev.activeCauseIndex];
            if (!cause) return prev;
            const repairSteps = getRepairGuideStepsFromCause(cause);
            if (!repairSteps.length) return prev;

            return {
                ...prev,
                phase: 'repair_step',
                repairMileage: '',
                repairNote: '',
                closeError: '',
                // Re-arm the one-shot Monitoring transition: starting a (new) repair guide means
                // completing it should move the ticket to Monitoring again — even on the 2nd/3rd
                // likely cause after the driver marked earlier ones "still happening".
                ticketMonitoringSet: false,
                activeCard: {
                    type: 'repair_step',
                    causeIndex: prev.activeCauseIndex,
                    stepIndex: 0,
                },
                timeline: appendGuidedTimelineEntries(
                    prev.timeline,
                    createGuidedCardEntry(prev, { actionLabel: 'Start DIY Repair Guide' }),
                    createGuidedTextEntry('assistant', "Let's work through the DIY repair one step at a time. Use the arrows to move between steps, then mark the repair complete on the last one."),
                ),
            };
        });
        setShouldAutoScroll(true);
    }, []);

    // Prev/next navigation for the single DIY repair card — moves between steps in
    // place (clamped to range), keeping the whole walkthrough in ONE card.
    const handleRepairStepNav = useCallback((direction) => {
        setGuidedCheckSession((prev) => {
            if (!prev?.report || prev.activeCard?.type !== 'repair_step') return prev;
            const cause = prev.report.likelyCauses?.[prev.activeCauseIndex];
            const repairSteps = getRepairGuideStepsFromCause(cause);
            const cur = prev.activeCard.stepIndex || 0;
            const target = direction === 'next' ? cur + 1 : cur - 1;
            if (target < 0 || target >= repairSteps.length) return prev;
            return {
                ...prev,
                activeCard: { type: 'repair_step', causeIndex: prev.activeCauseIndex, stepIndex: target },
            };
        });
        setShouldAutoScroll(true);
    }, []);

    const handleRepairStepAction = useCallback((action) => {
        if (action === 'help') {
            setGuidedCheckSession((prev) => {
                if (!prev?.report) return prev;
                const cause = prev.report.likelyCauses?.[prev.activeCauseIndex];
                setInput(`I need help with the DIY repair for ${cause?.likelyRepair || cause?.recommendedCheck || 'this issue'}.`);
                return prev;
            });
            setShouldAutoScroll(true);
            return;
        }

        // "Mark repair complete" (shown on the last step) moves to the outcome check.
        setGuidedCheckSession((prev) => {
            if (!prev?.report || prev.activeCard?.type !== 'repair_step') return prev;
            const nextTimeline = appendGuidedTimelineEntries(
                prev.timeline,
                createGuidedCardEntry(prev, { action: 'complete', actionLabel: 'Repair complete' }),
                createGuidedTextEntry('user', 'Repair complete'),
            );

            return {
                ...prev,
                phase: 'repair_outcome_check',
                closeError: '',
                activeCard: {
                    type: 'repair_outcome_check',
                    causeIndex: prev.activeCauseIndex,
                },
                timeline: [
                    ...nextTimeline,
                    createGuidedTextEntry('assistant', 'Repair guide complete — nice work. Did this fix the issue?'),
                ],
            };
        });
        setShouldAutoScroll(true);
    }, []);

    // After the repair guide, confirm whether the issue is actually resolved.
    // Only a "fixed" answer proceeds to logging + closing the ticket; "not fixed"
    // keeps the ticket open and continues diagnosing the next likely cause.

    // Ask the backend for a few MORE likely causes (excluding the ones already
    // tried) when the first round of DIY didn't fix the issue.
    const fetchMoreLikelyCauses = useCallback(async (threadId, excludeTitles = []) => {
        if (!isAuthenticated || !threadId) return [];
        try {
            const resp = await fetch(`${CHAT_API_BASE_URL}/secure/threads/${encodeURIComponent(threadId)}/more-causes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ exclude_titles: excludeTitles, vehicle_label: activeVehicleLabel }),
            });
            if (!resp.ok) return [];
            const data = await resp.json();
            return Array.isArray(data?.causes) ? data.causes : [];
        } catch {
            return [];
        }
    }, [isAuthenticated, activeVehicleLabel]);

    // When no DIY causes remain (even after extra ones), recommend a professional.
    const suggestRepairShopFallback = useCallback((ruledOutCauseIds) => {
        setGuidedCheckSession((prev) => (prev?.report ? {
            ...prev,
            phase: 'done',
            ruledOutCauseIds: ruledOutCauseIds || prev.ruledOutCauseIds,
            selectedOutcome: '',
            notes: '',
            isFetchingMoreCauses: false,
            activeCard: null,
            timeline: appendGuidedTimelineEntries(
                prev.timeline,
                createGuidedTextEntry('assistant', 'We’ve worked through the likely causes and a few extra possibilities, but it’s still not resolved. At this point it’s best looked at by a professional. Would you like help finding a nearby repair shop? I can also tell them exactly what we’ve already checked.'),
            ),
        } : prev));
    }, []);

    // Move the diagnostic ticket linked to a guided report to a new stage/status. Used to run
    // the Open → Monitoring → Resolved lifecycle: a completed repair puts the ticket in
    // Monitoring (not auto-resolved); a confirmed fix closes it; "still present" resumes
    // diagnosis on the SAME ticket. Best-effort — silently no-ops without a resolvable ticket.
    const patchDiagnosticTicketState = useCallback(async (report, body) => {
        const ticket = (
            resolveDiagnosticTicketForReport(report) ||
            activeDiagnosticTicket ||
            currentDiagnosticTicket ||
            syncedInlineDiagnosticTicket
        );
        const phone = authState?.identity || resolveReportPhone(report) || resolveTicketPhone(ticket) || '';
        if (!phone) return null;
        // Patch every ticket that represents THIS conversation: the report's ticket AND the
        // ticket the current thread is displayed under (a conversation can carry more than one
        // ticket id, and the sidebar labels the thread by its own linked ticket — so patching
        // only the report's ticket can leave the sidebar showing a stale status).
        const ticketIds = [...new Set([
            resolveRealDiagnosticTicketId(ticket),
            resolveReportDiagnosticTicketId(report),
            resolveRealDiagnosticTicketId(currentDiagnosticTicket),
            resolveRealDiagnosticTicketId(syncedInlineDiagnosticTicket),
            resolveRealDiagnosticTicketId(activeDiagnosticTicket),
        ].filter(Boolean).map(String))];
        if (ticketIds.length === 0) return null;
        let updated = null;
        try {
            for (const id of ticketIds) {
                const resp = await fetch(
                    `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(id)}`,
                    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
                );
                if (resp.ok) updated = await resp.json();
            }
            // Refresh BOTH the diagnostics list and the threads list — the sidebar labels a
            // thread from its cached list_threads status, which otherwise stays stale.
            refreshDiagnosticTickets?.();
            refreshThreadTitles?.();
            notifyDiagnosticTicketChanged?.(updated);
            return updated;
        } catch (e) {
            console.warn('Diagnostic ticket state update failed:', e);
            return null;
        }
    }, [activeDiagnosticTicket, currentDiagnosticTicket, syncedInlineDiagnosticTicket, authState?.identity, refreshDiagnosticTickets, refreshThreadTitles, notifyDiagnosticTicketChanged]);

    // When a guided repair is marked complete (outcome-check card is shown), move the ticket
    // Open → Monitoring exactly once — the work is done but the issue isn't confirmed resolved.
    useEffect(() => {
        if (guidedCheckSession?.phase !== 'repair_outcome_check') return;
        if (guidedCheckSession.ticketMonitoringSet) return;
        const report = guidedCheckSession.report;
        setGuidedCheckSession((prev) => (prev ? { ...prev, ticketMonitoringSet: true } : prev));
        patchDiagnosticTicketState(report, {
            stage: 'monitoring',
            status: 'awaiting_outcome',
            summary: 'Repair completed — monitoring whether the original issue returns.',
        });
    }, [guidedCheckSession?.phase, guidedCheckSession?.ticketMonitoringSet, patchDiagnosticTicketState]);

    const handleRepairOutcome = useCallback(async (action) => {
        const snapshot = guidedCheckSession;
        if (!snapshot?.report) return;

        // "Not sure yet" — keep the ticket in Monitoring (do NOT resolve). The driver can
        // confirm fixed or report it still happening any time later.
        if (action === 'unsure') {
            // Offer browser notifications so the day-3 follow-up reaches the driver even with
            // the tab closed (best-effort; called within the click gesture for the permission prompt).
            tryEnableWebPushQuietly();
            // Arm the 3-day follow-up so the ticket doesn't sit in Monitoring forever — the
            // backend scheduler will check back in via chat.
            (async () => {
                try {
                    const phone = authState?.identity;
                    const ticketId = resolveReportDiagnosticTicketId(snapshot.report)
                        || resolveRealDiagnosticTicketId(currentDiagnosticTicket)
                        || resolveRealDiagnosticTicketId(syncedInlineDiagnosticTicket)
                        || resolveRealDiagnosticTicketId(activeDiagnosticTicket);
                    if (phone && ticketId) {
                        await fetch(
                            `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/${encodeURIComponent(ticketId)}/schedule-followup`,
                            { method: 'POST' },
                        );
                        refreshDiagnosticTickets?.();
                    }
                } catch (e) {
                    console.warn('[diy unsure] schedule follow-up failed', e);
                }
            })();
            setGuidedCheckSession((prev) => (prev?.report ? {
                ...prev,
                phase: 'done',
                activeCard: null,
                timeline: appendGuidedTimelineEntries(
                    prev.timeline,
                    createGuidedTextEntry('user', 'Not sure yet'),
                    createGuidedTextEntry('assistant', 'No problem — I’ll keep this ticket in Monitoring. Come back and tell me “it’s fixed” once you’re confident, or “it’s still happening” and we’ll keep diagnosing on this same ticket.'),
                ),
            } : prev));
            setShouldAutoScroll(true);
            return;
        }
        const causes = Array.isArray(snapshot.report.likelyCauses) ? snapshot.report.likelyCauses : [];
        const currentCause = causes[snapshot.activeCauseIndex];

        if (action === 'fixed') {
            // The driver confirmed the original issue is gone → move Monitoring → Resolved now
            // (so the sidebar reads "Resolved" even if they defer logging). The following
            // log-and-close step then records it to service history and finalizes the ticket.
            patchDiagnosticTicketState(snapshot.report, { status: 'resolved', outcome: 'successful' });
            setGuidedCheckSession((prev) => (prev?.report ? {
                ...prev,
                phase: 'repair_done_choice',
                closeError: '',
                activeCard: { type: 'repair_done_choice', causeIndex: prev.activeCauseIndex },
                timeline: appendGuidedTimelineEntries(
                    prev.timeline,
                    createGuidedTextEntry('user', 'Yes, it’s fixed'),
                    createGuidedTextEntry('assistant', 'Great news! Let’s log the repair and close the ticket.'),
                ),
            } : prev));
            setShouldAutoScroll(true);
            return;
        }

        // Not fixed — the issue is still present. Resume diagnosis on the SAME ticket: move it
        // back out of Monitoring into active diagnostics, keeping all existing context.
        patchDiagnosticTicketState(snapshot.report, { stage: 'diagnostics', status: 'open' });
        const ruledOutCauseIds = currentCause?.id
            ? [...new Set([...(snapshot.ruledOutCauseIds || []), currentCause.id])]
            : (snapshot.ruledOutCauseIds || []);
        const nextCauseIndex = causes.findIndex((cause, index) => (
            index > snapshot.activeCauseIndex && !ruledOutCauseIds.includes(cause.id)
        ));

        if (nextCauseIndex >= 0) {
            setGuidedCheckSession((prev) => (prev?.report ? {
                ...prev,
                activeCauseIndex: nextCauseIndex,
                currentStepIndex: 0,
                phase: 'next_cause',
                ruledOutCauseIds,
                selectedOutcome: '',
                notes: '',
                activeCard: { type: 'next_cause', causeIndex: nextCauseIndex, ruledOutTitle: currentCause?.title || '' },
                timeline: appendGuidedTimelineEntries(
                    prev.timeline,
                    createGuidedTextEntry('user', 'No, still happening'),
                    createGuidedTextEntry('assistant', 'Thanks for confirming. Since that didn’t resolve it, let’s check the next likely cause.'),
                ),
            } : prev));
            setShouldAutoScroll(true);
            return;
        }

        // No causes left in the current report. First time: pull a few MORE causes
        // from the backend and keep going. After that (or if none can be found):
        // recommend a repair shop. Never auto-close — the issue is unresolved.
        const canFetchMore = isAuthenticated && currentThreadId && !snapshot.extraCausesFetched;
        if (!canFetchMore) {
            setGuidedCheckSession((prev) => (prev?.report ? {
                ...prev,
                ruledOutCauseIds,
                timeline: appendGuidedTimelineEntries(prev.timeline, createGuidedTextEntry('user', 'No, still happening')),
            } : prev));
            suggestRepairShopFallback(ruledOutCauseIds);
            setShouldAutoScroll(true);
            return;
        }

        // Show progress while we look for more causes.
        setGuidedCheckSession((prev) => (prev?.report ? {
            ...prev,
            ruledOutCauseIds,
            isFetchingMoreCauses: true,
            activeCard: null,
            timeline: appendGuidedTimelineEntries(
                prev.timeline,
                createGuidedTextEntry('user', 'No, still happening'),
                createGuidedTextEntry('assistant', 'Let me dig a little deeper and find a few more possible causes…'),
            ),
        } : prev));
        setShouldAutoScroll(true);

        const excludeTitles = causes.map((cause) => cause.title).filter(Boolean);
        const moreCauses = await fetchMoreLikelyCauses(currentThreadId, excludeTitles);

        if (moreCauses && moreCauses.length) {
            setGuidedCheckSession((prev) => {
                if (!prev?.report) return prev;
                const baseCauses = Array.isArray(prev.report.likelyCauses) ? prev.report.likelyCauses : [];
                const reIded = moreCauses.map((cause, idx) => ({ ...cause, id: `extra-cause-${baseCauses.length + idx + 1}` }));
                const mergedReport = normalizeDiagnosticReportForUi({ ...prev.report, likelyCauses: [...baseCauses, ...reIded] });
                const newIndex = baseCauses.length;
                saveDiagnosticReport(mergedReport);
                return {
                    ...prev,
                    report: mergedReport,
                    extraCausesFetched: true,
                    isFetchingMoreCauses: false,
                    activeCauseIndex: newIndex,
                    currentStepIndex: 0,
                    phase: 'next_cause',
                    selectedOutcome: '',
                    notes: '',
                    activeCard: { type: 'next_cause', causeIndex: newIndex, ruledOutTitle: currentCause?.title || '' },
                    timeline: appendGuidedTimelineEntries(
                        prev.timeline,
                        createGuidedTextEntry('assistant', 'Here are a few more possible causes — let’s check the next one.'),
                    ),
                };
            });
            setShouldAutoScroll(true);
            return;
        }

        // Couldn't find anything new → recommend a shop.
        setGuidedCheckSession((prev) => (prev?.report ? { ...prev, extraCausesFetched: true, isFetchingMoreCauses: false } : prev));
        suggestRepairShopFallback(ruledOutCauseIds);
        setShouldAutoScroll(true);
    }, [guidedCheckSession, isAuthenticated, currentThreadId, fetchMoreLikelyCauses, suggestRepairShopFallback, patchDiagnosticTicketState]);

    const handleRepairLogChoice = useCallback((action) => {
        setGuidedCheckSession((prev) => {
            if (!prev?.report) return prev;
            if (action === 'back') {
                return {
                    ...prev,
                    phase: 'repair_done_choice',
                    closeError: '',
                    activeCard: {
                        type: 'repair_done_choice',
                        causeIndex: prev.activeCauseIndex,
                    },
                };
            }
            if (action === 'later') {
                return {
                    ...prev,
                    phase: 'done',
                    activeCard: null,
                    timeline: appendGuidedTimelineEntries(
                        prev.timeline,
                        createGuidedCardEntry(prev, { actionLabel: 'Maybe later' }),
                        createGuidedTextEntry('assistant', 'No problem — the repair guide is saved in this chat. You can log it and close the ticket later from here or from the report.'),
                    ),
                };
            }

            return {
                ...prev,
                phase: 'repair_log_form',
                closeError: '',
                activeCard: {
                    type: 'repair_log_form',
                    causeIndex: prev.activeCauseIndex,
                },
            };
        });
        setShouldAutoScroll(true);
    }, []);

    const handleRepairLogFieldChange = useCallback((field, value) => {
        setGuidedCheckSession((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
                closeError: '',
                // Editing the mileage re-arms the odometer-decrease warning.
                ...(field === 'repairMileage' ? { odometerDecreaseConfirmed: false } : {}),
            };
        });
    }, []);

    const handleRepairLogSubmit = useCallback(async () => {
        const snapshot = guidedCheckSession;
        if (!snapshot?.report) return;

        const cause = snapshot.report.likelyCauses?.[snapshot.activeCauseIndex];
        if (!cause) return;

        // The mileage is entered in the user's preferred unit; convert to MILES once so every
        // downstream compare/store uses the canonical unit (never km stored as miles).
        const repairOdoMiles = toMiles(snapshot.repairMileage, distanceUnit);

        // Enforce the 1,000,000-mile maximum (#412) before saving/syncing the odometer.
        if (repairOdoMiles !== null && repairOdoMiles > MAX_ODOMETER_MILES) {
            setGuidedCheckSession((prev) => prev ? { ...prev, closeError: ODOMETER_TOO_HIGH_MESSAGE } : prev);
            return;
        }

        // Odometer sanity: a reading normally only increases. The repair-log mileage SYNCS
        // the vehicle's odometer below, so a value LOWER than the current reading would roll
        // it back (likely a typo or wrong unit). Warn once and require a re-submit to confirm.
        const _currentOdoMiles = activeVehicleInfo?.mileageMiles ?? normalizeOdometerInput(activeVehicleInfo?.mileage);
        if (
            repairOdoMiles !== null && repairOdoMiles > 0 &&
            _currentOdoMiles !== null && _currentOdoMiles > 0 &&
            repairOdoMiles < _currentOdoMiles &&
            !snapshot.odometerDecreaseConfirmed
        ) {
            setGuidedCheckSession((prev) => prev ? {
                ...prev,
                odometerDecreaseConfirmed: true,
                closeError: `The reading you entered (${formatMileage(repairOdoMiles, distanceUnit)}) is lower than the current odometer (${formatMileage(_currentOdoMiles, distanceUnit)}). An odometer normally only goes up — press Submit again to confirm, or Back to fix it.`,
            } : prev);
            return;
        }

        setGuidedCheckSession((prev) => prev ? { ...prev, isClosingTicket: true, closeError: '' } : prev);

        try {
            let linkedTicket = (
                resolveDiagnosticTicketForReport(snapshot.report) ||
                activeDiagnosticTicket ||
                currentDiagnosticTicket ||
                syncedInlineDiagnosticTicket
            );
            let closeTargetPhone = (
                authState?.identity ||
                resolveReportPhone(snapshot.report) ||
                resolveTicketPhone(linkedTicket) ||
                resolveTicketPhone(activeDiagnosticTicket) ||
                resolveTicketPhone(currentDiagnosticTicket) ||
                resolveTicketPhone(syncedInlineDiagnosticTicket) ||
                resolveTicketPhone(recentDiagnostics?.find((ticket) => !isClosedDiagnosticTicket(ticket))) ||
                resolveTicketPhone(recentDiagnostics?.[0]) ||
                ''
            );
            linkedTicket = await resolveCloseableDiagnosticTicket({
                report: snapshot.report,
                currentThreadId,
                linkedTicket,
                activeDiagnosticTicket,
                currentDiagnosticTicket,
                inlineDiagnosticTicket: syncedInlineDiagnosticTicket,
                recentDiagnostics,
                refreshDiagnosticTickets,
                phone: closeTargetPhone,
            }) || linkedTicket;
            closeTargetPhone = closeTargetPhone || resolveTicketPhone(linkedTicket);
            const closeTicketId = resolveRealDiagnosticTicketId(linkedTicket) || resolveReportDiagnosticTicketId(snapshot.report) || null;
            // Prefer the user's own repair note (if they gave one) over the generic
            // sentence, so the Glovebox History card shows what was actually done.
            const trimmedRepairNote = (snapshot.repairNote || '').trim();
            const closeSummary = trimmedRepairNote || `${cause.likelyRepair || cause.recommendedCheck || 'Repair'} completed from chat DIY flow.`;

            // If this report is a scheduled-maintenance item (not a fault), tell the backend
            // the service so it marks that maintenance done in the Garage schedule when the
            // ticket closes. Only maintenance reports send this — faults never do.
            const isMaintenanceReport = (
                snapshot.report?.diagnosisStage === 'Maintenance' ||
                /\bmaintenance\b/i.test(snapshot.report?.issue || '')
            );
            const maintenanceTitle = isMaintenanceReport
                ? (cause?.likelyRepair || snapshot.report?.issue || '')
                : '';
            const maintenanceOdometer = repairOdoMiles;

            if (!closeTargetPhone) {
                // A guest has no phone on file, so the repair can't be saved / ticket
                // closed. Tell them what to do next instead of a vague internal error.
                throw new Error(isAuthenticated
                    ? 'Ticket details are missing for this chat.'
                    : 'Login to unlock all features.');
            }

            let closedTicket = null;
            if (closeTicketId) {
                const response = await fetch(
                    `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(closeTargetPhone)}/${encodeURIComponent(closeTicketId)}`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            stage: 'closed',
                            status: 'closed',
                            outcome: 'successful',
                            summary: closeSummary,
                            diy_checks: Array.isArray(snapshot.report?.completedGuidedChecks) ? snapshot.report.completedGuidedChecks : [],
                            maintenance_title: maintenanceTitle || '',
                            maintenance_odometer: maintenanceOdometer,
                        }),
                    }
                );

                if (response.ok) {
                    closedTicket = await response.json();
                } else if (response.status === 404) {
                    closedTicket = await closeMatchingDiagnosticTicket({
                        phone: closeTargetPhone,
                        report: snapshot.report,
                        currentThreadId,
                        linkedTicket,
                        summary: closeSummary,
                        maintenanceTitle,
                        maintenanceOdometer,
                    });
                } else {
                    let errorMessage = 'Unable to close ticket';
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData?.detail || errorData?.error || errorMessage;
                    } catch {}
                    throw new Error(errorMessage);
                }
            } else {
                closedTicket = await closeMatchingDiagnosticTicket({
                    phone: closeTargetPhone,
                    report: snapshot.report,
                    currentThreadId,
                    linkedTicket,
                    summary: closeSummary,
                    maintenanceTitle,
                    maintenanceOdometer,
                });
            }

            const existingRepairEntries = Array.isArray(snapshot.report.completedRepairGuides)
                ? snapshot.report.completedRepairGuides
                : [];
            const existingEntry = existingRepairEntries.find((entry) => entry.causeId === cause.id);
            const completedRepair = buildCompletedRepairGuide({
                cause,
                mileage: snapshot.repairMileage,
                note: snapshot.repairNote,
                existingEntry,
            });
            const updatedReport = {
                ...snapshot.report,
                completedRepairGuides: [
                    ...existingRepairEntries.filter((entry) => entry.causeId !== cause.id),
                    completedRepair,
                ],
                diagnosticTicketId: closedTicket?.diagnostic_ticket_id || closeTicketId,
                ticketNumber: snapshot.report.ticketNumber || resolveDiagnosticTicketNumber(linkedTicket),
                ticketStatus: closedTicket?.status || 'closed',
                ticketStage: closedTicket?.stage || 'closed',
                status: 'Closed',
                customerPhone: closeTargetPhone,
            };
            saveDiagnosticReport(updatedReport);
            const closedThreadId = resolveTicketThreadId(linkedTicket) || snapshot.report.threadId || currentThreadId || '';
            window.dispatchEvent(new CustomEvent('dash_active_diagnostic_ticket', {
                detail: {
                    ...(linkedTicket || {}),
                    ...(closedTicket || {}),
                    diagnostic_ticket_id: closedTicket?.diagnostic_ticket_id || closeTicketId,
                    display_ticket_number: resolveDiagnosticTicketNumber(linkedTicket) || linkedTicket?.display_ticket_number || '',
                    ticket_number: linkedTicket?.ticket_number || closedTicket?.ticket_number || closeTicketId,
                    thread_id: closedThreadId,
                    source_repair_log_id: closedThreadId || resolveTicketThreadId(linkedTicket),
                    title: resolveTicketTitle(linkedTicket) || snapshot.report.issue,
                    symptom_summary: linkedTicket?.symptom_summary || snapshot.report.issue,
                    vehicle_label: getDiagnosticTicketVehicleLabel(linkedTicket) || snapshot.report.vehicle || '',
                    vehicleLabel: getDiagnosticTicketVehicleLabel(linkedTicket) || snapshot.report.vehicle || '',
                    phone_number: closeTargetPhone,
                    stage: closedTicket?.stage || 'closed',
                    status: closedTicket?.status || 'closed',
                    outcome: closedTicket?.outcome || 'successful',
                },
            }));
            await refreshDiagnosticTickets?.();

            // Sync the vehicle's odometer with the mileage entered in the repair log
            // so the Garage and its upcoming-maintenance schedule reflect the latest
            // reading. Without this the Garage keeps showing the old odometer.
            const repairOdometer = repairOdoMiles;
            const odometerVehicleId = (
                linkedTicket?.vehicle_id ||
                linkedTicket?.vehicle?.vehicle_id ||
                closedTicket?.vehicle_id ||
                activeDiagnosticTicket?.vehicle_id ||
                null
            );
            if (repairOdometer !== null && repairOdometer > 0 && odometerVehicleId && closeTargetPhone) {
                try {
                    await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            phone_number: closeTargetPhone,
                            vehicle_id: odometerVehicleId,
                            is_new_vehicle: false,
                            odometer_reading: repairOdometer,
                            mileage: repairOdometer,
                        }),
                    });
                    // Tell the Garage / vehicle selector to refetch with the new reading.
                    window.dispatchEvent(new CustomEvent('dash_vehicle_updated'));
                    window.dispatchEvent(new CustomEvent('dash_vehicle_switched'));
                } catch (odometerError) {
                    console.warn('Odometer sync from repair log failed:', odometerError);
                }
            }

            // Mark the scheduled-maintenance service DONE in the Garage schedule so its "Overdue"
            // reminder clears. Done here (not only via the ticket-close hook) because the DIY
            // maintenance report has NO ticket to close — this uses the existing maintenance-confirm
            // endpoint directly, so it works for both the check flow and the DIY report flow.
            if (isMaintenanceReport && closeTargetPhone) {
                const maintenanceServiceKey = resolveMaintenanceServiceKey(
                    maintenanceTitle || cause?.likelyRepair || snapshot.report?.issue || ''
                );
                if (maintenanceServiceKey) {
                    // Pass vehicle_id when we know it (the check flow's ticket vehicle). When it's
                    // unknown (the DIY report has no ticket), omit it — the backend resolves the
                    // user's vehicle.
                    const confirmBody = {
                        phone_number: closeTargetPhone,
                        service_key: maintenanceServiceKey,
                        done: true,
                    };
                    if (odometerVehicleId) confirmBody.vehicle_id = odometerVehicleId;
                    if (repairOdometer && repairOdometer > 0) confirmBody.odometer = repairOdometer;
                    try {
                        await fetch(`${getUserDetailsBaseUrl()}/api/maintenance-confirm`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(confirmBody),
                        });
                        // Refresh the Garage maintenance schedule so the item drops off "Overdue".
                        window.dispatchEvent(new CustomEvent('dash_vehicle_updated'));
                    } catch (maintErr) {
                        console.warn('Maintenance schedule mark-done failed:', maintErr);
                    }
                }
            }

            setGuidedCheckSession((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    report: updatedReport,
                    phase: 'repair_logged',
                    isClosingTicket: false,
                    closeError: '',
                    activeCard: {
                        type: 'repair_logged',
                        causeIndex: prev.activeCauseIndex,
                    },
                    timeline: appendGuidedTimelineEntries(
                        prev.timeline,
                        createGuidedCardEntry(prev, {
                            actionLabel: 'Log it and close the ticket',
                            repairMileage: snapshot.repairMileage,
                            repairNote: snapshot.repairNote,
                        }),
                        createGuidedTextEntry('user', 'Log it and close the ticket'),
                        createGuidedTextEntry('assistant', `${cause.likelyRepair || 'Repair'} marked as complete and the ticket is now closed.`),
                    ),
                };
            });
        } catch (error) {
            setGuidedCheckSession((prev) => prev ? {
                ...prev,
                isClosingTicket: false,
                closeError: error?.message || 'Unable to close ticket',
            } : prev);
        }
        setShouldAutoScroll(true);
    }, [
        guidedCheckSession,
        resolveDiagnosticTicketForReport,
        activeDiagnosticTicket,
        currentDiagnosticTicket,
        syncedInlineDiagnosticTicket,
        currentThreadId,
        authState?.identity,
        isAuthenticated,
        recentDiagnostics,
        refreshDiagnosticTickets,
        activeVehicleInfo,
        distanceUnit,
    ]);

    // Render messages in a stable chronological order so a late/merged message
    // (the client merge helpers append at the end) can never appear out of
    // sequence — e.g. all user bubbles grouped above all replies. Primary key is
    // the timestamp; ties are broken by the monotonic server message id, then by
    // the original insertion order (stable) for optimistic messages that have
    // neither yet. This mirrors the backend's deterministic (created_at, id) order.
    const renderedMessages = useMemo(() => {
        const indexed = messages.map((message, index) => ({ message, index }));
        const parseTs = (message) => {
            const ts = Date.parse(message?.timestamp || '');
            return Number.isNaN(ts) ? null : ts;
        };
        indexed.sort((a, b) => {
            const ta = parseTs(a.message);
            const tb = parseTs(b.message);
            if (ta != null && tb != null && ta !== tb) return ta - tb;
            const sa = a.message?.serverId;
            const sb = b.message?.serverId;
            if (sa != null && sb != null && sa !== sb) return sa - sb;
            return a.index - b.index;
        });
        return indexed.map((entry) => entry.message);
    }, [messages]);

    // A backend [OPEN_ADD_VEHICLE] marker means CarFido is deferring an add-vehicle request to
    // the structured Add Vehicle form (so new vehicles are entered and validated there, never
    // collected conversationally). Open the form, pre-filled from the user's request.
    const openAddVehicleHandledRef = useRef('');
    useEffect(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m?.role !== 'assistant') continue;
            if (!/\[OPEN_ADD_VEHICLE\]/i.test(String(m.content || ''))) break; // only the latest assistant turn
            const key = String(m.id || m.serverId || i);
            if (openAddVehicleHandledRef.current === key) break;
            openAddVehicleHandledRef.current = key;
            let prefill = null;
            for (let j = i - 1; j >= 0; j--) {
                if (messages[j]?.role === 'user') {
                    try { prefill = parseVehicleFromAddRequest(String(messages[j].content || '')); } catch { prefill = null; }
                    break;
                }
            }
            window.dispatchEvent(new CustomEvent('dash_open_add_vehicle', { detail: { prefill } }));
            break;
        }
    }, [messages]);

    const diagnosticTicketCardMessageIndex = !guidedCheckSession && syncedInlineDiagnosticTicket
        ? (() => {
            const cardAnchorIndex = renderedMessages.findIndex((message) => (
                isDiagnosticTicketCardAnchorMessage(message)
            ));
            if (cardAnchorIndex >= 0) return cardAnchorIndex;
            const strictAnchor = renderedMessages.findIndex((message) => isDiagnosticTicketAnchorMessage(message));
            if (strictAnchor >= 0) return strictAnchor;
            // A ticket exists but no strict anchor matched — this happens when the first
            // diagnostic reply mentions a symptom word that isn't in the keyword list
            // (e.g. "loose steering"). Fall back to the FIRST assistant reply that asks a
            // diagnostic question so the ticket card still appears on the first turn.
            return renderedMessages.findIndex((message) => {
                if (!message || message.role !== 'assistant') return false;
                if (isDiagnosticReportMessage(message) || suppressesInlineDiagnosticTicket(message)) return false;
                const content = cleanDisplayText(getMessageContent(message)).toLowerCase();
                if (!content || content.includes('previous issue about') || content.includes('something new')) return false;
                return /\?/.test(content) || hasInlineChoiceButtons(message);
            });
        })()
        : -1;

    const startNextGuidedCause = useCallback((causeIndex) => {
        setGuidedCheckSession((prev) => {
            if (!prev?.report) return prev;
            const cause = prev.report.likelyCauses?.[causeIndex];
            if (!cause) return prev;
            return {
                ...prev,
                activeCauseIndex: causeIndex,
                currentStepIndex: 0,
                phase: 'step',
                activeCard: {
                    type: 'step',
                    causeIndex,
                    stepIndex: 0,
                },
                timeline: appendGuidedTimelineEntries(
                    prev.timeline,
                    createGuidedCardEntry(prev, { actionLabel: `Start ${cause?.recommendedCheck || 'next check'}` })
                ),
            };
        });
        setShouldAutoScroll(true);
    }, []);

    // Advance the single guided-check card to the next step, or to the result card when the
    // last step is done. Shared by the observe buttons and the evidence-capture continuation.
    const advanceGuidedStep = useCallback(() => {
        setGuidedCheckSession((prev) => {
            if (!prev?.report) return prev;
            const cause = prev.report.likelyCauses?.[prev.activeCauseIndex];
            const steps = getGuidedStepsFromCause(cause);
            const currentStep = prev.currentStepIndex || 0;

            // Advance to the next step IN PLACE (single-card flow) — no timeline entries,
            // so the walkthrough stays as ONE card instead of stacking a card per step.
            if (currentStep + 1 < steps.length) {
                return {
                    ...prev,
                    currentStepIndex: currentStep + 1,
                    phase: 'step',
                    evidence: undefined,
                    activeCard: {
                        type: 'step',
                        causeIndex: prev.activeCauseIndex,
                        stepIndex: currentStep + 1,
                    },
                };
            }

            // Last step done → move to recording the outcome.
            return {
                ...prev,
                phase: 'result',
                selectedOutcome: '',
                notes: '',
                evidence: undefined,
                activeCard: {
                    type: 'result',
                    causeIndex: prev.activeCauseIndex,
                },
            };
        });
        setShouldAutoScroll(true);
    }, []);

    const handleGuidedStepAction = useCallback((action) => {
        if (action === 'help') {
            if (guidedCheckSession?.report) {
                const cause = guidedCheckSession.report.likelyCauses?.[guidedCheckSession.activeCauseIndex];
                const stepNumber = (guidedCheckSession.currentStepIndex || 0) + 1;
                setInput(`I need help with ${cause?.recommendedCheck || 'this diagnostic check'} step ${stepNumber}.`);
            }
            setShouldAutoScroll(true);
            return;
        }

        // "Found an Issue" → pause on an evidence-capture card so the user can attach
        // photos/videos and/or type what they saw. It's stored on the ticket as Evidence,
        // then the guided flow continues. Skippable.
        if (action === 'issue') {
            setGuidedCheckSession((prev) => {
                if (!prev?.report) return prev;
                return {
                    ...prev,
                    phase: 'evidence',
                    evidence: { submitting: false, error: '', saved: null },
                    activeCard: {
                        type: 'evidence',
                        causeIndex: prev.activeCauseIndex,
                        stepIndex: prev.currentStepIndex || 0,
                    },
                };
            });
            setShouldAutoScroll(true);
            return;
        }

        advanceGuidedStep();
    }, [guidedCheckSession, advanceGuidedStep]);

    const handleGuidedEvidenceSkip = useCallback(() => {
        advanceGuidedStep();
    }, [advanceGuidedStep]);

    const handleGuidedEvidenceSubmit = useCallback(async ({ note, files }) => {
        const session = guidedCheckSession;
        if (!session?.report) return;
        const cause = session.report.likelyCauses?.[session.activeCauseIndex];
        const stepIdx = session.currentStepIndex || 0;
        const step = getGuidedStepsFromCause(cause)?.[stepIdx];
        const checkTitle = cause?.title || cause?.recommendedCheck || step?.instruction || 'Diagnostic check';

        setGuidedCheckSession((prev) => prev ? { ...prev, evidence: { submitting: true, error: '', saved: null } } : prev);

        const threadIdForEvidence = currentThreadId
            || session.report?.threadId || session.report?.thread_id || session.threadId || null;
        const vehicleIdForEvidence = (currentThread?.vehicle_id ?? activeVehicleInfo?.vehicle_id) || undefined;
        const attachmentsForEvidence = Array.isArray(files) && files.length > 0 ? files : undefined;

        try {
            const url = isAuthenticated
                ? `${API_BASE_URL}/api/diagnostic/evidence/secure`
                : `${API_BASE_URL}/api/diagnostic/evidence`;
            const body = isAuthenticated
                ? {
                    note,
                    check_title: checkTitle,
                    thread_id: threadIdForEvidence ? Number(threadIdForEvidence) : undefined,
                    vehicle_id: vehicleIdForEvidence,
                    attachments: attachmentsForEvidence,
                }
                : {
                    user_id: activeUserId,
                    session_id: activeSessionId,
                    note,
                    check_title: checkTitle,
                    vehicle_id: vehicleIdForEvidence,
                    attachments: attachmentsForEvidence,
                };
            const resp = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getTesterModeHeaders() },
                credentials: isAuthenticated ? 'include' : 'same-origin',
                body: JSON.stringify(body),
            });
            if (!resp.ok) throw new Error(`Evidence save failed (${resp.status})`);
            const data = await resp.json().catch(() => ({}));
            const mediaCount = Number(data?.saved_count || 0);
            const noteSaved = Boolean(data?.note_saved);
            setGuidedCheckSession((prev) => prev ? {
                ...prev,
                evidence: { submitting: false, error: '', saved: { mediaCount, noteSaved } },
            } : prev);
            // Show the saved confirmation briefly, then continue the guided walkthrough.
            setTimeout(() => { advanceGuidedStep(); }, 1300);
        } catch (e) {
            console.error('[evidence] save failed', e);
            setGuidedCheckSession((prev) => prev ? {
                ...prev,
                evidence: { submitting: false, error: 'Could not save evidence. Please try again.', saved: null },
            } : prev);
        }
    }, [guidedCheckSession, currentThreadId, currentThread, activeVehicleInfo, isAuthenticated, activeUserId, activeSessionId, advanceGuidedStep]);

    // Prev/next navigation for the single guided-check card — moves between steps
    // in place (clamped to range) without recording anything or stacking cards.
    const handleGuidedStepNav = useCallback((direction) => {
        setGuidedCheckSession((prev) => {
            if (!prev?.report || prev.activeCard?.type !== 'step') return prev;
            const cause = prev.report.likelyCauses?.[prev.activeCauseIndex];
            const steps = getGuidedStepsFromCause(cause);
            const cur = typeof prev.activeCard.stepIndex === 'number'
                ? prev.activeCard.stepIndex
                : (prev.currentStepIndex || 0);
            const target = direction === 'next' ? cur + 1 : cur - 1;
            if (target < 0 || target >= steps.length) return prev;
            return {
                ...prev,
                currentStepIndex: target,
                activeCard: { type: 'step', causeIndex: prev.activeCauseIndex, stepIndex: target },
            };
        });
        setShouldAutoScroll(true);
    }, []);

    const handleGuidedOutcomeSelect = useCallback((value) => {
        setGuidedCheckSession((prev) => prev ? { ...prev, selectedOutcome: value } : prev);
    }, []);

    const handleGuidedNotesChange = useCallback((value) => {
        setGuidedCheckSession((prev) => prev ? { ...prev, notes: value } : prev);
    }, []);

    const handleGuidedResultSave = useCallback(() => {
        const outcomeLabels = GUIDED_CHECK_OUTCOME_LABELS;

        let reportToPersist = null;

        setGuidedCheckSession((prev) => {
            if (!prev?.report || !prev.selectedOutcome) return prev;

            const currentCause = prev.report.likelyCauses?.[prev.activeCauseIndex];
            const existingCompletedChecks = Array.isArray(prev.report.completedGuidedChecks)
                ? prev.report.completedGuidedChecks
                : [];
            const existingEntry = existingCompletedChecks.find((entry) => entry.causeId === currentCause?.id);
            const completedEntry = buildCompletedGuidedCheck({
                cause: currentCause,
                outcome: prev.selectedOutcome,
                notes: prev.notes,
                existingEntry,
            });
            const updatedReport = {
                ...prev.report,
                completedGuidedChecks: [
                    ...existingCompletedChecks.filter((entry) => entry.causeId !== currentCause?.id),
                    completedEntry,
                ],
            };
            reportToPersist = updatedReport;
            const likelyCauses = Array.isArray(prev.report.likelyCauses) ? prev.report.likelyCauses : [];
            const nextCauseIndex = likelyCauses.findIndex((cause, index) => (
                index > prev.activeCauseIndex && !prev.ruledOutCauseIds.includes(cause.id)
            ));
            const nextTimeline = appendGuidedTimelineEntries(
                prev.timeline,
                createGuidedCardEntry(prev, {
                    outcomeLabel: outcomeLabels[prev.selectedOutcome],
                    selectedOutcome: prev.selectedOutcome,
                    notes: prev.notes,
                }),
                createGuidedTextEntry('user', `Result: ${outcomeLabels[prev.selectedOutcome]}`),
            );

            if (prev.selectedOutcome === 'issue') {
                return {
                    ...prev,
                    report: updatedReport,
                    phase: 'confirmed',
                    activeCard: {
                        type: 'confirmed',
                        causeIndex: prev.activeCauseIndex,
                    },
                    timeline: [
                        ...nextTimeline,
                        createGuidedTextEntry('assistant', `That result supports ${currentCause?.title}. Review the report and move forward with the repair plan.`),
                    ],
                };
            }

            if (nextCauseIndex >= 0) {
                const ruledOutCauseIds = prev.selectedOutcome === 'normal'
                    ? [...new Set([...prev.ruledOutCauseIds, currentCause.id])]
                    : prev.ruledOutCauseIds;
                return {
                    ...prev,
                    report: updatedReport,
                    activeCauseIndex: nextCauseIndex,
                    currentStepIndex: 0,
                    phase: 'next_cause',
                    ruledOutCauseIds,
                    selectedOutcome: '',
                    notes: '',
                    activeCard: {
                        type: 'next_cause',
                        causeIndex: nextCauseIndex,
                        ruledOutTitle: prev.selectedOutcome === 'normal' ? currentCause?.title : '',
                    },
                    timeline: [
                        ...nextTimeline,
                        createGuidedTextEntry(
                            'assistant',
                            prev.selectedOutcome === 'normal'
                                ? "Got it — that cause appears less likely. Here's what to check next."
                                : "No problem — let's keep that cause in mind and move to the next likely cause."
                        ),
                    ],
                };
            }

            return {
                ...prev,
                report: updatedReport,
                phase: 'done',
                selectedOutcome: '',
                notes: '',
                activeCard: null,
                timeline: [
                    ...nextTimeline,
                    createGuidedTextEntry('assistant', 'No further guided checks remain on this report. Review the diagnostic report for next steps.'),
                ],
            };
        });
        if (reportToPersist) {
            saveDiagnosticReport(reportToPersist);
        }
        setShouldAutoScroll(true);
    }, []);

    const canShowSendButton = input.trim().length > 0 || (!isShopMode && attachments.length > 0);
    const isDashResponding = isLoading && isLoadingRef.current;
    const isComposerBusy = (isLoading && !isDashResponding) || isRecording || isTranscribingVoiceDraft || isRepairGuideActive || isSendingShopMessage || isUpdatingGarageRequestStatus;
    const canSubmitMessage = canShowSendButton && !isDashResponding && !isComposerBusy;
    const mobileComposerBottom = mobileKeyboardOffset > 0
        ? `${mobileKeyboardOffset + 4}px`
        : 'calc(56px + max(4px, env(safe-area-inset-bottom)))';
    const chatScrollBottomPadding = mobileKeyboardOffset > 0
        ? `${mobileKeyboardOffset + 128}px`
        : undefined;

    useEffect(() => {
        if (canShowSendButton && handsFree.enabled) {
            handsFree.stop();
        }
    }, [canShowSendButton, handsFree.enabled, handsFree.stop]);

    const showGarageShopBanner = linkedGarageRequests.length > 0;
    // The diagnostic context banner ("Diagnosing: …") belongs to the diagnostic chat, not the
    // shop conversation — hide it while viewing a shop chat.
    const showTopContext = Boolean((activeChatContext && !isShopMode) || guidedCheckSession || showGarageShopBanner);
    const openGarageForShop = async (reportArg, shopResultArg = null) => {
        const normalizedReport = reportArg ? normalizeDiagnosticReportForUi(reportArg) : null;
        const linkedTicket = normalizedReport
            ? resolveStrictDiagnosticTicketForReport(
                normalizedReport,
                [
                    currentDiagnosticTicket,
                    syncedInlineDiagnosticTicket,
                    activeDiagnosticTicket,
                    ...(recentDiagnostics || []),
                ],
                currentThreadId,
            )
            : (activeChatContext?.ticket || currentDiagnosticTicket || syncedInlineDiagnosticTicket || activeDiagnosticTicket || null);
        let diagnosticTicketId = getReportDiagnosticTicketId(normalizedReport || {})
            || resolveRealDiagnosticTicketId(linkedTicket)
            || '';

        // Any report WITHOUT a diagnostic ticket (a scheduled-maintenance report, or any ticketless
        // report) needs a lightweight ticket so the shop drawer can request a partner estimate — not
        // just list shops. Fault reports already carry a ticket, so this only fires when one is
        // genuinely missing. Creating it here (rather than relying on fragile maintenance-detection)
        // guarantees the shop drawer gets a ticket number + the report to send. Falls back to the
        // contact-only drawer if creation fails.
        if (!diagnosticTicketId && normalizedReport) {
            try {
                const estimateTitle = normalizedReport.issue
                    || normalizedReport.likelyCauses?.[0]?.title
                    || 'Vehicle service';
                const { diagnosticTicketId: newId, ticketNumber } = await createMaintenanceEstimateTicket({
                    title: estimateTitle,
                    summary: normalizedReport.summary || estimateTitle,
                });
                if (newId) {
                    diagnosticTicketId = String(newId);
                    normalizedReport.diagnosticTicketId = newId;
                    if (ticketNumber) normalizedReport.ticketNumber = ticketNumber;
                }
            } catch (err) {
                console.warn('Estimate ticket creation failed; opening shops in contact-only mode:', err);
            }
        }

        if (normalizedReport) {
            saveDiagnosticReport(normalizedReport);
        }
        const fallbackIssue = cleanDisplayText(
            normalizedReport?.issue ||
            shopResultArg?.issueSummary ||
            resolveTicketTitle(linkedTicket) ||
            activeChatContext?.title ||
            'Start Diagnostic'
        );
        const defaultZip = getZipFromShopResult(shopResultArg);
        navigate('/garage', {
            state: {
                openEstimateTicketId: diagnosticTicketId || null,
                openEstimateThreadId: resolveTicketThreadId(linkedTicket) || normalizedReport?.threadId || normalizedReport?.thread_id || currentThreadId || null,
                // A ticket-less report still needs the shop list. Pass the report's vehicle + issue
                // (and the ticket id if we just created one) so the Garage opens the shop drawer —
                // with estimate-request enabled when a ticket exists, contact-only otherwise.
                openShopReport: {
                    vehicle: normalizedReport?.vehicle || activeVehicleLabel || '',
                    issue: fallbackIssue || 'Start Diagnostic',
                    ticketId: diagnosticTicketId || null,
                    defaultZip,
                },
            },
        });
    };
    openGarageForShopRef.current = openGarageForShop;
    const openGarageChat = useCallback((request) => {
        const requestId = request?.request_id || request?.id;
        if (!requestId) return;
        setChatMode('shop');
        setSelectedGarageRequestId(String(requestId));
        setShouldAutoScroll(true);
        window.setTimeout(() => {
            chatInputRef.current?.focus();
        }, 50);
    }, []);
    const renderShopConversationPane = () => {
        if (!selectedGarageRequest) return null;
        const shouldShowScheduleCard = canSchedulePartnerAppointment(selectedGarageRequest);
        const pendingRescheduleRequest = getPendingRescheduleRequest(selectedGarageRequest);
        const shouldShowRescheduleCard = Boolean(pendingRescheduleRequest) && canReschedulePartnerAppointment(selectedGarageRequest);
        const currentRequestStatus = String(selectedGarageRequest?.request_status || '').toLowerCase();
        const hasScheduledAppointment = Boolean(selectedGarageRequest?.scheduled_start_at);
        const hasAppointmentMessage = shopConversationMessages.some((message) => (
            message?.is_status_update && message?.event === 'scheduled' && message?.appointment
        ));
        const fallbackAppointmentMessage = hasScheduledAppointment && !hasAppointmentMessage
            ? {
                message_id: `appointment-${selectedGarageRequest.request_id}`,
                request_id: selectedGarageRequest.request_id,
                sender_role: 'system',
                sender_label: 'Update',
                message_text: 'Appointment confirmed',
                created_at: getRequestTimelineEventCreatedAt(selectedGarageRequest, 'scheduled') ||
                    selectedGarageRequest.created_at ||
                    selectedGarageRequest.updated_at ||
                    selectedGarageRequest.scheduled_start_at,
                is_status_update: true,
                event: 'scheduled',
                appointment: buildAppointmentFromRequest(selectedGarageRequest),
                is_fallback_appointment: true,
            }
            : null;
        const sourceServiceStatuses = new Set(shopConversationMessages
            .filter((message) => message?.is_status_update && SHOP_SERVICE_CARD_STATUSES.includes(String(message?.event || '').toLowerCase()))
            .map((message) => String(message?.event || '').toLowerCase()));
        const syntheticServiceMessages = SHOP_SERVICE_CARD_STATUSES
            .filter((eventStatus) => (
                hasReachedShopConversationStatus(currentRequestStatus, eventStatus) &&
                !sourceServiceStatuses.has(eventStatus)
            ))
            .map((eventStatus) => ({
                message_id: `service-card-${selectedGarageRequest.request_id}-${eventStatus}`,
                request_id: selectedGarageRequest.request_id,
                sender_role: 'system',
                sender_label: 'Update',
                message_text: eventStatus === 'ready_for_pickup' ? 'Vehicle is ready for pickup' : 'Vehicle is in service',
                created_at: getShopServicePromptSortTime(selectedGarageRequest, shopConversationMessages, eventStatus),
                is_status_update: true,
                event: eventStatus,
                is_synthetic_service_prompt: true,
            }));
        const orderedConversationMessages = [
            ...shopConversationMessages,
            ...(fallbackAppointmentMessage ? [fallbackAppointmentMessage] : []),
            ...syntheticServiceMessages,
        ].sort((a, b) => {
            const aTime = new Date(a?.created_at || 0).getTime();
            const bTime = new Date(b?.created_at || 0).getTime();
            if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return aTime - bTime;
            if (Number.isFinite(aTime) !== Number.isFinite(bTime)) return Number.isFinite(aTime) ? -1 : 1;
            const rankDelta = getShopConversationSortRank(a) - getShopConversationSortRank(b);
            if (rankDelta) return rankDelta;
            return 0;
        });
        const hasScheduledCardInTimeline = orderedConversationMessages.some((message) => (
            message?.is_status_update && message?.event === 'scheduled' && message?.appointment
        ));
        const visitUpdateSent = hasDriverVisitUpdateMessage(shopConversationMessages, selectedGarageRequest);
        const visitReminderCard = shouldShowScheduledVisitReminder(selectedGarageRequest) && !hasScheduledCardInTimeline
            ? (
                <ShopConversationVisitReminderCard
                    key={`visit-reminder-${selectedGarageRequest.request_id}`}
                    request={selectedGarageRequest}
                    onConfirmVisit={() => confirmGarageVisit(selectedGarageRequest)}
                    isSubmitting={isSendingShopMessage || isUpdatingGarageRequestStatus}
                    visitUpdateSent={visitUpdateSent}
                />
            )
            : null;

        if (!orderedConversationMessages.length) {
            return (
                <>
                    <ShopModeEmptyState request={selectedGarageRequest} />
                    {visitReminderCard}
                    {shouldShowScheduleCard && (
                        <ShopConversationScheduleCard
                            request={selectedGarageRequest}
                            onScheduled={(updatedRequest) => {
                                applyGarageRequestUpdate(updatedRequest);
                                setShouldAutoScroll(true);
                            }}
                        />
                    )}
                    {shouldShowRescheduleCard && (
                        <ShopConversationScheduleCard
                            request={selectedGarageRequest}
                            mode="reschedule"
                            rescheduleRequest={pendingRescheduleRequest}
                            onScheduled={(updatedRequest) => {
                                applyGarageRequestUpdate(updatedRequest);
                                setShouldAutoScroll(true);
                            }}
                        />
                    )}
                </>
            );
        }

        const renderedMessages = orderedConversationMessages.map((message) => {
            // Status updates / estimate-sent / notifications render as a centered neutral notice
            // (in chronological position) rather than a left/right chat bubble.
            if (message.is_status_update) {
                if (message.event === 'estimate_sent' && message.estimate) {
                    return (
                        <React.Fragment key={message.message_id || `estimate-${message.created_at}`}>
                            <ShopConversationEstimateCard
                                message={message}
                                request={selectedGarageRequest}
                                onUpdateLineAuthorization={updateGarageEstimateLineAuthorization}
                                onSendEstimateMessage={(text) => sendShopMessage(text, {
                                    requestId: selectedGarageRequest.request_id,
                                    clearComposer: false,
                                    focusComposer: false,
                                })}
                                isMessageBusy={isSendingShopMessage}
                            />
                            <ShopConversationStatusNotice message={message} />
                        </React.Fragment>
                    );
                }
                if (message.event === 'scheduled' && message.appointment) {
                    return (
                        <React.Fragment key={message.message_id || `appointment-${message.created_at}`}>
                            <ShopConversationAppointmentCard
                                appointment={message.appointment}
                                createdAt={message.created_at}
                            />
                            {!message.is_fallback_appointment && <ShopConversationStatusNotice message={message} />}
                            <ShopConversationVisitReminderCard
                                request={selectedGarageRequest}
                                onConfirmVisit={() => confirmGarageVisit(selectedGarageRequest)}
                                isSubmitting={isSendingShopMessage || isUpdatingGarageRequestStatus}
                                visitUpdateSent={visitUpdateSent}
                                actionsEnabled={currentRequestStatus === 'scheduled'}
                            />
                        </React.Fragment>
                    );
                }
                if (message.event === 'reschedule_requested' && message.reschedule_request) {
                    return (
                        <ShopConversationRescheduleNoticeCard
                            key={message.message_id || `reschedule-${message.created_at}`}
                            request={selectedGarageRequest}
                            rescheduleRequest={message.reschedule_request}
                            createdAt={message.created_at}
                        />
                    );
                }
                if (['in_service', 'ready_for_pickup'].includes(String(message.event || '').toLowerCase())) {
                    const eventStatus = String(message.event || '').toLowerCase();
                    return (
                        <React.Fragment key={message.message_id || `service-${message.event}-${message.created_at}`}>
                            <ShopConversationServicePickupCard
                                request={selectedGarageRequest}
                                statusOverride={eventStatus}
                                actionsEnabled={eventStatus === currentRequestStatus}
                                onConfirmPickup={() => confirmGaragePickup(selectedGarageRequest)}
                                isSubmitting={isUpdatingGarageRequestStatus}
                            />
                            {!message.is_synthetic_service_prompt && <ShopConversationStatusNotice message={message} />}
                        </React.Fragment>
                    );
                }
                if (String(message.event || '').toLowerCase() === 'declined') {
                    return (
                        <React.Fragment key={message.message_id || `declined-${message.created_at}`}>
                            <ShopConversationDeclineCard
                                request={selectedGarageRequest}
                                message={message}
                            />
                            <ShopConversationStatusNotice message={message} />
                        </React.Fragment>
                    );
                }
                return (
                    <ShopConversationStatusNotice
                        key={message.message_id || `status-${message.event}-${message.created_at}`}
                        message={message}
                    />
                );
            }
            const mine = message.sender_role !== 'shop';
            const showShopReadyReport = Boolean(message?.is_initial_request) ||
                (Array.isArray(message?.attachments) && message.attachments.some((attachment) => attachment?.type === 'shop_ready_report'));
            return (
                <div
                    key={message.message_id || `${message.sender_role}-${message.created_at}`}
                    className={`flex w-full gap-4 md:gap-6 ${mine ? 'justify-end' : 'justify-start'}`}
                >
                    {!mine && (
                        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-600/12 text-teal-700">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9 5 3h14l2 6" />
                                <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                                <path d="M9 22V12h6v10" />
                            </svg>
                        </div>
                    )}
                    <div className={`flex max-w-[85%] flex-col ${mine ? 'items-end' : 'items-start'} md:max-w-[75%]`}>
                        {!mine && (
                            <p className="mb-1 ml-0.5 text-[10px] font-semibold text-teal-700">
                                {message.sender_label || selectedGarageRequest.partner_shop?.name || 'Shop'}
                            </p>
                        )}
                        <div className={`rounded-2xl px-4 py-3 text-[15px] leading-relaxed shadow-sm ${
                            mine
                                ? 'rounded-tr-sm bg-[#414141] text-white'
                                : 'rounded-tl-sm border border-teal-200/70 border-l-2 border-l-teal-500 bg-teal-50/60 text-[#414141]'
                        }`}>
                            <ShopConversationMessageText text={message.message_text || ''} mine={mine} />
                        </div>
                        {showShopReadyReport && (
                            <ShopReadyReportCard
                                request={selectedGarageRequest}
                                className="mt-2"
                            />
                        )}
                        {message.created_at && (
                            <p className={`mt-1 text-[10px] font-bold ${mine ? 'text-[#898989]' : 'text-teal-600'}`}>
                                {formatShopConversationTime(message.created_at)}
                            </p>
                        )}
                    </div>
                </div>
            );
        });

        return (
            <>
                {renderedMessages}
                {visitReminderCard}
                {shouldShowScheduleCard && (
                    <ShopConversationScheduleCard
                        request={selectedGarageRequest}
                        onScheduled={(updatedRequest) => {
                            applyGarageRequestUpdate(updatedRequest);
                            setShouldAutoScroll(true);
                        }}
                    />
                )}
                {shouldShowRescheduleCard && (
                    <ShopConversationScheduleCard
                        request={selectedGarageRequest}
                        mode="reschedule"
                        rescheduleRequest={pendingRescheduleRequest}
                        onScheduled={(updatedRequest) => {
                            applyGarageRequestUpdate(updatedRequest);
                            setShouldAutoScroll(true);
                        }}
                    />
                )}
                {['picked_up', 'completed'].includes(currentRequestStatus) && (
                    <ShopConversationPickupOutcomeCard
                        outcome={garagePickupOutcome[selectedGarageRequest?.request_id] || ''}
                        isSubmitting={isSavingPickupOutcome}
                        status={currentRequestStatus}
                        onResolved={() => confirmGaragePickupOutcome(selectedGarageRequest, 'resolved')}
                        onNotSure={() => confirmGaragePickupOutcome(selectedGarageRequest, 'not_sure')}
                    />
                )}
            </>
        );
    };
    const introVehicleLabel = activeVehicleInfo?.label || activeVehicleLabel;
    const canRenderChatIntro = !isInitialLoading && !guidedCheckSession && !isAddVehicleCard;
    const showEmptyChatIntro = canRenderChatIntro && messages.length === 0;
    const showFreshChatIntro = canRenderChatIntro && !introVehicleLabel;
    const showReturningChatIntro = canRenderChatIntro && Boolean(introVehicleLabel);
    const guidedActiveCardKey = guidedCheckSession?.activeCard
        ? [
            guidedCheckSession.activeCard.type,
            guidedCheckSession.activeCard.causeIndex,
            typeof guidedCheckSession.activeCard.stepIndex === 'number' ? guidedCheckSession.activeCard.stepIndex : '',
            guidedCheckSession.phase,
        ].join(':')
        : '';

    useEffect(() => {
        focusChatInput();
    }, [focusChatInput, currentThreadId, isInitialLoading, showEmptyChatIntro]);

    useEffect(() => {
        if (!isLoading && !isRecording && !isTranscribingVoiceDraft) {
            focusChatInput();
        }
    }, [focusChatInput, isLoading, isRecording, isTranscribingVoiceDraft]);

    useEffect(() => {
        if (!showEmptyChatIntro) {
            setIsFreshChatIntroThinking(false);
            return undefined;
        }

        setFreshChatIntroTime(getCurrentChatTime());
        setIsFreshChatIntroThinking(true);
        const timer = window.setTimeout(() => {
            setIsFreshChatIntroThinking(false);
        }, 650);

        return () => window.clearTimeout(timer);
    }, [showEmptyChatIntro]);

    useEffect(() => {
        if (!guidedActiveCardKey) return undefined;
        const timer = window.setTimeout(() => {
            guidedActiveCardRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
        }, 80);

        return () => window.clearTimeout(timer);
    }, [guidedActiveCardKey]);

    return (
        <div
            className="flex flex-col bg-transparent w-full h-full font-nunito overflow-hidden relative"
        >

            {/* #711: infographic intro disabled — new visitors land directly in chat.
            Onboarding Overlay — fixed full-screen, shown only to anonymous first-time users
            {shouldShowOnboarding && (
                <OnboardingBanner onDismiss={handleDismissOnboarding} />
            )}
            */}

            {/* OpenAI-style open layout */}
            <div className="w-full flex-1 flex flex-col relative overflow-hidden min-h-0">
                {activeChatContext && !guidedCheckSession && !isShopMode && (() => {
                    const bannerReport = getLatestPersistedDiagnosticReport(messages);
                    // Diagnostic progress toward the symptom report — only for a diagnosis chat.
                    const bannerProgress = (activeChatContext?.type || 'diagnosis') === 'diagnosis'
                        ? getDiagnosticProgress(messages, {
                            report: bannerReport,
                            generating: autoReportGenerating,
                            resolved: isClosedDiagnosticTicket(activeChatContext?.ticket),
                        })
                        : null;
                    return (
                        <ChatContextBanner
                            context={activeChatContext}
                            report={bannerReport}
                            progress={bannerProgress}
                            onViewReport={(event) => openDiagnosticReport(bannerReport, event)}
                            onFindParts={() => sendMessage(`Find parts for my ${activeVehicleLabel || 'vehicle'}`, null, false, {
                                clientIntent: 'parts',
                                suppressDiagnosticTicket: true,
                            })}
                            onFindShop={() => openGarageForShop(bannerReport)}
                            findShopLabel={getFindShopLabelForReport(bannerReport, 'Shops')}
                        />
                    );
                })()}
                {guidedCheckSession && (
                    <GuidedCheckBanner
                        session={guidedCheckSession}
                        onViewReport={() => openDiagnosticReport(guidedCheckSession.report)}
                    />
                )}
                {showGarageShopBanner && (
                    <LinkedGarageShopBanner
                        requests={linkedGarageRequests}
                        chatMode={chatMode}
                        selectedRequestId={selectedGarageRequest?.request_id || ''}
                        onSelectDash={() => setChatMode('dash')}
                        onSelectRequest={openGarageChat}
                    />
                )}

                {/* Chat Arena */}
                <div
                    ref={scrollContainerRef}
                    onScroll={handleChatScroll}
                    className="flex-1 overflow-y-auto custom-scrollbar min-h-0"
                >
                    <div
                        className={`max-w-3xl mx-auto flex min-h-full w-full flex-col px-4 pb-44 md:pb-48 space-y-8 md:space-y-10 ${
                        showTopContext ? 'pt-4 md:pt-6' : 'pt-14 md:pt-8'
                    }`}
                        style={chatScrollBottomPadding ? { paddingBottom: chatScrollBottomPadding } : undefined}
                    >

                        {/* Loading older history indicator */}
                        {isLoading && offset > 0 && hasMore && (
                            <div className="flex justify-center py-4">
                                <div className="w-6 h-6 border-2 border-black/10 border-t-black rounded-full animate-spin"></div>
                            </div>
                        )}

                        {chatMode === 'shop' && selectedGarageRequest ? (
                            <>
                                {renderShopConversationPane()}
                                <div ref={messageEndRef} className="order-[60] h-4" />
                            </>
                        ) : (
                            <>
                        {showEmptyChatIntro && isFreshChatIntroThinking && (
                            <FreshChatThinking />
                        )}

                        {showFreshChatIntro && (!showEmptyChatIntro || !isFreshChatIntroThinking) && (
                            <FreshChatIntro
                                timestamp={freshChatIntroTime}
                                onQuickAction={handleFreshChatQuickAction}
                                showSuggestions={showEmptyChatIntro}
                            />
                        )}

                        {showReturningChatIntro && (!showEmptyChatIntro || !isFreshChatIntroThinking) && (() => {
                            // Returning user — personalise with their vehicle + any open issue.
                            const _detail = [
                                activeVehicleInfo?.mileageMiles != null ? formatMileage(activeVehicleInfo.mileageMiles, distanceUnit) : '',
                                activeVehicleInfo?.engine || '',
                            ].filter(Boolean).join(', ');
                            const _t = currentDiagnosticTicket;
                            // Skip the open-issue callout when revisiting an existing thread — it's
                            // computed from whatever ticket is CURRENTLY active, which is often
                            // unrelated to the old thread being reopened, so it reads as a confusing
                            // non-sequitur. A revisited thread's greeting should look exactly like a
                            // fresh chat's, minus this line.
                            const _openIssue = (!revisitedThreadStartedAt && _t && !isClosedDiagnosticTicket(_t))
                                ? (() => {
                                    const _title = cleanDisplayText(resolveTicketTitle(_t) || _t.title || '');
                                    const _status = cleanDisplayText(_t.display_status || '');
                                    if (!_title) return '';
                                    return _status ? `${_title} — ${_status}` : _title;
                                })()
                                : '';
                            return (
                                <ReturningUserIntro
                                    timestamp={freshChatIntroTime}
                                    vehicleLabel={introVehicleLabel}
                                    vehicleDetail={_detail}
                                    openIssue={_openIssue}
                                    revisitedStartedAt={revisitedThreadStartedAt ? formatThreadStartedAt(revisitedThreadStartedAt) : null}
                                    onSuggestion={(text) => {
                                        if (/^find parts$/i.test(text)) {
                                            sendMessage(text, null, false, {
                                                clientIntent: 'parts',
                                                suppressDiagnosticTicket: true,
                                            });
                                            return;
                                        }
                                        if (/^diagnose/i.test(text)) setActiveClientIntent(null);
                                        sendMessage(text);
                                    }}
                                    showSuggestions={showEmptyChatIntro}
                                />
                            );
                        })()}

                        {isAddVehicleCard && (
                            <>
                                {/* The user's request bubble — show exactly what they typed
                                    ("i want to add my corolla"). This request opens the form via the
                                    add-vehicle redirect (it is NOT sent to the backend, so it isn't in
                                    `messages`), so show it whenever there's a real typed request — not
                                    only on an empty session — otherwise the user's input vanishes. On an
                                    empty session with no typed request, fall back to a generic label. */}
                                {(chatVehicleUserMessage || messages.length === 0) && (
                                    <div className="order-[51] flex w-full justify-end animate-in fade-in duration-500">
                                        <div className="max-w-[85%] md:max-w-[68%] rounded-[28px] bg-[#414141] px-5 py-3 text-[15px] font-semibold leading-relaxed text-white shadow-sm md:px-5 md:py-3.5 md:text-base">
                                            {chatVehicleUserMessage || 'Add my vehicle'}
                                        </div>
                                    </div>
                                )}
                                {/* order-[52]: render the Add-Vehicle card at the BOTTOM of the session,
                                    in line with normal conversation flow — AFTER all regular messages
                                    (order-[10]/order-[40]) and the status/skeleton rows (order-[48]/[50]),
                                    just above the order-[60] scroll anchor. Not pinned above the chat. */}
                                <div ref={addVehicleCardRef} className="order-[52] flex gap-4 md:gap-6 w-full animate-in fade-in duration-500">
                                    <DashAvatar />
                                    <div className="flex-1 overflow-hidden pt-1 text-black">
                                        <div className="mb-2 max-w-2xl rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-semibold leading-relaxed text-[#414141] shadow-none md:text-base">
                                            Let's get your vehicle added. Fill in the details below - at minimum I need the year, make, model, and odometer reading.
                                        </div>
                                        <ChatVehicleFormCard
                                            form={chatVehicleForm}
                                            onFieldChange={handleChatVehicleFieldChange}
                                            onMaintenanceChange={handleChatVehicleMaintenanceChange}
                                            onSubmit={handleSubmitChatVehicleForm}
                                            onClose={closeChatVehicleForm}
                                            isSubmitting={isSavingChatVehicle}
                                            error={chatVehicleFormError}
                                            suggestions={chatVehicleFormSuggestions}
                                            onApplySuggestion={handleApplyVehicleSuggestion}
                                            distanceUnit={distanceUnit}
                                        />
                                        <div className="mt-2 ml-1 text-xs font-semibold text-[#414141]">
                                            {chatVehicleFormTime || freshChatIntroTime}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {renderedMessages.map((msg, idx) => {
                            // The symptom report is generated automatically, so the backend's
                            // "I have all the details I need. Ready to generate your report?"
                            // confirmation prompt is purely a trigger — never show it (and its
                            // duplicate CarFido avatar) once generation is pending/active/done.
                            // It stays in `messages` state so the auto-generate effect still
                            // detects the signal; we only skip RENDERING its block here.
                            if (
                                msg.role === 'assistant' &&
                                !msg.isLoadingReport &&
                                !isDiagnosticReportMessage(msg) &&
                                isReportReadyPromptMessage(extractSuggestionsAndContent(msg.content || '').content)
                            ) {
                                // Hide the trigger prompt ONLY while the report is actively being
                                // built or once it actually exists — NOT based on the persistent
                                // "attempted" refs. Otherwise a FAILED generation (refs stay set,
                                // no card) would hide every later ready-prompt reply, making CarFido
                                // look like it stopped responding (it re-emits the prompt for chit-chat
                                // like "Hey"/"Hello"). In the stuck/pre-report state the prompt must
                                // stay visible so CarFido is clearly still responding.
                                const _reportActiveOrDone = (
                                    autoReportGenerating ||
                                    messages.some((m) => m.isLoadingReport || isDiagnosticReportMessage(m)) ||
                                    Boolean(liveDiagnosticReportCard?.diagnosticReport)
                                );
                                // The backend re-emits "Ready to generate…?" every time the user
                                // replies (e.g. "Yeah"), which stacks up as a confirm loop. Collapse
                                // it: hide every copy except the most recent one, so at most ONE
                                // ready-prompt is ever on screen (and that last one is hidden too once
                                // the report is building/done, above).
                                const _laterReadyPrompt = renderedMessages.slice(idx + 1).some((later) => (
                                    later?.role === 'assistant' &&
                                    !later.isLoadingReport &&
                                    !isDiagnosticReportMessage(later) &&
                                    isReportReadyPromptMessage(extractSuggestionsAndContent(later.content || '').content)
                                ));
                                if (_reportActiveOrDone || _laterReadyPrompt) return null;
                            }
                            // A failed / timed-out reply: a visible error with a Retry button so the
                            // driver is never left in silence.
                            if (msg.isStreamError) {
                                return (
                                    <div key={msg.id ?? idx} className="order-[10] flex w-full justify-start animate-in fade-in duration-300">
                                        <div className="max-w-[85%] rounded-[20px] border border-[#ffd2d2] bg-[#fff5f5] px-4 py-3 md:max-w-[68%]">
                                            <p className="text-sm font-bold text-[#b42318]">{msg.content}</p>
                                            {msg.retryUserMessage && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
                                                        setStatus('');
                                                        sendMessage(msg.retryUserMessage);
                                                    }}
                                                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#b42318] px-3.5 py-1.5 text-xs font-black text-white transition hover:bg-[#8f1a12]"
                                                >
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6" /><path d="M20.5 15a9 9 0 1 1-2.1-9.4L23 10" /></svg>
                                                    Retry
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                            const prevMsg = renderedMessages[idx - 1];
                            const showDayDivider = msg.timestamp && !isSameLocalDay(msg.timestamp, prevMsg?.timestamp);
                            const orderClass = msg.afterGuidedSession ? 'order-[40]' : 'order-[10]';
                            return (
                            <React.Fragment key={msg.id ?? idx}>
                            {showDayDivider && (
                                <div className={`flex items-center gap-3 text-[11px] font-bold uppercase tracking-wide text-black/30 ${orderClass}`}>
                                    <div className="h-px flex-1 bg-black/10" />
                                    <span>{formatDayDividerLabel(msg.timestamp)}</span>
                                    <div className="h-px flex-1 bg-black/10" />
                                </div>
                            )}
                            <div className={`flex w-full group ${msg.role === 'user' ? 'flex-col items-end' : 'justify-start'} ${orderClass} animate-in fade-in duration-500`}>
                                    {msg.role === 'user' ? (
                                        <>
                                        <div className="max-w-[85%] md:max-w-[68%] bg-white text-black px-5 py-3 md:px-5 md:py-3.5 rounded-[26px] rounded-br-lg border border-[#E6E6E6] shadow-[0_2px_10px_rgba(0,0,0,0.05)]">
                                            {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                                <AttachmentPreviewList files={msg.attachments} variant="message" onOpenImage={setAttachmentPreview} />
                                            )}
                                            {msg.isVoice ? (
                                                <div className="py-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenTranscriptId(prev => prev === msg.id ? null : msg.id)}
                                                    className="w-full flex items-center gap-3 text-left"
                                                    aria-expanded={openTranscriptId === msg.id}
                                                >
                                                    <div className="w-10 h-10 bg-black/5 rounded-full flex items-center justify-center">
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                                                    </div>
                                                    <div className="flex flex-col flex-1 min-w-0">
                                                        <span className="text-xs font-black uppercase text-black/40 tracking-widest">Voice Note</span>
                                                        <div className="flex gap-1 mt-1">
                                                            {[1,2,3,4,5,6].map(i => <div key={i} className="w-1 h-3 bg-black/20 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.1}s` }} />)}
                                                        </div>
                                                    </div>
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        className={`flex-shrink-0 text-black/30 transition-transform ${openTranscriptId === msg.id ? 'rotate-180' : ''}`}
                                                    >
                                                        <path d="m6 9 6 6 6-6" />
                                                    </svg>
                                                </button>
                                                {openTranscriptId === msg.id && (
                                                    <div className="mt-3 border-t border-black/10 pt-3">
                                                        <div className="text-[10px] font-black uppercase tracking-widest text-black/35 mb-1.5">
                                                            Actual transcription
                                                        </div>
                                                        <div className="text-[15px] md:text-base leading-relaxed text-black/70 whitespace-pre-wrap font-medium">
                                                            {msg.content || 'Transcription is being prepared...'}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            ) : (
                                                msg.content && (
                                                    <div className={`${Array.isArray(msg.attachments) && msg.attachments.length > 0 ? 'mt-3' : ''} text-[15px] md:text-base leading-relaxed whitespace-pre-wrap font-medium`}>
                                                        {msg.content}
                                                    </div>
                                                )
                                        )}
                                    </div>
                                        {msg.timestamp && (
                                            <div className="mt-1 mr-1 text-[11px] font-semibold text-black/35">
                                                {formatMessageTimestamp(msg.timestamp)}
                                            </div>
                                        )}
                                        </>
                                ) : (
                                    <div
                                        ref={!activeMessage && idx === renderedMessages.length - 1 ? responseAnchorRef : undefined}
                                        className={`flex gap-4 md:gap-6 w-full group scroll-mt-4 ${renderedMessages[idx - 1]?.role === 'assistant' ? 'mt-1' : ''}`}
                                    >
                                        {renderedMessages[idx - 1]?.role === 'assistant' ? (
                                            // Consecutive assistant messages share ONE CarFido logo —
                                            // render an aligned spacer instead of a second avatar.
                                            <div className="hidden sm:block flex-shrink-0 w-8 h-8 md:w-9 md:h-9" aria-hidden="true" />
                                        ) : (
                                            <DashAvatar />
                                        )}
                                        <div className="flex-1 text-black overflow-hidden pt-1">
                                            <div className="prose prose-neutral prose-base max-w-none text-black prose-headings:font-black prose-a:text-[#D81F26] prose-strong:font-black prose-p:leading-relaxed prose-p:font-medium prose-pre:bg-neutral-900 prose-pre:text-white">
                                                {(msg.audioUrl || msg.audioUrls?.length > 0 || msg.isWaitingForAudio) && (
                                                    <div className="mb-6 bg-white border border-neutral-200/60 rounded-[24px] p-4 flex items-center gap-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-in fade-in slide-in-from-top-4 duration-500 max-w-sm">
                                                        <AudioMessage 
                                                            msg={msg} 
                                                            playingAudioId={playingAudioId} 
                                                            setPlayingAudioId={setPlayingAudioId}
                                                            audioProgress={audioProgress}
                                                            setAudioProgress={setAudioProgress}
                                                            setMessages={setMessages}
                                                            stopPlaybackSignal={stopAssistantAudioSignal}
                                                        />
                                                    </div>
                                                )}
                                                {(() => {
                                                    const messagesThroughCurrent = renderedMessages.slice(0, idx + 1);
                                                    const suppressLegacyDiagnosticPdf = isLegacyDiagnosticPdfMessage(msg);
                                                    const isPlainDocumentMessage = Boolean(msg.pdfUrl) && !msg.diagnosticReport && !suppressLegacyDiagnosticPdf;
                                                    const reportActionsAllowed = (
                                                        hasPersistedDiagnosticReport(messagesThroughCurrent) ||
                                                        (
                                                            messagesThroughCurrent.some(isDiagnosticReportMessage) &&
                                                            canGenerateDiagnosticReport(messagesThroughCurrent)
                                                        )
                                                    );
                                                    const markdownContent = extractSuggestionsAndContent(msg.content).content;
                                                    const visibleContent = suppressLegacyDiagnosticPdf
                                                        ? ''
                                                        : isPlainDocumentMessage
                                                        ? markdownContent
                                                        : isDiagnosticReportMessage(msg) && !msg.diagnosticReport && !canGenerateDiagnosticReport(messagesThroughCurrent)
                                                        ? ''
                                                        : reportActionsAllowed
                                                        ? markdownContent
                                                        // The "diagnose before repair steps" gate only applies DURING an active
                                                        // diagnosis. A maintenance / how-to answer (e.g. tire-rotation DIY steps
                                                        // from the Garage "Ask CarFido" flow) has no fault to diagnose, so its steps
                                                        // must show directly instead of being replaced by the pre-report notice.
                                                        : hasDiagnosticTicketContext(messagesThroughCurrent)
                                                        ? getPreReportDiagnosticContent(markdownContent)
                                                        : markdownContent;

                                                    const parsedShopResult = parseShopResultBlock(visibleContent);
                                                    const shopResult = parsedShopResult?.key
                                                        ? (msg.shopResult || shopResultsByKeyRef.current[parsedShopResult.key] || parsedShopResult)
                                                        : null;
                                                    const displayContent = contentWithoutShopResultBlock(visibleContent, parsedShopResult);
                                                    return (
                                                        <>
                                                            {displayContent ? renderMessageWithLinkCards(stripQuestionLabel(displayContent), {
                                                                partsCards: buildPartsCards(msg.partsData),
                                                            }) : null}
                                                            {shopResult && <ShopResultsButton result={shopResult} onOpen={(result) => openGarageForShop(null, result)} />}
                                                        </>
                                                    );
                                                })()}
                                                {msg.type === 'vehicle_added_card' && msg.vehicle && (
                                                    <VehicleAddedCard vehicle={msg.vehicle} />
                                                )}
                                                {!hasExplicitNonDiagnosticThreadType && isDiagnosticReportMessage(msg) && msg.diagnosticReport && (() => {
                                                    const report = msg.diagnosticReport;
                                                    return (
                                                        <div data-diagnostic-report-card="true">
                                                        <InlineDiagnosticReportCard
                                                            report={report}
                                                            {...referralPromptPropsForReport(report)}
                                                            customerPhone={authState?.phone}
                                                            onViewReport={(reportArg, event) => openDiagnosticReport(reportArg || report, event)}
                                                            onBeginDiyCheck={(reportArg) => {
                                                                const session = createGuidedCheckSession({
                                                                    report: normalizeDiagnosticReportForUi(reportArg || report),
                                                                    causeIndex: 0,
                                                                });
                                                                if (session) {
                                                                    setGuidedCheckSession(session);
                                                                    setShouldAutoScroll(true);
                                                                }
                                                            }}
                                                            onFindParts={() => sendMessage(`Find parts for my ${activeVehicleLabel || 'vehicle'}`, null, false, {
                                                                clientIntent: 'parts',
                                                                suppressDiagnosticTicket: true,
                                                            })}
                                                            onFindShop={() => openGarageForShop(report)}
                                                            findShopLabel={getFindShopLabelForReport(report)}
                                                            onAskDash={() => chatInputRef.current?.focus()}
                                                        />
                                                        </div>
                                                    );
                                                })()}
                                                {/*
                                                    Legacy diagnostic PDFs are intentionally not shown in chat.
                                                    Diagnostic report requests now render as snapshot cards instead;
                                                    keep this generic document card for non-diagnostic PDFs/uploads.
                                                */}
                                                {msg.pdfUrl && !msg.diagnosticReport && !isLegacyDiagnosticPdfMessage(msg) && (
                                                    <div className="mt-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                        <a 
                                                            href={msg.pdfUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="group inline-flex items-center gap-3 px-4 py-3 bg-white border border-neutral-200 text-neutral-800 text-sm font-semibold rounded-2xl shadow-sm hover:shadow-md hover:border-neutral-300 transition-all duration-200 no-underline"
                                                        >
                                                            <div className="flex items-center justify-center w-10 h-10 bg-[#F2F2F2] text-[#414141] rounded-xl group-hover:bg-[#E6E6E6] transition-colors">
                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="leading-tight text-black">View Document</span>
                                                                <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider">PDF / Image File</span>
                                                            </div>
                                                        </a>
                                                    </div>
                                                )}
                                                {/* Google Maps Location Card */}
                                                {msg.mapUrl && (
                                                    <div className="mt-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                        <a 
                                                            href={msg.mapUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="group inline-flex items-center gap-3 px-4 py-3 bg-white border border-neutral-200 text-neutral-800 text-sm font-semibold rounded-2xl shadow-sm hover:shadow-md hover:border-neutral-300 transition-all duration-200 no-underline"
                                                        >
                                                            <div className="flex items-center justify-center w-10 h-10 bg-green-50 text-green-600 rounded-xl group-hover:bg-green-100 transition-colors">
                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="leading-tight text-black">Nearby Repair Shops</span>
                                                                <span className="text-[11px] text-green-400 font-bold uppercase tracking-wider">Google Maps / Get Directions</span>
                                                            </div>
                                                        </a>
                                                    </div>
                                                )}
                                                {msg.docUrl && (
                                                    <div className="mt-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                        <a
                                                            href={msg.docUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="group inline-flex items-center gap-3 px-4 py-3 bg-white border border-[#D9D9D9] text-neutral-800 text-sm font-semibold rounded-2xl shadow-sm hover:shadow-md hover:border-[#414141] transition-all duration-200 no-underline"
                                                        >
                                                            <div className="flex items-center justify-center w-10 h-10 bg-[#F2F2F2] text-[#414141] rounded-xl group-hover:bg-[#E6E6E6] transition-colors">
                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="leading-tight text-black">Your Uploaded File</span>
                                                                <span className="text-[11px] text-[#414141] font-bold uppercase tracking-wider">Tap to Open</span>
                                                            </div>
                                                        </a>
                                                    </div>
                                                )}

                                                 {msg.gloveboxDocs && msg.gloveboxDocs.length > 0 && (
                                                    <div className="mt-6 space-y-8">
                                                        {(() => {
                                                            const displayDocs = msg.gloveboxDocs.filter(doc => !msg.pdfUrl || doc.url !== msg.pdfUrl);
                                                            if (displayDocs.length === 0) return null;

                                                            const isReport = (d) => {
                                                                const n = (d.name || "").toLowerCase();
                                                                const l = (d.label || "").toLowerCase();
                                                                const t = (d.type || "").toLowerCase();
                                                                const keywords = ["report", "summary", "plan", "workplan", "work", "service", "diy", "analysis", "diagnostic", "guide", "manual", "history"];
                                                                return keywords.some(k => n.includes(k) || l.includes(k)) || t.includes("report") || t.includes("agent");
                                                            };

                                                            const reports = displayDocs.filter(isReport);
                                                            const others = displayDocs.filter(d => !isReport(d));

                                                            const renderCarousel = (docs, title) => (
                                                                <div className="flex flex-col gap-3">
                                                                    <div className="flex items-center gap-2 px-1">
                                                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-black/40">{title}</span>
                                                                        <div className="h-px flex-1 bg-neutral-100" />
                                                                    </div>
                                                                    <div className="flex overflow-x-auto custom-scrollbar gap-4 pb-4 px-1 -mx-1 snap-x">
                                                                        {docs.slice(0, 4).map((doc, i) => (
                                                                            <a 
                                                                                key={i} 
                                                                                href={doc.url || '#'} 
                                                                                target={doc.url ? "_blank" : "_self"} 
                                                                                rel="noopener noreferrer"
                                                                                className="group flex-shrink-0 w-64 flex flex-col bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-neutral-300 transition-all duration-200 snap-start no-underline cursor-pointer"
                                                                            >
                                                                                <div className="flex items-center gap-3 mb-3">
                                                                                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-neutral-100 text-neutral-700 rounded-xl group-hover:bg-neutral-200 transition-colors text-xl">
                                                                                        {doc.icon || '📄'}
                                                                                    </div>
                                                                                    <div className="flex flex-col overflow-hidden">
                                                                                        <span className="text-sm font-bold text-black truncate">{doc.name}</span>
                                                                                        <span className="text-xs text-neutral-500 font-semibold uppercase">{doc.label}</span>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="mt-auto px-1">
                                                                                    <span className="text-[11px] font-bold text-neutral-400">{doc.date}</span>
                                                                                </div>
                                                                            </a>
                                                                        ))}
                                                                        
                                                                        {docs.length >= 5 && (
                                                                            <button 
                                                                                onClick={() => {
                                                                                    setOverlayDocs(docs);
                                                                                    setOverlayTitle(title);
                                                                                    setShowGloveboxOverlay(true);
                                                                                }}
                                                                                className="group flex-shrink-0 w-64 flex flex-col items-center justify-center bg-neutral-50 border border-dashed border-neutral-300 rounded-2xl p-4 hover:bg-neutral-100 hover:border-neutral-400 transition-all duration-200 snap-start cursor-pointer"
                                                                            >
                                                                                <div className="w-12 h-12 bg-neutral-200 rounded-full flex items-center justify-center text-neutral-500 mb-3 group-hover:scale-110 transition-transform">
                                                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                                                                                </div>
                                                                                <span className="text-sm font-black text-neutral-600">
                                                                                    View All {title.split(' ').slice(-1)}
                                                                                </span>
                                                                                <span className="text-[10px] font-bold text-neutral-400 uppercase mt-1">+{docs.length - 4} More Files</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );

                                                            const contentText = (msg.content || "").toLowerCase();
                                                            const reportKeywords = ["report", "summary", "plan", "workplan", "work", "service", "diy", "analysis", "diagnostic", "guide", "manual", "history"];
                                                            const mentionsReports = reportKeywords.some(k => contentText.includes(k));
                                                            const mentionsGlovebox = contentText.includes("glovebox") || contentText.includes("document") || contentText.includes("file");

                                                            // Only filter if one is mentioned and not the other
                                                            const showReportsOnly = mentionsReports && !mentionsGlovebox;
                                                            const showGloveboxOnly = mentionsGlovebox && !mentionsReports;

                                                            return (
                                                                <>
                                                                    {reports.length > 0 && !showGloveboxOnly && renderCarousel(reports, "Service Reports")}
                                                                    {others.length > 0 && !showReportsOnly && renderCarousel(others, "Digital Glovebox")}
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                {msg.isLoadingGlovebox && !msg.gloveboxDocs && (
                                                    <div className="mt-6 flex gap-4 overflow-x-auto pb-4 px-1 -mx-1">
                                                        {[1, 2, 3].map(i => (
                                                            <div key={i} className="flex-shrink-0 w-64 h-28 bg-neutral-50 border border-neutral-100 rounded-2xl p-4 animate-pulse">
                                                                <div className="flex items-center gap-3 mb-4">
                                                                    <div className="w-10 h-10 bg-neutral-200 rounded-xl" />
                                                                    <div className="flex-1 space-y-2">
                                                                        <div className="h-3 bg-neutral-200 rounded-full w-3/4" />
                                                                        <div className="h-2 bg-neutral-200 rounded-full w-1/2" />
                                                                    </div>
                                                                </div>
                                                                <div className="h-2 bg-neutral-100 rounded-full w-1/4" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {msg.isLoadingReport && !msg.pdfUrl && !/\b(service\s+history|service\s+record|updating\s+service|saving\s+service|adding\s+service)\b/i.test(msg.status || '') && (
                                                    <div className="mt-4">
                                                        <ReportGenerationSkeleton status={msg.status || 'Generating diagnostic report...'} />
                                                    </div>
                                                )}
                                                {idx === diagnosticTicketCardMessageIndex && syncedInlineDiagnosticTicket && isDiagnosticTicketCardAnchorMessage(msg) && (
                                                    <DiagnosticTicketCard ticket={syncedInlineDiagnosticTicket} />
                                                )}
                                                {msg.serviceCard && (
                                                    <ServiceCard
                                                        service={msg.serviceCard}
                                                        vehicleLabel={activeVehicleLabel}
                                                        distanceUnit={distanceUnit}
                                                        onOpen={(svc) => navigate('/glovebox', { state: { tab: 'history', openServiceEventId: svc.serviceEventId } })}
                                                    />
                                                )}
                                                {msg.followupCard && (
                                                    <MonitoringFollowupCard
                                                        card={msg.followupCard}
                                                        outcome={followupOutcomes[msg.followupCard.ticket_id] || ''}
                                                        isSubmitting={followupSubmittingTicketId === msg.followupCard.ticket_id}
                                                        onResolved={() => handleFollowupOutcome(msg.followupCard, 'resolved')}
                                                        onStillHappening={() => handleFollowupOutcome(msg.followupCard, 'still')}
                                                        onNotSure={() => handleFollowupOutcome(msg.followupCard, 'not_sure')}
                                                    />
                                                )}
                                            </div>

                                            {/* Dynamic Suggested Replies */}
                                            {idx === renderedMessages.length - 1 && !activeMessage && !isLoading && (() => {
                                                const messagesThroughCurrent = renderedMessages.slice(0, idx + 1);
                                                // A monitoring follow-up carries its own Resolved / Still happening /
                                                // Not sure choices. Generic reply chips (Find parts / Find a repair shop)
                                                // underneath just clutter it and aren't what the prompt is asking, so
                                                // suppress them entirely for a follow-up message.
                                                if (msg.followupCard) return null;
                                                // The diagnostic report card renders its own action buttons (View Report,
                                                // Find Parts, Find a Shop, Begin DIY Check). The dynamic suggestion chips
                                                // below it (Continue diagnosis / Find parts / Find a repair shop) just
                                                // duplicate those actions — suppress them for the report-card message.
                                                if (isDiagnosticReportMessage(msg) && msg.diagnosticReport) return null;
                                                const reportActionsAllowed = (
                                                    hasPersistedDiagnosticReport(messagesThroughCurrent) ||
                                                    (
                                                        messagesThroughCurrent.some(isDiagnosticReportMessage) &&
                                                        canGenerateDiagnosticReport(messagesThroughCurrent)
                                                    )
                                                );
                                                // Hide reply chips while a diagnostic report is being generated —
                                                // the user shouldn't tap stale suggestions mid-build.
                                                if (autoReportGenerating || messages.some((m) => m.isLoadingReport)) return null;
                                                if ((status || '').toLowerCase().startsWith('generating diagnostics')) return null;
                                                // Hide reply chips once the report is ready to auto-generate.
                                                // The skeleton will appear next, so stale chips below it are confusing.
                                                const reportReadyToGenerate = (
                                                    !isDiagnosticReportMessage(msg) &&
                                                    hasDiagnosticTicketContext(messagesThroughCurrent) &&
                                                    canGenerateDiagnosticReport(messagesThroughCurrent) &&
                                                    (
                                                        isDiagnosticFinalizedMessage(msg) ||
                                                        isReportEligibleDiagnosticMessage(msg, messagesThroughCurrent)
                                                    )
                                                );
                                                const reportAttemptKey = currentThreadId ? `thread:${currentThreadId}` : (activeReportStorageKey || 'default');
                                                const liveReportAutoGenerationPending = liveReportReadyAttemptKeysRef.current.has(reportAttemptKey);
                                                if (reportReadyToGenerate && liveReportAutoGenerationPending) return null;
                                                const explicitSuggestions = getMessageSuggestions(msg);
                                                const suggestions = explicitSuggestions.length > 0
                                                    ? explicitSuggestions
                                                    : getFallbackSuggestionsForAssistantMessage(msg, activeChatContext);
                                                const isGarageListTurn = isGarageVehicleListResponse(msg.content);
                                                // Mid-diagnosis (ticket open, no report yet): show ONLY diagnostic answer
                                                // chips. Strip shop / parts / work-list / "generate report" actions so the
                                                // focus stays on narrowing the cause; they return once the report exists.
                                                const duringActiveDiagnosis = !isGarageListTurn && !reportActionsAllowed && hasDiagnosticTicketContext(messagesThroughCurrent);
                                                // Only strip repair-action chips (e.g. "Show DIY steps") when a diagnosis is
                                                // actually active. A maintenance/how-to answer contains repair-guidance words
                                                // ("difficulty rating", "step-by-step") but is NOT a fault — its DIY chip must
                                                // stay, or the chips flicker/change as ticket state settles.
                                                const hasPreReportRepairGuidance = duringActiveDiagnosis && isRepairGuidanceContent(msg.content);
                                                const passesPreReportFilter = (reply) => (
                                                    isGarageListTurn ||
                                                    (
                                                        !isReportChoiceText(reply) &&
                                                        (!hasPreReportRepairGuidance || !isRepairActionSuggestion(reply)) &&
                                                        (!duringActiveDiagnosis || !isNonDiagnosticActionSuggestion(reply))
                                                    )
                                                );
                                                // When CarFido is actively asking a question, the user continues by
                                                // ANSWERING it — a "Continue diagnosis" chip under a live question is
                                                // redundant and reads as if the conversation stalled. Drop it in that case.
                                                const assistantIsAskingQuestion = /\?\s*$/.test(
                                                    cleanDisplayText(extractSuggestionsAndContent(msg.content || '').content || '')
                                                );
                                                const dropRedundantContinue = (reply) => (
                                                    !(assistantIsAskingQuestion && isContinueDiagnosisCommand(reply))
                                                );
                                                const filteredSuggestions = reportActionsAllowed
                                                    ? suggestions
                                                    : suggestions.filter(passesPreReportFilter);
                                                const visibleSuggestions = (filteredSuggestions.length > 0
                                                    ? filteredSuggestions
                                                    : getFallbackSuggestionsForAssistantMessage(msg, activeChatContext).filter((reply) => (
                                                        reportActionsAllowed || passesPreReportFilter(reply)
                                                    ))
                                                )
                                                    .filter(dropRedundantContinue)
                                                    .filter((reply) => PARTS_FLOW_ENABLED || !isPartsFlowSuggestion(reply));
                                                if (visibleSuggestions.length === 0) return null;
                                                return (
                                                    <div className="mt-4 flex flex-wrap gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                                        {visibleSuggestions.map((reply, rIdx) => (
                                                            <button
                                                                key={rIdx}
                                                                type="button"
                                                                onClick={() => sendMessage(reply)}
                                                                className="px-4 py-2 rounded-full text-xs font-bold text-[#414141] bg-[#F9F9F9] hover:bg-[#F2F2F2] hover:border-[#414141] border border-[#d9d9d9] shadow-sm transition-all duration-200 active:scale-95 whitespace-normal break-words text-center leading-snug flex max-w-full items-center justify-center min-h-[36px]"
                                                            >
                                                                {reply}
                                                            </button>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                            {msg.timestamp && (
                                                <div className="mt-2 ml-1 text-[11px] font-semibold text-black/35">
                                                    {formatMessageTimestamp(msg.timestamp)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            </React.Fragment>
                            );
                        })}
                        {!hasExplicitNonDiagnosticThreadType && liveDiagnosticReportCard?.diagnosticReport && !renderedMessages.some((message) => (
                            message?.diagnosticReport?.id === liveDiagnosticReportCard.diagnosticReport.id
                        )) && (
                            <div ref={liveReportCardRef} data-diagnostic-report-card="true" key={liveDiagnosticReportCard.id} className="order-[45] flex w-full justify-start group animate-in fade-in duration-500">
                                <div className="flex gap-4 md:gap-6 w-full group">
                                    <DashAvatar />
                                    <div className="flex-1 text-black overflow-hidden pt-1">
                                        <InlineDiagnosticReportCard
                                            report={liveDiagnosticReportCard.diagnosticReport}
                                            {...referralPromptPropsForReport(liveDiagnosticReportCard.diagnosticReport)}
                                            customerPhone={authState?.phone}
                                            onViewReport={(reportArg, event) => openDiagnosticReport(reportArg || liveDiagnosticReportCard.diagnosticReport, event)}
                                            onBeginDiyCheck={(reportArg) => {
                                                const session = createGuidedCheckSession({
                                                    report: normalizeDiagnosticReportForUi(reportArg || liveDiagnosticReportCard.diagnosticReport),
                                                    causeIndex: 0,
                                                });
                                                if (session) {
                                                    setGuidedCheckSession(session);
                                                    setShouldAutoScroll(true);
                                                }
                                            }}
                                            onFindParts={() => sendMessage(`Find parts for my ${activeVehicleLabel || 'vehicle'}`, null, false, {
                                                clientIntent: 'parts',
                                                suppressDiagnosticTicket: true,
                                            })}
                                            onFindShop={() => openGarageForShop(liveDiagnosticReportCard.diagnosticReport)}
                                            findShopLabel={getFindShopLabelForReport(liveDiagnosticReportCard.diagnosticReport)}
                                            onAskDash={() => chatInputRef.current?.focus()}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {guidedCheckSession?.timeline.map((entry) => {
                            if (entry?.type === 'guided_card_snapshot') {
                                return (
                                    <div key={entry.id} className="order-[20] flex w-full group justify-start animate-in fade-in duration-500">
                                        <div className="flex gap-4 md:gap-6 w-full group">
                                            <DashAvatar />
                                            <div className="flex-1 text-black overflow-hidden pt-1">
                                                <GuidedCardSnapshot session={guidedCheckSession} entry={entry} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={entry.id} className={`order-[20] flex w-full group ${entry.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in duration-500`}>
                                    {entry.role === 'user' ? (
                                        <div className="max-w-[85%] md:max-w-[68%] bg-[#414141] text-white px-5 py-3 md:px-5 md:py-3.5 rounded-[26px] shadow-sm">
                                            <div className="text-[15px] md:text-base leading-relaxed whitespace-pre-wrap font-medium">
                                                {cleanDisplayText(entry.content)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex gap-4 md:gap-6 w-full group">
                                            <DashAvatar />
                                            <div className="flex-1 text-black overflow-hidden pt-1">
                                                <div className="prose prose-neutral prose-base max-w-none text-black prose-headings:font-black prose-a:text-[#D81F26] prose-strong:font-black prose-p:leading-relaxed prose-p:font-medium">
                                                    {renderMarkdownWithLinks(cleanDisplayText(entry.content))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {guidedCheckSession?.activeCard && (() => {
                            const session = guidedCheckSession;
                            const cause = session.report?.likelyCauses?.[session.activeCard.causeIndex];
                            if (!cause) return null;
                            const steps = getGuidedStepsFromCause(cause);
                            const repairSteps = getRepairGuideStepsFromCause(cause);
                            const activeGuidedStepIndex = typeof session.activeCard.stepIndex === 'number'
                                ? session.activeCard.stepIndex
                                : Math.min(session.currentStepIndex || 0, Math.max(steps.length - 1, 0));
                            const guidedStep = steps[activeGuidedStepIndex] || steps[0] || {
                                instruction: cause?.recommendedCheck || 'Continue the guided diagnostic check.',
                                detail: cause?.checkDescription || '',
                                toolLabel: cause?.toolSummary || '',
                            };
                            return (
                                <div
                                    key={guidedActiveCardKey}
                                    ref={guidedActiveCardRef}
                                    className="order-[30] flex w-full group justify-start animate-dash-card-enter scroll-mt-28 md:scroll-mt-32 scroll-mb-40"
                                >
                                    <div className="flex gap-4 md:gap-6 w-full group">
                                        <DashAvatar />
                                        <div className="flex-1 text-black overflow-hidden pt-1">
                                            {session.activeCard.type === 'step' && (
                                                <CompactGuidedCheckStepCard
                                                    cause={cause}
                                                    step={guidedStep}
                                                    stepIndex={activeGuidedStepIndex}
                                                    totalSteps={steps.length}
                                                    onAction={handleGuidedStepAction}
                                                    onAskHelp={() => handleGuidedStepAction('help')}
                                                    onPrev={() => handleGuidedStepNav('prev')}
                                                    onNext={() => handleGuidedStepNav('next')}
                                                    canPrev={activeGuidedStepIndex > 0}
                                                    canNext={activeGuidedStepIndex < steps.length - 1}
                                                />
                                            )}
                                            {session.activeCard.type === 'result' && (
                                                <CompactGuidedCheckResultCard
                                                    cause={cause}
                                                    step={guidedStep}
                                                    selectedOutcome={session.selectedOutcome}
                                                    notes={session.notes}
                                                    onSelectOutcome={handleGuidedOutcomeSelect}
                                                    onNotesChange={handleGuidedNotesChange}
                                                    onSave={handleGuidedResultSave}
                                                    onAskHelp={() => handleGuidedStepAction('help')}
                                                />
                                            )}
                                            {session.activeCard.type === 'evidence' && (
                                                <CompactEvidenceCaptureCard
                                                    checkTitle={cause?.title || cause?.recommendedCheck || guidedStep?.instruction}
                                                    isSubmitting={session.evidence?.submitting}
                                                    error={session.evidence?.error}
                                                    saved={session.evidence?.saved}
                                                    onSubmit={handleGuidedEvidenceSubmit}
                                                    onSkip={handleGuidedEvidenceSkip}
                                                />
                                            )}
                                            {session.activeCard.type === 'next_cause' && (
                                                <CompactNextLikelyCauseCard
                                                    cause={cause}
                                                    ruledOutTitle={session.activeCard.ruledOutTitle}
                                                    onStart={() => startNextGuidedCause(session.activeCard.causeIndex)}
                                                />
                                            )}
                                            {session.activeCard.type === 'confirmed' && (
                                                <CompactConfirmedCauseCard
                                                    cause={cause}
                                                    completedCheck={(session.report?.completedGuidedChecks || []).find((entry) => entry.causeId === cause?.id)}
                                                    onStartRepairGuide={handleStartRepairGuide}
                                                    onViewReport={() => openDiagnosticReport(session.report)}
                                                />
                                            )}
                                            {session.activeCard.type === 'repair_step' && !repairSafetyAcked && (
                                                // Gate every repair-step view behind the safety checklist until it's
                                                // acknowledged this app session — covers first start, deep-links
                                                // straight to a step, and resuming a repair after the app was killed.
                                                <CompactRepairSafetyChecklistCard
                                                    items={getRepairSafetyItems(cause)}
                                                    onContinue={() => { setRepairSafetyAcked(true); setShouldAutoScroll(true); }}
                                                />
                                            )}
                                            {session.activeCard.type === 'repair_step' && repairSafetyAcked && (
                                                <CompactRepairGuideStepperCard
                                                    cause={cause}
                                                    steps={repairSteps}
                                                    stepIndex={session.activeCard.stepIndex || 0}
                                                    onPrev={() => handleRepairStepNav('prev')}
                                                    onNext={() => handleRepairStepNav('next')}
                                                    canPrev={(session.activeCard.stepIndex || 0) > 0}
                                                    canNext={(session.activeCard.stepIndex || 0) < repairSteps.length - 1}
                                                    onComplete={() => handleRepairStepAction('complete')}
                                                    onAskDash={() => handleRepairStepAction('help')}
                                                />
                                            )}
                                            {session.activeCard.type === 'repair_outcome_check' && (
                                                <CompactRepairOutcomeCard
                                                    onFixed={() => handleRepairOutcome('fixed')}
                                                    onUnsure={() => handleRepairOutcome('unsure')}
                                                    onNotFixed={() => handleRepairOutcome('not_fixed')}
                                                />
                                            )}
                                            {session.activeCard.type === 'repair_done_choice' && (
                                                <CompactRepairLogChoiceCard
                                                    cause={cause}
                                                    onLogNow={() => handleRepairLogChoice('log')}
                                                    onLater={() => handleRepairLogChoice('later')}
                                                />
                                            )}
                                            {session.activeCard.type === 'repair_log_form' && (
                                                <CompactRepairLogFormCard
                                                    cause={cause}
                                                    mileage={session.repairMileage}
                                                    note={session.repairNote}
                                                    distanceUnit={distanceUnit}
                                                    onMileageChange={(value) => handleRepairLogFieldChange('repairMileage', value)}
                                                    onNoteChange={(value) => handleRepairLogFieldChange('repairNote', value)}
                                                    onBack={() => handleRepairLogChoice('back')}
                                                    onSubmit={handleRepairLogSubmit}
                                                    isSubmitting={session.isClosingTicket}
                                                    error={session.closeError}
                                                />
                                            )}
                                            {session.activeCard.type === 'repair_logged' && (
                                                <CompactRepairLoggedCard cause={cause} />
                                            )}
                                            {![
                                                'step',
                                                'result',
                                                'evidence',
                                                'next_cause',
                                                'confirmed',
                                                'repair_step',
                                                'repair_outcome_check',
                                                'repair_done_choice',
                                                'repair_log_form',
                                                'repair_logged',
                                            ].includes(session.activeCard.type) && (
                                                <GuidedCardSkeleton />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Optimized Streaming Message (O(1) rendering) */}
                        {activeMessage && !activeMessage.isReportGenerationAnchored && (
                            <div
                                ref={responseAnchorRef}
                                className={`flex gap-4 md:gap-6 w-full scroll-mt-4 animate-in fade-in duration-300 ${activeMessage.afterGuidedSession ? 'order-[40]' : 'order-[10]'}`}
                            >
                                <DashAvatar thinking />
                                <div className="flex-1 text-black overflow-hidden pt-1">
                                    <div className="prose prose-neutral prose-base max-w-none text-black prose-headings:font-black prose-a:text-[#D81F26] prose-strong:font-black prose-p:leading-relaxed prose-p:font-medium">
                                        {(activeMessage.audioUrl || activeMessage.audioUrls?.length > 0 || activeMessage.isWaitingForAudio) && (
                                            <div className="mb-6 bg-white border border-neutral-200/60 rounded-[24px] p-4 flex items-center gap-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-in fade-in slide-in-from-top-4 duration-500 max-w-sm">
                                                <AudioMessage
                                                    msg={activeMessage}
                                                    playingAudioId={playingAudioId}
                                                    setPlayingAudioId={setPlayingAudioId}
                                                    audioProgress={audioProgress}
                                                    setAudioProgress={setAudioProgress}
                                                    setMessages={setMessages}
                                                    stopPlaybackSignal={stopAssistantAudioSignal}
                                                />
                                            </div>
                                        )}
                                        {(() => {
                                            const rawStreamingContent = extractSuggestionsAndContent(activeMessage.content).content;
                                            const visibleStreamingContent = (
                                                hasGeneratedDiagnosticReport() && canGenerateDiagnosticReport()
                                                    ? removeReportChoicePrompt(rawStreamingContent)
                                                    // Only gate repair steps behind a report DURING an active diagnosis.
                                                    // Maintenance / how-to answers (no fault) show their steps directly.
                                                    : hasDiagnosticTicketContext()
                                                    ? getPreReportDiagnosticContent(rawStreamingContent)
                                                    : rawStreamingContent
                                            ) || '';

                                            const shopResult = parseShopResultBlock(visibleStreamingContent);
                                            const displayStreamingContent = contentWithoutShopResultBlock(visibleStreamingContent, shopResult);

                                            if (displayStreamingContent || shopResult) {
                                                return (
                                                    <>
                                                        {displayStreamingContent ? renderMessageWithLinkCards(stripQuestionLabel(displayStreamingContent), {
                                                            suppressRawLinkLists: true,
                                                            partsCards: buildPartsCards(activeMessage?.partsData),
                                                        }) : null}
                                                        {shopResult && <ShopResultsButton result={shopResult} onOpen={(result) => openGarageForShop(null, result)} />}
                                                    </>
                                                );
                                            }

                                            return (
                                                <p className="my-0 text-[10px] md:text-xs font-black text-black/40 uppercase tracking-[0.2em] italic">
                                                    {status || 'Reviewing your request...'}
                                                </p>
                                            );
                                        })()}
                                        
                                        {activeMessage.isLoadingGlovebox && (
                                            <div className="mt-6 flex gap-4 overflow-x-auto pb-4 px-1 -mx-1">
                                                {[1, 2].map(i => (
                                                    <div key={i} className="flex-shrink-0 w-64 h-28 bg-neutral-50 border border-neutral-100 rounded-2xl p-4 animate-pulse">
                                                        <div className="flex items-center gap-3 mb-4">
                                                            <div className="w-10 h-10 bg-neutral-200 rounded-xl" />
                                                            <div className="flex-1 space-y-2">
                                                                <div className="h-3 bg-neutral-200 rounded-full w-3/4" />
                                                                <div className="h-2 bg-neutral-200 rounded-full w-1/2" />
                                                            </div>
                                                        </div>
                                                        <div className="h-2 bg-neutral-100 rounded-full w-1/4" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                    </div>
                                    {Array.isArray(activeMessage?.suggestions) && withoutPartsFlowSuggestions(activeMessage.suggestions).length > 0 ? (
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {withoutPartsFlowSuggestions(activeMessage.suggestions).map((reply, rIdx) => (
                                                <button
                                                    key={`${reply}-${rIdx}`}
                                                    type="button"
                                                    onClick={() => sendMessage(reply)}
                                                    className="px-4 py-2 rounded-full text-xs font-bold text-[#414141] bg-[#F9F9F9] hover:bg-[#F2F2F2] hover:border-[#414141] border border-[#d9d9d9] shadow-sm transition-all duration-200 active:scale-95 whitespace-normal break-words text-center leading-snug flex max-w-full items-center justify-center min-h-[36px]"
                                                >
                                                    {reply}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mt-4 flex flex-wrap gap-2" aria-hidden="true">
                                            {[0, 1, 2].map(i => (
                                                <div
                                                    key={i}
                                                    className={`h-9 rounded-full bg-white/55 border border-[#d9d9d9]/70 shadow-sm animate-pulse ${
                                                        i === 0 ? 'w-32' : i === 1 ? 'w-40' : 'w-28'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Auto-generated report skeleton. Show it ONLY while a report is ACTUALLY
                            being generated (autoReportGenerating). It must never appear during a
                            normal conversation pause — even right after a "report-ready" answer —
                            because the user could keep chatting instead of generating a report.
                            Derived (no state/effect), so it can't get stuck: it disappears the
                            moment generation finishes or a message-based loader takes over. */}
                        {(() => {
                            const reportGenerationImminent = autoReportGenerating;
                            if (!reportGenerationImminent || messages.some((m) => m.isLoadingReport)) return null;
                            return (
                                <div className="order-[48] flex gap-4 md:gap-6 w-full animate-in fade-in">
                                    <DashAvatar />
                                    <div className="flex-1 pt-1">
                                        <ReportGenerationSkeleton status="Generating diagnostic report..." />
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Status / Streaming indicator */}
                        {status && !activeMessage && !autoReportGenerating && (
                            <div className="order-[50] flex gap-4 md:gap-6 w-full animate-in fade-in">
                                <DashAvatar thinking tone="light" />
                                <div className="flex-1 pt-1.5">
                                    <p className="text-[10px] md:text-xs font-black text-black/40 uppercase tracking-[0.2em] flex items-center gap-2 italic">
                                        {status}
                                    </p>
                                </div>
                            </div>
                        )}
                        <div ref={messageEndRef} className="order-[60] h-4" />
                            </>
                        )}
                    </div>
                </div>

                <ScrollToBottomButton
                    visible={!shouldAutoScroll}
                    onClick={() => scrollToBottom('smooth')}
                />

                {/* Floating Bottom Input — ChatGPT/Claude style. On the Add-Vehicle tab it stays
                    visible; typing there starts a normal chat (the tab persists in the sidebar). */}
                <div
                    className="fixed inset-x-0 bottom-[var(--mobile-composer-bottom)] w-full px-3 shrink-0 z-40 transition-[bottom] duration-100 ease-out sm:px-4 md:relative md:inset-auto md:bottom-auto md:z-10 md:px-6 md:duration-200 md:ease-[cubic-bezier(0.2,0,0,1)] lg:px-6 pb-[max(8px,env(safe-area-inset-bottom))] md:pb-[max(clamp(14px,2.6vh,28px),env(safe-area-inset-bottom))]"
                    style={{
                        '--mobile-composer-bottom': mobileComposerBottom,
                        willChange: 'bottom',
                    }}
                >
                    <div className="mx-auto w-full max-w-[520px] sm:max-w-[620px] md:max-w-[680px] lg:max-w-3xl">

                        {/* Safety Notice Pill (only shown on empty/new chat) */}
                        {!showEmptyChatIntro && !isAddVehicleCard && messages.length === 0 && !isInitialLoading && (
                            <div className="flex justify-center mb-4 select-none">
                                <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-black/5 bg-[#fafafa]/90 text-[11px] md:text-xs font-semibold text-black/55 shadow-[0_2px_8px_rgba(0,0,0,0.02)] animate-in fade-in slide-in-from-bottom-2 duration-300">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-black/35 flex-shrink-0">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    </svg>
                                    <span>
                                        <span className="font-black text-black/75">Safety notice:</span> CarFido provides general guidance only — use caution and verify critical repairs.
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Legal notice, shown only before the user starts a new conversation */}
                        {!isAddVehicleCard && messages.length === 0 && !isInitialLoading && (
                            <div className="mb-2 text-center text-[11px] font-medium text-fg-muted leading-normal animate-in fade-in duration-500">
                                {renderWithPolicyLinks(ANONYMOUS_CHAT_NOTICE)}
                            </div>
                        )}

                        {/* Attachment Preview Tray */}
                        {attachments.length > 0 && (
                            <div className="mb-2 animate-in slide-in-from-bottom-2">
                                <AttachmentPreviewList files={attachments} variant="composer" onRemove={removeAttachment} onOpenImage={setAttachmentPreview} />
                            </div>
                        )}
                        {attachmentError && (
                            <div className="mb-2 flex items-start gap-2 rounded-2xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-xs font-black text-[#b42318]" role="alert">
                                <span className="min-w-0 flex-1">{attachmentError}</span>
                                <button
                                    type="button"
                                    onClick={() => setAttachmentError('')}
                                    className="-mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#b42318] transition hover:bg-[#ffdede]"
                                    aria-label="Close upload warning"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* The floating pill — glass morphism card */}
                        <form
                            onSubmit={handleSend}
                            className={`relative flex items-end gap-1 bg-white/85 backdrop-blur-xl rounded-[26px] border border-[#D9D9D9] shadow-[0_8px_40px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.05)] focus-within:border-[#D81F26] focus-within:shadow-[0_8px_48px_rgba(0,0,0,0.12),0_2px_12px_rgba(0,0,0,0.07)] transition-[border-color,box-shadow] duration-300 px-2 py-2 md:rounded-[30px] md:px-2.5 md:py-2.5 ${showEmptyChatIntro ? 'animate-dash-composer-in' : ''}`}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                onChange={handleFileChange}
                                accept={CHAT_FILE_INPUT_ACCEPT}
                                multiple
                            />

                            {/* Attach button */}
                            <button
                                type="button"
                                onClick={() => {
                                    if (isShopMode || isSendingShopMessage) return;
                                    setAttachmentError('');
                                    fileInputRef.current?.click();
                                }}
                                disabled={isShopMode || isSendingShopMessage}
                                className={`flex-shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full transition-all ${
                                    isShopMode || isSendingShopMessage
                                        ? 'cursor-not-allowed text-black/15'
                                        : attachments.length > 0
                                        ? 'bg-[#F9F9F9] text-[#414141]'
                                        : 'text-black/30 hover:text-black/60 hover:bg-black/5'
                                }`}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                            </button>

                            {/* Textarea */}
                            <textarea
                                ref={chatInputRef}
                                value={input}
                                onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setInput(nextValue);
                                    if (pendingVoiceDraft && !nextValue.trim()) {
                                        setPendingVoiceDraft(false);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (!isDashResponding) {
                                            handleSend(e);
                                        }
                                    }
                                }}
                                placeholder={finalPlaceholder}
                                rows={1}
                                maxLength={4000}
                                disabled={isComposerBusy}
                                autoFocus={!isMobileView}
                                onPointerDown={() => { lastComposerPointerDownRef.current = Date.now(); }}
                                onFocus={() => {
                                    setIsChatInputFocused(true);
                                    // Only auto-scroll to the latest message when the user
                                    // INTENTIONALLY focused the composer (tapped/clicked it).
                                    // A window-restore focus — e.g. after taking a screenshot
                                    // or switching tabs/apps — must NOT move the scroll position.
                                    const userInitiated = (Date.now() - (lastComposerPointerDownRef.current || 0)) < 700;
                                    if (!userInitiated) return;
                                    setShouldAutoScroll(true);
                                    const scrollBehavior = isMobileViewport() ? 'auto' : 'smooth';
                                    window.setTimeout(() => scrollToBottom(scrollBehavior), 90);
                                    window.setTimeout(() => scrollToBottom(scrollBehavior), 220);
                                }}
                                onBlur={() => setIsChatInputFocused(false)}
                                style={{ height: 'auto' }}
                                className={`flex-1 min-w-0 bg-transparent border-none py-2 px-1 text-[15px] text-black font-medium outline-none resize-none max-h-32 custom-scrollbar placeholder:text-black/25 placeholder:text-sm placeholder:truncate md:max-h-40 md:py-2.5 md:text-base md:placeholder:text-[15px] ${
                                    isComposerBusy ? 'opacity-40' : ''
                                }`}
                            />

                            {/* Character counter */}
                            {input.length > 3000 && (
                                <span className="absolute -top-5 right-4 text-[9px] font-black text-black/20">
                                    {input.length}/4000
                                </span>
                            )}

                            {/* Transcription button */}
                            <button
                                type="button"
                                onClick={() => {
                                    if (isShopMode) return;
                                    if (handsFree.enabled) {
                                        handsFree.stop();
                                    }
                                    toggleRecording();
                                }}
                                disabled={isShopMode || isTranscribingVoiceDraft || isDashResponding || isRepairGuideActive}
                                className={`flex-shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full transition-all ${
                                    isRecording
                                    ? 'bg-red-500 text-white animate-pulse shadow-[0_0_12px_rgba(239,68,68,0.5)]'
                                    : isShopMode || isTranscribingVoiceDraft || isDashResponding
                                    ? 'text-black/15 cursor-not-allowed'
                                    : 'text-black/30 hover:text-black/60 hover:bg-black/5'
                                }`}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                            </button>

                            {isDashResponding ? (
                                <button
                                    type="button"
                                    onClick={() => activeResponseStopRef.current?.()}
                                    aria-label="Stop response"
                                    title="Stop response"
                                    className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black text-white shadow-[0_4px_14px_rgba(0,0,0,0.25)] transition-all duration-200 hover:shadow-[0_6px_20px_rgba(0,0,0,0.3)] hover:scale-105 active:scale-95"
                                >
                                    <span className="w-3.5 h-3.5 rounded-[3px] bg-white" aria-hidden="true" />
                                </button>
                            ) : canShowSendButton ? (
                                <button
                                    type="submit"
                                    onPointerDown={(event) => event.preventDefault()}
                                    disabled={!canSubmitMessage}
                                    aria-label="Send message"
                                    className={`flex-shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full transition-all duration-200 ${
                                        !canSubmitMessage
                                        ? 'bg-black/8 text-black/20 cursor-not-allowed'
                                        : 'bg-black text-white shadow-[0_4px_14px_rgba(0,0,0,0.25)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.3)] hover:scale-105 active:scale-95'
                                    }`}
                                >
                                    {isTranscribingVoiceDraft ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>
                                    )}
                                </button>
                            ) : (
                                // Hands-free voice button temporarily disabled. To restore, delete the
                                // `null` below and uncomment the block. This branch is the tail of a
                                // ternary, so it needs an expression — hence `null` rather than nothing.
                                null
                                /*
                                <HandsFreeVoiceButton
                                    mode={handsFree.mode}
                                    label={handsFree.label}
                                    enabled={handsFree.enabled}
                                    disabled={isShopMode || isLoading || isRecording || isTranscribingVoiceDraft || authState?.status === 'loading' || isRepairGuideActive}
                                    error={handsFree.error}
                                    onToggle={handsFree.toggle}
                                />
                                */
                            )}

                            {/* Recording overlay inside form area */}
                            {isRecording && (
                                <div className="absolute inset-0 bg-[#F9F9F9]/95 backdrop-blur-md rounded-[28px] flex items-center px-4 gap-4 animate-in fade-in shadow-[0_4px_20px_rgba(239,68,68,0.08)] z-20">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                        <span className="text-[11px] font-black text-red-500 tabular-nums">{formatTime(recordingSeconds)}</span>
                                    </div>
                                    <div className="flex-1 flex gap-0.5 justify-center items-center h-10">
                                        {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(i => (
                                            <div key={i} className="w-1 bg-red-400/40 rounded-full animate-voice-bar" style={{ height: `${Math.random() * 12 + 6}px`, animationDelay: `${i * 0.05}s` }} />
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button 
                                            type="button"
                                            onClick={() => stopRecording('transcribe')}
                                            aria-label="Stop recording"
                                            className="h-9 px-3 flex items-center justify-center gap-2 text-black/50 hover:text-red-500 rounded-full hover:bg-red-50 transition-all border border-red-100/80 bg-white/80"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                <rect x="6" y="6" width="12" height="12" rx="2" />
                                            </svg>
                                            {/* <span className="text-[10px] font-black uppercase tracking-[0.18em]"></span> */}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={sendRecording}
                                            aria-label="Send recording"
                                            className="h-9 px-3 flex items-center justify-center gap-2 rounded-full bg-black text-white shadow-[0_4px_14px_rgba(0,0,0,0.18)] hover:scale-[1.02] active:scale-95 transition-all"
                                        >
                                            {/* <span className="text-[10px] font-black uppercase tracking-[0.18em]"></span> */}
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="m5 12 7-7 7 7"/>
                                                <path d="M12 19V5"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>

                    </div>
                </div>
            </div>


            {/* Slide-out Overlay Gallery */}
            <div 
                className={`fixed inset-0 z-[100] transition-all duration-300 ${showGloveboxOverlay ? 'visible' : 'invisible pointer-events-none'}`}
            >
                {/* Backdrop */}
                <div 
                    className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${showGloveboxOverlay ? 'opacity-100' : 'opacity-0'}`}
                    onClick={() => setShowGloveboxOverlay(false)}
                />
                
                {/* Panel */}
                <div 
                    className={`absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl transition-transform duration-500 ease-out transform ${showGloveboxOverlay ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
                >
                    <div className="p-6 border-b border-neutral-100 flex items-center justify-between bg-neutral-50/50">
                        <div>
                            <h3 className="text-xl font-black text-black leading-tight">{overlayTitle}</h3>
                            <p className="text-[11px] font-bold text-neutral-400 uppercase tracking-widest mt-1">Complete History</p>
                        </div>
                        <button 
                            onClick={() => setShowGloveboxOverlay(false)}
                            className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 hover:text-black transition-all shadow-sm"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-4 bg-white">
                        {overlayDocs.map((doc, i) => (
                            <a 
                                key={i} 
                                href={doc.url || '#'} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="group flex items-center gap-4 bg-neutral-50 p-4 rounded-2xl border border-neutral-100 hover:bg-white hover:border-neutral-300 hover:shadow-lg transition-all duration-300 no-underline"
                            >
                                <div className="w-14 h-14 bg-white rounded-xl shadow-sm border border-neutral-100 flex items-center justify-center text-2xl group-hover:scale-105 transition-transform">
                                    {doc.icon || '📄'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-black text-black truncate mb-0.5">{doc.name}</h4>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-tight">{doc.label}</span>
                                        <span className="w-1 h-1 bg-neutral-300 rounded-full" />
                                        <span className="text-[10px] font-bold text-neutral-400 tabular-nums">{doc.date}</span>
                                    </div>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-neutral-200/50 flex items-center justify-center text-neutral-500 group-hover:bg-black group-hover:text-white transition-all">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                </div>
                            </a>
                        ))}
                    </div>
                    
                    <div className="p-6 border-t border-neutral-100 bg-neutral-50/50">
                        <p className="text-[10px] text-center text-neutral-400 font-bold uppercase tracking-widest">
                            {overlayDocs.length} Documents Found in History
                        </p>
                    </div>
                </div>
            </div>

            <AttachmentImageModal file={attachmentPreview} onClose={() => setAttachmentPreview(null)} />

            <style dangerouslySetInnerHTML={{ __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.05); border-radius: 10px; }
                .custom-scrollbar:hover::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); }
                @keyframes voice-bar {
                    0%, 100% { transform: scaleY(1); }
                    50% { transform: scaleY(2); }
                }
                .animate-voice-bar {
                    animation: voice-bar 0.6s ease-in-out infinite;
                }
                @keyframes dash-thinking-ring {
                    0%, 100% { opacity: 0.36; transform: scale(0.96); }
                    50% { opacity: 0.9; transform: scale(1.08); }
                }
                @keyframes dash-thinking-logo {
                    0%, 100% { opacity: 0.82; }
                    50% { opacity: 1; }
                }
                .dash-thinking-ring {
                    animation: dash-thinking-ring 1.8s ease-in-out infinite;
                    transform-origin: center;
                }
                .dash-thinking-logo {
                    animation: dash-thinking-logo 2.2s ease-in-out infinite;
                }
                .playing svg { color: #ef4444; }
                textarea:focus { outline: none; box-shadow: none; }
                .flex-1.overflow-y-auto { -webkit-overflow-scrolling: touch; }
            `}} />
        </div>
    );
};

export default ChatPage;
